import { DEFAULT_LANGUAGE, type AppLanguage, getCurrentLanguage } from "./config";

type TranslationMaps = Record<Exclude<AppLanguage, "pt-BR">, Record<string, string>>;

export type TranslationParameters = Record<string, string | number>;

type PatternRule = {
  pattern: RegExp;
  replacements: Record<Exclude<AppLanguage, "pt-BR">, string>;
};

type GlossaryEntry = {
  source: string;
  target: string;
};

const EXACT_TRANSLATIONS: TranslationMaps = {
  "pt-PT": {
    "Categorias financeiras": "Categorias financeiras",
    "Gerencie separadamente as categorias usadas em receitas e despesas.": "Faça a gestão separada das categorias usadas em receitas e despesas.",
    "Tipo de categoria financeira": "Tipo de categoria financeira",
    "Nova categoria": "Nova categoria",
    "Renomear uma categoria não altera o nome preservado nos lançamentos anteriores. Categorias inativas continuam no histórico, mas não podem ser usadas em novos lançamentos.": "Alterar o nome de uma categoria não altera o nome preservado nos registos anteriores. As categorias inativas continuam no histórico, mas não podem ser usadas em novos registos.",
    "Carregando categorias...": "A carregar categorias...",
    "Não foi possível carregar as categorias.": "Não foi possível carregar as categorias.",
    "Nenhuma categoria de receita cadastrada.": "Nenhuma categoria de receita registada.",
    "Nenhuma categoria de despesa cadastrada.": "Nenhuma categoria de despesa registada.",
    "Editar categoria": "Editar categoria",
    "Nome da categoria": "Nome da categoria",
    "Informe o nome da categoria.": "Introduza o nome da categoria.",
    "Categoria atualizada com sucesso.": "Categoria atualizada com sucesso.",
    "Categoria cadastrada com sucesso.": "Categoria registada com sucesso.",
    "Categoria desativada com sucesso.": "Categoria desativada com sucesso.",
    "Categoria reativada com sucesso.": "Categoria reativada com sucesso.",
    "Desativar categoria": "Desativar categoria",
    "Reativar categoria": "Reativar categoria",
    "A categoria deixará de aparecer em novos lançamentos. O histórico será preservado.": "A categoria deixará de aparecer em novos registos. O histórico será preservado.",
    "A categoria voltará a aparecer em novos lançamentos.": "A categoria voltará a aparecer em novos registos.",
    "Categoria de receita": "Categoria de receita",
    "Nenhuma categoria de receita ativa cadastrada.": "Nenhuma categoria de receita ativa registada.",
    "Nenhuma categoria de despesa ativa cadastrada.": "Nenhuma categoria de despesa ativa registada.",
    "Selecione uma categoria": "Selecione uma categoria",
    "Configurar categorias": "Configurar categorias",
    "Selecione uma categoria de receita.": "Selecione uma categoria de receita.",
    "Selecione uma categoria de despesa.": "Selecione uma categoria de despesa.",
    "Avaliação vinculada": "Avaliação associada",
    "Modelo clínico registrado": "Modelo clínico registado",
    "Editar registros de outros profissionais": "Editar registos de outros profissionais",
    "Adicionar correções a registros finalizados": "Adicionar correções a registos finalizados",
    "Autorizações clínicas explícitas": "Autorizações clínicas explícitas",
    "Estas autorizações clínicas sensíveis não são concedidas automaticamente ao administrador.": "Estas autorizações clínicas sensíveis não são concedidas automaticamente ao administrador.",
    "Existem alterações não salvas. Deseja descartá-las?": "Existem alterações não guardadas. Deseja descartá-las?",
    "Avaliação atualizada com sucesso.": "Avaliação atualizada com sucesso.",
    "Este registro foi alterado por outro usuário enquanto você o editava. Atualize os dados antes de salvar novamente.": "Este registo foi alterado por outro utilizador enquanto o editava. Atualize os dados antes de guardar novamente.",
    "Atualizar registro": "Atualizar registo",
    "Editar avaliação clínica": "Editar avaliação clínica",
    "Finalizado": "Finalizado",
    "Finalizar registro clínico": "Finalizar registo clínico",
    "Após finalizar, este registro não poderá mais ser alterado diretamente. Se for necessário corrigir alguma informação depois, será criada uma correção mantendo o conteúdo original no histórico.": "Após finalizar, este registo deixará de poder ser alterado diretamente. Se for necessário corrigir alguma informação, será criada uma correção que mantém o conteúdo original no histórico.",
    "Finalizando...": "A finalizar...",
    "Finalizar registro": "Finalizar registo",
    "Adicionar correção": "Adicionar correção",
    "O conteúdo original será preservado. A correção será acrescentada ao histórico do registro.": "O conteúdo original será preservado. A correção será acrescentada ao histórico do registo.",
    "Motivo da correção": "Motivo da correção",
    "Informe pelo menos 10 caracteres.": "Introduza pelo menos 10 caracteres.",
    "Conteúdo da correção": "Conteúdo da correção",
    "Correção adicionada ao histórico.": "Correção adicionada ao histórico.",
    "Informe um motivo com pelo menos 10 caracteres.": "Introduza um motivo com pelo menos 10 caracteres.",
    "Informe o conteúdo da correção.": "Introduza o conteúdo da correção.",
    "Registro finalizado com sucesso.": "Registo finalizado com sucesso.",
    "Criado por": "Criado por",
    "Autoria não disponível": "Autoria não disponível",
    "Última alteração por": "Última alteração por",
    "Finalizado por": "Finalizado por",
    "Editar avaliação": "Editar avaliação",
    "Finalizar": "Finalizar",
    "Ver histórico": "Ver histórico",
    "Histórico do registro clínico": "Histórico do registo clínico",
    "Linha do tempo": "Cronologia",
    "Carregando histórico...": "A carregar histórico...",
    "Correção adicionada": "Correção adicionada",
    "Primeiro estado disponível no histórico": "Primeiro estado disponível no histórico",
    "Registro criado": "Registo criado",
    "Registro finalizado": "Registo finalizado",
    "Revisão salva": "Revisão guardada",
    "Revisão": "Revisão",
    "Carregar mais": "Carregar mais",
    "Este é o primeiro estado disponível após a adoção do histórico. Não há versões anteriores reconstruíveis.": "Este é o primeiro estado disponível após a adoção do histórico. Não existem versões anteriores que possam ser reconstruídas.",
    "versão": "versão",
    "Sem modelo clínico registrado": "Sem modelo clínico registado",
    "Documentos desta revisão": "Documentos desta revisão",
    "O conteúdo original foi preservado. Esta correção foi acrescentada ao histórico.": "O conteúdo original foi preservado. Esta correção foi acrescentada ao histórico.",
    "Correção adicionada por": "Correção adicionada por",
    "Escolha uma revisão ou correção para consultar o conteúdo preservado.": "Escolha uma revisão ou correção para consultar o conteúdo preservado.",
    "Selecione um item do histórico": "Selecione um item do histórico",
    "Plano cancelado com segurança.": "Plano cancelado com segurança.",
    "Plano reativado com segurança.": "Plano reativado com segurança.",
    "Cancelar Plano de Tratamento": "Cancelar Plano de Tratamento",
    "Reativar Plano de Tratamento": "Reativar Plano de Tratamento",
    "Sessões utilizadas": "Sessões utilizadas",
    "{count} sessão restante": "{count} sessão restante",
    "{count} sessões restantes": "{count} sessões restantes",
    "{count} recebível": "{count} conta a receber",
    "{count} recebíveis": "{count} contas a receber",
    "{count} pagamento": "{count} pagamento",
    "{count} pagamentos": "{count} pagamentos",
    "{count} receita": "{count} receita",
    "{count} receitas": "{count} receitas",
    "Verificando efeitos do cancelamento...": "A verificar os efeitos do cancelamento...",
    "Não foi possível verificar os efeitos do cancelamento. Feche o diálogo e tente novamente.": "Não foi possível verificar os efeitos do cancelamento. Feche a janela e tente novamente.",
    "Consequências do cancelamento": "Consequências do cancelamento",
    "Cancelar o Plano impede seu uso futuro, mas não apaga atendimentos, pagamentos, produções ou repasses já registrados. Pendências financeiras serão encaminhadas para revisão.": "Cancelar o Plano impede a sua utilização futura, mas não elimina atendimentos, pagamentos, produções ou compensações profissionais já registados. As pendências financeiras serão encaminhadas para revisão.",
    "Agendamentos futuros": "Marcações futuras",
    "Saldo financeiro": "Saldo financeiro",
    "Situação do NPS": "Situação do NPS",
    "Sem solicitação": "Sem pedido",
    "Estes agendamentos serão cancelados": "Estas marcações serão canceladas",
    "Financeiro preservado e enviado para revisão": "Informação financeira preservada e enviada para revisão",
    "recebíveis": "contas a receber",
    "pagamentos": "pagamentos",
    "receitas": "receitas",
    "pago": "pago",
    "Efeitos da reativação": "Efeitos da reativação",
    "Agendamentos cancelados não serão restaurados automaticamente.": "As marcações canceladas não serão restauradas automaticamente.",
    "A reativação não cria uma nova venda nem resolve pendências financeiras.": "A reativação não cria uma nova venda nem resolve pendências financeiras.",
    "Tokens NPS revogados não serão restaurados automaticamente.": "Os tokens NPS revogados não serão restaurados automaticamente.",
    "O status será recalculado conforme validade, venda e uso efetivo do Plano.": "O estado será recalculado conforme a validade, a venda e a utilização efetiva do Plano.",
    "Motivo da reativação": "Motivo da reativação",
    "Descreva o motivo com detalhes": "Descreva o motivo com detalhe",
    "Informe pelo menos 30 caracteres.": "Introduza pelo menos 30 caracteres.",
    "Cancelando Plano...": "A cancelar o Plano...",
    "Reativando Plano...": "A reativar o Plano...",
    "Confirmar cancelamento": "Confirmar cancelamento",
    "Confirmar reativação": "Confirmar reativação",
    "Reativar Plano": "Reativar Plano",
    "Cancelar Plano": "Cancelar Plano",
    "Ciclo do atendimento": "Ciclo do atendimento",
    "Este atendimento possui efeitos operacionais materializados.": "Este atendimento possui efeitos operacionais materializados.",
    "A conclusão registra produção, repasse e os demais efeitos aplicáveis.": "A conclusão regista produção, compensação profissional e os restantes efeitos aplicáveis.",
    "Concluir atendimento": "Concluir atendimento",
    "Reverter conclusão": "Reverter conclusão",
    "Você não possui as permissões necessárias para reverter esta conclusão.": "Não possui as permissões necessárias para reverter esta conclusão.",
    "Concluir atendimento?": "Concluir atendimento?",
    "A conclusão registrará produção, repasse, progresso do plano e financeiro quando aplicável.": "A conclusão registará produção, compensação profissional, progresso do plano e financeiro quando aplicável.",
    "Concluindo...": "A concluir...",
    "Efeitos da reversão": "Efeitos da reversão",
    "A produção ativa será cancelada e preservada no histórico.": "A produção ativa será cancelada e preservada no histórico.",
    "O repasse relacionado será cancelado ou marcado para tratamento administrativo.": "A compensação profissional associada será cancelada ou marcada para tratamento administrativo.",
    "As sessões utilizadas e o status do plano serão recalculados.": "As sessões utilizadas e o estado do plano serão recalculados.",
    "Uma entrega NPS ativa poderá ser invalidada até uma nova conclusão.": "Uma entrega NPS ativa poderá ser invalidada até uma nova conclusão.",
    "A reversão do atendimento não cancela nem estorna automaticamente os registros financeiros existentes. Eles serão sinalizados para revisão.": "A reversão do atendimento não cancela nem estorna automaticamente os registos financeiros existentes. Estes serão assinalados para revisão.",
    "Se existir repasse relacionado, a operação exigirá a permissão para cancelar repasses.": "Se existir compensação profissional associada, a operação exigirá permissão para a cancelar.",
    "Novo estado operacional": "Novo estado operacional",
    "Para remarcar, reverta primeiro e depois utilize a ação específica de remarcação.": "Para remarcar, reverta primeiro e depois utilize a ação específica de remarcação.",
    "Explique por que a conclusão precisa ser revertida": "Explique por que motivo a conclusão precisa de ser revertida",
    "Informe pelo menos 30 caracteres para registrar a reversão.": "Introduza pelo menos 30 caracteres para registar a reversão.",
    "Confirmar reversão": "Confirmar reversão",
    "Atendimento concluído com sucesso.": "Atendimento concluído com sucesso.",
    "Não foi possível concluir o atendimento.": "Não foi possível concluir o atendimento.",
    "Conclusão revertida. Os registros financeiros relacionados foram sinalizados para revisão.": "Conclusão revertida. Os registos financeiros associados foram assinalados para revisão.",
    "Conclusão revertida com sucesso.": "Conclusão revertida com sucesso.",
    "O repasse possui pagamentos preservados e requer tratamento administrativo.": "A compensação profissional possui pagamentos preservados e requer tratamento administrativo.",
    "Não foi possível reverter a conclusão.": "Não foi possível reverter a conclusão.",
    "Utilize a ação Concluir atendimento para registrar a conclusão.": "Utilize a ação Concluir atendimento para registar a conclusão.",
    "Este atendimento já foi concluído. Utilize Reverter conclusão para alterar seu estado.": "Este atendimento já foi concluído. Utilize Reverter conclusão para alterar o seu estado.",
    "Restaure primeiro o agendamento para um estado operacional antes de executar esta ação.": "Restaure primeiro o agendamento para um estado operacional antes de executar esta ação.",
    "Status atual": "Estado atual",
    "Destino após a reversão": "Destino após a reversão",
    "Verificando efeitos da reversão...": "A verificar os efeitos da reversão...",
    "Não foi possível verificar os efeitos da reversão. Feche o diálogo e tente novamente.": "Não foi possível verificar os efeitos da reversão. Feche o diálogo e tente novamente.",
    "Existem pagamentos de repasse relacionados. Eles serão preservados e o repasse será marcado para tratamento administrativo.": "Existem pagamentos de compensação profissional associados. Serão preservados e a compensação será assinalada para tratamento administrativo.",
    "Revisão financeira necessária": "Revisão financeira necessária",
    "Este registro financeiro foi preservado e precisa de uma decisão financeira explícita.": "Este registo financeiro foi preservado e necessita de uma decisão financeira explícita.",
    "A conclusão do atendimento foi revertida. Este registro foi preservado e precisa de decisão financeira.": "A conclusão do atendimento foi revertida. Este registo foi preservado e precisa de decisão financeira.",
    "Confirmar manutenção do recebível": "Confirmar manutenção do valor a receber",
    "Confirme somente se a cobrança deve permanecer válida após a reversão clínica.": "Confirme apenas se a cobrança deve permanecer válida após a reversão clínica.",
    "Justificativa financeira": "Justificação financeira",
    "Registre por que o recebível deve ser mantido": "Registe por que motivo o valor a receber deve ser mantido",
    "Confirmar manutenção": "Confirmar manutenção",
    "Manutenção do recebível confirmada.": "Manutenção do valor a receber confirmada.",
    "Trilha de navegação": "Navegação estrutural",
    "Mais": "Mais",
    "Slide anterior": "Diapositivo anterior",
    "Próximo slide": "Diapositivo seguinte",
    "Paginação": "Paginação",
    "Ir para a página anterior": "Ir para a página anterior",
    "Ir para a próxima página": "Ir para a página seguinte",
    "Mais páginas": "Mais páginas",
    "Atendimentos realizados no período": "Atendimentos realizados no período",
    "Agendados": "Marcados",
    "Não agendados": "Não marcados",
    "Leads por status": "Leads por estado",
    "Agendamentos vinculados e lançamentos manuais": "Marcações associadas e registos manuais de receita",
    "Rotina clínica": "Rotina clínica",
    "Gestão": "Gestão",
    "Alternar menu Financeiro": "Alternar menu Financeiro",
    "Navegação principal": "Navegação principal",
    "Trocar paciente": "Mudar paciente",
    "Selecione uma forma de pagamento para registrar o recebimento.": "Selecione uma forma de pagamento para registar o recebimento.",
    "Acompanhe recebimentos, valores a receber e despesas sem misturar caixa e competência.": "Acompanhe recebimentos, valores a receber e despesas sem misturar caixa e competência.",
    "Onde recebeu": "Onde recebeu",
    "Recebimentos distribuídos por setor, conforme a data de pagamento.": "Recebimentos distribuídos por setor, de acordo com a data de pagamento.",
    "Nenhum recebimento com setor vinculado no período.": "Nenhum recebimento com setor associado no período.",
    "Quem recebeu": "Quem recebeu",
    "Recebimentos distribuídos por profissional, conforme a data de pagamento.": "Recebimentos distribuídos por profissional, de acordo com a data de pagamento.",
    "Nenhum recebimento vinculado a profissional no período.": "Nenhum recebimento associado a profissional no período.",
    "Leitura dos indicadores financeiros": "Leitura dos indicadores financeiros",
    "Os cards de recebimento usam a data de pagamento. As despesas usam a data de competência e não compõem resultado de caixa nesta tela.": "Os cartões de recebimento usam a data de pagamento. As despesas usam a data de competência e não compõem resultado de caixa neste ecrã.",
    "Valor comercial confirmado; não representa recebimento.": "Valor comercial confirmado; não representa recebimento.",
    "Entrada registrada por data de pagamento.": "Entrada registada por data de pagamento.",
    "Saldo pendente de cobrança; não representa entrada em caixa.": "Saldo pendente de cobrança; não representa entrada em caixa.",
    "Serviço executado, separado de venda e recebimento.": "Serviço executado, separado de venda e recebimento.",
    "A competência reconhece cada evento no período correspondente; ela é analisada separadamente do caixa.": "A competência reconhece cada evento no período correspondente; é analisada separadamente do caixa.",
    "A DRE e o rateio gerencial analisam competência; o rateio não altera o caixa.": "A DRE e a repartição gerencial analisam competência; a repartição não altera o caixa.",
    "Caixa bruto": "Caixa bruta",
    "Caixa líquido": "Caixa líquida",
    "Saídas efetivas": "Saídas efetivas",
    "Resultado de caixa": "Resultado de caixa",
    "Caixa líquido menos saídas efetivas, sem apuração contábil.": "Caixa líquido menos saídas efetivas, sem apuração contábil.",
    "Ticket médio de caixa": "Ticket médio de caixa",
    "Recebimentos líquidos por evento de caixa": "Recebimentos líquidos por evento de caixa",
    "Indicadores contábeis indisponíveis": "Indicadores contabilísticos indisponíveis",
    "Relatórios de competência e indicadores unitários dependem de produção canônica, competência e custos confiáveis.": "Relatórios de competência e indicadores unitários dependem de produção canónica, competência e custos fiáveis.",
    "Valor vendido/cobrado": "Valor vendido/cobrado",
    "Total estornado": "Total estornado",
    "Saldo vencido": "Saldo vencido",
    "Não foi possível carregar as despesas.": "Não foi possível carregar as despesas.",
    "Despesa registrada.": "Despesa registada.",
    "Recorrência criada.": "Recorrência criada.",
    "Pagamento registrado.": "Pagamento registado.",
    "Reversão operacional": "Reversão operacional",
    "Reversão registrada.": "Reversão registada.",
    "Cancelamento operacional": "Cancelamento operacional",
    "Despesa cancelada.": "Despesa cancelada.",
    "Despesas e saídas": "Despesas e saídas",
    "Obrigações, pagamentos e resultado de caixa sem rateio automático.": "Obrigações, pagamentos e resultado de caixa sem rateio automático.",
    "Aberta": "Aberta",
    "Pagamento parcial": "Pagamento parcial",
    "Paga": "Paga",
    "Vencida": "Vencida",
    "Direta do setor": "Direta do setor",
    "Direta do profissional": "Direta do profissional",
    "Corporativa não alocada": "Corporativa não alocada",
    "Nova despesa": "Nova despesa",
    "Outras": "Outras",
    "Pagamento imediato": "Pagamento imediato",
    "Nova recorrência": "Nova recorrência",
    "Dia de vencimento": "Dia de vencimento",
    "Criar recorrência": "Criar recorrência",
    "Nenhuma despesa encontrada": "Nenhuma despesa encontrada",
    "Produção clínica": "Produção clínica",
    "Produções geradas por atendimento concluído, separadas do financeiro e prontas para conferência operacional.": "Produções geradas por atendimento concluído, separadas do financeiro e prontas para conferência operacional.",
    "Não foi possível carregar a produção clínica.": "Não foi possível carregar a produção clínica.",
    "Não foi possível carregar os filtros de produção clínica.": "Não foi possível carregar os filtros de produção clínica.",
    "Não foi possível gerar a produção clínica.": "Não foi possível gerar a produção clínica.",
    "Não foi possível cancelar a produção clínica.": "Não foi possível cancelar a produção clínica.",
    "Produção clínica gerada com sucesso.": "Produção clínica gerada com sucesso.",
    "Produção clínica cancelada com sucesso.": "Produção clínica cancelada com sucesso.",
    "Produções para revisar": "Producoes para rever",
    "Produzido": "Produzido",
    "Avulso": "Avulso",
    "Revisar valor": "Rever valor",
    "Filtros": "Filtros",
    "Todas": "Todas",
    "Cancelada": "Cancelada",
    "Classificação": "Classificação",
    "Venda do atendimento": "Venda da consulta",
    "Valor registrado no atendimento": "Valor registado na consulta",
    "Cortesia": "Cortesia",
    "Valor do atendimento não determinado": "Valor da consulta não determinado",
    "Sessão adicional sem aditivo comercial": "Sessão adicional sem aditamento comercial",
    "Venda de plano de tratamento": "Venda de plano de tratamento",
    "Valor registrado no plano de tratamento": "Valor registado no plano de tratamento",
    "Venda comercial": "Venda comercial",
    "Atendimento avulso": "Consulta avulsa",
    "Atendimento avulso para revisão": "Consulta avulsa para revisão",
    "Sessão adicional de plano": "Sessão adicional do plano",
    "Valor confirmado": "Valor confirmado",
    "Valor estimado": "Valor estimado",
    "Valor a revisar": "Valor a rever",
    "Origem não identificada": "Origem não identificada",
    "Tipo não identificado": "Tipo não identificado",
    "Classificação não identificada": "Classificação não identificada",
    "Sem venda, serviço ou valor operacional determinístico": "Sem venda, serviço ou valor operacional determinístico",
    "Motivo de revisão não identificado": "Motivo de revisão não identificado",
    "Recebimento": "Recebimento",
    "Estorno": "Reembolso",
    "Ajuste": "Ajuste",
    "Lançamento manual de caixa": "Registo manual de caixa",
    "Tipo de lançamento não identificado": "Tipo de registo não identificado",
    "Forma de pagamento não identificada": "Forma de pagamento não identificada",
    "Cartão de crédito": "Cartão de crédito",
    "Cartão de débito": "Cartão de débito",
    "Transferência": "Transferência bancária",
    "confirmed": "confirmado",
    "inferred": "inferido",
    "legacy": "legado",
    "unknown": "desconhecido",
    "commercial sale": "venda comercial",
    "treatment plan": "plano de tratamento",
    "appointment snapshot": "snapshot do atendimento",
    "courtesy": "cortesia",
    "additional session requires review": "sessão adicional requer revisão",
    "Detalhes": "Detalhes",
    "Nenhuma produção clínica encontrada": "Nenhuma produção clínica encontrada",
    "Detalhes da produção": "Detalhes da produção",
    "Sessão": "Sessão",
    "Cancelar produção clínica": "Cancelar produção clínica",
    "A produção será marcada como cancelada sem alterar vendas, recebíveis ou caixa.": "A produção será marcada como cancelada sem alterar vendas, recebíveis ou caixa.",
    "Cancelar produção": "Cancelar produção",
    "Cancelando...": "A cancelar...",
    "Gerar pendentes": "Gerar pendentes",
    "Configurações": "Configurações",
    "Central Administrativa": "Central Administrativa",
    "Administração": "Administração",
    "Categorias": "Categorias",
    "Carregando configurações...": "A carregar configurações...",
    "Navegação de configurações": "Navegação de configurações",
    "Seção": "Secção",
    "Voltar": "Voltar",
    "Voltar às configurações": "Voltar às configurações",
    "Seção não encontrada": "Secção não encontrada",
    "Nenhuma configuração encontrada para este endereço.": "Nenhuma configuração encontrada para este endereço.",
    "Centralize as configurações administrativas da clínica sem duplicar cadastros ou fontes de dados.": "Centralize as configurações administrativas da clínica sem duplicar registos ou fontes de dados.",
    "Clínica": "Clínica",
    "Configurações da clínica": "Configurações da clínica",
    "Dados institucionais, moeda e fuso horário da clínica.": "Dados institucionais, moeda e fuso horário da clínica.",
    "Operação": "Operação",
    "Organize setores usados por profissionais, agenda, filtros e relatórios.": "Organize setores usados por profissionais, agenda, filtros e relatórios.",
    "Status da Agenda": "Status da Agenda",
    "Configure nomes, cores, ordem e visibilidade dos status sem alterar os estados técnicos.": "Configure nomes, cores, ordem e visibilidade dos status sem alterar os estados técnicos.",
    "Não foi possível carregar os status configurados da agenda.": "Não foi possível carregar os status configurados da agenda.",
    "Catálogo visual de status": "Catálogo visual de status",
    "A configuração visual altera rótulos, cores, ordem e visibilidade sem mudar os estados técnicos usados nas regras da Agenda.": "A configuração visual altera rótulos, cores, ordem e visibilidade sem mudar os estados técnicos usados nas regras da Agenda.",
    "Restaurar padrão": "Restaurar padrão",
    "Salvar status": "Salvar status",
    "Status da agenda salvos.": "Status da agenda salvos.",
    "Seu perfil não possui permissão para alterar os status da Agenda.": "Seu perfil não possui permissão para alterar os status da Agenda.",
    "Revise nomes vazios, cores inválidas ou ordens negativas antes de salvar.": "Revise nomes vazios, cores inválidas ou ordens negativas antes de salvar.",
    "Estado técnico": "Estado técnico",
    "Nome exibido": "Nome exibido",
    "Cor": "Cor",
    "Ordem": "Ordem",
    "Prévia": "Prévia",
    "Serviços e Procedimentos": "Serviços e Procedimentos",
    "Gerencie o catálogo canônico usado na agenda, planos e financeiro.": "Faça a gestão do catálogo canónico usado na agenda, planos e financeiro.",
    "Atendimento clínico": "Atendimento clínico",
    "Modelos clínicos": "Modelos clínicos",
    "Crie e gerencie modelos reutilizáveis para avaliações, evoluções e outros registros clínicos.": "Crie e faça a gestão de modelos reutilizáveis para avaliações, evoluções e outros registos clínicos.",
    "Comercial": "Comercial",
    "Origens de Leads": "Origens de Leads",
    "Padronize as origens usadas no funil comercial e nos filtros.": "Padronize as origens usadas no funil comercial e nos filtros.",
    "Motivos de não agendamento": "Motivos de não marcação",
    "Mantenha o catálogo oficial usado nos desfechos comerciais.": "Mantenha o catálogo oficial usado nos desfechos comerciais.",
    "Financeiro": "Financeiro",
    "DRE Gerencial": "Demonstração de Resultados Gerencial",
    "Demonstrativo gerencial por competência, com caixa exibido em bloco comparativo separado.": "Demonstrativo gerencial por competência, com caixa exibido em bloco comparativo separado.",
    "Não foi possível carregar a DRE gerencial.": "Não foi possível carregar a DRE gerencial.",
    "Não foi possível carregar os filtros da DRE gerencial.": "Não foi possível carregar os filtros da DRE gerencial.",
    "Não foi possível carregar a origem da linha.": "Não foi possível carregar a origem da linha.",
    "Não foi possível carregar os detalhes da linha.": "Não foi possível carregar os detalhes da linha.",
    "Não foi possível exportar a DRE gerencial.": "Não foi possível exportar a DRE gerencial.",
    "Aviso gerencial": "Aviso de gestão",
    "Linha": "Linha",
    "Origem dos dados": "Origem dos dados",
    "Data considerada": "Data considerada",
    "Detalhes de {line}": "Detalhes de {line}",
    "Entender {line}": "Compreender {line}",
    "Ver detalhes de {line}": "Ver detalhes de {line}",
    "Nenhum detalhe encontrado": "Nenhum detalhe encontrado",
    "Despesas consideradas no rateio": "Despesas consideradas na repartição",
    "Competência versus caixa": "Competência versus caixa",
    "Receita reconhecida": "Receita reconhecida",
    "Caixa recebido": "Caixa recebido",
    "Custos e despesas reconhecidos": "Custos e despesas reconhecidos",
    "Qualidade dos dados": "Qualidade dos dados",
    "Origem da linha": "Origem da linha",
    "Nenhuma origem encontrada": "Nenhuma origem encontrada",
    "Nenhum demonstrativo encontrado": "Nenhum demonstrativo encontrado",
    "Visão": "Visão",
    "Visão original": "Visão original",
    "Visão pós-rateio": "Visão pós-rateio",
    "Comparar": "Comparar",
    "Impacto do rateio gerencial": "Impacto do rateio gerencial",
    "Análise exclusivamente gerencial": "Análise exclusivamente gerencial",
    "Análise por setor": "Análise por setor",
    "Análise por profissional": "Análise por profissional",
    "Análise por serviço": "Análise por serviço",
    "DRE original": "DRE original",
    "DRE pós-rateio": "DRE pós-rateio",
    "DRE comparativa": "DRE comparativa",
    "Histórico de rateios": "Histórico de rateios",
    "Não calculável": "Não calculável",
    "Cobertura do rateio": "Cobertura do rateio",
    "Setores impactados": "Setores impactados",
    "Comparativo consolidado": "Comparativo consolidado",
    "Maiores impactos por setor": "Maiores impactos por setor",
    "Evolução do rateio": "Evolução do rateio",
    "Período sem movimento": "Período sem movimento",
    "Revisar rateios gerenciais": "Revisar rateios gerenciais",
    "Esta análise redistribui despesas corporativas entre setores para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa é alterado.": "Esta análise redistribui despesas corporativas entre setores para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa é alterado.",
    "Nenhum rateio gerencial ativo foi encontrado neste período.": "Nenhum rateio gerencial ativo foi encontrado neste período.",
    "Todas as despesas corporativas elegíveis do período foram rateadas.": "Todas as despesas corporativas elegíveis do período foram rateadas.",
    "Este setor não recebeu despesas por rateio no período selecionado.": "Este setor não recebeu despesas por rateio no período selecionado.",
    "O rateio redistribui despesas entre setores e não altera o resultado consolidado da clínica.": "O rateio redistribui despesas entre setores e não altera o resultado consolidado da clínica.",
    "APPLIED": "Aplicado",
    "Resultado antes de rateios": "Resultado de gestão antes de repartições",
    "Resultado pós-rateio": "Resultado após repartição",
    "Despesas redistribuídas": "Despesas redistribuídas",
    "Despesas rateadas": "Despesas rateadas",
    "Despesas ainda não rateadas": "Despesas ainda não repartidas",
    "Lotes considerados": "Lotes considerados",
    "Snapshots gerenciais": "Snapshots gerenciais",
    "Diferença consolidada": "Diferença consolidada",
    "Resultado consolidado preservado": "Resultado consolidado preservado",
    "Lote revertido": "Lote revertido",
    "Nenhum rateio aplicado no período": "Nenhum rateio aplicado no período",
    "Nenhum rateio ativo no período": "Nenhum rateio ativo no período",
    "Despesa original": "Despesa original",
    "Rateio recebido": "Rateio recebido",
    "Despesa pós-rateio": "Despesa pós-rateio",
    "Resultado original": "Resultado original",
    "Diferença": "Diferença",
    "Despesas recebidas por rateio": "Despesas recebidas por rateio",
    "Esta visão redistribui despesas corporativas para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa foi alterado.": "Esta visão redistribui despesas corporativas para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa foi alterado.",
    "A comparação demonstra o impacto setorial do rateio. O resultado consolidado da clínica permanece inalterado.": "A comparação demonstra o impacto setorial do rateio. O resultado consolidado da clínica permanece inalterado.",
    "Visão original sem efeitos de snapshots de rateio gerencial.": "Visão original sem efeitos de snapshots de rateio gerencial.",
    "Despesas corporativas redistribuídas na leitura gerencial a partir de snapshots aplicados.": "Despesas corporativas redistribuídas na leitura gerencial a partir de snapshots aplicados.",
    "Resultado gerencial por competência após redistribuição analítica de despesas corporativas aplicadas.": "Resultado gerencial por competência após redistribuição analítica de despesas corporativas aplicadas.",
    "Métodos de pagamento": "Métodos de pagamento",
    "Configure meios, adquirentes, prazos e taxas existentes.": "Configure meios, adquirentes, prazos e taxas existentes.",
    "Regras de repasse": "Regras de pagamento a profissionais",
    "Configure regras versionadas de repasse por profissional, setor ou serviço.": "Configure regras de pagamento versionadas por profissional, setor ou serviço.",
    "Rateio gerencial": "Repartição de despesas de gestão",
    "Configure regras versionadas de rateio percentual por setor.": "Configure regras versionadas de repartição percentual de despesas por setor.",
    "Configurações comerciais": "Configurações comerciais",
    "Gerencie origens e motivos administrativos na Central de Configurações.": "Faça a gestão de origens e motivos administrativos na Central de Configurações.",
    "Gerenciar origens": "Gerir origens",
    "Gerenciar motivos": "Gerir motivos",
    "Gerenciar catálogo": "Gerir catálogo",
    "Gerenciar templates": "Gerir templates",
    "Gerenciar métodos": "Gerir métodos",
    "Use este cadastro para padronizar as origens utilizadas no funil comercial.": "Use este registo para padronizar as origens utilizadas no funil comercial.",
    "Dados cadastrais": "Dados de registo",
    "Estes dados vêm do cadastro canônico da clínica e não são duplicados nesta central.": "Estes dados vêm do registo canónico da clínica e não são duplicados nesta central.",
    "CNPJ": "CNPJ",
    "Preferências regionais": "Preferências regionais",
    "A moeda e o fuso horário são compartilhados por toda a clínica.": "A moeda e o fuso horário são partilhados por toda a clínica.",
    "Alterações não salvas": "Alterações não guardadas",
    "Salve as alterações antes de sair desta seção.": "Guarde as alterações antes de sair desta secção.",
    "Salvar configurações": "Guardar configurações",
    "Não alocado": "Não alocado",
    "até": "até",
    "Filtros avançados": "Filtros avançados",
    "Filtros ativos": "Filtros ativos",
    "Indicadores comerciais": "Indicadores comerciais",
    "Lista de leads": "Lista de leads",
    "Carregando leads...": "A carregar leads...",
    "Nenhum resultado encontrado": "Nenhum resultado encontrado",
    "Nenhum lead cadastrado": "Nenhum lead registado",
    "Ajuste ou limpe os filtros para ampliar a busca.": "Ajuste ou limpe os filtros para alargar a pesquisa.",
    "Crie um lead para começar o acompanhamento comercial.": "Crie um lead para iniciar o acompanhamento comercial.",
    "Acompanhe, priorize e avance as oportunidades comerciais.": "Acompanhe, priorize e avance as oportunidades comerciais.",
    "Localização": "Localização",
    "Filtrar por cidade": "Filtrar por localidade",
    "Filtrar por bairro": "Filtrar por bairro",
    "Gênero": "Género",
    "Data de cadastro do paciente": "Data de registo do paciente",
    "Cadastro a partir de": "Registo a partir de",
    "Cadastro até antes de": "Registo até antes de",
    "Direção": "Direção",
    "Crescente": "Crescente",
    "Decrescente": "Decrescente",
    "Catálogo": "Catálogo",
    "Catálogo de Serviços e Procedimentos": "Catálogo de Serviços e Procedimentos",
    "Gerencie serviços e procedimentos utilizados na agenda, no prontuário clínico, nos planos de tratamento e no financeiro, preservando os dados históricos.": "Faça a gestão dos serviços e procedimentos utilizados na agenda, no processo clínico, nos planos de tratamento e no módulo financeiro, preservando os dados históricos.",
    "Novo item do catálogo": "Novo item do catálogo",
    "Item do catálogo atualizado com sucesso.": "Item do catálogo atualizado com sucesso.",
    "Item do catálogo cadastrado com sucesso.": "Item do catálogo registado com sucesso.",
    "Item do catálogo inativado com sucesso.": "Item do catálogo inativado com sucesso.",
    "Item do catálogo reativado com sucesso.": "Item do catálogo reativado com sucesso.",
    "Não foi possível salvar o item do catálogo.": "Não foi possível guardar o item do catálogo.",
    "Não foi possível alterar o status do item do catálogo.": "Não foi possível alterar o estado do item do catálogo.",
    "Não foi possível carregar o catálogo.": "Não foi possível carregar o catálogo.",
    "Procedimento": "Procedimento",
    "Serviço ou procedimento": "Serviço ou procedimento",
    "Serviço não informado": "Serviço não informado",
    "Sem item do catálogo": "Sem item do catálogo",
    "Outro lançamento": "Outro lançamento",
    "Todos os tipos": "Todos os tipos",
    "Buscar por nome ou descrição": "Pesquisar por nome ou descrição",
    "Duração": "Duração",
    "Preço padrão": "Preço predefinido",
    "Uso histórico": "Utilização histórica",
    "Toda a clínica": "Toda a clínica",
    "Nenhum item do catálogo encontrado.": "Nenhum item do catálogo encontrado.",
    "Editar item do catálogo": "Editar item do catálogo",
    "Cadastrar item do catálogo": "Registar item do catálogo",
    "Alterações no catálogo não modificam agendamentos, planos de tratamento ou lançamentos financeiros já registrados.": "As alterações ao catálogo não modificam marcações, planos de tratamento ou lançamentos financeiros já registados.",
    "Carregando serviços...": "A carregar serviços...",
    "Sugestão": "Sugestão",
    "Agendamentos antigos podem permanecer sem serviço.": "Marcações antigas podem permanecer sem serviço.",
    "O preço padrão é apenas uma sugestão; o valor efetivo fica salvo no lançamento.": "O preço predefinido é apenas uma sugestão; o valor efetivo fica guardado no lançamento.",
    "Buscar": "Pesquisar",
    "Agendamento": "Marcação",
    "Sim": "Sim",
    "Não": "Não",
    "Recebido": "Recebido",
    "Receber": "Receber",
    "Em aberto": "Em aberto",
    "Quantidade": "Quantidade",
    "Histórico financeiro": "Histórico financeiro",
    "Nenhuma receita encontrada para este paciente.": "Nenhuma receita encontrada para este paciente.",
    "Todos os setores": "Todos os setores",
    "Filtrar por setor": "Filtrar por setor",
    "Setor inativo": "Setor inativo",
    "Setor selecionado": "Setor selecionado",
    "Resultado direto do setor": "Resultado direto do setor",
    "Despesas atribuídas": "Despesas atribuídas",
    "Despesas a pagar": "Despesas a pagar",
    "Despesas vencidas": "Despesas vencidas",
    "Despesas pagas": "Despesas pagas",
    "Despesas corporativas": "Despesas corporativas",
    "Despesas diretas": "Despesas diretas",
    "Repasses pagos": "Repasses pagos",
    "Dados não atribuídos": "Dados não atribuídos",
    "Nenhum setor encontrado": "Nenhum setor encontrado",
    "Setores": "Setores",
    "Ticket médio por lançamento": "Ticket médio por lançamento",
    "Requer custo realizado atribuído às aquisições": "Requer custo realizado atribuído às aquisições",
    "Receita média por paciente pagante": "Receita média por paciente pagante",
    "Receita realizada no período; não representa LTV": "Receita realizada no período; não representa LTV",
    "Requer custo e receita atribuídos à campanha": "Requer custo e receita atribuídos à campanha",
    "Erro ao carregar setores": "Erro ao carregar setores",
    "Já existe um setor com este nome": "Já existe um setor com este nome",
    "O nome do setor deve ter entre 2 e 80 caracteres": "O nome do setor deve ter entre 2 e 80 caracteres",
    "Selecione um setor ativo da clínica": "Selecione um setor ativo da clínica",
    "Selecione um setor da clínica": "Selecione um setor da clínica",
    "Setor não encontrado": "Setor não encontrado",
    "Informe ao menos uma alteração": "Indique pelo menos uma alteração",
    "Idioma": "Idioma",
    "Idioma da interface": "Idioma da interface",
    "Moeda": "Moeda",
    "Moeda do sistema": "Moeda do sistema",
    "Configuração regional da clínica": "Configuração regional da clínica",
    "Configuração regional salva": "Configuração regional guardada",
    "A moeda e o fuso são compartilhados por toda a clínica.": "A moeda e o fuso são partilhados por toda a clínica.",
    "Somente administradores podem alterar esta configuração.": "Apenas administradores podem alterar esta configuração.",
    "A moeda está bloqueada porque já existe histórico financeiro.": "A moeda está bloqueada porque já existe histórico financeiro.",
    "Alterar a moeda não converte valores existentes.": "Alterar a moeda não converte valores existentes.",
    "Fuso horário": "Fuso horário",
    "São Paulo (América)": "São Paulo (América)",
    "Nova York (América)": "Nova Iorque (América)",
    "Lisboa (Europa)": "Lisboa (Europa)",
    "Madri (Europa)": "Madrid (Europa)",
    "Roma (Europa)": "Roma (Europa)",
    "Buscar cidade ou fuso": "Pesquisar cidade ou fuso",
    "O fuso define dias, horários da agenda e períodos dos indicadores; instantes salvos não são reescritos.": "O fuso define dias, horários da agenda e períodos dos indicadores; os instantes guardados não são reescritos.",
    "Preferências": "Preferências",
    "Abrir preferências": "Abrir preferências",
    "Ajuste idioma, moeda e tema da interface.": "Ajuste o idioma, a moeda e o tema da interface.",
    "Ajuste idioma e tema da interface.": "Ajuste o idioma e o tema da interface.",
    "Tema": "Tema",
    "Tema da interface": "Tema da interface",
    "América Latina": "América Latina",
    "Europa": "Europa",
    "Português": "Português",
    "Inglês": "Inglês",
    "Espanhol": "Espanhol",
    "Dashboard": "Dashboard",
    "Leads": "Leads",
    "Abrir lead": "Abrir lead",
    "Abrir próximo follow-up": "Abrir próximo follow-up",
    "Campanhas, agendamentos e desempenho comercial.": "Campanhas, marcações e desempenho comercial.",
    "Data do Pagamento": "Data do pagamento",
    "Editar": "Editar",
    "Erro ao carregar dados de marketing": "Erro ao carregar dados de marketing",
    "Erro ao carregar leads": "Erro ao carregar leads",
    "Erro ao excluir lead": "Erro ao eliminar lead",
    "Excluir": "Eliminar",
    "Excluir lead": "Eliminar lead",
    "Filtrar status": "Filtrar por estado",
    "Leads por Origem": "Leads por origem",
    "Nenhum dado disponível": "Sem dados disponíveis",
    "Nenhum follow-up pendente no momento.": "Nenhum follow-up pendente neste momento.",
    "Nova Despesa": "Nova despesa",
    "Nova Receita": "Nova receita",
    "Novo Lead": "Novo lead",
    "Paciente": "Paciente",
    "Receita registrada com sucesso!": "Receita registada com sucesso!",
    "Receitas": "Receitas",
    "Resumo Financeiro": "Resumo financeiro",
    "Central Financeira do Paciente": "Central financeira do paciente",
    "Visão filtrada dos lançamentos financeiros vinculados ao paciente.": "Vista filtrada dos lançamentos financeiros associados ao paciente.",
    "Novo lançamento": "Novo lançamento",
    "Receita total do paciente": "Receita total do paciente",
    "Total recebido": "Total recebido",
    "Total em aberto": "Total em aberto",
    "Próximo vencimento": "Próximo vencimento",
    "Planos ativos": "Planos ativos",
    "Pendências financeiras": "Pendências financeiras",
    "Lançamentos em aberto, vencidos ou parcialmente pagos.": "Lançamentos em aberto, vencidos ou parcialmente pagos.",
    "pendência(s)": "pendência(s)",
    "Nenhuma pendência financeira para este paciente.": "Nenhuma pendência financeira para este paciente.",
    "Saldo pendente": "Saldo pendente",
    "Histórico financeiro do paciente": "Histórico financeiro do paciente",
    "Receitas e valores a receber filtrados pelo paciente aberto.": "Receitas e valores a receber filtrados pelo paciente aberto.",
    "Buscar por descrição, profissional, valor, data ou status": "Pesquisar por descrição, profissional, valor, data ou estado",
    "Pagos": "Pagos",
    "Editar lançamento": "Editar lançamento",
    "Tipo do lançamento": "Tipo do lançamento",
    "Consulta avulsa": "Consulta avulsa",
    "Outro lançamento personalizado": "Outro lançamento personalizado",
    "Selecionar plano existente": "Selecionar plano existente",
    "Selecione um plano": "Selecione um plano",
    "Sem plano vinculado": "Sem plano associado",
    "Sem profissional vinculado": "Sem profissional associado",
    "Sem agendamento vinculado": "Sem marcação associada",
    "Receber pagamento": "Receber pagamento",
    "Valor recebido": "Valor recebido",
    "Data do recebimento": "Data do recebimento",
    "Detalhes do lançamento": "Detalhes do lançamento",
    "Plano vinculado": "Plano vinculado",
    "Agendamento vinculado": "Marcação associada",
    "Cancelar lançamento": "Cancelar lançamento",
    "Este agendamento já possui lançamento financeiro vinculado.": "Esta marcação já possui lançamento financeiro associado.",
    "Este plano já possui lançamento financeiro vinculado.": "Este plano já possui lançamento financeiro associado.",
    "Informe um valor válido.": "Informe um valor válido.",
    "Status do lead atualizado!": "Estado do lead atualizado!",
    "Todos os profissionais": "Todos os profissionais",
    "Marketing": "Marketing",
    "Pacientes": "Pacientes",
    "Cadastro e prontuário de pacientes": "Registo e processo clínico dos pacientes",
    "Cadastro e prontuário do paciente": "Registo e processo clínico do paciente",
    "Novo Paciente": "Novo paciente",
    "Buscar paciente existente": "Pesquisar paciente existente",
    "Cadastrar novo paciente": "Registar novo paciente",
    "Busca por nome, telefone ou CPF": "Pesquisa por nome, telefone ou NIF",
    "Busca por nome, telefone, CPF, gênero, cidade ou bairro": "Pesquisa por nome, telefone, NIF, género, cidade ou bairro",
    "Buscar paciente": "Pesquisar paciente",
    "Buscar paciente por nome, telefone ou CPF": "Pesquisar paciente por nome, telefone ou NIF",
    "Digite o nome do paciente": "Escreva o nome do paciente",
    "Buscando pacientes...": "A pesquisar pacientes...",
    "Não foi possível buscar os pacientes. Tente novamente.": "Não foi possível pesquisar os pacientes. Tente novamente.",
    "Selecione um paciente e um profissional.": "Selecione um paciente e um profissional.",
    "Erro ao carregar dados do paciente": "Erro ao carregar dados do paciente",
    "Carregando...": "A carregar...",
    "Carregando pacientes...": "A carregar pacientes...",
    "Nenhuma despesa encontrada no período selecionado.": "Nenhuma despesa encontrada no período selecionado.",
    "Nenhum paciente": "Sem pacientes",
    "Nenhum paciente encontrado": "Nenhum paciente encontrado",
    "Nascimento": "Data de nascimento",
    "Cadastro": "Registo",
    "Ver": "Ver",
    "Paciente criado com sucesso!": "Paciente criado com sucesso!",
    "Paciente existente selecionado.": "Paciente existente selecionado.",
    "Encontramos um ou mais pacientes com dados semelhantes nesta clínica. Revise os cadastros antes de continuar.": "Encontrámos um ou mais pacientes com dados semelhantes nesta clínica. Reveja os registos antes de continuar.",
    "Já existe um cadastro com este CPF nesta clínica. Como bases históricas podem conter duplicidades, você pode selecionar o cadastro existente ou continuar a criação após revisar os dados.": "Já existe um registo com este CPF nesta clínica. Como bases históricas podem conter duplicados, pode selecionar o registo existente ou continuar a criação após rever os dados.",
    "Confiança": "Confiança",
    "Critérios": "Critérios",
    "Usar este cadastro": "Usar este registo",
    "Revisei e preciso criar um novo cadastro": "Revi e preciso de criar um novo registo",
    "Continuar criação": "Continuar criação",
    "Paciente atualizado com sucesso!": "Paciente atualizado com sucesso!",
    "Editar Paciente": "Editar paciente",
    "RG": "Documento secundário",
    "Estado civil": "Estado civil",
    "Selecione o estado civil": "Selecione o estado civil",
    "Solteiro(a)": "Solteiro(a)",
    "Casado(a)": "Casado(a)",
    "União Estável": "União de facto",
    "Divorciado(a)": "Divorciado(a)",
    "Viúvo(a)": "Viúvo(a)",
    "Separado(a)": "Separado(a)",
    "Prefiro não informar": "Prefiro não indicar",
    "Profissão": "Profissão",
    "Convênio e filiação": "Seguro de saúde e filiação",
    "Número do convênio": "Número do seguro de saúde",
    "CNS": "CNS",
    "Nome da mãe": "Nome da mãe",
    "Documento da mãe": "Documento da mãe",
    "Nome do pai": "Nome do pai",
    "Documento do pai": "Documento do pai",
    "Filiação": "Filiação",
    "Texto livre. Use vírgulas, espaços ou uma linha por item.": "Texto livre. Use vírgulas, espaços ou uma linha por item.",
    "Masculino": "Masculino",
    "Feminino": "Feminino",
    "Logradouro": "Morada",
    "Complemento": "Complemento",
    "Contato de Emergência": "Contacto de emergência",
    "Histórico Médico / Observações": "Histórico clínico / Observações",
    "Cadastrar": "Registar",
    "Erro ao carregar planos de tratamento": "Erro ao carregar planos de tratamento",
    "Carregando planos de tratamento...": "A carregar planos de tratamento...",
    "Buscando endereço pelo CEP...": "A procurar morada pelo código postal...",
    "Plano cancelado com sucesso!": "Plano cancelado com sucesso!",
    "Plano reativado com sucesso!": "Plano reativado com sucesso!",
    "Planos de tratamento": "Planos de tratamento",
    "Acompanhe venda, execução e saldo de sessões do paciente.": "Acompanhe a venda, a execução e o saldo de sessões do paciente.",
    "Novo plano": "Novo plano",
    "Nenhum plano cadastrado para este paciente ainda.": "Nenhum plano registado para este paciente ainda.",
    "Profissional": "Profissional",
    "Venda em": "Venda em",
    "Venda ainda não registrada": "Venda ainda não registada",
    "Sessões": "Sessões",
    "Saldo": "Saldo",
    "restantes": "restantes",
    "Valor": "Valor",
    "Valor bruto": "Valor bruto",
    "A receber": "Valor a receber",
    "À Receber": "À receber",
    "Novo valor a receber": "Novo valor a receber",
    "Novo à receber": "Novo valor a receber",
    "Editar valor a receber": "Editar valor a receber",
    "Editar à receber": "Editar valor a receber",
    "Gerar valor a receber": "Gerar valor a receber",
    "Gerar à receber": "Gerar valor a receber",
    "Valor a receber gerado com sucesso": "Valor a receber gerado com sucesso",
    "À receber gerado com sucesso": "Valor a receber gerado com sucesso",
    "À receber gerado": "Valor a receber gerado",
    "À receber já gerado": "Valor a receber já gerado",
    "Valor a receber criado com sucesso.": "Valor a receber criado com sucesso.",
    "À receber criado com sucesso.": "Valor a receber criado com sucesso.",
    "Valor a receber atualizado com sucesso.": "Valor a receber atualizado com sucesso.",
    "À receber atualizado com sucesso.": "Valor a receber atualizado com sucesso.",
    "Valor a receber cancelado com sucesso.": "Valor a receber cancelado com sucesso.",
    "À receber cancelado com sucesso.": "Valor a receber cancelado com sucesso.",
    "Controle valores pendentes, vencidos e recebimentos parciais sem misturar com receitas já recebidas.": "Controle valores pendentes, vencidos e recebimentos parciais sem misturar com receitas já recebidas.",
    "Total a receber": "Total a receber",
    "Total à receber": "Total a receber",
    "Total vencido": "Total vencido",
    "Recebido no período": "Recebido no período",
    "Total parcial": "Total parcial",
    "Pacientes com pendência": "Pacientes com pendência",
    "Todos os pacientes": "Todos os pacientes",
    "Todos status": "Todos os estados",
    "Todos pacientes": "Todos os pacientes",
    "Todas as formas": "Todas as formas",
    "Forma de pagamento prevista": "Forma de pagamento prevista",
    "Pagamento previsto": "Pagamento previsto",
    "Sem forma prevista": "Sem forma prevista",
    "Sem forma definida": "Sem forma definida",
    "Data do pagamento ou vencimento": "Data do pagamento ou vencimento",
    "Valor total": "Valor total",
    "Saldo restante": "Saldo restante",
    "Marcar como recebido": "Marcar como recebido",
    "Registrar pagamento parcial": "Registrar pagamento parcial",
    "Registrar baixa": "Registrar baixa",
    "Recebimento registrado com sucesso.": "Recebimento registado com sucesso.",
    "Recebimento parcial registrado com sucesso.": "Recebimento parcial registado com sucesso.",
    "Nenhum valor a receber encontrado com os filtros selecionados.": "Nenhum valor a receber encontrado com os filtros selecionados.",
    "Nenhum valor à receber encontrado com os filtros selecionados.": "Nenhum valor a receber encontrado com os filtros selecionados.",
    "Detalhes do valor a receber": "Detalhes do valor a receber",
    "Detalhes do à receber": "Detalhes do valor a receber",
    "Nenhuma baixa registrada.": "Nenhuma baixa registada.",
    "Valor a receber indisponível": "Valor a receber indisponível",
    "À receber indisponível": "Valor a receber indisponível",
    "Gerando...": "A gerar...",
    "Vencimento": "Vencimento",
    "Vencido": "Vencido",
    "Pago": "Pago",
    "Cancelado": "Cancelado",
    "Valor líquido": "Valor líquido",
    "Valor da receita": "Valor da receita",
    "Validade": "Validade",
    "Sem prazo": "Sem prazo",
    "Venda registrada no financeiro.": "Venda registada no financeiro.",
    "Plano ainda sem receita vinculada no financeiro.": "Plano ainda sem receita associada no financeiro.",
    "Rascunho": "Rascunho",
    "Vendido": "Vendido",
    "Em execução": "Em execução",
    "Renovar": "Renovar",
    "Reativar": "Reativar",
    "Plano de tratamento atualizado com sucesso!": "Plano de tratamento atualizado com sucesso!",
    "Renovação do plano criada com sucesso!": "Renovação do plano criada com sucesso!",
    "Plano de tratamento cadastrado com sucesso!": "Plano de tratamento registado com sucesso!",
    "Editar Plano de Tratamento": "Editar plano de tratamento",
    "Renovar Plano de Tratamento": "Renovar plano de tratamento",
    "Novo Plano de Tratamento": "Novo plano de tratamento",
    "Ex.: Reabilitação lombar": "Ex.: reabilitação lombar",
    "Selecione o profissional": "Selecione o profissional",
    "Quantidade de sessões": "Número de sessões",
    "Validade inicial": "Validade inicial",
    "Validade final": "Validade final",
    "Objetivo do tratamento, observações e combinados.": "Objetivo do tratamento, observações e acordos.",
    "Registrar nova venda na renovação": "Registar nova venda na renovação",
    "Registrar venda do plano agora": "Registar venda do plano agora",
    "Quando marcado, o financeiro recebe uma receita de pacote vinculada ao plano.": "Quando assinalado, o financeiro recebe uma receita de pacote associada ao plano.",
    "Forma de pagamento": "Forma de pagamento",
    "Selecione uma forma de pagamento": "Selecione uma forma de pagamento",
    "Selecione uma forma de pagamento.": "Selecione uma forma de pagamento.",
    "Nenhuma forma de pagamento ativa": "Nenhuma forma de pagamento ativa",
    "Cadastre uma forma de pagamento ativa antes de registrar receitas.": "Registe uma forma de pagamento ativa antes de registar receitas.",
    "Forma de pagamento não cadastrada ({method})": "Forma de pagamento não registada ({method})",
    "{name} (inativa)": "{name} (inativa)",
    "Registro histórico": "Registo histórico",
    "Forma inativa": "Forma inativa",
    "Vínculo histórico indisponível": "Associação histórica indisponível",
    "Forma de pagamento histórica não vinculada": "Forma de pagamento histórica não associada",
    "Convênio/cobertura": "Convénio/cobertura",
    "Identificador técnico: {identifier}": "Identificador técnico: {identifier}",
    "Parcela {current} de {total} da obrigação": "Prestação {current} de {total} da obrigação",
    "Parcelas": "Prestações",
    "Parcelas do meio de pagamento": "Prestações do meio de pagamento",
    "Indisponível": "Indisponível",
    "Valor líquido histórico indisponível": "Valor líquido histórico indisponível",
    "Operações anteriores à preservação histórica podem não possuir detalhamento de taxas.": "As operações anteriores à preservação histórica podem não incluir o detalhe das taxas.",
    "Cartão de crédito à vista": "Cartão de crédito à vista",
    "Resumo do período": "Resumo do período",
    "Taxas/descontos": "Taxas/descontos",
    "Observação opcional": "Observação opcional",
    "Total no período": "Total no período",
    "Atendimento - Paciente": "Consulta - Paciente",
    "Nenhuma receita encontrada para este profissional no período selecionado.": "Nenhuma receita encontrada para este profissional no período selecionado.",
    "Nenhuma receita encontrada com os filtros selecionados.": "Nenhuma receita encontrada com os filtros selecionados.",
    "Data da venda": "Data da venda",
    "Descrição financeira": "Descrição financeira",
    "Salvar plano": "Guardar plano",
    "Criar renovação": "Criar renovação",
    "Cadastrar plano": "Registar plano",
    "Profissionais": "Profissionais",
    "Gestão de corpo clínico e especialidades": "Gestão da equipa clínica e especialidades",
    "Novo Profissional": "Novo profissional",
    "Editar Profissional": "Editar profissional",
    "Profissional cadastrado com sucesso!": "Profissional registado com sucesso!",
    "Profissional atualizado com sucesso!": "Profissional atualizado com sucesso!",
    "Erro ao carregar profissionais": "Erro ao carregar profissionais",
    "Nenhum profissional encontrado": "Nenhum profissional encontrado",
    "Catálogo de setores": "Catálogo de setores",
    "Use este cadastro para padronizar leads, profissionais e financeiro.": "Utilize este registo para normalizar leads, profissionais e financeiro.",
    "Setor removido com sucesso!": "Setor removido com sucesso!",
    "Setor adicionado com sucesso!": "Setor adicionado com sucesso!",
    "Erro ao remover setor": "Erro ao remover setor",
    "Erro ao adicionar setor": "Erro ao adicionar setor",
    "Novo setor": "Novo setor",
    "Adicionar setor": "Adicionar setor",
    "Buscar por nome, setor ou especialidade...": "Pesquisar por nome, setor ou especialidade...",
    "Especialidade": "Especialidade",
    "Especialidades": "Especialidades",
    "Registro": "Cédula profissional",
    "Cor na agenda": "Cor na agenda",
    "Duração da consulta": "Duração da consulta",
    "A duração da consulta deve ser de pelo menos 15 minutos.": "A duração da consulta deve ser de pelo menos 15 minutos.",
    "Limite de pacientes por horário": "Limite de pacientes por horário",
    "Limite por horário": "Limite por horário",
    "Minutos": "Minutos",
    "Ex.: Traumato, Ortopedia": "Ex.: Traumato, Ortopedia",
    "Horários / Agenda": "Horários / Agenda",
    "Configure a capacidade semanal do profissional. Isso alimenta a ocupação percentual da agenda.": "Configure a capacidade semanal do profissional. Isto alimenta a taxa de ocupação da agenda.",
    "Segunda": "Segunda",
    "Terça": "Terça",
    "Quarta": "Quarta",
    "Quinta": "Quinta",
    "Sexta": "Sexta",
    "Sábado": "Sábado",
    "Domingo": "Domingo",
    "Adicionar faixa": "Adicionar período",
    "Sem horários configurados neste dia.": "Sem horários configurados neste dia.",
    "Sem horários disponíveis": "Sem horários disponíveis",
    "Agenda": "Agenda",
    "Gerencie os agendamentos da clínica": "Gerir as marcações da clínica",
    "Novo Agendamento": "Nova marcação",
    "Hoje": "Hoje",
    "Ontem": "Ontem",
    "Esta semana": "Esta semana",
    "Últimos 7 dias": "Últimos 7 dias",
    "Este mês": "Este mês",
    "Este ano": "Este ano",
    "Mês seguinte": "Mês seguinte",
    "Mês anterior": "Mês anterior",
    "Por período": "Por período",
    "Todos profissionais": "Todos os profissionais",
    "Ver detalhes": "Ver detalhes",
    "Carregando dados...": "A carregar dados...",
    "Erro ao carregar dados.": "Erro ao carregar dados.",
    "Nenhum Lead encontrado": "Nenhum lead encontrado",
    "Erro ao carregar agendamentos": "Erro ao carregar marcações",
    "Horário inválido": "Horário inválido",
    "Não é possível criar agendamentos em datas anteriores à data atual.": "Não é possível criar marcações em datas anteriores à data atual.",
    "Este profissional já atingiu o limite de pacientes para este horário. Escolha outro horário ou ajuste o limite no cadastro do profissional.": "Este profissional já atingiu o limite de pacientes para este horário. Escolha outro horário ou ajuste o limite no cadastro do profissional.",
    "Este profissional não possui faixa de horário cadastrada para o período selecionado. Ajuste a agenda do profissional ou escolha outro horário.": "Este profissional não possui faixa de horário cadastrada para o período selecionado. Ajuste a agenda do profissional ou escolha outro horário.",
    "Agendado com sucesso": "Marcação criada com sucesso.",
    "Agendamento atualizado": "Marcação atualizada.",
    "Receita gerada com sucesso": "Receita gerada com sucesso.",
    "Plano de tratamento": "Plano de tratamento",
    "Renovação do plano": "Renovação do plano",
    "Selecionar plano": "Selecionar plano",
    "Sem plano": "Sem plano",
    "sessões restantes": "sessões restantes",
    "validade até": "validade até",
    "Nenhum plano vendido ou ativo disponível para este paciente e profissional.": "Não há nenhum plano vendido ou ativo disponível para este paciente e este profissional.",
    "Selecione paciente e profissional para usar um plano.": "Selecione o paciente e o profissional para usar um plano.",
    "Início": "Início",
    "Término": "Término",
    "Intervalos de": "Intervalos de",
    "minutos": "minutos",
    "Atendimentos vinculados ao plano ficam com valor por sessão zerado. O faturamento entra na venda do plano.": "Marcações associadas ao plano ficam com valor por sessão a zero. A faturação entra na venda do plano.",
    "Agendar": "Marcar",
    "Detalhes do Agendamento": "Detalhes da marcação",
    "Criado em": "Criado em",
    "Histórico de importação": "Histórico de importação",
    "A reversão deve seguir a ordem inversa da importação para preservar os vínculos.": "A reversão deve seguir a ordem inversa da importação para preservar os vínculos.",
    "Nenhuma importação registrada para esta clínica.": "Nenhuma importação registada para esta clínica.",
    "Aplicação revertida": "Aplicação revertida",
    "Rastreáveis": "Rastreáveis",
    "Revertido em": "Revertido em",
    "Reverter": "Reverter",
    "Revertendo...": "A reverter...",
    "Reverter este lote removerá os registros importados por ele. Deseja continuar?": "Reverter este lote removerá os registos importados por ele. Deseja continuar?",
    "CPFs inválidos legados": "CPFs inválidos legados",
    "Reprocessados por ID externo": "Reprocessados por ID externo",
    "Importação revertida com sucesso.": "Importação revertida com sucesso.",
    "registros removidos": "registos removidos",
    "Erro ao carregar histórico de importação.": "Erro ao carregar histórico de importação.",
    "Erro ao reverter importação.": "Erro ao reverter importação.",
    "Importação bloqueada pela ordem de dependência. Consulte o histórico da clínica.": "Importação bloqueada pela ordem de dependência. Consulte o histórico da clínica.",
    "Remarcado": "Remarcado",
    "Remarcar agendamento": "Remarcar marcação",
    "Selecione a nova data, horário e profissional para atualizar o agendamento.": "Selecione a nova data, horário e profissional para atualizar a marcação.",
    "Nova data": "Nova data",
    "Informe a nova data, horário e profissional para remarcar.": "Informe a nova data, horário e profissional para remarcar.",
    "Não é possível remarcar para datas anteriores à data atual.": "Não é possível remarcar para datas anteriores à data atual.",
    "O plano vinculado pertence a outro profissional. Remova o plano ou mantenha o profissional atual.": "O plano vinculado pertence a outro profissional. Remova o plano ou mantenha o profissional atual.",
    "Atual": "Atual",
    "Motivo do cancelamento": "Motivo do cancelamento",
    "Preencha quando o agendamento for cancelado": "Preencha quando a marcação for cancelada",
    "Disponível somente para status cancelado": "Disponível apenas para estado cancelado",
    "Informe pelo menos 30 caracteres para registrar o cancelamento.": "Informe pelo menos 30 caracteres para registar o cancelamento.",
    "Informe um motivo do cancelamento com pelo menos 30 caracteres.": "Informe um motivo do cancelamento com pelo menos 30 caracteres.",
    "Gerar receita": "Gerar receita",
    "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.": "Crie uma receita associada a esta marcação, ao paciente e ao profissional.",
    "Agendamento cancelado": "Marcação cancelada",
    "Receita já gerada": "Receita já gerada",
    "Sessão coberta por plano": "Sessão coberta por plano",
    "Não é permitido gerar receita para um agendamento com status cancelado.": "Não é permitido gerar receita para uma marcação com estado cancelado.",
    "Este atendimento consome uma sessão do plano vinculado. O faturamento deve ser registrado na venda do plano, não por atendimento.": "Esta marcação consome uma sessão do plano associado. A faturação deve ser registada na venda do plano, não por marcação.",
    "Data do pagamento": "Data do pagamento",
    "Descrição da receita": "Descrição da receita",
    "Gerando receita...": "A gerar receita...",
    "Receita indisponível": "Receita indisponível",
    "Coberto por plano": "Coberto por plano",
    "Receita gerada": "Receita gerada",
    "Fechar": "Fechar",
    "Avaliação": "Avaliação",
    "Tratamento": "Tratamento",
    "Retorno": "Retorno",
    "Reavaliação": "Reavaliação",
    "Confirmado": "Confirmado",
    "Em andamento": "Em curso",
    "Não compareceu": "Falta",
    "Consulta": "Consulta",
    "Pacote": "Pacote",
    "Atendimento": "Atendimento",
    "Eficiência assistencial, recorrência e satisfação do paciente em um único lugar.": "Eficiência assistencial, recorrência e satisfação do paciente num único lugar.",
    "Carregando indicadores assistenciais...": "A carregar indicadores assistenciais...",
    "Meta de retenção atualizada com sucesso!": "Meta de retenção atualizada com sucesso!",
    "NPS atualizado com sucesso!": "NPS atualizado com sucesso!",
    "NPS registrado com sucesso!": "NPS registado com sucesso!",
    "Responder": "Responder",
    "Responder NPS": "Responder NPS",
    "Registre a resposta do NPS para o plano selecionado.": "Registe a resposta do NPS para o plano selecionado.",
    "Registros de NPS já respondidos pelo paciente.": "Registos de NPS já respondidos pelo paciente.",
    "Link da pesquisa copiado.": "Link do questionário copiado.",
    "Novo link gerado e copiado.": "Novo link gerado e copiado.",
    "Não foi possível copiar o link.": "Não foi possível copiar o link.",
    "Esta pesquisa já foi respondida. O link não pode ser copiado novamente.": "Este questionário já foi respondido. O link não pode ser copiado novamente.",
    "Esta pesquisa expirou. Gere uma nova pesquisa para enviar um link válido.": "Este questionário expirou. Gere um novo questionário para enviar um link válido.",
    "Esta pesquisa expirou. Gere um novo link para continuar.": "Este questionário expirou. Gere um novo link para continuar.",
    "O link desta pesquisa já foi emitido de forma protegida. Use o envio automático ou gere uma nova pesquisa.": "O link deste questionário já foi emitido de forma protegida. Use o envio automático ou gere um novo questionário.",
    "Não foi possível copiar o link porque a pesquisa não foi encontrada ou não pertence mais a esta clínica.": "Não foi possível copiar o link porque o questionário não foi encontrado ou já não pertence a esta clínica.",
    "Não foi possível copiar o link por um erro inesperado. Tente novamente em instantes.": "Não foi possível copiar o link por um erro inesperado. Tente novamente dentro de instantes.",
    "O link foi gerado, mas o navegador bloqueou a cópia automática. Permita o acesso à área de transferência e tente novamente.": "O link foi gerado, mas o navegador bloqueou a cópia automática. Permita o acesso à área de transferência e tente novamente.",
    "Pacientes atendidos": "Pacientes acompanhados",
    "Primeira consulta": "Primeira consulta",
    "Avaliações no período selecionado": "Avaliações no período selecionado",
    "Consultas avulsas": "Consultas avulsas",
    "Atendimentos sem plano vinculado": "Consultas sem plano associado",
    "Pacientes em tratamento": "Pacientes em tratamento",
    "Pacientes com plano no período": "Pacientes com plano no período",
    "Pacientes atendidos no período": "Pacientes acompanhados no período",
    "Resumo dos atendimentos concluídos entre": "Resumo das consultas concluídas entre",
    "Atendimentos": "Consultas",
    "Com retorno": "Com retorno",
    "Nenhum atendimento concluído no período selecionado.": "Nenhuma consulta concluída no período selecionado.",
    "atendimento concluído": "consulta concluída",
    "atendimentos concluídos": "consultas concluídas",
    "Paciente recorrente": "Paciente recorrente",
    "Paciente novo no período": "Paciente novo no período",
    "Primeiro atendimento": "Primeira consulta",
    "Último atendimento": "Última consulta",
    "Tipos de atendimento": "Tipos de consulta",
    "com retorno no período": "com retorno no período",
    "Taxa de retenção": "Taxa de retenção",
    "NPS pós-tratamento": "NPS pós-tratamento",
    "resposta": "resposta",
    "respostas": "respostas",
    "Horas ocupadas": "Horas ocupadas",
    "Horários ocupados": "Horários ocupados",
    "Horários": "Horários",
    "horários ativos": "marcações ativas",
    "horários disponíveis": "horários disponíveis",
    "faltas no período": "faltas no período",
    "Novas x recorrentes": "Novas x recorrentes",
    "Novas consultas x retornos": "Novas consultas x seguimentos",
    "Novas consultas x recorrentes": "Novas consultas x recorrentes",
    "Resumo do NPS": "Resumo do NPS",
    "Absenteísmo": "Absenteísmo",
    "faltas em": "faltas em",
    "horários": "marcações",
    "Mix assistencial": "Mix assistencial",
    "Promotores": "Promotores",
    "Passivos": "Passivos",
    "Detratores": "Detratores",
    "Sessões por paciente": "Sessões por paciente",
    "Leitura operacional": "Leitura operacional",
    "Cruze eficiência, faltas e satisfação para agir mais rápido na rotina da clínica.": "Combine eficiência, faltas e satisfação para agir mais depressa na rotina da clínica.",
    "Retornos por paciente": "Seguimentos por paciente",
    "Média de sessões concluídas por paciente no período.": "Média de sessões concluídas por paciente no período.",
    "Absenteísmo x conclusão": "Absenteísmo x conclusão",
    "Faltas comparadas aos atendimentos concluídos no mesmo recorte.": "Faltas comparadas com as consultas concluídas no mesmo período.",
    "Pacientes fidelizados": "Pacientes fidelizados",
    "Pacientes com 2 ou mais atendimentos concluídos no período.": "Pacientes com 2 ou mais consultas concluídas no período.",
    "Agenda do dia": "Agenda do dia",
    "Agenda da semana": "Agenda da semana",
    "Agenda do mês": "Agenda do mês",
    "horários ocupados": "horários ocupados",
    "Horas": "Horas",
    "Concluídos": "Concluídos",
    "de ocupação sobre": "de ocupação sobre",
    "disponíveis": "disponíveis",
    "Sem carga horária configurada nos profissionais para calcular a taxa percentual.": "Sem carga horária configurada nos profissionais para calcular a taxa percentual.",
    "Meta de retenção": "Meta de retenção",
    "Meta": "Meta",
    "Pacientes com 2 ou mais atendimentos concluídos no período selecionado.": "Pacientes com 2 ou mais consultas concluídas no período selecionado.",
    "Gap": "Desvio",
    "de": "de",
    "pacientes retornaram no período.": "pacientes retornaram no período.",
    "Defina uma meta percentual para acompanhar a fidelização da base.": "Defina um objetivo percentual para acompanhar a fidelização da base.",
    "Meta mensal (%)": "Meta mensal (%)",
    "Segmentação por setor": "Segmentação por setor",
    "Compare ocupação, retenção e satisfação para encontrar gargalos operacionais.": "Compare ocupação, retenção e satisfação para identificar bloqueios operacionais.",
    "Ocupação": "Ocupação",
    "Mix": "Mix",
    "Retenção": "Retenção",
    "Respostas": "Respostas",
    "Nenhum dado assistencial encontrado no período selecionado.": "Sem dados assistenciais no período selecionado.",
    "concluídos": "concluídos",
    "faltas": "faltas",
    "novas": "novas",
    "recorrentes": "recorrentes",
    "Detalhes do setor": "Detalhes do setor",
    "Período considerado": "Período considerado",
    "Agendamentos realizados": "Marcações realizadas",
    "Agendamentos cancelados": "Marcações canceladas",
    "Taxa de comparecimento": "Taxa de comparência",
    "Novos atendimentos": "Novas consultas",
    "Atendimentos recorrentes": "Consultas recorrentes",
    "Profissionais vinculados": "Profissionais associados",
    "Nenhum profissional vinculado a este setor.": "Nenhum profissional associado a este setor.",
    "Sem registro informado": "Sem registo informado",
    "Leitura do setor": "Leitura do setor",
    "Respostas NPS": "Respostas NPS",
    "Média de sessões por paciente": "Média de sessões por paciente",
    "Filtro de detalhes": "Filtro de detalhes",
    "Selecione uma opção para listar paciente e profissional vinculados.": "Selecione uma opção para listar o paciente e o profissional associados.",
    "Total encontrado": "Total encontrado",
    "Buscar nos detalhes": "Pesquisar nos detalhes",
    "Ordenar por": "Ordenar por",
    "Mais recentes": "Mais recentes",
    "Nome do paciente": "Nome do paciente",
    "Mais atendimentos": "Mais consultas",
    "Carregando detalhes...": "A carregar detalhes...",
    "Erro ao carregar detalhes.": "Erro ao carregar detalhes.",
    "Paciente não informado": "Paciente não informado",
    "Profissional não informado": "Profissional não informado",
    "Nenhum registro encontrado para este filtro.": "Nenhum registo encontrado para este filtro.",
    "Fila de NPS automático": "Lista de NPS automático",
    "Todo plano concluído gera um link público para o paciente responder após o tratamento.": "Cada plano concluído gera um link público para o paciente responder após o tratamento.",
    "Pendentes": "Pendentes",
    "Respondidas": "Respondidas",
    "Ação": "Ação",
    "Nenhuma pesquisa automática no período selecionado.": "Nenhum questionário automático no período selecionado.",
    "Respondida": "Respondida",
    "Pendente": "Pendente",
    "Expirada": "Expirado",
    "Em": "Em",
    "Criada em": "Criada em",
    "Expirou em {date}": "Expirou em {date}",
    "Copiar link": "Copiar link",
    "Gerar novo link": "Gerar novo link",
    "O link público segue o formato": "O link público segue o formato",
    "e pode ser enviado pela equipe no canal preferido.": "e pode ser enviado pela equipa no canal preferido.",
    "Feedbacks recentes": "Feedbacks recentes",
    "Leitura rápida da satisfação capturada após os tratamentos.": "Leitura rápida da satisfação recolhida após os tratamentos.",
    "Nota": "Nota",
    "Comentário": "Comentário",
    "Nenhum NPS registrado ainda.": "Sem NPS registado ainda.",
    "Período inicial": "Período inicial",
    "Período final": "Período final",
    "Mês atual": "Mês atual",
    "Excelência percebida": "Excelência percebida",
    "Boa satisfação": "Boa satisfação",
    "Atenção aos detratores": "Atenção aos detratores",
    "Experiência crítica": "Experiência crítica",
    "Metas": "Metas",
    "Usuários": "Utilizadores",
    "Clínicas": "Clínicas",
    "Auditoria": "Auditoria",
    "Selecione a clínica": "Selecione a clínica",
    "Sair": "Terminar sessão",
    "Workspace": "Workspace",
    "Control Tower": "Control Tower",
    "Tema claro": "Tema claro",
    "Tema escuro": "Tema escuro",
    "Ativar tema claro": "Ativar tema claro",
    "Ativar tema escuro": "Ativar tema escuro",
    "Entrar": "Iniciar sessão",
    "Criar clínica": "Criar clínica",
    "Criando...": "Criando...",
    "Entrando...": "A iniciar sessão...",
    "Fazer login": "Iniciar sessão",
    "Voltar para a página inicial": "Voltar para a página inicial",
    "Criar conta": "Criar conta",
    "Bem-vindo de volta": "Bem-vindo de volta",
    "Portal de acesso do super admin": "Portal de acesso do super admin",
    "Use suas credenciais para entrar na camada central da plataforma.": "Utilize as suas credenciais para aceder à camada central da plataforma.",
    "Entre com suas credenciais para acessar a plataforma.": "Introduza as suas credenciais para aceder à plataforma.",
    "E-mail": "E-mail",
    "Mostrar senha": "Mostrar palavra-passe",
    "Ocultar senha": "Ocultar palavra-passe",
    "Senha": "Palavra-passe",
    "Não tem uma conta?": "Não tem uma conta?",
    "Já tem uma conta?": "Já tem uma conta?",
    "Seu nome": "O seu nome",
    "Sua cidade": "A sua cidade",
    "Mínimo 6 caracteres": "Mínimo de 6 caracteres",
    "Mínimo 8 caracteres": "Mínimo de 8 caracteres",
    "Repita sua senha": "Repita a sua palavra-passe",
    "Nome da clínica": "Nome da clínica",
    "Nome da sua clínica": "Nome da sua clínica",
    "Telefone da clínica": "Telefone da clínica",
    "E-mail da clínica": "E-mail da clínica",
    "Endereço": "Morada",
    "Rua, avenida, etc.": "Rua, avenida, etc.",
    "Número": "Número",
    "Cidade": "Localidade",
    "Estado": "Estado",
    "Nome completo do responsável": "Nome completo do responsável",
    "E-mail do responsável": "E-mail do responsável",
    "Confirmar senha": "Confirmar palavra-passe",
    "Buscar endereço pelo CEP...": "Pesquisar morada pelo código postal...",
    "Cadastro realizado!": "Registo concluído!",
    "Logo você receberá um e-mail de confirmação com a liberação do seu acesso!": "Em breve receberá um e-mail de confirmação com a ativação do seu acesso.",
    "Desde já...": "Desde já...",
    "Seja bem-vindo!!!": "Bem-vindo!",
    "Carregando métricas...": "A carregar métricas...",
    "Selecione uma clínica para visualizar o dashboard.": "Selecione uma clínica para ver o dashboard.",
    "Visão geral": "Visão geral",
    "Visão geral de": "Visão geral de",
    "Indicadores exibidos para": "Indicadores exibidos para",
    "Visão consolidada da clínica": "Visão consolidada da clínica",
    "Visão individual por setor": "Visão individual por setor",
    "Indicadores gerenciais": "Indicadores gerenciais",
    "Indicadores com fonte técnica segura para o período e setor selecionados.": "Indicadores com fonte técnica segura para o período e setor selecionados.",
    "Primeiras consultas": "Primeiras consultas",
    "Agenda classificada como primeiro atendimento": "Agenda classificada como primeira consulta",
    "Agendamentos sem plano de tratamento vinculado": "Marcações sem plano de tratamento associado",
    "Vendas confirmadas por data da venda": "Vendas confirmadas por data da venda",
    "Despesas por competência / (agendamentos vinculados + lançamentos manuais)": "Despesas por competência / (marcações associadas + registos manuais de receita)",
    "Resultado financeiro / (agendamentos vinculados + lançamentos manuais)": "Resultado financeiro / (marcações associadas + registos manuais de receita)",
    "Resumo por setor": "Resumo por setor",
    "Visão consolidada por setor com dados operacionais e financeiros permitidos para o seu perfil.": "Visão consolidada por setor com dados operacionais e financeiros permitidos para o seu perfil.",
    "Visão individual do setor selecionado com dados permitidos para o seu perfil.": "Visão individual do setor selecionado com dados permitidos para o seu perfil.",
    "Entradas financeiras": "Entradas financeiras",
    "Despesas reconhecidas por competência": "Despesas reconhecidas por competência",
    "Despesas por competência do período": "Despesas por competência do período",
    "Resultado financeiro": "Resultado financeiro",
    "Entradas realizadas menos despesas por competência, sem DRE contábil.": "Entradas realizadas menos despesas por competência, sem demonstração contabilística.",
    "Entradas financeiras por lançamento realizado": "Entradas financeiras por lançamento realizado",
    "Sem dados setoriais confiáveis para o período selecionado.": "Sem dados setoriais confiáveis para o período selecionado.",
    "Ano anterior": "Ano anterior",
    "Próximo ano": "Próximo ano",
    "Mês Atual": "Mês atual",
    "no período": "no período",
    "Receita Total": "Faturação total",
    "Receita": "Receita",
    "Receita bruta": "Receita bruta",
    "Receita líquida": "Receita líquida",
    "Despesas": "Despesas",
    "Lucro": "Lucro",
    "Prejuízo": "Prejuízo",
    "Equilíbrio": "Equilíbrio",
    "Total de Entradas": "Total de entradas",
    "Total de saídas": "Total de saídas",
    "Resultado do Período": "Resultado do período",
    "Soma de todas as receitas registradas no período selecionado.": "Soma de todas as receitas registadas no período selecionado.",
    "Soma de todas as despesas registradas no período selecionado.": "Soma de todas as despesas registadas no período selecionado.",
    "Diferença entre o total de entradas e o total de saídas no período selecionado.": "Diferença entre o total de entradas e o total de saídas no período selecionado.",
    "Ticket Médio": "Ticket médio",
    "Últimos 30 dias": "Últimos 30 dias",
    "Leads convertidos": "Leads convertidos",
    "Atrasados": "Atrasados",
    "Para hoje": "Para hoje",
    "Próximos": "Próximos",
    "Configuração de follow-up": "Definições de follow-up",
    "Defina em quantos dias a clínica costuma retomar um lead pendente.": "Defina após quantos dias a clínica deve retomar um lead pendente.",
    "Intervalo padrão (dias)": "Intervalo predefinido (dias)",
    "Salvar intervalo": "Guardar intervalo",
    "Nome": "Nome",
    "Contato": "Contacto",
    "Telefone": "Telefone",
    "Origem": "Origem",
    "Setor": "Setor",
    "Campanha": "Campanha",
    "Nova Campanha": "Nova campanha",
    "Nome da campanha": "Nome da campanha",
    "Inicio": "Início",
    "Fim": "Fim",
    "Canal": "Canal",
    "Publico alvo": "Público-alvo",
    "Público alvo": "Público-alvo",
    "Instagram, Google...": "Instagram, Google...",
    "Ex: Dor lombar": "Ex.: dor lombar",
    "Criar campanha": "Criar campanha",
    "Criar Campanha": "Criar campanha",
    "Campanha criada com sucesso!": "Campanha criada com sucesso!",
    "Mes atual": "Mês atual",
    "Filtrar por origem": "Filtrar por origem",
    "Filtrar por campanha": "Filtrar por campanha",
    "Todas as origens": "Todas as origens",
    "Todas as campanhas": "Todas as campanhas",
    "marcaram consulta": "marcaram consulta",
    "convertidos": "convertidos",
    "Conversão comercial": "Conversão comercial",
    "Investimento em campanhas": "Investimento em campanhas",
    "Somatório das campanhas filtradas": "Somatório das campanhas filtradas",
    "Somatorio das campanhas filtradas": "Somatório das campanhas filtradas",
    "Detalhes da conversão comercial": "Detalhes da conversão comercial",
    "Detalhes do investimento em campanhas": "Detalhes do investimento em campanhas",
    "Período analisado": "Período analisado",
    "Os dados abaixo respeitam os filtros atuais da página Marketing.": "Os dados abaixo respeitam os filtros atuais da página Marketing.",
    "Leads considerados neste cálculo": "Leads considerados neste cálculo",
    "Marcaram consulta": "Marcaram consulta",
    "Leads que avançaram para agendamento": "Leads que avançaram para marcação",
    "Leads convertidos em pacientes": "Leads convertidos em pacientes",
    "Taxa de conversão": "Taxa de conversão",
    "Percentual de leads convertidos": "Percentagem de leads convertidos",
    "Leads considerados na conversão": "Leads considerados na conversão",
    "Investimento total": "Investimento total",
    "Campanhas filtradas": "Campanhas filtradas",
    "Campanhas consideradas no período": "Campanhas consideradas no período",
    "Investimento médio": "Investimento médio",
    "Média por campanha filtrada": "Média por campanha filtrada",
    "Custo por lead": "Custo por lead",
    "Investimento dividido pelos leads do período": "Investimento dividido pelos leads do período",
    "Detalhamento de campanhas": "Detalhe de campanhas",
    "Período": "Período",
    "Nenhuma campanha encontrada com os filtros atuais.": "Nenhuma campanha encontrada com os filtros atuais.",
    "Nenhum lead convertido no período selecionado.": "Nenhum lead convertido no período selecionado.",
    "Meta de agendamento": "Meta de marcações",
    "Progresso:": "Progresso:",
    "novos pacientes": "novos pacientes",
    "Receita projetada": "Faturação projetada",
    "Alertas operacionais": "Alertas operacionais",
    "Follow-ups vencidos / NPS pendentes": "Follow-ups vencidos / NPS pendentes",
    "Fila unificada de follow-up vencido e NPS pendente.": "Lista unificada de follow-ups vencidos e NPS pendentes.",
    "Nenhum alerta operacional no momento.": "Nenhum alerta operacional neste momento.",
    "Follow-up vencido": "Follow-up vencido",
    "NPS pendente": "NPS pendente",
    "Lead precisa de novo contato comercial.": "Lead precisa de novo contacto comercial.",
    "Plano": "Plano",
    "aguardando envio ou resposta.": "a aguardar envio ou resposta.",
    "Programa Coluna Ativa": "Programa Coluna Ativa",
    "Recondicionamento Pos-corrida": "Recondicionamento Pos-corrida",
    "Recondicionamento Pós-corrida": "Recondicionamento Pós-corrida",
    "Acupuntura Sono e Stress": "Acupuntura Sono e Stress",
    "Ombro Sem Travar": "Ombro Sem Travar",
    "Respirar Melhor com Pilates": "Respirar Melhor com Pilates",
    "Ritmo e Equilibrio Senior": "Ritmo e Equilibrio Senior",
    "Ritmo e Equilíbrio Sênior": "Ritmo e Equilíbrio Sênior",
    "Meta mensal de agendamento": "Meta mensal de marcações",
    "Defina a meta da clínica e acompanhe o progresso do mês atual.": "Defina o objetivo da clínica e acompanhe o progresso do mês atual.",
    "Quantidade de agendamentos": "Número de marcações",
    "Salvar meta": "Guardar meta",
    "Meta mensal de agendamento atualizada!": "Meta mensal de marcações atualizada!",
    "Erro ao atualizar meta": "Erro ao atualizar meta",
    "Relatório por origem": "Relatório por origem",
    "Relatório por campanha": "Relatório por campanha",
    "Relatório por setor": "Relatório por setor",
    "Marcou": "Marcações",
    "Convertidos": "Convertidos",
    "Taxa": "Taxa",
    "Investimento": "Investimento",
    "Nenhuma campanha cadastrada.": "Nenhuma campanha registada.",
    "Sem canal": "Sem canal",
    "Campanhas cadastradas": "Campanhas registadas",
    "Público": "Público",
    "Orçamento": "Orçamento",
    "Nenhuma campanha cadastrada ainda.": "Nenhuma campanha registada ainda.",
    "Ativa": "Ativa",
    "Inativa": "Inativa",
    "Editar campanha": "Editar campanha",
    "Ativar campanha": "Ativar campanha",
    "Desativar campanha": "Desativar campanha",
    "Remover campanha": "Remover campanha",
    "Campanha atualizada com sucesso!": "Campanha atualizada com sucesso!",
    "Status da campanha atualizado!": "Estado da campanha atualizado!",
    "Campanha removida da gestão ativa.": "Campanha removida da gestão ativa.",
    "Adultos de 30 a 55 anos com dor lombar recorrente e rotina de escritorio.": "Adultos de 30 a 55 anos com dor lombar recorrente e rotina de escritorio.",
    "Profissionais com pouco tempo e interesse em aulas no horário de almoço.": "Profissionais com pouco tempo e interesse em aulas no horário de almoço.",
    "Empresas de tecnologia e RH buscando palestras e triagens posturais.": "Empresas de tecnologia e RH buscando palestras e triagens posturais.",
    "Ex-pacientes e familiares que ja haviam demonstrado interesse em retornar.": "Ex-pacientes e familiares que ja haviam demonstrado interesse em retornar.",
    "leads": "leads",
    "marcaram": "com marcação",
    "Agendamentos": "Marcações",
    "Leads filtrados": "Leads filtrados",
    "Lead": "Lead",
    "clínica": "clínica",
    "Resultado": "Resultado",
    "Data": "Data",
    "Nenhum lead encontrado com os filtros atuais.": "Nenhum lead encontrado com os filtros atuais.",
    "Follow-up": "Follow-up",
    "Status": "Estado",
    "Editar Lead": "Editar lead",
    "Selecione o setor": "Selecione o setor",
    "Sem campanha": "Sem campanha",
    "Resultado comercial": "Resultado comercial",
    "Marcou consulta": "Marcou consulta",
    "Não marcou": "Não marcou",
    "Próximo follow-up": "Próximo follow-up",
    "Hora": "Hora",
    "Follow-up no horário": "Follow-up no horário",
    "Existem leads com follow-up para agora.": "Existem leads com follow-up para agora.",
    "Motivo de não agendamento": "Motivo da não marcação",
    "Sem setor": "Sem setor",
    "Setor da venda": "Setor da venda",
    "Setor do atendimento": "Setor do atendimento",
    "O setor informado diverge do setor histórico da origem": "O setor indicado diverge do setor histórico da origem",
    "O setor histórico de um recebível originado não pode ser reclassificado": "O setor histórico de um valor a receber com origem não pode ser reclassificado",
    "Nenhum lead encontrado": "Nenhum lead encontrado",
    "Nenhum lead neste grupo.": "Nenhum lead neste grupo.",
    "Abrir WhatsApp": "Abrir WhatsApp",
    "Abrir no WhatsApp": "Abrir no WhatsApp",
    "WhatsApp": "WhatsApp",
    "Telefone não informado": "Telefone não informado",
    "Telefone inválido": "Telefone inválido",
    "Conversar pelo WhatsApp": "Conversar pelo WhatsApp",
    "Informe um telefone válido para abrir o WhatsApp.": "Informe um telefone válido para abrir o WhatsApp.",
    "Oi {name}, tudo bem com você? Aqui é da {clinicName}.": "Olá {name}, tudo bem consigo? Aqui é da {clinicName}.",
    "Tudo bem?": "Tudo bem?",
    "Tudo bem com você?": "Tudo bem consigo?",
    "Tudo bem? Estou entrando em contato sobre seu agendamento.": "Tudo bem? Estou a entrar em contacto sobre a sua marcação.",
    "Converter em paciente": "Converter em paciente",
    "Converter": "Converter",
    "Convertendo...": "A converter...",
    "Vincular a este paciente": "Associar a este paciente",
    "Criar novo paciente": "Criar novo paciente",
    "Lead vinculado ao paciente existente.": "Lead associado ao paciente existente.",
    "O cadastro do paciente será criado a partir deste lead e a conversão ficará registrada no funil.": "O registo do paciente será criado a partir deste lead e a conversão ficará registada no funil.",
    "Agendar consulta": "Criar marcação",
    "Agendar consulta do lead": "Criar marcação do lead",
    "Agendando...": "A criar marcação...",
    "O agendamento será criado usando o paciente já convertido a partir deste lead.": "A marcação será criada com o paciente já convertido a partir deste lead.",
    "Para agendar uma consulta a partir do lead, primeiro converta esse lead em paciente.": "Para criar uma marcação a partir do lead, converta primeiro este lead em paciente.",
    "Limpar data": "Limpar data",
    "Informe uma data de nascimento válida.": "Introduza uma data de nascimento válida.",
    "A data de nascimento não pode ser futura.": "A data de nascimento não pode ser futura.",
    "Informe uma data de nascimento a partir de 1900.": "Introduza uma data de nascimento a partir de 1900.",
    "Bairro": "Bairro",
    "Prontuário do paciente": "Processo clínico do paciente",
    "Módulo clínico": "Módulo clínico",
    "Ficha de Avaliação": "Ficha de avaliação",
    "Avaliações clínicas por paciente": "Avaliações clínicas por paciente",
    "Abrir prontuário": "Abrir processo clínico",
    "Paciente selecionado": "Paciente selecionado",
    "Último registro": "Último registo",
    "Não foi possível carregar pacientes.": "Não foi possível carregar pacientes.",
    "Não foi possível carregar a ficha de avaliação.": "Não foi possível carregar a ficha de avaliação.",
    "Nenhum paciente encontrado para a busca.": "Nenhum paciente encontrado para a pesquisa.",
    "Selecione um paciente para abrir a ficha de avaliação.": "Selecione um paciente para abrir a ficha de avaliação.",
    "Dados de identificação não informados": "Dados de identificação não informados",
    "Evoluções": "Evoluções",
    "Evolução": "Evolução",
    "Alta": "Alta",
    "Avaliações": "Avaliações",
    "Avaliação inicial": "Avaliação inicial",
    "Avaliação padrão": "Avaliação padrão",
    "Nova Avaliação": "Nova avaliação",
    "Registrar avaliação clínica": "Registar avaliação clínica",
    "Tipo de avaliação": "Tipo de avaliação",
    "Título da avaliação": "Título da avaliação",
    "Objetivo clínico": "Objetivo clínico",
    "Achados principais": "Achados principais",
    "Hipótese / diagnóstico funcional": "Hipótese / diagnóstico funcional",
    "Plano terapêutico": "Plano terapêutico",
    "Salvar avaliação": "Guardar avaliação",
    "Avaliação registrada com sucesso.": "Avaliação registada com sucesso.",
    "Não foi possível salvar a avaliação clínica.": "Não foi possível guardar a avaliação clínica.",
    "Informações do paciente": "Informações do paciente",
    "Fluxo clínico": "Fluxo clínico",
    "Paciente → Avaliação → Evoluções": "Paciente → Avaliação → Evoluções",
    "Cada avaliação organiza as evoluções clínicas vinculadas ao atendimento.": "Cada avaliação organiza as evoluções clínicas associadas ao atendimento.",
    "Pesquisa clínica": "Pesquisa clínica",
    "Busque por sintomas, diagnóstico, profissional, data, conduta ou plano terapêutico.": "Pesquise por sintomas, diagnóstico, profissional, data, conduta ou plano terapêutico.",
    "Buscar no prontuário clínico": "Pesquisar no processo clínico",
    "Selecione uma avaliação antes de criar uma evolução.": "Selecione uma avaliação antes de criar uma evolução.",
    "Buscar sintomas, diagnósticos, medicamentos ou palavras-chave": "Pesquisar sintomas, diagnósticos, medicamentos ou palavras-chave",
    "Avaliações registradas": "Avaliações registadas",
    "Cada avaliação agrupa as evoluções clínicas vinculadas.": "Cada avaliação agrupa as evoluções clínicas associadas.",
    "Carregando avaliações...": "A carregar avaliações...",
    "Nenhuma avaliação encontrada para a busca.": "Nenhuma avaliação encontrada para a pesquisa.",
    "Nenhuma avaliação clínica registrada.": "Nenhuma avaliação clínica registada.",
    "Evoluções vinculadas": "Evoluções associadas",
    "Criar evolução": "Criar evolução",
    "Editar evolução": "Editar evolução",
    "Editar evolução clínica": "Editar evolução clínica",
    "Evolução atualizada com sucesso.": "Evolução atualizada com sucesso.",
    "Nenhuma evolução vinculada a esta avaliação.": "Nenhuma evolução associada a esta avaliação.",
    "Esta avaliação reúne evoluções antigas sem vínculo direto, preservando o histórico clínico integral.": "Esta avaliação reúne evoluções antigas sem associação direta, preservando o histórico clínico integral.",
    "Histórico preservado": "Histórico preservado",
    "Evoluções antigas sem avaliação vinculada.": "Evoluções antigas sem avaliação associada.",
    "Preservar histórico clínico registrado antes da estrutura Avaliação → Evoluções.": "Preservar histórico clínico registado antes da estrutura Avaliação → Evoluções.",
    "registro(s)": "registo(s)",
    "Documentos": "Documentos",
    "Plano de Tratamento": "Plano de tratamento",
    "Erro ao carregar histórico do paciente": "Erro ao carregar histórico do paciente",
    "Carregando histórico do paciente...": "A carregar histórico do paciente...",
    "Resumo clínico": "Resumo clínico",
    "Situação clínica do paciente": "Situação clínica do paciente",
    "Informações essenciais para orientar o atendimento atual.": "Informações essenciais para orientar o atendimento atual.",
    "Editar cadastro": "Editar cadastro",
    "Sessões restantes": "Sessões restantes",
    "Próxima consulta": "Próxima consulta",
    "Última avaliação": "Última avaliação",
    "Pendência financeira": "Pendência financeira",
    "Timeline Clínica": "Timeline clínica",
    "Histórico cronológico dos principais eventos do paciente.": "Histórico cronológico dos principais eventos do paciente.",
    "Nenhum evento clínico encontrado.": "Nenhum evento clínico encontrado.",
    "Abrir registro": "Abrir registo",
    "Cadastro realizado": "Registo concluído",
    "Consulta agendada": "Consulta marcada",
    "Avaliação criada": "Avaliação criada",
    "Evolução registrada": "Evolução registada",
    "Plano vendido": "Plano vendido",
    "Documento anexado": "Documento anexado",
    "Pagamento registrado": "Pagamento registado",
    "Pesquisar por nome, categoria ou data": "Pesquisar por nome, categoria ou data",
    "Todas as categorias": "Todas as categorias",
    "Relacionamento": "Relacionamento",
    "realizadas": "realizadas",
    "Sessões realizadas": "Sessões realizadas",
    "Objetivos alcançados": "Objetivos alcançados",
    "Pendências": "Pendências",
    "Finalizada": "Finalizada",
    "Ocultar detalhes": "Ocultar detalhes",
    "URL da foto": "URL da fotografia",
    "Foto do paciente": "Fotografia do paciente",
    "Gerenciar foto do paciente": "Gerir fotografia do paciente",
    "Inserir foto": "Adicionar fotografia",
    "Alterar foto": "Alterar fotografia",
    "Selecionar imagem": "Selecionar imagem",
    "Remover foto": "Remover fotografia",
    "Salvar foto": "Guardar fotografia",
    "Processando imagem...": "A processar imagem...",
    "Enviando foto...": "A enviar fotografia...",
    "Removendo foto...": "A remover fotografia...",
    "Pré-visualização da foto do paciente": "Pré-visualização da fotografia do paciente",
    "Use uma imagem nítida e centralizada. A pré-visualização mostra o recorte final.": "Utilize uma imagem nítida e centrada. A pré-visualização mostra o recorte final.",
    "Formatos aceitos: JPG, PNG e WEBP. Tamanho máximo:": "Formatos aceites: JPG, PNG e WEBP. Tamanho máximo:",
    "Selecione uma imagem JPG, PNG ou WEBP.": "Selecione uma imagem JPG, PNG ou WEBP.",
    "A imagem selecionada excede o tamanho máximo permitido.": "A imagem selecionada excede o tamanho máximo permitido.",
    "Não foi possível processar a imagem selecionada.": "Não foi possível processar a imagem selecionada.",
    "Foto do paciente atualizada com sucesso.": "Fotografia do paciente atualizada com sucesso.",
    "Foto do paciente removida com sucesso.": "Fotografia do paciente removida com sucesso.",
    "Alergias": "Alergias",
    "Medicamentos em uso": "Medicamentos em uso",
    "Condições clínicas": "Condições clínicas",
    "Condições clínicas relevantes": "Condições clínicas relevantes",
    "Alertas clínicos": "Alertas clínicos",
    "Alertas clínicos importantes": "Alertas clínicos importantes",
    "Plano atual": "Plano atual",
    "Sem plano ativo": "Sem plano ativo",
    "Convênio não informado": "Seguro de saúde não informado",
    "Total de consultas": "Total de consultas",
    "Acompanhamento": "Acompanhamento",
    "anos": "anos",
    "Plano ativo": "Plano ativo",
    "Nova Evolução": "Nova evolução",
    "Registrar evolução clínica": "Registar evolução clínica",
    "Template inteligente": "Modelo inteligente",
    "Selecionar modelo": "Selecionar modelo",
    "Profissional responsável": "Profissional responsável",
    "Não vincular": "Não associar",
    "Selecionar profissional": "Selecionar profissional",
    "Resumo rápido": "Resumo rápido",
    "Resumo para aparecer na timeline": "Resumo para aparecer na cronologia",
    "Escala de Dor": "Escala de dor",
    "Testes Clínicos": "Testes clínicos",
    "Conduta": "Conduta",
    "Registre o relato clínico da evolução do paciente.": "Registe o relato clínico da evolução do paciente.",
    "Ex.: 0/10, 5/10 ou EVA 7": "Ex.: 0/10, 5/10 ou EVA 7",
    "Registre testes clínicos realizados e seus resultados.": "Registe testes clínicos realizados e os seus resultados.",
    "Registre a conduta adotada e orientações realizadas.": "Registe a conduta adotada e as orientações realizadas.",
    "S - Subjetivo": "S - Subjetivo",
    "O - Objetivo": "O - Objetivo",
    "A - Avaliação": "A - Avaliação",
    "P - Plano": "P - Plano",
    "Tags clínicas": "Etiquetas clínicas",
    "Separe por vírgula": "Separe por vírgula",
    "Anexos": "Anexos",
    "Imagem": "Imagem",
    "Exame": "Exame",
    "Documento": "Documento",
    "Outros": "Outros",
    "Remover": "Remover",
    "Salvar evolução": "Guardar evolução",
    "Evolução registrada com sucesso.": "Evolução registada com sucesso.",
    "Não foi possível salvar a evolução clínica.": "Não foi possível guardar a evolução clínica.",
    "Linha do tempo clínica": "Cronologia clínica",
    "Carregando evoluções...": "A carregar evoluções...",
    "Nenhuma evolução encontrada para a busca.": "Nenhuma evolução encontrada para a pesquisa.",
    "Nenhuma evolução clínica registrada.": "Nenhuma evolução clínica registada.",
    "Última alteração": "Última alteração",
    "Anexar documento": "Anexar documento",
    "Envie PDFs, exames, imagens ou fotos vinculadas ao prontuário do paciente.": "Envie PDFs, exames, imagens ou fotografias associados ao processo clínico do paciente.",
    "Anexar": "Anexar",
    "Selecionar arquivo": "Selecionar ficheiro",
    "Selecionar arquivos": "Selecionar ficheiros",
    "Nenhum arquivo selecionado": "Nenhum ficheiro selecionado",
    "Nenhum arquivo escolhido": "Nenhum ficheiro escolhido",
    "Arquivo selecionado": "Ficheiro selecionado",
    "{count} arquivos selecionados": "{count} ficheiros selecionados",
    "Remover arquivo": "Remover ficheiro",
    "Trocar arquivo": "Substituir ficheiro",
    "Formatos aceitos: {formats}": "Formatos aceites: {formats}",
    "Limite de tamanho: {size}": "Limite de tamanho: {size}",
    "Enviando...": "A enviar...",
    "Documento anexado com sucesso.": "Documento anexado com sucesso.",
    "Selecione um arquivo para anexar.": "Selecione um ficheiro para anexar.",
    "Arquivo": "Ficheiro",
    "Foto": "Fotografia",
    "O arquivo deve ter no máximo 15MB.": "O ficheiro deve ter no máximo 15 MB.",
    "O arquivo selecionado possui mais de {max} MB.\nSelecione um arquivo menor para continuar.": "O ficheiro selecionado tem mais de {max} MB.\nSelecione um ficheiro menor para continuar.",
    "O tamanho máximo permitido para upload é {max} MB.": "O tamanho máximo permitido para carregamento é {max} MB.",
    "Limite de armazenamento atingido. Remova arquivos ou solicite ampliação da capacidade.": "Limite de armazenamento atingido. Remova ficheiros ou solicite ampliação da capacidade.",
    "Tipo de arquivo incompatível com a categoria selecionada.": "Tipo de ficheiro incompatível com a categoria selecionada.",
    "Não foi possível anexar o documento.": "Não foi possível anexar o documento.",
    "Não foi possível abrir o documento.": "Não foi possível abrir o documento.",
    "Documentos do prontuário": "Documentos do processo clínico",
    "Arquivos anexados diretamente ao prontuário do paciente.": "Ficheiros anexados diretamente ao processo clínico do paciente.",
    "Carregando documentos...": "A carregar documentos...",
    "Nenhum documento anexado ao prontuário.": "Nenhum documento anexado ao processo clínico.",
    "Enviado em": "Enviado em",
    "Visualizar": "Visualizar",
    "Baixar": "Transferir",
    "Documentos vinculados": "Documentos associados",
    "selecionado(s)": "selecionado(s)",
    "{count} documento vinculado": "{count} documento associado",
    "{count} documentos vinculados": "{count} documentos associados",
    "Nenhum documento vinculado a este registro.": "Nenhum documento associado a este registo.",
    "Documentos disponíveis para vincular": "Documentos disponíveis para associar",
    "Remover vínculo": "Remover associação",
    "Vincular documento": "Associar documento",
    "Tipos aceitos: PDF, JPG, PNG e WEBP. Limite:": "Tipos aceites: PDF, JPG, PNG e WEBP. Limite:",
    "Nenhum documento do paciente disponível para vincular.": "Nenhum documento do paciente disponível para associar.",
    "Documento removido com sucesso.": "Documento removido com sucesso.",
    "Vinculado a": "Associado a",
    "registro clínico": "registo clínico",
    "registros clínicos": "registos clínicos",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite: 15MB.": "Tipos aceites: PDF, JPG, PNG, WEBP e GIF. Limite: 15 MB.",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite:": "Tipos aceites: PDF, JPG, PNG, WEBP e GIF. Limite:",
    "Evolução de": "Evolução de",
    "Anexos vinculados às evoluções clínicas do paciente.": "Anexos associados às evoluções clínicas do paciente.",
    "Receita do paciente": "Receita do paciente",
    "Carregando financeiro do paciente...": "A carregar financeiro do paciente...",
    "Nenhum lançamento financeiro encontrado.": "Nenhum lançamento financeiro encontrado.",
    "Último pagamento": "Último pagamento",
    "Paciente desde": "Paciente desde",
    "Preferências e campanhas": "Preferências e campanhas",
    "Sem dados adicionais de marketing vinculados no cadastro atual.": "Sem dados adicionais de marketing associados ao registo atual.",
    "Dados comerciais e origem do relacionamento do paciente.": "Dados comerciais e origem da relação com o paciente.",
    "Registrar queixa principal, objetivos do paciente e contexto clínico inicial.": "Registar queixa principal, objetivos do paciente e contexto clínico inicial.",
    "Descrever achados relevantes da avaliação física, testes funcionais e observações clínicas.": "Descrever achados relevantes da avaliação física, testes funcionais e observações clínicas.",
    "Definir hipótese clínica, diagnóstico funcional e prioridades terapêuticas.": "Definir hipótese clínica, diagnóstico funcional e prioridades terapêuticas.",
    "Definir plano terapêutico, frequência, metas e critérios de acompanhamento.": "Definir plano terapêutico, frequência, objetivos e critérios de acompanhamento.",
    "Comparar evolução clínica, adesão ao plano e novas demandas do paciente.": "Comparar evolução clínica, adesão ao plano e novas necessidades do paciente.",
    "Registrar mudanças de mobilidade, força, dor, função e desempenho.": "Registar alterações de mobilidade, força, dor, função e desempenho.",
    "Revisar interpretação clínica frente aos objetivos terapêuticos.": "Rever interpretação clínica face aos objetivos terapêuticos.",
    "Atualizar condutas, metas, frequência e próximos passos.": "Atualizar condutas, objetivos, frequência e próximos passos.",
    "Registrar critérios clínicos de alta e percepção funcional do paciente.": "Registar critérios clínicos de alta e perceção funcional do paciente.",
    "Documentar achados finais, evolução funcional e pontos de atenção.": "Documentar achados finais, evolução funcional e pontos de atenção.",
    "Concluir ciclo terapêutico conforme objetivos atingidos ou encaminhamentos necessários.": "Concluir ciclo terapêutico conforme objetivos atingidos ou encaminhamentos necessários.",
    "Orientar manutenção, prevenção de recidiva e sinais para retorno.": "Orientar manutenção, prevenção de recidiva e sinais para retorno.",
    "Avaliação inicial e definição de conduta.": "Avaliação inicial e definição de conduta.",
    "Paciente relata queixa principal, início dos sintomas, fatores de melhora/piora e expectativas do tratamento.": "Paciente relata queixa principal, início dos sintomas, fatores de melhoria/agravamento e expectativas do tratamento.",
    "Realizada avaliação física, inspeção, testes funcionais e registro dos achados relevantes.": "Realizada avaliação física, inspeção, testes funcionais e registo dos achados relevantes.",
    "Hipótese clínica inicial compatível com o quadro apresentado. Definidas prioridades terapêuticas.": "Hipótese clínica inicial compatível com o quadro apresentado. Definidas prioridades terapêuticas.",
    "Orientações iniciais, proposta de plano terapêutico e agendamento das próximas sessões.": "Orientações iniciais, proposta de plano terapêutico e marcação das próximas sessões.",
    "Acompanhamento de retorno.": "Acompanhamento de retorno.",
    "Paciente em retorno para acompanhamento da resposta clínica e evolução funcional.": "Paciente em retorno para acompanhamento da resposta clínica e evolução funcional.",
    "Registrar testes clínicos ou funcionais realizados no retorno.": "Registar testes clínicos ou funcionais realizados no retorno.",
    "Manter ou ajustar condutas conforme resposta clínica observada.": "Manter ou ajustar condutas conforme resposta clínica observada.",
    "Paciente relata evolução desde a última consulta, adesão às orientações e sintomas atuais.": "Paciente relata evolução desde a última consulta, adesão às orientações e sintomas atuais.",
    "Reavaliados sinais clínicos, mobilidade, dor, função e resposta às condutas anteriores.": "Reavaliados sinais clínicos, mobilidade, dor, função e resposta às condutas anteriores.",
    "Quadro em acompanhamento, com ajustes conforme resposta clínica observada.": "Quadro em acompanhamento, com ajustes conforme resposta clínica observada.",
    "Manter ou ajustar condutas, reforçar orientações e programar próximo retorno.": "Manter ou ajustar condutas, reforçar orientações e programar próximo retorno.",
    "Reavaliação clínica periódica.": "Reavaliação clínica periódica.",
    "Paciente informa percepção de progresso, limitações persistentes e novas queixas, se houver.": "Paciente informa perceção de progresso, limitações persistentes e novas queixas, se houver.",
    "Comparados indicadores funcionais, amplitude, força, dor e desempenho em relação à avaliação anterior.": "Comparados indicadores funcionais, amplitude, força, dor e desempenho em relação à avaliação anterior.",
    "Evolução analisada frente aos objetivos terapêuticos e necessidade de ajustes.": "Evolução analisada face aos objetivos terapêuticos e necessidade de ajustes.",
    "Atualizar metas, condutas e frequência conforme evolução clínica.": "Atualizar objetivos, condutas e frequência conforme evolução clínica.",
    "Alta clínica": "Alta clínica",
    "Evolução de alta clínica.": "Evolução de alta clínica.",
    "Evolução final do ciclo terapêutico, com critérios de alta e orientações registradas.": "Evolução final do ciclo terapêutico, com critérios de alta e orientações registadas.",
    "Registrar testes finais, medidas funcionais e critérios clínicos avaliados.": "Registar testes finais, medidas funcionais e critérios clínicos avaliados.",
    "Encerramento do ciclo terapêutico.": "Encerramento do ciclo terapêutico.",
    "Paciente relata melhora funcional e compreensão das orientações para manutenção.": "Paciente relata melhoria funcional e compreensão das orientações para manutenção.",
    "Critérios clínicos e funcionais de alta avaliados e registrados.": "Critérios clínicos e funcionais de alta avaliados e registados.",
    "Paciente apto para alta clínica conforme evolução e objetivos atingidos.": "Paciente apto para alta clínica conforme evolução e objetivos atingidos.",
    "Orientações domiciliares, sinais de alerta e recomendação de retorno se necessário.": "Orientações domiciliárias, sinais de alerta e recomendação de retorno se necessário.",
    "Ajuste de tratamento": "Ajuste de tratamento",
    "Ajuste de conduta terapêutica.": "Ajuste de conduta terapêutica.",
    "Evolução registrada para ajuste do plano terapêutico conforme resposta do paciente.": "Evolução registada para ajuste do plano terapêutico conforme resposta do paciente.",
    "Registrar achados ou testes que justificam o ajuste terapêutico.": "Registar achados ou testes que justificam o ajuste terapêutico.",
    "Paciente relata resposta parcial, desconfortos ou necessidade de mudança no plano.": "Paciente relata resposta parcial, desconfortos ou necessidade de alteração no plano.",
    "Observados achados que indicam ajuste de carga, técnica, frequência ou estratégia.": "Observados achados que indicam ajuste de carga, técnica, frequência ou estratégia.",
    "Conduta revisada de acordo com tolerância, resposta clínica e objetivos.": "Conduta revista de acordo com tolerância, resposta clínica e objetivos.",
    "Aplicar ajustes definidos e monitorar resposta nas próximas sessões.": "Aplicar ajustes definidos e monitorizar resposta nas próximas sessões.",
    "Ansiedade": "Ansiedade",
    "Hipertensão": "Hipertensão",
    "Diabetes": "Diabetes",
    "Obesidade": "Obesidade",
    "Selecione": "Selecione",
    "Valor previsto": "Valor previsto",
    "Apagar lead": "Eliminar lead",
    "Apagar": "Eliminar",
    "Esse lead será removido permanentemente do cadastro.": "Este lead será eliminado permanentemente do registo.",
    "Novo": "Novo",
    "Em Contato": "Em Contato",
    "Em contato": "Em contacto",
    "Convertido": "Convertido",
    "Não agendado": "Não marcado",
    "Preço": "Preço",
    "Sem retorno": "Sem resposta",
    "Sem interesse": "Sem interesse",
    "Conflito de horário": "Conflito de horário",
    "Já está em tratamento": "Já está em tratamento",
    "Distância": "Distância",
    "Conveniência": "Conveniência",
    "Outro": "Outro",
    "Indicação": "Indicação",
    "Site": "Site",
    "Presencial": "Presencial",
    "Fisioterapia": "Fisioterapia",
    "Acupuntura": "Acupuntura",
    "Pilates": "Pilates",
    "Abril Retorno sem Dor": "Abril Retorno sem Dor",
    "Pilates no Almoco": "Pilates no Almoco",
    "Pilates no Almoço": "Pilates no Almoço",
    "Semana da Postura Corporativa": "Semana da Postura Corporativa",
    "Volta pra Casa": "Volta pra Casa",
    "Chegou por reels sobre dor ao acordar e queria avaliação rápida.": "Chegou por reels sobre dor ao acordar e queria avaliação rápida.",
    "Corredor amador procurando retorno seguro apos dor no joelho.": "Corredor amador procurando retorno seguro apos dor no joelho.",
    "Indicação do RH da empresa parceira para pilates no almoço.": "Indicação do RH da empresa parceira para pilates no almoço.",
    "Chegou por indicacao da esposa para melhorar sono e tensao cervical.": "Chegou por indicacao da esposa para melhorar sono e tensao cervical.",
    "Pediu plano para voltar a treinar sem piorar dor no quadril.": "Pediu plano para voltar a treinar sem piorar dor no quadril.",
    "Ja saiu com aula teste marcada para a proxima semana.": "Ja saiu com aula teste marcada para a proxima semana.",
    "RH gostou da proposta, mas o colaborador pediu mais prazo para decidir.": "RH gostou da proposta, mas o colaborador pediu mais prazo para decidir.",
    "Quer entender se a clínica atende no horário do fim do expediente.": "Quer entender se a clínica atende no horário do fim do expediente.",
    "Preencheu formulário à noite e ainda não retornou ao WhatsApp.": "Preencheu formulário à noite e ainda não retornou ao WhatsApp.",
    "Interesse em pilates para fortalecer depois da gestação.": "Interesse em pilates para fortalecer depois da gestação.",
    "Parou no preco do pacote e pediu segunda opcao mais enxuta.": "Parou no preco do pacote e pediu segunda opcao mais enxuta.",
    "Mudou de cidade e desistiu do agendamento.": "Mudou de cidade e desistiu da marcação.",
    "Pacientes Ativos": "Pacientes ativos",
    "Consultas": "Consultas",
    "Status dos Agendamentos": "Estado das marcações",
    "Agendado": "Marcado",
    "Em atendimento": "Em atendimento",
    "Atendimento realizado": "Atendimento realizado",
    "Faltou": "Faltou",
    "Em Andamento": "Em curso",
    "Concluído": "Concluído",
    "Não Compareceu": "Falta",
    "Não foi possível marcar o agendamento como atendimento realizado.": "Não foi possível marcar a marcação como atendimento realizado.",
    "CAC": "CAC",
    "Custo de Aquisição": "Custo de aquisição",
    "LTV": "LTV",
    "Valor por Paciente": "Valor por paciente",
    "ROI": "ROI",
    "Retorno sobre Investimento": "Retorno sobre investimento",
    "Pesquisa de experiência": "Questionário de experiência",
    "Carregando pesquisa...": "A carregar questionário...",
    "Esta pesquisa não está disponível.": "Este questionário não está disponível.",
    "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.": "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipa.",
    "Sua nota": "A sua nota",
    "Conte para a clínica o que funcionou bem ou o que pode melhorar.": "Diga à clínica o que funcionou bem ou o que pode melhorar.",
    "Enviar resposta": "Enviar resposta",
    "Desenvolvido por": "Desenvolvido por",
    "Operação inteligente para clínicas em crescimento": "Operação inteligente para clínicas em crescimento",
    "organiza a jornada da clínica do primeiro acesso até a operação diária.": "organiza a jornada da clínica do primeiro acesso até a operação diária.",
    "Uma camada de gestão criada para abrir clínicas, estruturar equipes e acompanhar marketing, agenda, atendimento e resultados com mais clareza.": "Uma camada de gestão criada para abrir clínicas, estruturar equipas e acompanhar marketing, agenda, consulta e resultados com mais clareza.",
    "Começar onboarding": "Iniciar onboarding",
    "Acessar plataforma": "Aceder à plataforma",
    "Portal Principal": "Portal Principal",
    "Onboarding guiado": "Onboarding guiado",
    "Cadastre a clínica e o administrador principal em um fluxo único e direto, com acesso imediato para começar a operação no": "Registe a clínica e o administrador principal num fluxo único e direto, com acesso imediato para iniciar a operação no",
    "Criar nova clínica": "Criar nova clínica",
    "Onboarding rápido para novas clínicas": "Onboarding rápido para novas clínicas",
    "Operação financeira, agenda e atendimento em um só lugar": "Operação financeira, agenda e atendimento num só lugar",
    "Estrutura preparada para multi-clínica e administração central": "Estrutura preparada para multi-clínica e administração central",
    "Entrada pública clara para login, cadastro e expansão do produto.": "Entrada pública clara para início de sessão, registo e expansão do produto.",
    "Clínicas organizadas com isolamento de dados e administração por unidade.": "Clínicas organizadas com isolamento de dados e administração por unidade.",
    "Visão integrada de operação, comercial, agenda, atendimento e financeiro.": "Visão integrada de operação, comercial, agenda, atendimento e financeiro.",
    "Plataforma para operação de clínicas, equipes e crescimento com mais clareza.": "Plataforma para operação de clínicas, equipas e crescimento com mais clareza.",
    "Estruture sua clínica e inicie a operação com governança desde o primeiro acesso.": "Estruture a sua clínica e inicie a operação com governança desde o primeiro acesso.",
    "Configure a clínica e o administrador principal no mesmo fluxo.": "Configure a clínica e o administrador principal no mesmo fluxo.",
    "Selecione uma nota de 0 a 10.": "Selecione uma nota de 0 a 10.",
    "Resposta enviada com sucesso.": "Resposta enviada com sucesso.",
    "Algo deu errado. Tente novamente.": "Algo correu mal. Tente novamente.",
    "Revise os campos informados e tente novamente.": "Revise os campos informados e tente novamente.",
    "Preencha o campo obrigatório: {fieldLabel}.": "Preencha o campo obrigatório: {fieldLabel}.",
    "Preencha todos os campos obrigatórios.": "Preencha todos os campos obrigatórios.",
    "Revise o valor informado para o campo: {fieldLabel}.": "Reveja o valor indicado no campo: {fieldLabel}.",
    "Revise os campos clínicos informados.": "Reveja os campos clínicos indicados.",
    "O campo informado precisa ser maior ou igual a 15.": "O campo informado precisa ser maior ou igual a 15.",
    "Acesso em análise ou bloqueado!": "Acesso em análise ou bloqueado!",
    "Acesso bloqueado": "Acesso bloqueado",
    "Seu perfil atual não possui permissão para acessar esta página.": "O seu perfil atual não tem permissão para aceder a esta página.",
    "Você não tem permissão para executar esta ação.": "Não tem permissão para executar esta ação.",
    "Você não tem permissão para editar a agenda.": "Não tem permissão para editar a agenda.",
    "Você não tem permissão para registrar lançamentos financeiros.": "Não tem permissão para registar lançamentos financeiros.",
    "Permissões do profissional": "Permissões do profissional",
    "Defina quais módulos este profissional poderá acessar.": "Defina a que módulos este profissional poderá aceder.",
    "Prontuário": "Processo clínico",
    "Erro ao fazer login": "Erro ao iniciar sessão",
    "Este portal é exclusivo para super admin.": "Este portal é exclusivo para super admin.",
    "As senhas não coincidem.": "As palavras-passe não coincidem.",
    "Erro ao criar conta": "Erro ao criar conta",
    "Falha ao consultar CEP": "Falha ao consultar o código postal",
    "CEP não encontrado.": "Código postal não encontrado.",
    "Não foi possível consultar o CEP.": "Não foi possível consultar o código postal.",
    "Senha inicial": "Palavra-passe inicial",
    "Nova senha": "Nova palavra-passe",
    "Buscar por nome ou email": "Pesquisar por nome ou e-mail",
    "Atualizando dados de marketing...": "A atualizar dados de marketing...",
    "Sem respostas ainda": "Ainda sem respostas",
    "Lead atualizado com sucesso!": "Lead atualizado com sucesso!",
    "Lead convertido em paciente com sucesso!": "Lead convertido em paciente com sucesso!",
    "Consulta agendada a partir do lead!": "Marcação criada a partir do lead!",
    "Criar": "Criar",
    "Atualizar": "Atualizar",
    "Cancelar": "Cancelar",
    "Confirmar": "Confirmar",
    "Total de registros": "Total de registos",
    "Página": "Página",
    "Página {currentPage} de {totalPages}": "Página {currentPage} de {totalPages}",
    "Registros por página": "Registos por página",
    "Anterior": "Anterior",
    "Processando...": "A processar...",
    "Salvando...": "A guardar...",
    "Tipo": "Tipo",
    "Tipo de tratamento": "Tipo de tratamento",
    "Tipo de atendimento": "Tipo de atendimento",
    "Tipo não informado": "Tipo não indicado",
    "Todos os tipos de tratamento": "Todos os tipos de tratamento",
    "Iniciar atendimento": "Iniciar atendimento",
    "Cadastre uma avaliação antes de iniciar uma evolução.": "Registe uma avaliação antes de iniciar uma evolução.",
    "Data da avaliação": "Data da avaliação",
    "Última evolução": "Última evolução",
    "Status do paciente": "Estado do paciente",
    "Paciente ativado com sucesso!": "Paciente ativado com sucesso!",
    "Paciente inativado com sucesso!": "Paciente inativado com sucesso!",
    "Motivos de Não Agendamento": "Motivos de não marcação",
    "Principais objeções dos leads que não realizaram um agendamento.": "Principais objeções dos leads que não efetuaram uma marcação.",
    "Leads não agendados": "Leads sem marcação",
    "Leads não agendados por origem": "Leads sem marcação por origem",
    "Distribuição server-side dos leads com desfecho não agendado.": "Distribuição no servidor dos leads com desfecho sem marcação.",
    "Principal motivo": "Principal motivo",
    "Representatividade": "Representatividade",
    "Nenhum motivo registrado": "Nenhum motivo registado",
    "Nenhum motivo de não agendamento encontrado para os filtros selecionados.": "Não foi encontrado nenhum motivo de não marcação para os filtros selecionados.",
    "Leads não agendados por motivo": "Leads sem marcação por motivo",
    "Detalhamento dos leads não agendados": "Detalhe dos leads sem marcação",
    "Data do desfecho": "Data do desfecho",
    "Desfecho registrado em": "Desfecho registado em",
    "Desfechos sem data definida": "Desfechos sem data definida",
    "Cobertura histórica incompleta": "Cobertura histórica incompleta",
    "Registros legados": "Registos legados",
    "Dados anteriores não possuem data confiável.": "Dados anteriores não possuem data fiável.",
    "Não foi possível determinar a data": "Não foi possível determinar a data",
    "Sem origem": "Sem origem",
    "Sem motivo": "Sem motivo",
    "Origem inativa": "Origem inativa",
    "Comercial responsável": "Responsável comercial",
    "Último follow-up": "Último follow-up",
    "Status comercial": "Estado comercial",
    "Nenhum lead encontrado para este motivo.": "Não foi encontrado nenhum lead para este motivo.",
    "Catálogo de Motivos de Não Agendamento": "Catálogo de motivos de não marcação",
    "Padronize as objeções utilizadas no acompanhamento comercial dos leads.": "Uniformize as objeções utilizadas no acompanhamento comercial dos leads.",
    "Novo motivo de não agendamento": "Novo motivo de não marcação",
    "Motivo adicionado com sucesso!": "Motivo adicionado com sucesso!",
    "Motivo atualizado com sucesso!": "Motivo atualizado com sucesso!",
    "Motivo ativado com sucesso!": "Motivo ativado com sucesso!",
    "Motivo inativado com sucesso!": "Motivo desativado com sucesso!",
    "Adicionar motivo": "Adicionar motivo",
    "Motivo": "Motivo",
    "Ativar": "Ativar",
    "Inativar": "Desativar",
    "Editar motivo de não agendamento": "Editar motivo de não marcação",
    "Inativar motivo?": "Desativar motivo?",
    "O motivo deixará de aparecer em novos cadastros, mas continuará preservado no histórico.": "O motivo deixará de aparecer em novos registos, mas continuará preservado no histórico.",
    "O motivo deve ter entre 2 e 100 caracteres": "O motivo deve ter entre 2 e 100 caracteres",
    "Já existe um motivo de não agendamento com este nome": "Já existe um motivo de não marcação com este nome",
    "Motivo de não agendamento não encontrado": "Motivo de não marcação não encontrado",
    "Selecione um motivo de não agendamento ativo": "Selecione um motivo de não marcação ativo",
    "Não respondeu": "Não respondeu",
    "Preço / Achou caro": "Preço / Considerou caro",
    "Queria convênio": "Pretendia utilizar um seguro de saúde",
    "Sem horário compatível": "Sem horário compatível",
    "Vai pensar": "Vai pensar",
    "Já fechou em outra clínica": "Já escolheu outra clínica",
    "Nenhum atendimento encontrado para o tipo e período selecionados.": "Não foi encontrado nenhum atendimento para o tipo e período selecionados.",
    "Categoria": "Categoria",
    "Pagamento": "Pagamento",
    "Não Pago": "Não Pago",
    "Novo Usuário": "Novo utilizador",
    "Novo usuário": "Novo utilizador",
    "Novo usuário da clínica": "Novo utilizador da clínica",
    "Criar usuário": "Criar utilizador",
    "Faturamento": "Faturação",
    "Margem": "Margem",
    "Função": "Função",
    "Permissões": "Permissões",
    "Administrador": "Administrador",
    "Administradores": "Administradores",
    "Administradores ativos": "Administradores ativos",
    "Ativo": "Ativo",
    "Ativos": "Ativos",
    "Inativo": "Inativo",
    "Inativos": "Inativos",
    "Usuário": "Utilizador",
    "Usuário ativo": "Utilizador ativo",
    "Usuários ativos": "Utilizadores ativos",
    "Usuários cadastrados": "Utilizadores registados",
    "Usuários da Clínica": "Utilizadores da clínica",
    "Registrar NPS pós-tratamento": "Registar NPS pós-tratamento",
    "Um feedback por plano de tratamento. Se já existir, o registro será atualizado.": "Um feedback por plano de tratamento. Se já existir, o registo será atualizado.",
    "Cadastre ou avance planos de tratamento para começar a registrar NPS.": "Registe ou avance planos de tratamento para começar a registar NPS.",
    "Nota NPS (0 a 10)": "Nota NPS (0 a 10)",
    "O que o paciente destacou sobre a experiência?": "O que o paciente destacou sobre a experiência?",
    "Última atualização em": "Última atualização em",
    "Atualizar NPS": "Atualizar NPS",
    "Registrar NPS": "Registar NPS",
    "Acessos da clínica": "Acessos da clínica",
    "Alterar permissão do usuário": "Alterar permissão do utilizador",
    "Alterar função do usuário": "Alterar função do utilizador",
    "Ativar usuário": "Ativar utilizador",
    "Desativar usuário": "Desativar utilizador",
    "Reativar usuário": "Reativar utilizador",
    "Bloqueada": "Bloqueada",
    "Bloqueado": "Bloqueado",
    "Bloquear clínica": "Bloquear clínica",
    "Liberar clínica": "Desbloquear clínica",
    "Convidar novo super admin": "Convidar novo super admin",
    "Email": "E-mail",
    "Clínicas cadastradas": "Clínicas registadas",
    "Clínicas ativas": "Clínicas ativas",
    "Super admins ativos": "Super admins ativos",
    "Usuários monitorados": "Utilizadores monitorizados",
    "Erro ao carregar painel de super admin": "Erro ao carregar o painel de super admin",
    "A clínica": "A clínica",
    "voltará a acessar a plataforma normalmente.": "voltará a aceder à plataforma normalmente.",
    "terá o acesso ao sistema interrompido até nova liberação.": "terá o acesso ao sistema interrompido até nova autorização.",
    "Liberar acesso": "Autorizar acesso",
    "Bloquear acesso": "Bloquear acesso",
    "Clínica liberada com sucesso.": "Clínica desbloqueada com sucesso.",
    "Clínica bloqueada com sucesso.": "Clínica bloqueada com sucesso.",
    "Erro ao atualizar status da clínica": "Erro ao atualizar o estado da clínica",
    "Conceder acesso de super admin": "Conceder acesso de super admin",
    "passará a ter acesso global ao sistema": "passará a ter acesso global ao sistema",
    "com o email": "com o e-mail",
    "Confirmar convite": "Confirmar convite",
    "Super admin convidado com sucesso.": "Super admin convidado com sucesso.",
    "Erro ao convidar super admin": "Erro ao convidar super admin",
    "Reativar super admin": "Reativar super admin",
    "Desativar super admin": "Desativar super admin",
    "voltará a acessar o painel global normalmente.": "voltará a aceder normalmente ao painel global.",
    "perderá o acesso ao painel global até nova liberação.": "perderá o acesso ao painel global até nova autorização.",
    "Revogar acesso": "Revogar acesso",
    "Super admin reativado com sucesso.": "Super admin reativado com sucesso.",
    "Super admin desativado com sucesso.": "Super admin desativado com sucesso.",
    "Erro ao atualizar super admin": "Erro ao atualizar super admin",
    "Super Admin": "Super Admin",
    "Buscar clínica, cidade, usuário ou e-mail": "Pesquisar clínica, localidade, utilizador ou e-mail",
    "Buscar por nome ou telefone...": "Pesquisar por nome ou telefone...",
    "Busque clínicas, confira seus usuários e controle liberação ou bloqueio operacional.": "Pesquise clínicas, consulte os utilizadores e controle a ativação ou o bloqueio operacional.",
    "Carregando clínicas...": "A carregar clínicas...",
    "Carregando usuários...": "A carregar utilizadores...",
    "Cidade não informada": "Localidade não indicada",
    "Classificar setor": "Classificar por setor",
    "Clínicas da plataforma": "Clínicas da plataforma",
    "Confirmar alteração": "Confirmar alteração",
    "Define se esse usuário pode acessar a clínica.": "Define se este utilizador pode aceder à clínica.",
    "Deixe em branco para manter": "Deixe em branco para manter a atual",
    "Editar usuário": "Editar utilizador",
    "Erro ao atualizar usuário": "Erro ao atualizar utilizador",
    "Erro ao carregar usuários": "Erro ao carregar utilizadores",
    "Erro ao criar usuário": "Erro ao criar utilizador",
    "Filtros aplicados a esta lista.": "Filtros aplicados a esta lista.",
    "Gerencie seus leads e conversões": "Gerir leads e conversões",
    "Intervalo de follow-up atualizado!": "Intervalo de follow-up atualizado!",
    "Lead criado com sucesso!": "Lead criado com sucesso!",
    "Origens do Lead": "Origens do lead",
    "Use este cadastro para padronizar as origens utilizadas no funil.": "Use este cadastro para padronizar as origens utilizadas no funil.",
    "Nova origem": "Nova origem",
    "Adicionar origem": "Adicionar origem",
    "Origem adicionada com sucesso!": "Origem adicionada com sucesso!",
    "Origem removida com sucesso!": "Origem removida com sucesso!",
    "Erro ao adicionar origem": "Erro ao adicionar origem",
    "Erro ao remover origem": "Erro ao remover origem",
    "Data de criação": "Data de criação",
    "Todos os períodos": "Todos os períodos",
    "Nenhum usuário adicional encontrado para esta clínica.": "Sem utilizadores adicionais nesta clínica.",
    "Nenhum usuário encontrado com os filtros informados.": "Sem utilizadores com os filtros indicados.",
    "Nenhum usuário vinculado a esta clínica.": "Nenhum utilizador associado a esta clínica.",
    "Nenhuma clínica encontrada com o filtro informado.": "Nenhuma clínica encontrada com o filtro indicado.",
    "Sem e-mail cadastrado": "Sem e-mail registado",
    "Sem telefone cadastrado": "Sem telefone registado",
    "Sem vínculo": "Sem associação",
    "Selecione a clínica de destino antes de importar pacientes, profissionais ou agenda do Zen Fisio.": "Selecione a clínica de destino antes de importar pacientes, profissionais ou agenda do Zen Fisio.",
    "Importe agendamentos históricos preservando o AgendamentoId, vinculando pacientes pelo id legado e profissionais pelo nome importado anteriormente.": "Importe agendamentos históricos preservando o AgendamentoId, associando pacientes pelo id legado e profissionais pelo nome importado anteriormente.",
    "Importar agenda": "Importar agenda",
    "Importando agenda...": "A importar agenda...",
    "Importação da agenda concluída.": "Importação da agenda concluída.",
    "Horários inválidos": "Horários inválidos",
    "Duplicados": "Duplicados",
    "Baixe o CSV para revisar vínculos ausentes, horários inválidos, duplicidades e setores criados.": "Transfira o CSV para rever associações ausentes, horários inválidos, duplicidades e setores criados.",
    "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda ou financeiro do Zen Fisio.": "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda ou financeiro do Zen Fisio.",
    "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.": "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou processo clínico do Zen Fisio.",
    "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.": "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.",
    "Importar financeiro": "Importar financeiro",
    "Importando financeiro...": "A importar financeiro...",
    "Importação financeira concluída.": "Importação financeira concluída.",
    "Preparando importação...": "A preparar importação...",
    "Importando dados...": "A importar dados...",
    "Validando registros...": "A validar registos...",
    "Finalizando importação...": "A finalizar importação...",
    "Importação em andamento": "Importação em curso",
    "Importação finalizada": "Importação finalizada",
    "Importação não concluída": "Importação não concluída",
    "Importação finalizada com sucesso.": "Importação finalizada com sucesso.",
    "A importação não foi concluída. Tente novamente.": "A importação não foi concluída. Tente novamente.",
    "Não foi possível concluir a importação. Tente novamente.": "Não foi possível concluir a importação. Tente novamente.",
    "Os dados estão sendo processados.": "Os dados estão a ser processados.",
    "Progresso da importação": "Progresso da importação",
    "Registros com erro/alerta": "Registos com erro/alerta",
    "Nova importação": "Nova importação",
    "Tentar novamente": "Tentar novamente",
    "Aguarde a importação em andamento terminar antes de iniciar outra.": "Aguarde a importação em curso terminar antes de iniciar outra.",
    "Agrupados": "Agrupados",
    "Baixe o CSV para revisar vínculos ausentes, formas other, agrupamentos e movimentos ignorados.": "Transfira o CSV para rever associações ausentes, formas other, agrupamentos e movimentos ignorados.",
    "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.": "Transfira o CSV para rever associações ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.",
    "Selecione o arquivo": "Selecione o ficheiro",
    "Avaliações clínicas": "Avaliações clínicas",
    "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.": "Importe avaliações do Zen Fisio como registos legados em Markdown, associados ao paciente, profissional e marcação.",
    "Importar avaliações": "Importar avaliações",
    "Importando avaliações...": "A importar avaliações...",
    "Evoluções clínicas": "Evoluções clínicas",
    "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.": "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.",
    "Importar evoluções": "Importar evoluções",
    "Importando evoluções...": "A importar evoluções...",
    "Importação de avaliações concluída.": "Importação de avaliações concluída.",
    "Importação de evoluções concluída.": "Importação de evoluções concluída.",
    "Importação Zen Fisio - pacientes": "Importação Zen Fisio - pacientes",
    "Importação Zen Fisio - profissionais": "Importação Zen Fisio - profissionais",
    "Importação Zen Fisio - agenda": "Importação Zen Fisio - agenda",
    "Importação Zen Fisio - financeiro": "Importação Zen Fisio - financeiro",
    "Importação Zen Fisio - prontuário": "Importação Zen Fisio - processo clínico",
    "Importação Zen Fisio revertida": "Importação Zen Fisio revertida",
    "Clínica bloqueada": "Clínica bloqueada",
    "Clínica liberada": "Clínica desbloqueada",
    "Super admin convidado": "Super admin convidado",
    "Super admin desativado": "Super admin desativado",
    "Super admin reativado": "Super admin reativado",
    "Acesso redefinido": "Acesso redefinido",
    "Sem detalhes adicionais.": "Sem detalhes adicionais.",
    "Convite emitido para": "Convite emitido para",
    "Senha redefinida e acesso reativado.": "Senha redefinida e acesso reativado.",
    "Senha redefinida mantendo o status atual.": "Senha redefinida mantendo o estado atual.",
    "Acesso liberado.": "Acesso liberado.",
    "Acesso revogado.": "Acesso revogado.",
    "Registros importados": "Registos importados",
    "ignorados": "ignorados",
    "alertas": "alertas",
    "Ação registrada com sucesso.": "Ação registada com sucesso.",
    "Sem agendamento": "Sem marcação",
    "Grupos vazios": "Grupos vazios",
    "Datas inválidas": "Datas inválidas",
    "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.": "Transfira o CSV para rever marcações ausentes, grupos vazios, duplicidades e datas inválidas.",
    "Selecionar agendamento": "Selecionar marcação",
    "Seu perfil atual não possui permissão para cadastrar ou editar usuários.": "O seu perfil atual não tem permissão para criar ou editar utilizadores.",
    "Somente administradores da clínica podem gerenciar acessos.": "Apenas administradores da clínica podem gerir acessos.",
    "Todas as funções": "Todas as funções",
    "Todos": "Todos",
    "Vincular paciente": "Associar paciente",
    "Vincular profissional": "Associar profissional",
    "Você": "Eu",
    "admin(s)": "admin(s)",
    "ativo(s)": "ativo(s)",
    "no plano": "no plano",
    "usuario@clínica.com": "utilizador@clinica.pt",
    "usuário(s)": "utilizador(es)",
    "Usuário atualizado com sucesso.": "Utilizador atualizado com sucesso.",
    "Usuário criado com sucesso.": "Utilizador criado com sucesso.",
    "Usuário desativado com sucesso.": "Utilizador desativado com sucesso.",
    "Usuário reativado com sucesso.": "Utilizador reativado com sucesso.",
    "Como foi sua experiência com": "Como foi a sua experiência com",
    "Data de Nascimento": "Data de nascimento",
    "Nome completo": "Nome completo",
    "Obrigado pelo feedback.": "Obrigado pelo feedback.",
    "Olá": "Olá",
    "Pendências de follow-up": "Follow-ups pendentes",
    "Priorize os leads com retorno mais urgente.": "Dê prioridade aos leads com retorno mais urgente.",
    "Resposta registrada em": "Resposta registada em",
    "Salvar alterações": "Guardar alterações",
    "Selecione um plano e informe a nota NPS.": "Selecione um plano e indique a nota NPS.",
    "Selecione uma clínica": "Selecione uma clínica",
    "CPF": "NIF",
    "CEP": "Código postal",
    "Agendamentos sem plano de tratamento": "Marcações sem plano de tratamento",
    "Atendimentos Avulsos": "Consultas avulsas",
    "Ver histórico completo": "Ver histórico completo",
    "Carregando trilha de auditoria...": "A carregar trilho de auditoria...",
    "Carregando super admins...": "A carregar super admins...",
    "Nenhum evento de auditoria registrado ainda.": "Nenhum evento de auditoria registado ainda.",
    "Auditoria recente": "Auditoria recente",
    "Últimas ações sensíveis executadas por super admins na plataforma.": "Últimas ações sensíveis executadas por super admins na plataforma.",
    "Quando": "Quando",
    "Responsável": "Responsável",
    "Alvo": "Alvo",
    "Resumo": "Resumo",
    "Convidar super admin": "Convidar super admin",
    "Convide novos perfis globais, revogue acessos quando necessário e redefina credenciais com auditoria.": "Convide novos perfis globais, revogue acessos quando necessário e redefina credenciais com auditoria.",
    "Buscar super admin por nome ou email": "Pesquisar super admin por nome ou e-mail",
    "Nenhum super admin encontrado com o filtro informado.": "Nenhum super admin encontrado com o filtro indicado.",
    "Ações": "Ações",
    "Desativado": "Desativado",
    "Redefinir acesso": "Redefinir acesso",
    "Revogar": "Revogar",
    "Redefinir acesso do super admin": "Redefinir acesso do super admin",
    "A senha de": "A palavra-passe de",
    "será redefinida e o acesso será reativado.": "será redefinida e o acesso será reativado.",
    "será redefinida mantendo o status atual da conta.": "será redefinida mantendo o estado atual da conta.",
    "Acesso redefinido com sucesso.": "Acesso redefinido com sucesso.",
    "Erro ao redefinir acesso": "Erro ao redefinir acesso",
    "Apenas perfis de super admin podem acessar esta área.": "Apenas perfis de super admin podem aceder a esta área.",
    "Seu usuário não possui permissão para visualizar clínicas globais ou controlar acessos centrais.": "O seu utilizador não tem permissão para ver clínicas globais ou controlar acessos centrais.",
    "Governança central do ecossistema": "Governação central do ecossistema",
    "Gerencie clínicas, super admins e trilhas de auditoria a partir de um único painel global.": "Gerir clínicas, super admins e trilhos de auditoria a partir de um único painel global.",
    "Nova senha para": "Nova palavra-passe para",
    "Nova senha temporária": "Nova palavra-passe temporária",
    "Reativar acesso": "Reativar acesso",
    "Se ativado, a conta volta a acessar o painel global após a redefinição.": "Se ativada, a conta volta a aceder ao painel global após a redefinição.",
    "Salvar nova senha": "Guardar nova palavra-passe",
    "Faturação": "Faturação",
    "Erro ao carregar pacientes": "Erro ao carregar pacientes",
    "Aparência": "Aparência",
    "Alterne entre tema claro e escuro.": "Alterne entre tema claro e escuro.",
    "Escolha o idioma usado nos menus e páginas.": "Escolha o idioma usado nos menus e páginas.",
    "Moeda usada nos valores financeiros.": "Moeda utilizada nos valores financeiros.",
    "Termine a sessão neste dispositivo.": "Termine a sessão neste dispositivo.",
    "Não foi possível carregar os modelos clínicos.": "Não foi possível carregar os modelos clínicos.",
    "Modelo clínico salvo como rascunho.": "Modelo clínico guardado como rascunho.",
    "Modelo clínico criado como rascunho.": "Modelo clínico criado como rascunho.",
    "Não foi possível salvar o modelo clínico.": "Não foi possível guardar o modelo clínico.",
    "Não foi possível concluir a ação.": "Não foi possível concluir a ação.",
    "Modelo duplicado como rascunho.": "Modelo duplicado como rascunho.",
    "Modelo publicado com sucesso.": "Modelo publicado com sucesso.",
    "Modelo inativado com sucesso.": "Modelo inativado com sucesso.",
    "Modelo reativado com sucesso.": "Modelo reativado com sucesso.",
    "Gerencie modelos versionados para avaliações, evoluções e outros registros clínicos, preservando o histórico.": "Faça a gestão de modelos versionados para avaliações, evoluções e outros registos clínicos, preservando o histórico.",
    "Novo modelo clínico": "Novo modelo clínico",
    "Modelo clínico": "Modelo clínico",
    "Versões": "Versões",
    "Associações": "Associações",
    "Última atualização": "Última atualização",
    "Modelo padrão": "Modelo predefinido",
    "Publicado": "Publicado",
    "Todos os serviços e procedimentos": "Todos os serviços e procedimentos",
    "Pré-visualizar": "Pré-visualizar",
    "Duplicar": "Duplicar",
    "Editar rascunho": "Editar rascunho",
    "Publicar": "Publicar",
    "Nova versão": "Nova versão",
    "Nenhum modelo clínico encontrado.": "Nenhum modelo clínico encontrado.",
    "Editar rascunho do modelo": "Editar rascunho do modelo",
    "Criar modelo clínico": "Criar modelo clínico",
    "Setores vinculados": "Setores associados",
    "Serviços e procedimentos vinculados": "Serviços e procedimentos associados",
    "Estrutura do modelo": "Estrutura do modelo",
    "Organize as seções e os campos do modelo. Alterações estruturais ficam disponíveis somente após a publicação.": "Organize as secções e os campos do modelo. As alterações estruturais ficam disponíveis apenas após a publicação.",
    "Adicionar seção": "Adicionar secção",
    "Identificador técnico": "Identificador técnico",
    "Título da seção": "Título da secção",
    "Descrição da seção": "Descrição da secção",
    "Identificador do campo": "Identificador do campo",
    "Rótulo": "Rótulo",
    "Tipo de campo": "Tipo de campo",
    "Texto curto": "Texto curto",
    "Texto longo": "Texto longo",
    "Escala numérica": "Escala numérica",
    "Seleção única": "Seleção única",
    "Seleção múltipla": "Seleção múltipla",
    "Caixa de seleção": "Caixa de seleção",
    "Data civil": "Data",
    "Texto informativo": "Texto informativo",
    "Sim/Não": "Sim/Não",
    "Medida com unidade": "Medida com unidade",
    "Obrigatório": "Obrigatório",
    "Opções": "Opções",
    "Nova seção": "Nova secção",
    "Novo campo": "Novo campo",
    "Testes clínicos": "Testes clínicos",
    "Mover seção para cima": "Mover secção para cima",
    "Mover seção para baixo": "Mover secção para baixo",
    "Excluir seção": "Eliminar secção",
    "Mover campo para cima": "Mover campo para cima",
    "Mover campo para baixo": "Mover campo para baixo",
    "Excluir campo": "Eliminar campo",
    "opcao_1:Opção 1, opcao_2:Opção 2": "opcao_1:Opção 1, opcao_2:Opção 2",
    "{count} vínculo selecionado": "{count} associação selecionada",
    "{count} vínculos selecionados": "{count} associações selecionadas",
    "Adicionar campo": "Adicionar campo",
    "Após publicar, a versão fica imutável. Para alterar a estrutura depois disso, crie uma nova versão.": "Após publicar, a versão fica imutável. Para alterar a estrutura depois disso, crie uma nova versão.",
    "Salvar rascunho": "Guardar rascunho",
    "Pré-visualizar modelo": "Pré-visualizar modelo",
    "Nenhuma versão disponível para pré-visualização.": "Nenhuma versão disponível para pré-visualização.",
    "Selecionar": "Selecionar",
    "Marcado": "Marcado",
    "Selecionar modelo clínico": "Selecionar modelo clínico",
    "Sem modelo versionado": "Sem modelo versionado",
    "Personalizado": "Personalizado",
    "Leads por origem": "Leads por origem",
    "Nenhum lead encontrado para o período selecionado.": "Nenhum lead encontrado para o período selecionado.",
    "Sem base suficiente": "Sem base suficiente",
    "Atribuição insuficiente": "Atribuição insuficiente",
    "Custo realizado ausente": "Custo realizado ausente",
    "Coorte longitudinal insuficiente": "Coorte longitudinal insuficiente",
    "Dados insuficientes": "Dados insuficientes",
    "Dashboard Executivo": "Dashboard Executivo",
    "Visão consolidada da clínica para decisões rápidas, sem duplicar os módulos detalhados.": "Visão consolidada da clínica para decisões rápidas, sem duplicar os módulos detalhados.",
    "A data final não pode ser anterior à data inicial.": "A data final não pode ser anterior à data inicial.",
    "dia": "dia",
    "Atualizando...": "A atualizar...",
    "Não foi possível carregar os indicadores executivos agora. Tente novamente em instantes.": "Não foi possível carregar os indicadores executivos agora. Tente novamente em instantes.",
    "Resumo executivo do período selecionado com os blocos permitidos para o seu perfil.": "Resumo executivo do período selecionado com os blocos permitidos para o seu perfil.",
    "Leads criados": "Leads criados",
    "Registros que compõem este indicador no período e setor selecionados.": "Registos que compõem este indicador no período e setor selecionados.",
    "Leads agendados": "Leads marcados",
    "Agendado no período": "Marcado no período",
    "Não agendado no período": "Não marcado no período",
    "Convertido no período": "Convertido no período",
    "Não convertido no período": "Não convertido no período",
    "Atendimentos concluídos": "Consultas concluídas",
    "Atendimentos no período": "Consultas no período",
    "Não concluído": "Não concluído",
    "Faltas": "Faltas",
    "Falta": "Falta",
    "Sem falta": "Sem falta",
    "Leads criados no período": "Leads criados no período",
    "Atendimentos realizados": "Atendimentos realizados",
    "consultas no período": "consultas no período",
    "Pacientes ativos": "Pacientes ativos",
    "Cadastro canônico de pacientes": "Registo canónico de pacientes",
    "Receita realizada": "Receita realizada",
    "lançamentos": "lançamentos",
    "Seu perfil pode acessar o Dashboard, mas ainda não possui permissões para blocos executivos detalhados.": "O seu perfil pode aceder ao Dashboard, mas ainda não possui permissões para blocos executivos detalhados.",
    "Captação e comercial": "Captação e comercial",
    "Funil de entrada resumido; a análise detalhada continua nos módulos de Leads e Marketing.": "Funil de entrada resumido; a análise detalhada continua nos módulos de Leads e Marketing.",
    "Abrir Marketing": "Abrir Marketing",
    "Abrir Leads": "Abrir Leads",
    "Taxa de agendamento": "Taxa de marcação",
    "agendados": "marcados",
    "Conversão em paciente": "Conversão em paciente",
    "Principal origem": "Principal origem",
    "Sem origem identificada": "Sem origem identificada",
    "Sem permissão para visualizar indicadores comerciais.": "Sem permissão para visualizar indicadores comerciais.",
    "Fonte oficial: métricas de CAC, LTV e ROI.": "Fonte oficial: métricas de CAC, LTV e ROI.",
    "Custo de aquisição atribuído.": "Custo de aquisição atribuído.",
    "ROI de marketing": "ROI de marketing",
    "Retorno atribuído ao investimento.": "Retorno atribuído ao investimento.",
    "Agenda, comparecimento e continuidade do cuidado em visão resumida.": "Agenda, comparência e continuidade do cuidado em visão resumida.",
    "Abrir Atendimento": "Abrir Atendimento",
    "Abrir Agenda": "Abrir Agenda",
    "Consultas no período": "Consultas no período",
    "Taxa de conclusão": "Taxa de conclusão",
    "Taxa de faltas": "Taxa de faltas",
    "Somente atendimentos concluídos no período e setor selecionados.": "Apenas as consultas concluídas no período e setor selecionados.",
    "Somente faltas no período e setor selecionados.": "Apenas as faltas no período e setor selecionados.",
    "Vendas confirmadas de planos no período e setor selecionados.": "Vendas confirmadas de planos no período e setor selecionados.",
    "Pacientes distintos": "Pacientes distintos",
    "Valor total vendido": "Valor total vendido",
    "Forma de pagamento recebida": "Forma de pagamento recebida",
    "Pacientes com atendimento concluído": "Pacientes com consulta concluída",
    "Pacientes com falta": "Pacientes com falta",
    "Faltas no período": "Faltas no período",
    "Detalhamento por profissional": "Detalhe por profissional",
    "Tipo de atendimento não identificado": "Tipo de consulta não identificado",
    "Profissional não identificado": "Profissional não identificado",
    "Retenção de pacientes": "Retenção de pacientes",
    "pacientes com continuidade": "pacientes com continuidade",
    "Sem permissão para visualizar indicadores da agenda.": "Sem permissão para visualizar indicadores da agenda.",
    "Entradas realizadas, despesas por competência e resultado do período com origem no serviço financeiro oficial.": "Entradas realizadas, despesas por competência e resultado do período com origem no serviço financeiro oficial.",
    "Abrir Financeiro": "Abrir Financeiro",
    "Despesas realizadas": "Despesas realizadas",
    "Resultado do período": "Resultado do período",
    "Por lançamento financeiro": "Por lançamento financeiro",
    "Sem permissão para visualizar indicadores financeiros.": "Sem permissão para visualizar indicadores financeiros.",
    "Entradas financeiras no período": "Entradas financeiras no período",
    "Entradas por setor": "Entradas por setor",
    "Despesas reconhecidas por competência no período": "Despesas reconhecidas por competência no período",
    "Valor líquido de planos vendidos": "Valor líquido dos planos vendidos",
    "Sem dados setoriais no período": "Sem dados setoriais no período",
    "Não foi possível carregar os repasses profissionais.": "Não foi possível carregar os repasses profissionais.",
    "Não foi possível carregar os filtros de repasse.": "Não foi possível carregar os filtros de repasse.",
    "Repasses pendentes gerados com sucesso.": "Repasses pendentes gerados com sucesso.",
    "Não foi possível gerar repasses pendentes.": "Não foi possível gerar repasses pendentes.",
    "Repasse aprovado com sucesso.": "Repasse aprovado com sucesso.",
    "Não foi possível aprovar o repasse.": "Não foi possível aprovar o repasse.",
    "Pagamento de repasse registrado com sucesso.": "Pagamento de repasse registado com sucesso.",
    "Não foi possível registrar o pagamento do repasse.": "Não foi possível registar o pagamento do repasse.",
    "Repasse cancelado com sucesso.": "Repasse cancelado com sucesso.",
    "Não foi possível cancelar o repasse.": "Não foi possível cancelar o repasse.",
    "Repasses profissionais": "Repasses profissionais",
    "Repasses calculados a partir de produção clínica válida, separados de venda, recebível e caixa do paciente.": "Repasses calculados a partir de produção clínica válida, separados de venda, recebível e caixa do paciente.",
    "Aprovados": "Aprovados",
    "Valor a pagar": "Valor a pagar",
    "Valor pago": "Valor pago",
    "Bloqueados": "Bloqueados",
    "Cancelados": "Cancelados",
    "Modalidade": "Modalidade",
    "Valor fixo": "Valor fixo",
    "Percentual da produção": "Percentagem da produção",
    "Com saldo": "Com saldo",
    "Sem saldo": "Sem saldo",
    "Produção base": "Produção base",
    "Regra": "Regra",
    "Valor do repasse": "Valor do repasse",
    "Nenhum repasse profissional encontrado": "Nenhum repasse profissional encontrado",
    "Aprovar": "Aprovar",
    "Pagar": "Pagar",
    "Detalhes do repasse": "Detalhes do repasse",
    "Produção de origem": "Produção de origem",
    "Versão": "Versão",
    "Saldo a pagar": "Saldo a pagar",
    "Bloqueio": "Bloqueio",
    "Valor da produção não disponível para cálculo": "Valor da produção indisponível para o cálculo",
    "Nenhuma regra de repasse aplicável": "Nenhuma regra de pagamento a profissionais aplicável",
    "Conflito entre regras de repasse": "Conflito entre regras de pagamento a profissionais",
    "Produção clínica cancelada": "Produção clínica cancelada",
    "Repasse fixo não autorizado para produção de cortesia": "Pagamento fixo ao profissional não autorizado para produção de cortesia",
    "Moeda da regra de repasse incompatível com a produção": "Moeda da regra de pagamento ao profissional incompatível com a produção",
    "Regra de repasse inválida": "Regra de pagamento ao profissional inválida",
    "Motivo do bloqueio não disponível": "Motivo do bloqueio indisponível",
    "Produção clínica de {date}": "Produção clínica de {date}",
    "Produção clínica vinculada": "Produção clínica associada",
    "Status do repasse não disponível": "Estado do repasse indisponível",
    "Regra de repasse não disponível": "Regra de pagamento ao profissional indisponível",
    "Registrar pagamento": "Registar pagamento",
    "Cancelar repasse": "Cancelar repasse",
    "O repasse será cancelado sem alterar venda, recebível ou caixa do paciente.": "O repasse será cancelado sem alterar venda, recebível ou caixa do paciente.",
    "Não foi possível carregar regras de repasse.": "Não foi possível carregar regras de repasse.",
    "Regra de repasse criada com sucesso.": "Regra de repasse criada com sucesso.",
    "Não foi possível criar a regra de repasse.": "Não foi possível criar a regra de repasse.",
    "Regra de repasse inativada com sucesso.": "Regra de repasse inativada com sucesso.",
    "Não foi possível inativar a regra de repasse.": "Não foi possível inativar a regra de repasse.",
    "Nova regra de repasse": "Nova regra de repasse",
    "Quando serviço e setor são informados, esta regra só se aplica se ambos corresponderem.": "Quando o serviço e o setor são indicados, esta regra só se aplica se ambos corresponderem.",
    "Prioridade das regras: serviço + setor, serviço, setor e regra geral.": "Prioridade das regras: serviço + setor, serviço, setor e regra geral.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional, serviço e setor.": "Já existe uma regra de pagamento a profissionais em vigor ou com um período de vigência sobreposto para este mesmo profissional, serviço e setor.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e serviço.": "Já existe uma regra de pagamento a profissionais em vigor ou com um período de vigência sobreposto para este mesmo profissional e serviço.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e setor.": "Já existe uma regra de pagamento a profissionais em vigor ou com um período de vigência sobreposto para este mesmo profissional e setor.",
    "Já existe uma regra geral de repasse vigente ou com vigência sobreposta para este profissional.": "Já existe uma regra geral de pagamento a profissionais em vigor ou com um período de vigência sobreposto para este profissional.",
    "Setor opcional": "Setor opcional",
    "Serviço opcional": "Serviço opcional",
    "Percentual": "Percentagem",
    "Vigência inicial": "Vigência inicial",
    "Vigência final": "Vigência final",
    "Permitir cortesia": "Permitir cortesia",
    "Criar regra": "Criar regra",
    "Vigência": "Vigência",
    "Nenhuma regra de repasse encontrada": "Nenhuma regra de repasse encontrada",
    "Bloco sem permissão": "Bloco sem permissão",
    "Oculto": "Oculto",
    "Fonte": "Fonte",
    "registros": "registos",
    "Abrir origem": "Abrir origem",
    "Nenhum dado gerencial encontrado": "Nenhum dado gerencial encontrado",
    "Repasse calculado": "Repasse calculado",
    "Repasse pago": "Repasse pago",
    "Repasse a pagar": "Repasse a pagar",
    "Não foi possível carregar a visão gerencial.": "Não foi possível carregar a visão gerencial.",
    "Não foi possível exportar a visão gerencial.": "Não foi possível exportar a visão gerencial.",
    "Visão gerencial": "Visão gerencial",
    "Vendido, produzido, recebido e repasses em fontes separadas, com comparações gerenciais temporais.": "Vendido, produzido, recebido e repasses em fontes separadas, com comparações gerenciais temporais.",
    "Exportar": "Exportar",
    "Exportar CSV": "Exportar CSV",
    "Excel": "Excel",
    "PDF": "PDF",
    "Gerando exportação": "A gerar exportação",
    "Gerando relatório": "A gerar relatório",
    "Gerar relatório": "Gerar relatório",
    "Exportação gerencial concluída.": "Exportação gerencial concluída.",
    "Relatório gerencial gerado com sucesso.": "Relatório gerencial gerado com sucesso.",
    "Não foi possível gerar a exportação.": "Não foi possível gerar a exportação.",
    "Não foi possível gerar o relatório.": "Não foi possível gerar o relatório.",
    "Histórico de lotes": "Histórico de lotes",
    "Detalhamento dos snapshots": "Detalhe dos snapshots",
    "Confiabilidade": "Confiabilidade",
    "Somente confirmados": "Somente confirmados",
    "Incluir inferidos": "Incluir inferidos",
    "Incluir legados": "Incluir legados",
    "Todos os dados": "Todos os dados",
    "Visão parcial": "Visão parcial",
    "Alguns blocos foram ocultados por permissão e não foram retornados no payload.": "Alguns blocos foram ocultados por permissão e não foram retornados no payload.",
    "Sem base": "Sem base",
    "Série temporal": "Série temporal",
    "Pendências de qualidade dos dados": "Pendências de qualidade dos dados",
    "Planos e pacotes": "Planos e pacotes",
    "Planos vendidos": "Planos vendidos",
    "Valor vendido em planos": "Valor vendido em planos",
    "Sessões produzidas": "Sessões produzidas",
    "Limitações": "Limitações",
    "Escala de dor": "Escala de dor",
    "Rateio gerencial habilitado.": "Rateio gerencial ativado.",
    "Rateio gerencial desabilitado.": "Rateio gerencial desativado.",
    "Simulação de rateio concluída.": "Simulação de rateio concluída.",
    "Rateio gerencial aplicado.": "Rateio gerencial aplicado.",
    "Aplicação de rateio revertida.": "Aplicação de rateio revertida.",
    "Elegibilidade da categoria atualizada.": "Elegibilidade da categoria atualizada.",
    "Regra de rateio criada em rascunho.": "Regra de rateio criada em rascunho.",
    "Regra de rateio ativada.": "Regra de rateio ativada.",
    "Regra de rateio inativada.": "Regra de rateio inativada.",
    "Nova versão criada em rascunho.": "Nova versão criada em rascunho.",
    "Rateio gerencial por setor": "Rateio gerencial por setor",
    "Configura regras percentuais por setor sem aplicar valores nesta etapa.": "Configura regras percentuais por setor sem aplicar valores nesta etapa.",
    "Habilitar rateio": "Ativar rateio",
    "Simular rateio": "Simular rateio",
    "Simulando": "A simular",
    "Simular": "Simular",
    "Aviso: simulação de rateio não aplicada": "Aviso: simulação de rateio não aplicada",
    "Aplicação controlada do rateio": "Aplicação controlada do rateio",
    "Fingerprint da simulação": "Fingerprint da simulação",
    "Fingerprint da aplicação": "Fingerprint da aplicação",
    "Aplicar rateio gerencial": "Aplicar rateio gerencial",
    "Cancelar aplicação": "Cancelar aplicação",
    "Observação da aplicação": "Observação da aplicação",
    "Chave de idempotência": "Chave de idempotência",
    "Aplicando": "Aplicando",
    "Confirmar aplicação": "Confirmar aplicação",
    "Aplicações de rateio": "Aplicações de rateio",
    "Aplicada em": "Aplicada em",
    "Período da aplicação": "Período da aplicação",
    "Total aplicado": "Total aplicado",
    "Nenhuma aplicação de rateio encontrada": "Nenhuma aplicação de rateio encontrada",
    "Aplicada": "Aplicada",
    "Revertida": "Revertida",
    "Detalhar aplicação": "Detalhar aplicação",
    "Preparar reversão": "Preparar reversão",
    "Itens aplicados": "Itens aplicados",
    "Setores aplicados": "Setores aplicados",
    "Motivo da reversão": "Motivo da reversão",
    "Revertendo": "Revertendo",
    "Reverter aplicação": "Reverter aplicação",
    "Despesas encontradas": "Despesas encontradas",
    "Elegíveis": "Elegíveis",
    "Simuladas": "Simuladas",
    "Total original": "Total original",
    "Total distribuído": "Total distribuído",
    "Diferença de arredondamento": "Diferença de arredondamento",
    "Participação": "Participação",
    "Valor simulado": "Valor simulado",
    "Nenhum valor simulado por setor": "Nenhum valor simulado por setor",
    "Qtd.": "Qtd.",
    "Nenhuma despesa fora da simulação": "Nenhuma despesa fora da simulação",
    "Despesa": "Despesa",
    "Competência da despesa": "Competência da despesa",
    "Original": "Original",
    "Distribuído": "Distribuído",
    "Despesa simulada": "Despesa simulada",
    "Rateio desabilitado": "Rateio desativado",
    "Despesa não elegível": "Despesa não elegível",
    "Categoria não elegível": "Categoria não elegível",
    "Sem regra aplicável": "Sem regra aplicável",
    "Sem competência": "Sem competência",
    "Competência não confirmada": "Competência não confirmada",
    "Moeda não compatível": "Moeda não compatível",
    "Despesa direta": "Despesa direta",
    "Regra inválida": "Regra inválida",
    "Não simulada": "Não simulada",
    "Categorias elegíveis": "Categorias elegíveis",
    "Não elegível": "Não elegível",
    "Usar padrão da clínica": "Usar padrão da clínica",
    "Usar regra da categoria": "Usar regra da categoria",
    "Nova regra de rateio": "Nova regra de rateio",
    "Nome da regra": "Nome da regra",
    "Escopo": "Ambito",
    "Padrão da clínica": "Padrão da clínica",
    "Categoria de despesa": "Categoria de despesa",
    "Sem categoria": "Sem categoria",
    "Soma dos percentuais": "Soma dos percentuais",
    "Criar rascunho": "Criar rascunho",
    "Nenhuma regra de rateio encontrada": "Nenhuma regra de rateio encontrada",
    "Ativar regra": "Ativar regra",
    "Inativar regra": "Inativar regra",
    "Histórico": "Histórico",
    "Histórico de versões": "Histórico de versões",
    "Dinheiro": "Dinheiro",
    "Cheque": "Cheque",
    "Aluguel": "Renda",
    "Contas": "Serviços",
    "Salários": "Salários",
    "Materiais": "Materiais",
    "Equipamentos": "Equipamentos",
    "Impostos": "Impostos",
    "Manutenção": "Manutenção",
    "Fixa": "Fixa",
    "Variável": "Variável",
    "Próximo": "Seguinte",
    "Modo de visualização": "Modo de visualização",
    "Nenhum agendamento encontrado para este período.": "Nenhuma marcação encontrada para este período.",
    "Sem agendamentos": "Sem marcações",
    "Planos vendidos no período": "Planos vendidos no período",
    "Desempenho comercial e financeiro dos planos vendidos no intervalo selecionado.": "Desempenho comercial e financeiro dos planos vendidos no período selecionado.",
    "Valor líquido total dos planos vendidos": "Valor líquido total dos planos vendidos",
    "Valor pendente": "Valor pendente",
    "Plano mais vendido": "Plano mais vendido",
    "Plano com maior valor líquido vendido": "Plano com maior valor líquido vendido",
    "Buscar por nome do plano": "Pesquisar por nome do plano",
    "Digite o nome do plano": "Introduza o nome do plano",
    "Tipo do plano": "Tipo de plano",
    "Situação financeira": "Situação financeira",
    "Todas as situações": "Todas as situações",
    "Parcial": "Parcial",
    "Qtde. vendida": "Qtd. vendida",
    "Detalhes das vendas do plano": "Detalhes das vendas do plano",
    "Parcelamento": "Pagamento em prestações",
    "Profissional ou responsável": "Profissional ou responsável",
    "Não informado": "Não indicado",
    "à vista": "A pronto",
    "parcelas": "prestações",
    "Período do relatório": "Período do relatório",
    "Relatório exportado com sucesso.": "Relatório exportado com sucesso.",
    "Não foi possível abrir a exportação.": "Não foi possível abrir a exportação.",
    "Erro ao carregar o relatório de planos vendidos.": "Erro ao carregar o relatório de planos vendidos.",
    "Ordenar em ordem crescente": "Ordenar por ordem crescente",
    "Ordenar em ordem decrescente": "Ordenar por ordem decrescente",
    "Meta de faturamento": "Meta de faturação",
    "Acompanhe o progresso do período selecionado.": "Acompanhe o progresso do período selecionado.",
    "Realizado": "Realizado",
    "Atingido": "Atingido",
    "Meta atingida no período.": "Meta atingida no período.",
    "Nenhuma meta financeira vigente para o período.": "Não existe uma meta financeira em vigor para o período.",
    "Indicadores por unidade de receita": "Indicadores por unidade de receita",
    "unidades": "unidades",
    "Receita realizada por agendamento vinculado ou lançamento manual": "Receita realizada por agendamento associado ou lançamento manual",
    "Base da métrica: atendimentos vinculados e lançamentos manuais de receita pagos no período.": "Base da métrica: atendimentos associados e lançamentos manuais de receita pagos no período.",
    "Unidades consideradas": "Unidades consideradas",
    "Custo por unidade de receita": "Custo por unidade de receita",
    "Resultado por unidade de receita": "Resultado por unidade de receita",
    "Ponto de equilíbrio em unidades de receita": "Ponto de equilíbrio em unidades de receita",
    "Formas de pagamento": "Formas de pagamento",
    "Cadastre meios de pagamento, adquirentes, prazos e taxas para preparar o cálculo automático financeiro.": "Registe meios de pagamento, adquirentes, prazos e taxas para preparar o cálculo financeiro automático.",
    "Nova forma de pagamento": "Nova forma de pagamento",
    "Editar forma de pagamento": "Editar forma de pagamento",
    "Todas as adquirentes": "Todas as adquirentes",
    "Todos os status": "Todos os estados",
    "Adquirente/maquininha": "Adquirente/terminal",
    "Tipo aceito": "Tipo aceite",
    "Taxa percentual": "Taxa percentual",
    "Taxa fixa": "Taxa fixa",
    "Prazo de recebimento": "Prazo de recebimento",
    "Limite de parcelas": "Limite de prestações",
    "Taxas por parcela": "Taxas por prestação",
    "Taxas": "Taxas",
    "Nenhuma forma de pagamento encontrada.": "Nenhuma forma de pagamento encontrada.",
    "Forma de pagamento cadastrada.": "Forma de pagamento registada.",
    "Forma de pagamento atualizada.": "Forma de pagamento atualizada.",
    "Forma de pagamento excluída.": "Forma de pagamento eliminada.",
    "Não foi possível excluir a forma de pagamento.": "Não foi possível eliminar a forma de pagamento.",
    "Preencha os campos obrigatérios.": "Preencha os campos obrigatérios.",
    "dias": "dias",
    "Informe a taxa percentual cobrada em cada quantidade de parcelas.": "Informe a taxa percentual cobrada em cada quantidade de prestações.",
    "Essas configurações ainda não alteram lançamentos existentes; elas deixam a clínica pronta para cálculo automático de taxas.": "Estas configurações ainda não alteram movimentos existentes; preparam a clínica para cálculo automático de taxas.",
    "Carregando relatórios financeiros...": "A carregar relatórios financeiros...",
    "Visão gerencial para acompanhar entradas, saídas e saldo de caixa.": "Visão de gestão para acompanhar entradas, saídas e saldo de caixa.",
    "Erro ao excluir receita": "Erro ao eliminar receita",
    "Erro ao excluir despesa": "Erro ao eliminar despesa",
    "Erro ao carregar dados financeiros": "Erro ao carregar dados financeiros",
    "Excluir receita": "Eliminar receita",
    "Excluir despesa": "Eliminar despesa",
    "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.": "Esta receita será eliminada e deixará de aparecer nas listagens e indicadores.",
    "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.": "Esta despesa será eliminada e deixará de aparecer nas listagens e indicadores.",
    "Não vinculado": "Não associado",
    "Paciente removido": "Paciente removido",
    "Profissional removido": "Profissional removido",
    "Fixas": "Fixas",
    "Variáveis": "Variáveis",
    "Leitura gerencial": "Leitura de gestão",
    "Um resumo rápido do comportamento financeiro no recorte atual.": "Um resumo rápido do comportamento financeiro no período atual.",
    "Recebimento médio de caixa": "Recebimento médio de caixa",
    "em pacotes": "em pacotes",
    "Indicadores de competência permanecem indisponíveis até haver produção canônica.": "Os indicadores de competência permanecem indisponíveis até existir produção canónica.",
    "Onde fatura": "Faturação por setor",
    "Receita distribuída por setor.": "Receitas distribuídas por setor.",
    "Nenhuma receita com setor vinculado no período.": "Sem receitas associadas a setores no período.",
    "Quem fatura": "Faturação por profissional",
    "Receita distribuída por profissional.": "Receitas distribuídas por profissional.",
    "Nenhuma receita vinculada a profissional no período.": "Sem receitas associadas a profissionais no período.",
    "Como entra": "Meios de pagamento",
    "Receita distribuída por forma de pagamento.": "Receitas distribuídas por meio de pagamento.",
    "Nenhuma receita registrada no período.": "Sem receitas registadas no período.",
    "CSV": "CSV",
    "Data final": "Data final",
    "Data inicial": "Data inicial",
    "Despesa excluída com sucesso!": "Despesa eliminada com sucesso!",
    "Despesa registrada com sucesso!": "Despesa registada com sucesso!",
    "Despesas fixas": "Despesas fixas",
    "Despesas variáveis": "Despesas variáveis",
    "Dia": "Dia",
    "Limpar filtros": "Limpar filtros",
    "Mês": "Mês",
    "Nenhum plano encontrado com os filtros selecionados.": "Nenhum plano encontrado com os filtros selecionados.",
    "OK": "OK",
    "Por profissional": "Por profissional",
    "Quantidade vendida": "Quantidade vendida",
    "Receita excluída com sucesso!": "Receita eliminada com sucesso!",
    "Registrar": "Registar",
    "Salvar": "Guardar",
    "Semana": "Semana",
    "Situação": "Situação",
    "Situação do pagamento": "Situação do pagamento",
    "Totais consolidados": "Totais consolidados",
    "Total": "Total",
    "Total de planos vendidos": "Total de planos vendidos",
    "Valor unit.": "Valor unit.",
    "Próxima": "Seguinte",
    "Armazenamento": "Armazenamento",
    "{used} de {total} utilizados": "{used} de {total} utilizados",
    "utilizados": "utilizados",
    "Arquivos": "Ficheiros",
    "Utilizado": "Utilizado",
    "Disponível": "Disponível",
    "Limite atingido": "Limite atingido",
    "Próximo do limite": "Perto do limite",
    "Atenção": "Atenção",
    "Normal": "Normal",
    "Sua clínica está próxima de atingir o limite de armazenamento.": "A sua clínica está próxima de atingir o limite de armazenamento.",
    "Sua clínica está utilizando mais de 80% da capacidade contratada.": "A sua clínica está a utilizar mais de 80% da capacidade contratada.",
    "Serviço": "Serviço",
    "min": "min",
    "Observações": "Observações",
    "Selecione uma clínica.": "Selecione uma clínica.",
    "e": "e",
    "Nenhum dado no período selecionado.": "Nenhum dado no período selecionado.",
    "Descrição": "Descrição",
    "Selecione uma clínica para continuar.": "Selecione uma clínica para continuar.",
    "Operação clínica": "Operação clínica",
    "Competência": "Competência",
    "Não alocada": "Não alocada",
    "Sem dados no período": "Sem dados no período",
    "Desconto": "Desconto",
    "Acréscimo": "Acréscimo",
    "Recorrente": "Recorrente",
    "Dado": "Dado",
    "Confiabilidade do dado": "Fiabilidade dos dados",
    "Não disponível": "Não disponível",
    "Preencha os campos obrigatórios.": "Preencha os campos obrigatórios.",
    "Valor unitário": "Valor unitário",
    "Ticket médio": "Ticket médio",
    "Visão Geral": "Visão geral",
    "Últimas Receitas": "Últimas receitas",
    "Últimas Despesas": "Últimas despesas",
    "Lançamentos": "Movimentos",
    "Cartão de crédito parcelado": "Cartão de crédito em prestações",
    "Remover origem": "Remover origem",
    "Última consulta": "Última consulta",
    "Registros": "Registos",
    "Leads no período": "Leads no período",
    "Nenhum dado setorial no período selecionado.": "Nenhum dado setorial no período selecionado.",
    "Convênio": "Seguro de saúde",
    "Importado": "Importado",
    "Sem regra": "Sem regra de repasse",
    "Histórico do paciente": "Histórico do paciente",
    "Atendimentos e financeiro do paciente.": "Atendimentos e informação financeira do paciente.",
    "Faltas registradas": "Faltas registadas",
    "Histórico de atendimentos": "Histórico de atendimentos",
    "Nenhum atendimento encontrado para este paciente.": "Nenhum atendimento encontrado para este paciente.",
    "Vínculo": "Vínculo",
    "Agendamento sem plano de tratamento": "Marcação sem plano de tratamento",
    "Exames": "Exames",
    "Imagens": "Imagens",
    "Fotos": "Fotos",
    "Agendamentos do paciente sem vínculo com plano de tratamento.": "Marcações do paciente sem vínculo com plano de tratamento.",
    "Nenhum agendamento sem plano de tratamento encontrado para este paciente.": "Nenhuma marcação sem plano de tratamento encontrada para este paciente.",
    "Importa Zen Fisio": "Importa Zen Fisio",
    "Migração por clínica": "Migração por clínica",
    "Clínica de destino": "Clínica de destino",
    "Destino selecionado:": "Destino selecionado:",
    "Nenhuma clínica selecionada": "Nenhuma clínica selecionada",
    "Importados": "Importados",
    "Importe o arquivo pacientes.csv preservando o isolamento multi-tenant e gerando alertas de CPF e nascimento.": "Importe o ficheiro pacientes.csv preservando o isolamento multi-tenant e gerando alertas de NIF e nascimento.",
    "Importar pacientes": "Importar pacientes",
    "Importando pacientes...": "A importar pacientes...",
    "Importe profissionais com setor automático, agenda padrão das 08:00 às 18:00, duração de 1 hora e limite de 1 paciente por horário.": "Importe profissionais com setor automático, agenda padrão das 08:00 às 18:00, duração de 1 hora e limite de 1 paciente por horário.",
    "Importar profissionais": "Importar profissionais",
    "Importando profissionais...": "A importar profissionais...",
    "Selecione um arquivo CSV.": "Selecione um ficheiro CSV.",
    "Selecione a clínica de destino.": "Selecione a clínica de destino.",
    "Ignorados": "Ignorados",
    "Alertas": "Alertas",
    "CPFs duplicados": "CPFs duplicados",
    "Nascimentos bloqueados": "Nascimentos bloqueados",
    "Setores criados": "Setores criados",
    "Agenda padrão": "Agenda padrão",
    "Pagamentos": "Pagamentos",
    "Arquivo de alertas gerado": "Ficheiro de alertas gerado",
    "Baixe o CSV para revisar CPFs duplicados, nascimentos futuros e datas inválidas bloqueadas.": "Transfira o CSV para rever CPFs duplicados, nascimentos futuros e datas inválidas bloqueadas.",
    "Baixe o CSV para revisar setores criados automaticamente e registros ignorados.": "Transfira o CSV para rever setores criados automaticamente e registos ignorados.",
    "Baixar alertas": "Transferir alertas",
    "Nenhum usuário encontrado.": "Nenhum utilizador encontrado.",
    "Receita gerencial líquida": "Receita de gestão líquida",
    "Resultado gerencial após custos diretos": "Resultado de gestão após custos diretos",
    "{count} usuário": "{count} utilizador",
    "{count} usuários": "{count} utilizadores",
    "{count} ativo": "{count} ativo",
    "{count} ativos": "{count} ativos",
    "{count} admin": "{count} administrador",
    "{count} admins": "{count} administradores",
    "A senha de {name} será redefinida e o acesso será reativado.": "A palavra-passe de {name} será redefinida e o acesso será reativado.",
    "A senha de {name} será redefinida mantendo o status atual da conta.": "A palavra-passe de {name} será redefinida mantendo o estado atual da conta.",
    "Nova senha para {name}.": "Nova palavra-passe para {name}.",
    "Destino selecionado: {clinic}": "Destino selecionado: {clinic}",
    "Destino selecionado: nenhuma clínica selecionada.": "Destino selecionado: nenhuma clínica selecionada.",
    "Autenticação necessária.": "Autenticação necessária.",
    "Esta operação exige o perfil de super admin.": "Esta operação exige o perfil de super admin.",
    "Clínica não encontrada.": "Clínica não encontrada.",
    "Este e-mail já está cadastrado.": "Este e-mail já está registado.",
    "Super admin não encontrado.": "Super admin não encontrado.",
    "Deve existir ao menos um super admin ativo.": "Deve existir pelo menos um super admin ativo.",
    "Informe a clínica de destino.": "Indique a clínica de destino.",
    "Informe o conteúdo CSV.": "Indique o conteúdo CSV.",
    "O histórico de importação está temporariamente indisponível.": "O histórico de importação está temporariamente indisponível.",
    "Lote de importação não encontrado.": "Lote de importação não encontrado.",
    "Somente lotes ativos podem ser revertidos.": "Apenas lotes ativos podem ser revertidos.",
    "O nome deve ter pelo menos {minimum} caracteres.": "O nome deve ter pelo menos {minimum} caracteres.",
    "Informe um e-mail válido.": "Indique um e-mail válido.",
    "A senha deve ter pelo menos {minimum} caracteres.": "A palavra-passe deve ter pelo menos {minimum} caracteres.",
    "A senha deve ter no máximo {maximum} caracteres.": "A palavra-passe deve ter no máximo {maximum} caracteres.",
    "A senha deve conter ao menos uma letra minúscula.": "A palavra-passe deve conter pelo menos uma letra minúscula.",
    "A senha deve conter ao menos uma letra maiúscula.": "A palavra-passe deve conter pelo menos uma letra maiúscula.",
    "A senha deve conter ao menos um número.": "A palavra-passe deve conter pelo menos um número.",
    "O CSV não contém as colunas obrigatórias: {missing_headers}.": "O CSV não contém as colunas obrigatórias: {missing_headers}.",
    "Já existe uma importação ativa de {import_type} para esta clínica.": "Já existe uma importação ativa de {import_type} para esta clínica.",
    "Importação bloqueada. Execute primeiro: {missing_prerequisites}.": "Importação bloqueada. Execute primeiro: {missing_prerequisites}.",
    "Importação bloqueada pela etapa posterior {blocking_import_type}.": "Importação bloqueada pela etapa posterior {blocking_import_type}.",
    "Reverta primeiro o lote posterior de {blocking_import_type}.": "Reverta primeiro o lote posterior de {blocking_import_type}.",
    "Não é possível reverter {entity_type}: existem {dependency_count} dependências.": "Não é possível reverter {entity_type}: existem {dependency_count} dependências.",
    "pacientes": "pacientes",
    "profissionais": "profissionais",
    "agendamentos": "marcações",
  },
  en: {
    "Categorias financeiras": "Financial categories",
    "Gerencie separadamente as categorias usadas em receitas e despesas.": "Manage the categories used for revenue and expenses separately.",
    "Tipo de categoria financeira": "Financial category type",
    "Nova categoria": "New category",
    "Renomear uma categoria não altera o nome preservado nos lançamentos anteriores. Categorias inativas continuam no histórico, mas não podem ser usadas em novos lançamentos.": "Renaming a category does not change the name preserved on earlier entries. Inactive categories remain in history but cannot be used for new entries.",
    "Carregando categorias...": "Loading categories...",
    "Não foi possível carregar as categorias.": "The categories could not be loaded.",
    "Nenhuma categoria de receita cadastrada.": "No revenue categories have been added.",
    "Nenhuma categoria de despesa cadastrada.": "No expense categories have been added.",
    "Editar categoria": "Edit category",
    "Nome da categoria": "Category name",
    "Informe o nome da categoria.": "Enter the category name.",
    "Categoria atualizada com sucesso.": "Category updated successfully.",
    "Categoria cadastrada com sucesso.": "Category added successfully.",
    "Categoria desativada com sucesso.": "Category deactivated successfully.",
    "Categoria reativada com sucesso.": "Category reactivated successfully.",
    "Desativar categoria": "Deactivate category",
    "Reativar categoria": "Reactivate category",
    "A categoria deixará de aparecer em novos lançamentos. O histórico será preservado.": "The category will no longer appear in new entries. Its history will be preserved.",
    "A categoria voltará a aparecer em novos lançamentos.": "The category will appear in new entries again.",
    "Categoria de receita": "Revenue category",
    "Nenhuma categoria de receita ativa cadastrada.": "No active revenue categories have been added.",
    "Nenhuma categoria de despesa ativa cadastrada.": "No active expense categories have been added.",
    "Selecione uma categoria": "Select a category",
    "Configurar categorias": "Configure categories",
    "Selecione uma categoria de receita.": "Select a revenue category.",
    "Selecione uma categoria de despesa.": "Select an expense category.",
    "Avaliação vinculada": "Linked assessment",
    "Modelo clínico registrado": "Registered clinical template",
    "Editar registros de outros profissionais": "Edit other professionals' records",
    "Adicionar correções a registros finalizados": "Add corrections to finalized records",
    "Autorizações clínicas explícitas": "Explicit clinical authorizations",
    "Estas autorizações clínicas sensíveis não são concedidas automaticamente ao administrador.": "These sensitive clinical authorizations are not granted automatically to administrators.",
    "Existem alterações não salvas. Deseja descartá-las?": "There are unsaved changes. Do you want to discard them?",
    "Avaliação atualizada com sucesso.": "Assessment updated successfully.",
    "Este registro foi alterado por outro usuário enquanto você o editava. Atualize os dados antes de salvar novamente.": "This record was changed by another user while you were editing it. Refresh the data before saving again.",
    "Atualizar registro": "Refresh record",
    "Editar avaliação clínica": "Edit clinical assessment",
    "Finalizado": "Finalized",
    "Finalizar registro clínico": "Finalize clinical record",
    "Após finalizar, este registro não poderá mais ser alterado diretamente. Se for necessário corrigir alguma informação depois, será criada uma correção mantendo o conteúdo original no histórico.": "After finalization, this record can no longer be changed directly. If information must be corrected later, a correction will be added while the original content remains in the history.",
    "Finalizando...": "Finalizing...",
    "Finalizar registro": "Finalize record",
    "Adicionar correção": "Add correction",
    "O conteúdo original será preservado. A correção será acrescentada ao histórico do registro.": "The original content will be preserved. The correction will be added to the record history.",
    "Motivo da correção": "Reason for correction",
    "Informe pelo menos 10 caracteres.": "Enter at least 10 characters.",
    "Conteúdo da correção": "Correction content",
    "Correção adicionada ao histórico.": "Correction added to the history.",
    "Informe um motivo com pelo menos 10 caracteres.": "Enter a reason with at least 10 characters.",
    "Informe o conteúdo da correção.": "Enter the correction content.",
    "Registro finalizado com sucesso.": "Record finalized successfully.",
    "Criado por": "Created by",
    "Autoria não disponível": "Authorship unavailable",
    "Última alteração por": "Last changed by",
    "Finalizado por": "Finalized by",
    "Editar avaliação": "Edit assessment",
    "Finalizar": "Finalize",
    "Ver histórico": "View history",
    "Histórico do registro clínico": "Clinical record history",
    "Linha do tempo": "Timeline",
    "Carregando histórico...": "Loading history...",
    "Correção adicionada": "Correction added",
    "Primeiro estado disponível no histórico": "First state available in the history",
    "Registro criado": "Record created",
    "Registro finalizado": "Record finalized",
    "Revisão salva": "Revision saved",
    "Revisão": "Revision",
    "Carregar mais": "Load more",
    "Este é o primeiro estado disponível após a adoção do histórico. Não há versões anteriores reconstruíveis.": "This is the first state available after history tracking was introduced. Earlier versions cannot be reconstructed.",
    "versão": "version",
    "Sem modelo clínico registrado": "No registered clinical template",
    "Documentos desta revisão": "Documents in this revision",
    "O conteúdo original foi preservado. Esta correção foi acrescentada ao histórico.": "The original content was preserved. This correction was added to the history.",
    "Correção adicionada por": "Correction added by",
    "Escolha uma revisão ou correção para consultar o conteúdo preservado.": "Choose a revision or correction to view its preserved content.",
    "Selecione um item do histórico": "Select a history item",
    "Plano cancelado com segurança.": "Plan canceled safely.",
    "Plano reativado com segurança.": "Plan reactivated safely.",
    "Cancelar Plano de Tratamento": "Cancel Treatment Plan",
    "Reativar Plano de Tratamento": "Reactivate Treatment Plan",
    "Sessões utilizadas": "Sessions used",
    "{count} sessão restante": "{count} remaining session",
    "{count} sessões restantes": "{count} remaining sessions",
    "{count} recebível": "{count} receivable",
    "{count} recebíveis": "{count} receivables",
    "{count} pagamento": "{count} payment",
    "{count} pagamentos": "{count} payments",
    "{count} receita": "{count} revenue record",
    "{count} receitas": "{count} revenue records",
    "Verificando efeitos do cancelamento...": "Checking cancellation effects...",
    "Não foi possível verificar os efeitos do cancelamento. Feche o diálogo e tente novamente.": "The cancellation effects could not be checked. Close the dialog and try again.",
    "Consequências do cancelamento": "Cancellation effects",
    "Cancelar o Plano impede seu uso futuro, mas não apaga atendimentos, pagamentos, produções ou repasses já registrados. Pendências financeiras serão encaminhadas para revisão.": "Canceling the Plan prevents future use but does not delete recorded appointments, payments, production, or professional payouts. Financial issues will be sent for review.",
    "Agendamentos futuros": "Future appointments",
    "Saldo financeiro": "Financial balance",
    "Situação do NPS": "NPS status",
    "Sem solicitação": "No request",
    "Estes agendamentos serão cancelados": "These appointments will be canceled",
    "Financeiro preservado e enviado para revisão": "Financial records preserved and sent for review",
    "recebíveis": "receivables",
    "pagamentos": "payments",
    "receitas": "revenue records",
    "pago": "paid",
    "Efeitos da reativação": "Reactivation effects",
    "Agendamentos cancelados não serão restaurados automaticamente.": "Canceled appointments will not be restored automatically.",
    "A reativação não cria uma nova venda nem resolve pendências financeiras.": "Reactivation does not create a new sale or resolve financial issues.",
    "Tokens NPS revogados não serão restaurados automaticamente.": "Revoked NPS tokens will not be restored automatically.",
    "O status será recalculado conforme validade, venda e uso efetivo do Plano.": "Status will be recalculated based on validity, sale, and actual Plan use.",
    "Motivo da reativação": "Reactivation reason",
    "Descreva o motivo com detalhes": "Describe the reason in detail",
    "Informe pelo menos 30 caracteres.": "Enter at least 30 characters.",
    "Cancelando Plano...": "Canceling Plan...",
    "Reativando Plano...": "Reactivating Plan...",
    "Confirmar cancelamento": "Confirm cancellation",
    "Confirmar reativação": "Confirm reactivation",
    "Reativar Plano": "Reactivate Plan",
    "Cancelar Plano": "Cancel Plan",
    "Ciclo do atendimento": "Appointment lifecycle",
    "Este atendimento possui efeitos operacionais materializados.": "This appointment has materialized operational effects.",
    "A conclusão registra produção, repasse e os demais efeitos aplicáveis.": "Completion records production, professional payout, and other applicable effects.",
    "Concluir atendimento": "Complete appointment",
    "Reverter conclusão": "Reverse completion",
    "Você não possui as permissões necessárias para reverter esta conclusão.": "You do not have the permissions required to reverse this completion.",
    "Concluir atendimento?": "Complete appointment?",
    "A conclusão registrará produção, repasse, progresso do plano e financeiro quando aplicável.": "Completion will record production, professional payout, plan progress, and financial effects when applicable.",
    "Concluindo...": "Completing...",
    "Efeitos da reversão": "Reversal effects",
    "A produção ativa será cancelada e preservada no histórico.": "The active production record will be canceled and preserved in history.",
    "O repasse relacionado será cancelado ou marcado para tratamento administrativo.": "The related professional payout will be canceled or flagged for administrative handling.",
    "As sessões utilizadas e o status do plano serão recalculados.": "Used sessions and plan status will be recalculated.",
    "Uma entrega NPS ativa poderá ser invalidada até uma nova conclusão.": "An active NPS delivery may be invalidated until the plan is completed again.",
    "A reversão do atendimento não cancela nem estorna automaticamente os registros financeiros existentes. Eles serão sinalizados para revisão.": "Reversing the appointment does not automatically cancel or refund existing financial records. They will be flagged for review.",
    "Se existir repasse relacionado, a operação exigirá a permissão para cancelar repasses.": "If a related payout exists, this operation will require permission to cancel payouts.",
    "Novo estado operacional": "New operational status",
    "Para remarcar, reverta primeiro e depois utilize a ação específica de remarcação.": "To reschedule, reverse the completion first and then use the dedicated rescheduling action.",
    "Explique por que a conclusão precisa ser revertida": "Explain why the completion must be reversed",
    "Informe pelo menos 30 caracteres para registrar a reversão.": "Enter at least 30 characters to record the reversal.",
    "Confirmar reversão": "Confirm reversal",
    "Atendimento concluído com sucesso.": "Appointment completed successfully.",
    "Não foi possível concluir o atendimento.": "The appointment could not be completed.",
    "Conclusão revertida. Os registros financeiros relacionados foram sinalizados para revisão.": "Completion reversed. Related financial records were flagged for review.",
    "Conclusão revertida com sucesso.": "Completion reversed successfully.",
    "O repasse possui pagamentos preservados e requer tratamento administrativo.": "The payout has preserved payments and requires administrative handling.",
    "Não foi possível reverter a conclusão.": "The completion could not be reversed.",
    "Utilize a ação Concluir atendimento para registrar a conclusão.": "Use Complete appointment to record the completion.",
    "Este atendimento já foi concluído. Utilize Reverter conclusão para alterar seu estado.": "This appointment is already completed. Use Reverse completion to change its status.",
    "Restaure primeiro o agendamento para um estado operacional antes de executar esta ação.": "Restore the appointment to an operational status before performing this action.",
    "Status atual": "Current status",
    "Destino após a reversão": "Destination after reversal",
    "Verificando efeitos da reversão...": "Checking reversal effects...",
    "Não foi possível verificar os efeitos da reversão. Feche o diálogo e tente novamente.": "Could not check the reversal effects. Close the dialog and try again.",
    "Existem pagamentos de repasse relacionados. Eles serão preservados e o repasse será marcado para tratamento administrativo.": "There are related payout payments. They will be preserved and the payout will be marked for administrative handling.",
    "Revisão financeira necessária": "Financial review required",
    "Este registro financeiro foi preservado e precisa de uma decisão financeira explícita.": "This financial record was preserved and requires an explicit financial decision.",
    "A conclusão do atendimento foi revertida. Este registro foi preservado e precisa de decisão financeira.": "The appointment completion was reversed. This record was preserved and requires a financial decision.",
    "Confirmar manutenção do recebível": "Confirm receivable maintenance",
    "Confirme somente se a cobrança deve permanecer válida após a reversão clínica.": "Confirm only if the charge should remain valid after the clinical reversal.",
    "Justificativa financeira": "Financial justification",
    "Registre por que o recebível deve ser mantido": "Record why the receivable should be maintained",
    "Confirmar manutenção": "Confirm maintenance",
    "Manutenção do recebível confirmada.": "Receivable maintenance confirmed.",
    "Trilha de navegação": "Breadcrumb",
    "Mais": "More",
    "Slide anterior": "Previous slide",
    "Próximo slide": "Next slide",
    "Paginação": "Pagination",
    "Ir para a página anterior": "Go to previous page",
    "Ir para a próxima página": "Go to next page",
    "Mais páginas": "More pages",
    "Rotina clínica": "Clinical workflow",
    "Gestão": "Management",
    "Alternar menu Financeiro": "Toggle Finance menu",
    "Navegação principal": "Main navigation",
    "Trocar paciente": "Change patient",
    "Selecione uma forma de pagamento para registrar o recebimento.": "Select a payment method to record the receipt.",
    "Acompanhe recebimentos, valores a receber e despesas sem misturar caixa e competência.": "Track receipts, receivables, and expenses without mixing cash and accrual.",
    "Onde recebeu": "Where payments were received",
    "Recebimentos distribuídos por setor, conforme a data de pagamento.": "Receipts by sector, according to payment date.",
    "Nenhum recebimento com setor vinculado no período.": "No receipt with a linked sector in the period.",
    "Quem recebeu": "Who received payments",
    "Recebimentos distribuídos por profissional, conforme a data de pagamento.": "Receipts by professional, according to payment date.",
    "Nenhum recebimento vinculado a profissional no período.": "No receipt linked to a professional in the period.",
    "Leitura dos indicadores financeiros": "How to read the financial indicators",
    "Os cards de recebimento usam a data de pagamento. As despesas usam a data de competência e não compõem resultado de caixa nesta tela.": "Receipt cards use the payment date. Expenses use the accrual date and do not form a cash result on this page.",
    "Valor comercial confirmado; não representa recebimento.": "Confirmed commercial value; it does not represent a receipt.",
    "Entrada registrada por data de pagamento.": "Cash inflow recorded by payment date.",
    "Saldo pendente de cobrança; não representa entrada em caixa.": "Outstanding collection balance; it does not represent a cash inflow.",
    "Serviço executado, separado de venda e recebimento.": "Service delivered, separate from sale and receipt.",
    "A competência reconhece cada evento no período correspondente; ela é analisada separadamente do caixa.": "Accrual recognizes each event in its corresponding period; it is analyzed separately from cash.",
    "A DRE e o rateio gerencial analisam competência; o rateio não altera o caixa.": "The managerial income statement and allocation analyze accrual; allocation does not change cash.",
    "Caixa bruto": "Gross cash",
    "Caixa líquido": "Net cash",
    "Saídas efetivas": "Effective outflows",
    "Resultado de caixa": "Cash result",
    "Caixa líquido menos saídas efetivas, sem apuração contábil.": "Caixa líquido menos saídas efetivas, sem apuração contábil.",
    "Ticket médio de caixa": "Average cash ticket",
    "Recebimentos líquidos por evento de caixa": "Net receipts per cash event",
    "Indicadores contábeis indisponíveis": "Accounting indicators unavailable",
    "Relatórios de competência e indicadores unitários dependem de produção canônica, competência e custos confiáveis.": "Accrual-basis reports and per-unit indicators depend on canonical production records, reliable accrual data, and reliable costs.",
    "Valor vendido/cobrado": "Sold/charged amount",
    "Total estornado": "Total refunded",
    "Saldo vencido": "Overdue balance",
    "Não foi possível carregar as despesas.": "Could not load expenses.",
    "Despesa registrada.": "Expense recorded.",
    "Recorrência criada.": "Recurrence created.",
    "Pagamento registrado.": "Payment recorded.",
    "Reversão operacional": "Operational reversal",
    "Reversão registrada.": "Reversal recorded.",
    "Cancelamento operacional": "Operational cancellation",
    "Despesa cancelada.": "Expense canceled.",
    "Despesas e saídas": "Expenses and outflows",
    "Obrigações, pagamentos e resultado de caixa sem rateio automático.": "Obligations, payments, and cash result without automatic allocation.",
    "Aberta": "Open",
    "Pagamento parcial": "Partial payment",
    "Paga": "Paid",
    "Vencida": "Overdue",
    "Direta do setor": "Direct to sector",
    "Direta do profissional": "Direct to professional",
    "Corporativa não alocada": "Unallocated corporate",
    "Nova despesa": "New expense",
    "Outras": "Other",
    "Pagamento imediato": "Immediate payment",
    "Nova recorrência": "New recurrence",
    "Dia de vencimento": "Due day",
    "Criar recorrência": "Create recurrence",
    "Nenhuma despesa encontrada": "No expenses found",
    "Próxima": "Next",
    "Produção clínica": "Clinical production",
    "Produções geradas por atendimento concluído, separadas do financeiro e prontas para conferência operacional.": "Clinical production generated from completed care services, separate from finance and ready for operational review.",
    "Não foi possível carregar a produção clínica.": "Could not load clinical production.",
    "Não foi possível carregar os filtros de produção clínica.": "Could not load clinical production filters.",
    "Não foi possível gerar a produção clínica.": "Could not generate clinical production.",
    "Não foi possível cancelar a produção clínica.": "Could not cancel clinical production.",
    "Produção clínica gerada com sucesso.": "Clinical production generated successfully.",
    "Produção clínica cancelada com sucesso.": "Clinical production canceled successfully.",
    "Produções para revisar": "Productions to review",
    "Produzido": "Produced",
    "Avulso": "Standalone",
    "Revisar valor": "Review value",
    "Filtros": "Filters",
    "Todas": "All",
    "Todos": "All",
    "Cancelada": "Canceled",
    "Classificação": "Classification",
    "Venda do atendimento": "Appointment sale",
    "Valor registrado no atendimento": "Value recorded for the appointment",
    "Cortesia": "Complimentary service",
    "Valor do atendimento não determinado": "Appointment value not determined",
    "Sessão adicional sem aditivo comercial": "Additional session without a commercial addendum",
    "Venda de plano de tratamento": "Treatment plan sale",
    "Valor registrado no plano de tratamento": "Value recorded in the treatment plan",
    "Venda comercial": "Commercial sale",
    "Atendimento avulso": "Standalone appointment",
    "Atendimento avulso para revisão": "Standalone appointment requiring review",
    "Sessão adicional de plano": "Additional plan session",
    "Valor confirmado": "Confirmed value",
    "Valor estimado": "Estimated value",
    "Valor a revisar": "Value to review",
    "Origem não identificada": "Unidentified source",
    "Tipo não identificado": "Unidentified type",
    "Classificação não identificada": "Unidentified classification",
    "Sem venda, serviço ou valor operacional determinístico": "No sale, service, or deterministic operational amount",
    "Motivo de revisão não identificado": "Review reason not identified",
    "Recebimento": "Receipt",
    "Estorno": "Refund",
    "Ajuste": "Adjustment",
    "Lançamento manual de caixa": "Manual cash entry",
    "Tipo de lançamento não identificado": "Unidentified entry type",
    "Forma de pagamento não identificada": "Unidentified payment method",
    "confirmed": "confirmed",
    "inferred": "inferred",
    "legacy": "legacy",
    "unknown": "unknown",
    "commercial sale": "commercial sale",
    "treatment plan": "treatment plan",
    "appointment snapshot": "appointment snapshot",
    "courtesy": "courtesy",
    "additional session requires review": "additional session requires review",
    "Ações": "Actions",
    "Detalhes": "Details",
    "Nenhuma produção clínica encontrada": "No clinical production found",
    "Detalhes da produção": "Production details",
    "Competência": "Accrual date",
    "Sessão": "Session",
    "Cancelar produção clínica": "Cancel clinical production",
    "A produção será marcada como cancelada sem alterar vendas, recebíveis ou caixa.": "Production will be marked as canceled without changing sales, receivables, or cash.",
    "Cancelar produção": "Cancel production",
    "Cancelando...": "Canceling...",
    "Gerar pendentes": "Generate pending",
    "Atualizar": "Refresh",
    "Configurações": "Settings",
    "Central Administrativa": "Administrative Center",
    "Administração": "Administration",
    "Categorias": "Categories",
    "Carregando configurações...": "Loading settings...",
    "Navegação de configurações": "Settings navigation",
    "Seção": "Section",
    "Voltar": "Back",
    "Voltar às configurações": "Back to settings",
    "Seção não encontrada": "Section not found",
    "Nenhuma configuração encontrada para este endereço.": "No settings section was found for this address.",
    "Centralize as configurações administrativas da clínica sem duplicar cadastros ou fontes de dados.": "Centralize the clinic's administrative settings without duplicating records or data sources.",
    "Clínica": "Clinic",
    "Configurações da clínica": "Clinic settings",
    "Dados institucionais, moeda e fuso horário da clínica.": "Institutional details, currency, and clinic timezone.",
    "Operação": "Operations",
    "Organize setores usados por profissionais, agenda, filtros e relatórios.": "Organize departments used by professionals, scheduling, filters, and reports.",
    "Status da Agenda": "Schedule statuses",
    "Configure nomes, cores, ordem e visibilidade dos status sem alterar os estados técnicos.": "Configure names, colors, order, and visibility without changing technical states.",
    "Não foi possível carregar os status configurados da agenda.": "Could not load configured schedule statuses.",
    "Catálogo visual de status": "Visual status catalog",
    "A configuração visual altera rótulos, cores, ordem e visibilidade sem mudar os estados técnicos usados nas regras da Agenda.": "Visual settings change labels, colors, order, and visibility without changing the technical states used by schedule rules.",
    "Restaurar padrão": "Restore defaults",
    "Salvar status": "Save statuses",
    "Status da agenda salvos.": "Schedule statuses saved.",
    "Seu perfil não possui permissão para alterar os status da Agenda.": "Your profile does not have permission to change schedule statuses.",
    "Revise nomes vazios, cores inválidas ou ordens negativas antes de salvar.": "Review blank names, invalid colors, or negative order values before saving.",
    "Estado técnico": "Technical state",
    "Nome exibido": "Displayed name",
    "Cor": "Color",
    "Ordem": "Order",
    "Prévia": "Preview",
    "Serviços e Procedimentos": "Services and Procedures",
    "Gerencie o catálogo canônico usado na agenda, planos e financeiro.": "Manage the canonical catalog used in scheduling, plans, and finance.",
    "Atendimento clínico": "Clinical care",
    "Modelos clínicos": "Clinical templates",
    "Crie e gerencie modelos reutilizáveis para avaliações, evoluções e outros registros clínicos.": "Create and manage reusable templates for assessments, clinical notes, and other clinical records.",
    "Comercial": "Sales",
    "Origens de Leads": "Lead origins",
    "Padronize as origens usadas no funil comercial e nos filtros.": "Standardize the origins used in the sales funnel and filters.",
    "Motivos de não agendamento": "No-schedule reasons",
    "Mantenha o catálogo oficial usado nos desfechos comerciais.": "Maintain the official catalog used in commercial outcomes.",
    "Financeiro": "Finance",
    "DRE Gerencial": "Managerial Income Statement",
    "Demonstrativo gerencial por competência, com caixa exibido em bloco comparativo separado.": "Managerial accrual statement with cash shown in a separate comparison block.",
    "Não foi possível carregar a DRE gerencial.": "Could not load the managerial income statement.",
    "Não foi possível carregar os filtros da DRE gerencial.": "Could not load managerial income statement filters.",
    "Não foi possível carregar a origem da linha.": "Could not load the line source.",
    "Não foi possível carregar os detalhes da linha.": "Could not load the line details.",
    "Não foi possível exportar a DRE gerencial.": "Could not export the managerial income statement.",
    "Aviso gerencial": "Managerial notice",
    "Linha": "Line",
    "Origem dos dados": "Data source",
    "Data considerada": "Recognition date",
    "Detalhes de {line}": "Details for {line}",
    "Entender {line}": "About {line}",
    "Ver detalhes de {line}": "View details for {line}",
    "Nenhum detalhe encontrado": "No details found",
    "Despesas consideradas no rateio": "Expenses included in the allocation",
    "Competência versus caixa": "Accrual versus cash",
    "Receita reconhecida": "Recognized revenue",
    "Caixa recebido": "Cash received",
    "Custos e despesas reconhecidos": "Recognized costs and expenses",
    "Qualidade dos dados": "Data quality",
    "Origem da linha": "Line source",
    "Nenhuma origem encontrada": "No source found",
    "Nenhum demonstrativo encontrado": "No statement found",
    "Visão": "View",
    "Visão original": "Original view",
    "Visão pós-rateio": "Post-allocation view",
    "Comparar": "Compare",
    "Impacto do rateio gerencial": "Managerial allocation impact",
    "Análise exclusivamente gerencial": "Managerial analysis only",
    "Análise por setor": "Analysis by sector",
    "Análise por profissional": "Analysis by professional",
    "Análise por serviço": "Analysis by service",
    "DRE original": "Original statement",
    "DRE pós-rateio": "Post-allocation statement",
    "DRE comparativa": "Comparative statement",
    "Histórico de rateios": "Allocation history",
    "Não calculável": "Not calculable",
    "Cobertura do rateio": "Allocation coverage",
    "Setores impactados": "Impacted sectors",
    "Comparativo consolidado": "Consolidated comparison",
    "Maiores impactos por setor": "Largest impacts by sector",
    "Evolução do rateio": "Allocation trend",
    "Período sem movimento": "Period with no activity",
    "Revisar rateios gerenciais": "Review managerial allocations",
    "Esta análise redistribui despesas corporativas entre setores para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa é alterado.": "This analysis redistributes corporate expenses across sectors for managerial purposes. No financial entry, payment, or cash amount is changed.",
    "Nenhum rateio gerencial ativo foi encontrado neste período.": "No active managerial allocation was found in this period.",
    "Todas as despesas corporativas elegíveis do período foram rateadas.": "All eligible corporate expenses in the period have been allocated.",
    "Este setor não recebeu despesas por rateio no período selecionado.": "This sector did not receive allocated expenses in the selected period.",
    "O rateio redistribui despesas entre setores e não altera o resultado consolidado da clínica.": "The allocation redistributes expenses across sectors and does not change the clinic consolidated result.",
    "APPLIED": "Applied",
    "Resultado antes de rateios": "Managerial result before allocations",
    "Resultado pós-rateio": "Post-allocation result",
    "Despesas redistribuídas": "Redistributed expenses",
    "Despesas rateadas": "Allocated expenses",
    "Despesas ainda não rateadas": "Expenses not yet allocated",
    "Lotes considerados": "Batches considered",
    "Snapshots gerenciais": "Managerial snapshots",
    "Diferença consolidada": "Consolidated difference",
    "Resultado consolidado preservado": "Consolidated result preserved",
    "Lote revertido": "Reversed batch",
    "Nenhum rateio aplicado no período": "No allocation applied in the period",
    "Nenhum rateio ativo no período": "No active allocation in the period",
    "Despesa original": "Original expense",
    "Rateio recebido": "Allocation received",
    "Despesa pós-rateio": "Post-allocation expense",
    "Resultado original": "Original result",
    "Diferença": "Difference",
    "Despesas recebidas por rateio": "Expenses received by allocation",
    "Esta visão redistribui despesas corporativas para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa foi alterado.": "This view redistributes corporate expenses for managerial purposes. No financial entry, payment, or cash amount was changed.",
    "A comparação demonstra o impacto setorial do rateio. O resultado consolidado da clínica permanece inalterado.": "The comparison shows the sector impact of the allocation. The clinic consolidated result remains unchanged.",
    "Visão original sem efeitos de snapshots de rateio gerencial.": "Original view with no effects from managerial allocation snapshots.",
    "Despesas corporativas redistribuídas na leitura gerencial a partir de snapshots aplicados.": "Corporate expenses redistributed in the managerial read model from applied snapshots.",
    "Resultado gerencial por competência após redistribuição analítica de despesas corporativas aplicadas.": "Managerial accrual result after analytic redistribution of applied corporate expenses.",
    "Métodos de pagamento": "Payment methods",
    "Configure meios, adquirentes, prazos e taxas existentes.": "Configure existing methods, acquirers, terms, and fees.",
    "Regras de repasse": "Payout Rules",
    "Configure regras versionadas de repasse por profissional, setor ou serviço.": "Configure versioned payout rules by professional, department, or service.",
    "Rateio gerencial": "Managerial Expense Allocation",
    "Configure regras versionadas de rateio percentual por setor.": "Configure versioned percentage-based expense allocation rules by department.",
    "Configurações comerciais": "Commercial settings",
    "Gerencie origens e motivos administrativos na Central de Configurações.": "Manage administrative origins and reasons in the Settings Center.",
    "Gerenciar origens": "Manage origins",
    "Gerenciar motivos": "Manage reasons",
    "Gerenciar catálogo": "Manage catalog",
    "Gerenciar templates": "Manage templates",
    "Gerenciar métodos": "Manage methods",
    "Use este cadastro para padronizar as origens utilizadas no funil comercial.": "Use this registry to standardize the origins used in the commercial funnel.",
    "Selecione uma clínica para continuar.": "Select a clinic to continue.",
    "Dados cadastrais": "Registration details",
    "Estes dados vêm do cadastro canônico da clínica e não são duplicados nesta central.": "These details come from the clinic's canonical record and are not duplicated in this center.",
    "CNPJ": "CNPJ",
    "Preferências regionais": "Regional preferences",
    "A moeda e o fuso horário são compartilhados por toda a clínica.": "Currency and timezone are shared across the whole clinic.",
    "Alterações não salvas": "Unsaved changes",
    "Salve as alterações antes de sair desta seção.": "Save your changes before leaving this section.",
    "Salvar configurações": "Save settings",
    "Categoria": "Category",
    "Não alocado": "Unallocated",
    "até": "to",
    "Filtros avançados": "Advanced filters",
    "Filtros ativos": "Active filters",
    "Indicadores comerciais": "Sales indicators",
    "Lista de leads": "Lead list",
    "Carregando leads...": "Loading leads...",
    "Nenhum resultado encontrado": "No results found",
    "Nenhum lead cadastrado": "No leads registered",
    "Ajuste ou limpe os filtros para ampliar a busca.": "Adjust or clear the filters to broaden your search.",
    "Crie um lead para começar o acompanhamento comercial.": "Create a lead to start sales follow-up.",
    "Acompanhe, priorize e avance as oportunidades comerciais.": "Track, prioritize, and advance sales opportunities.",
    "Localização": "Location",
    "Filtrar por cidade": "Filter by city",
    "Filtrar por bairro": "Filter by neighborhood",
    "Gênero": "Gender",
    "Data de cadastro do paciente": "Patient registration date",
    "Cadastro a partir de": "Registration from",
    "Cadastro até antes de": "Registration until before",
    "Direção": "Direction",
    "Crescente": "Ascending",
    "Decrescente": "Descending",
    "Catálogo": "Catalog",
    "Catálogo de Serviços e Procedimentos": "Services and Procedures Catalog",
    "Gerencie serviços e procedimentos utilizados na agenda, no prontuário clínico, nos planos de tratamento e no financeiro, preservando os dados históricos.": "Manage services and procedures used across scheduling, clinical records, treatment plans, and finance while preserving historical data.",
    "Novo item do catálogo": "New catalog item",
    "Item do catálogo atualizado com sucesso.": "Catalog item updated successfully.",
    "Item do catálogo cadastrado com sucesso.": "Catalog item created successfully.",
    "Item do catálogo inativado com sucesso.": "Catalog item deactivated successfully.",
    "Item do catálogo reativado com sucesso.": "Catalog item reactivated successfully.",
    "Não foi possível salvar o item do catálogo.": "The catalog item could not be saved.",
    "Não foi possível alterar o status do item do catálogo.": "The catalog item's status could not be changed.",
    "Não foi possível carregar o catálogo.": "The catalog could not be loaded.",
    "Serviço": "Service",
    "Procedimento": "Procedure",
    "Serviço ou procedimento": "Service or procedure",
    "Serviço não informado": "No service specified",
    "Sem item do catálogo": "No catalog item",
    "Outro lançamento": "Other entry",
    "Todos os tipos": "All types",
    "Buscar por nome ou descrição": "Search by name or description",
    "Duração": "Duration",
    "Preço padrão": "Default price",
    "Uso histórico": "Historical usage",
    "Toda a clínica": "Whole clinic",
    "Nenhum item do catálogo encontrado.": "No catalog items found.",
    "Editar item do catálogo": "Edit catalog item",
    "Cadastrar item do catálogo": "Add catalog item",
    "Alterações no catálogo não modificam agendamentos, planos de tratamento ou lançamentos financeiros já registrados.": "Catalog changes do not affect existing appointments, treatment plans, or financial records.",
    "Carregando serviços...": "Loading services...",
    "Sugestão": "Suggestion",
    "Agendamentos antigos podem permanecer sem serviço.": "Older appointments may remain without a service.",
    "O preço padrão é apenas uma sugestão; o valor efetivo fica salvo no lançamento.": "The default price is only a suggestion; the effective amount is saved on the entry.",
    "Buscar": "Search",
    "Agendamento": "Appointment",
    "Sim": "Yes",
    "Não": "No",
    "Recebido": "Received",
    "Receber": "Receive",
    "Em aberto": "Open",
    "Quantidade": "Quantity",
    "Histórico financeiro": "Financial history",
    "Nenhuma receita encontrada para este paciente.": "No revenue found for this patient.",
    "Todos os setores": "All departments",
    "Filtrar por setor": "Filter by sector",
    "Setor inativo": "Inactive sector",
    "Setor selecionado": "Selected sector",
    "Resultado direto do setor": "Direct sector result",
    "Despesas atribuídas": "Assigned expenses",
    "Despesas a pagar": "Expenses payable",
    "Despesas vencidas": "Overdue expenses",
    "Despesas pagas": "Expenses paid",
    "Despesas corporativas": "Corporate expenses",
    "Despesas diretas": "Direct expenses",
    "Repasses pagos": "Paid payouts",
    "Dados não atribuídos": "Unassigned data",
    "Nenhum setor encontrado": "No sector found",
    "Setores": "Departments",
    "Ticket médio por lançamento": "Average revenue per entry",
    "Requer custo realizado atribuído às aquisições": "Requires realized cost attributed to acquisitions",
    "Receita média por paciente pagante": "Average revenue per paying patient",
    "Receita realizada no período; não representa LTV": "Realized period revenue; this is not LTV",
    "Requer custo e receita atribuídos à campanha": "Requires campaign-attributed cost and revenue",
    "Erro ao carregar setores": "Error loading departments",
    "Já existe um setor com este nome": "A department with this name already exists",
    "O nome do setor deve ter entre 2 e 80 caracteres": "The department name must be between 2 and 80 characters",
    "Selecione um setor ativo da clínica": "Select an active clinic department",
    "Selecione um setor da clínica": "Select a clinic department",
    "Setor não encontrado": "Department not found",
    "Informe ao menos uma alteração": "Enter at least one change",
    "Idioma": "Language",
    "Idioma da interface": "Interface language",
    "Moeda": "Currency",
    "Moeda do sistema": "System currency",
    "Configuração regional da clínica": "Clinic regional settings",
    "Configuração regional salva": "Regional settings saved",
    "A moeda e o fuso são compartilhados por toda a clínica.": "Currency and timezone are shared across the clinic.",
    "Somente administradores podem alterar esta configuração.": "Only administrators can change this setting.",
    "A moeda está bloqueada porque já existe histórico financeiro.": "Currency is locked because financial history already exists.",
    "Alterar a moeda não converte valores existentes.": "Changing currency does not convert existing values.",
    "Fuso horário": "Timezone",
    "São Paulo (América)": "São Paulo (Americas)",
    "Nova York (América)": "New York (Americas)",
    "Lisboa (Europa)": "Lisbon (Europe)",
    "Madri (Europa)": "Madrid (Europe)",
    "Roma (Europa)": "Rome (Europe)",
    "Buscar cidade ou fuso": "Search city or timezone",
    "O fuso define dias, horários da agenda e períodos dos indicadores; instantes salvos não são reescritos.": "The timezone defines days, appointment times and metric periods; saved instants are not rewritten.",
    "Preferências": "Preferences",
    "Abrir preferências": "Open preferences",
    "Ajuste idioma, moeda e tema da interface.": "Adjust interface language, currency, and theme.",
    "Ajuste idioma e tema da interface.": "Adjust interface language and theme.",
    "Tema": "Theme",
    "Tema da interface": "Interface theme",
    "América Latina": "Latin America",
    "Europa": "Europe",
    "Português": "Portuguese",
    "Inglês": "English",
    "Espanhol": "Spanish",
    "Dashboard": "Dashboard",
    "Leads": "Leads",
    "Abrir lead": "Open lead",
    "Abrir próximo follow-up": "Open next follow-up",
    "Campanhas, agendamentos e desempenho comercial.": "Campaigns, appointments, and sales performance.",
    "Data do Pagamento": "Payment date",
    "Editar": "Edit",
    "Erro ao carregar dados de marketing": "Error loading marketing data",
    "Erro ao carregar leads": "Error loading leads",
    "Erro ao excluir lead": "Error deleting lead",
    "Excluir": "Delete",
    "Excluir lead": "Delete lead",
    "Filtrar status": "Filter status",
    "Leads por Origem": "Leads by source",
    "Nenhum dado disponível": "No data available",
    "Nenhum follow-up pendente no momento.": "No pending follow-ups right now.",
    "Nova Despesa": "New expense",
    "Nova Receita": "New revenue",
    "Novo Lead": "New lead",
    "Paciente": "Patient",
    "Receita registrada com sucesso!": "Revenue recorded successfully!",
    "Receitas": "Revenue",
    "Resumo Financeiro": "Financial summary",
    "Central Financeira do Paciente": "Patient financial center",
    "Visão filtrada dos lançamentos financeiros vinculados ao paciente.": "Filtered view of financial entries linked to this patient.",
    "Novo lançamento": "New entry",
    "Receita total do paciente": "Total patient revenue",
    "Total recebido": "Total received",
    "Total em aberto": "Outstanding total",
    "Próximo vencimento": "Next due date",
    "Planos ativos": "Active plans",
    "Pendências financeiras": "Financial pending items",
    "Lançamentos em aberto, vencidos ou parcialmente pagos.": "Open, overdue, or partially paid entries.",
    "pendência(s)": "pending item(s)",
    "Nenhuma pendência financeira para este paciente.": "No financial pending items for this patient.",
    "Saldo pendente": "Outstanding balance",
    "Histórico financeiro do paciente": "Patient financial history",
    "Receitas e valores a receber filtrados pelo paciente aberto.": "Revenue and receivables filtered by the current patient.",
    "Buscar por descrição, profissional, valor, data ou status": "Search by description, professional, amount, date, or status",
    "Pagos": "Paid",
    "Editar lançamento": "Edit entry",
    "Tipo do lançamento": "Entry type",
    "Consulta avulsa": "Single appointment",
    "Outro lançamento personalizado": "Other custom entry",
    "Selecionar plano existente": "Select existing plan",
    "Selecione um plano": "Select a plan",
    "Sem plano vinculado": "No linked plan",
    "Sem profissional vinculado": "No linked professional",
    "Sem agendamento vinculado": "No linked appointment",
    "Receber pagamento": "Receive payment",
    "Valor recebido": "Amount received",
    "Data do recebimento": "Receipt date",
    "Detalhes do lançamento": "Entry details",
    "Plano vinculado": "Linked plan",
    "Agendamento vinculado": "Linked appointment",
    "Cancelar lançamento": "Cancel entry",
    "Este agendamento já possui lançamento financeiro vinculado.": "This appointment already has a linked financial entry.",
    "Este plano já possui lançamento financeiro vinculado.": "This plan already has a linked financial entry.",
    "Informe um valor válido.": "Enter a valid amount.",
    "Status do lead atualizado!": "Lead status updated.",
    "Todos os profissionais": "All professionals",
    "Marketing": "Marketing",
    "Pacientes": "Patients",
    "Cadastro e prontuário de pacientes": "Patient records and clinical notes",
    "Cadastro e prontuário do paciente": "Patient record and clinical chart",
    "Novo Paciente": "New patient",
    "Tipo de tratamento": "Treatment type",
    "Tipo de atendimento": "Care type",
    "Tipo não informado": "Type not provided",
    "Todos os tipos de tratamento": "All treatment types",
    "Iniciar atendimento": "Start care",
    "Cadastre uma avaliação antes de iniciar uma evolução.": "Record an assessment before starting a clinical note.",
    "Data da avaliação": "Assessment date",
    "Última evolução": "Latest clinical note",
    "Status do paciente": "Patient status",
    "Paciente ativado com sucesso!": "Patient activated successfully!",
    "Paciente inativado com sucesso!": "Patient deactivated successfully!",
    "Motivos de Não Agendamento": "Non-booking reasons",
    "Principais objeções dos leads que não realizaram um agendamento.": "Main objections preventing leads from booking an appointment.",
    "Leads não agendados": "Leads without appointments",
    "Leads não agendados por origem": "Leads without appointments by source",
    "Distribuição server-side dos leads com desfecho não agendado.": "Server-side distribution of leads marked as not booked.",
    "Principal motivo": "Top reason",
    "Representatividade": "Share",
    "Nenhum motivo registrado": "No reason recorded",
    "Nenhum motivo de não agendamento encontrado para os filtros selecionados.": "No non-booking reasons were found for the selected filters.",
    "Leads não agendados por motivo": "Leads without appointments by reason",
    "Detalhamento dos leads não agendados": "Details of leads without appointments",
    "Data do desfecho": "Outcome date",
    "Desfecho registrado em": "Outcome recorded on",
    "Desfechos sem data definida": "Outcomes without a defined date",
    "Cobertura histórica incompleta": "Incomplete historical coverage",
    "Registros legados": "Legacy records",
    "Dados anteriores não possuem data confiável.": "Earlier records do not have a reliable date.",
    "Não foi possível determinar a data": "Could not determine the date",
    "Sem origem": "No source",
    "Sem motivo": "No reason",
    "Origem inativa": "Inactive source",
    "Comercial responsável": "Sales representative",
    "Último follow-up": "Last follow-up",
    "Status comercial": "Commercial status",
    "Nenhum lead encontrado para este motivo.": "No leads were found for this reason.",
    "Catálogo de Motivos de Não Agendamento": "Non-booking reason catalog",
    "Padronize as objeções utilizadas no acompanhamento comercial dos leads.": "Standardize the objections used in lead follow-up.",
    "Novo motivo de não agendamento": "New non-booking reason",
    "Motivo adicionado com sucesso!": "Reason added successfully!",
    "Motivo atualizado com sucesso!": "Reason updated successfully!",
    "Motivo ativado com sucesso!": "Reason activated successfully!",
    "Motivo inativado com sucesso!": "Reason deactivated successfully!",
    "Adicionar motivo": "Add reason",
    "Motivo": "Reason",
    "Ativar": "Activate",
    "Inativar": "Deactivate",
    "Editar motivo de não agendamento": "Edit non-booking reason",
    "Inativar motivo?": "Deactivate reason?",
    "O motivo deixará de aparecer em novos cadastros, mas continuará preservado no histórico.": "The reason will no longer appear in new records, but it will remain in the history.",
    "O motivo deve ter entre 2 e 100 caracteres": "The reason must be between 2 and 100 characters",
    "Já existe um motivo de não agendamento com este nome": "A non-booking reason with this name already exists",
    "Motivo de não agendamento não encontrado": "Non-booking reason not found",
    "Selecione um motivo de não agendamento ativo": "Select an active non-booking reason",
    "Não respondeu": "Did not respond",
    "Preço / Achou caro": "Price / Too expensive",
    "Queria convênio": "Wanted insurance coverage",
    "Sem horário compatível": "No suitable time",
    "Vai pensar": "Will think about it",
    "Já fechou em outra clínica": "Chose another clinic",
    "Nenhum atendimento encontrado para o tipo e período selecionados.": "No appointments were found for the selected type and date range.",
    "Buscar paciente existente": "Find existing patient",
    "Cadastrar novo paciente": "Add new patient",
    "Busca por nome, telefone ou CPF": "Search by name, phone, or document ID",
    "Busca por nome, telefone, CPF, gênero, cidade ou bairro": "Search by name, phone, document ID, gender, city, or neighborhood",
    "Buscar paciente": "Search patient",
    "Buscar paciente por nome, telefone ou CPF": "Search patient by name, phone, or document ID",
    "Digite o nome do paciente": "Enter the patient's name",
    "Buscando pacientes...": "Searching patients...",
    "Não foi possível buscar os pacientes. Tente novamente.": "We couldn't search for patients. Try again.",
    "Selecione um paciente e um profissional.": "Select a patient and a professional.",
    "Erro ao carregar dados do paciente": "Error loading patient data",
    "Carregando...": "Loading...",
    "Carregando pacientes...": "Loading patients...",
    "Nenhuma despesa encontrada no período selecionado.": "No expenses found in the selected period.",
    "Nenhum paciente": "No patients",
    "Nenhum paciente encontrado": "No patients found",
    "Nascimento": "Birth date",
    "Cadastro": "Registration",
    "Ver": "View",
    "Paciente criado com sucesso!": "Patient created successfully!",
    "Paciente existente selecionado.": "Existing patient selected.",
    "Encontramos um ou mais pacientes com dados semelhantes nesta clínica. Revise os cadastros antes de continuar.": "We found one or more patients with similar data in this clinic. Review the records before continuing.",
    "Já existe um cadastro com este CPF nesta clínica. Como bases históricas podem conter duplicidades, você pode selecionar o cadastro existente ou continuar a criação após revisar os dados.": "A record with this CPF already exists in this clinic. Because historical databases may contain duplicates, you can select the existing record or continue creating a new one after reviewing the data.",
    "Confiança": "Confidence",
    "Critérios": "Criteria",
    "Usar este cadastro": "Use this record",
    "Revisei e preciso criar um novo cadastro": "I reviewed it and need to create a new record",
    "Continuar criação": "Continue creation",
    "Paciente atualizado com sucesso!": "Patient updated successfully!",
    "Editar Paciente": "Edit patient",
    "RG": "Secondary document",
    "Estado civil": "Marital status",
    "Selecione o estado civil": "Select marital status",
    "Solteiro(a)": "Single",
    "Casado(a)": "Married",
    "União Estável": "Domestic partnership",
    "Divorciado(a)": "Divorced",
    "Viúvo(a)": "Widowed",
    "Separado(a)": "Separated",
    "Prefiro não informar": "Prefer not to say",
    "Profissão": "Occupation",
    "Convênio e filiação": "Insurance and family details",
    "Número do convênio": "Insurance number",
    "CNS": "National health card",
    "Nome da mãe": "Mother's name",
    "Documento da mãe": "Mother's document",
    "Nome do pai": "Father's name",
    "Documento do pai": "Father's document",
    "Filiação": "Family details",
    "Texto livre. Use vírgulas, espaços ou uma linha por item.": "Free text. Use commas, spaces, or one item per line.",
    "Masculino": "Male",
    "Feminino": "Female",
    "Logradouro": "Street",
    "Complemento": "Address line 2",
    "Contato de Emergência": "Emergency contact",
    "Histórico Médico / Observações": "Medical history / notes",
    "Cadastrar": "Create",
    "Erro ao carregar planos de tratamento": "Error loading treatment plans",
    "Carregando planos de tratamento...": "Loading treatment plans...",
    "Buscando endereço pelo CEP...": "Looking up address by ZIP code...",
    "Plano cancelado com sucesso!": "Plan cancelled successfully!",
    "Plano reativado com sucesso!": "Plan reactivated successfully!",
    "Planos de tratamento": "Treatment plans",
    "Acompanhe venda, execução e saldo de sessões do paciente.": "Track the sale, delivery, and remaining sessions for the patient.",
    "Novo plano": "New plan",
    "Nenhum plano cadastrado para este paciente ainda.": "No treatment plan has been added for this patient yet.",
    "Profissional": "Professional",
    "Venda em": "Sold on",
    "Venda ainda não registrada": "Sale not recorded yet",
    "Sessões": "Sessions",
    "Saldo": "Balance",
    "restantes": "remaining",
    "Valor": "Amount",
    "Valor bruto": "Gross amount",
    "A receber": "Accounts receivable",
    "À Receber": "Accounts receivable",
    "Novo valor a receber": "New receivable",
    "Novo à receber": "New receivable",
    "Editar valor a receber": "Edit receivable",
    "Editar à receber": "Edit receivable",
    "Gerar valor a receber": "Create receivable",
    "Gerar à receber": "Create receivable",
    "Valor a receber gerado com sucesso": "Receivable created successfully",
    "À receber gerado com sucesso": "Receivable created successfully",
    "À receber gerado": "Receivable created",
    "À receber já gerado": "Receivable already created",
    "Valor a receber criado com sucesso.": "Receivable created successfully.",
    "À receber criado com sucesso.": "Receivable created successfully.",
    "Valor a receber atualizado com sucesso.": "Receivable updated successfully.",
    "À receber atualizado com sucesso.": "Receivable updated successfully.",
    "Valor a receber cancelado com sucesso.": "Receivable cancelled successfully.",
    "À receber cancelado com sucesso.": "Receivable cancelled successfully.",
    "Controle valores pendentes, vencidos e recebimentos parciais sem misturar com receitas já recebidas.": "Track pending, overdue, and partial payments without mixing them with received revenue.",
    "Total a receber": "Total receivable",
    "Total à receber": "Total receivable",
    "Total vencido": "Overdue total",
    "Recebido no período": "Received in period",
    "Total parcial": "Partial total",
    "Pacientes com pendência": "Patients with pending balance",
    "Todos os pacientes": "All patients",
    "Todos status": "All statuses",
    "Todos pacientes": "All patients",
    "Todas as formas": "All methods",
    "Forma de pagamento prevista": "Expected payment method",
    "Pagamento previsto": "Expected payment",
    "Sem forma prevista": "No expected method",
    "Sem forma definida": "No method set",
    "Data do pagamento ou vencimento": "Payment or due date",
    "Valor total": "Total amount",
    "Saldo restante": "Remaining balance",
    "Marcar como recebido": "Mark as received",
    "Registrar pagamento parcial": "Record partial payment",
    "Registrar baixa": "Record payment",
    "Recebimento registrado com sucesso.": "Payment recorded successfully.",
    "Recebimento parcial registrado com sucesso.": "Partial payment recorded successfully.",
    "Nenhum valor a receber encontrado com os filtros selecionados.": "No receivable found with the selected filters.",
    "Nenhum valor à receber encontrado com os filtros selecionados.": "No receivable found with the selected filters.",
    "Detalhes do valor a receber": "Receivable details",
    "Detalhes do à receber": "Receivable details",
    "Nenhuma baixa registrada.": "No payment recorded.",
    "Valor a receber indisponível": "Receivable unavailable",
    "À receber indisponível": "Receivable unavailable",
    "Gerando...": "Creating...",
    "Vencimento": "Due date",
    "Vencido": "Expired",
    "Pago": "Paid",
    "Cancelado": "Cancelled",
    "Valor líquido": "Net amount",
    "Valor da receita": "Revenue amount",
    "Validade": "Validity",
    "Sem prazo": "No deadline",
    "Venda registrada no financeiro.": "Sale recorded in finance.",
    "Plano ainda sem receita vinculada no financeiro.": "This plan still has no linked revenue entry in finance.",
    "Rascunho": "Draft",
    "Vendido": "Sold",
    "Em execução": "In progress",
    "Renovar": "Renew",
    "Reativar": "Reactivate",
    "Plano de tratamento atualizado com sucesso!": "Treatment plan updated successfully!",
    "Renovação do plano criada com sucesso!": "Plan renewal created successfully!",
    "Plano de tratamento cadastrado com sucesso!": "Treatment plan created successfully!",
    "Editar Plano de Tratamento": "Edit treatment plan",
    "Renovar Plano de Tratamento": "Renew treatment plan",
    "Novo Plano de Tratamento": "New treatment plan",
    "Ex.: Reabilitação lombar": "Ex.: Low back rehabilitation",
    "Selecione o profissional": "Select the professional",
    "Quantidade de sessões": "Number of sessions",
    "Validade inicial": "Valid from",
    "Validade final": "Valid until",
    "Descrição": "Description",
    "Objetivo do tratamento, observações e combinados.": "Treatment goals, notes, and agreements.",
    "Registrar nova venda na renovação": "Record a new sale on renewal",
    "Registrar venda do plano agora": "Record plan sale now",
    "Quando marcado, o financeiro recebe uma receita de pacote vinculada ao plano.": "When enabled, finance receives a package revenue entry linked to this plan.",
    "Forma de pagamento": "Payment method",
    "Selecione uma forma de pagamento": "Select a payment method",
    "Selecione uma forma de pagamento.": "Select a payment method.",
    "Nenhuma forma de pagamento ativa": "No active payment method",
    "Cadastre uma forma de pagamento ativa antes de registrar receitas.": "Create an active payment method before recording revenue.",
    "Forma de pagamento não cadastrada ({method})": "Unregistered payment method ({method})",
    "{name} (inativa)": "{name} (inactive)",
    "Registro histórico": "Historical record",
    "Forma inativa": "Inactive method",
    "Vínculo histórico indisponível": "Historical link unavailable",
    "Forma de pagamento histórica não vinculada": "Unlinked historical payment method",
    "Convênio/cobertura": "Insurance/coverage",
    "Identificador técnico: {identifier}": "Technical identifier: {identifier}",
    "Parcela {current} de {total} da obrigação": "Obligation installment {current} of {total}",
    "Parcelas": "Installments",
    "Parcelas do meio de pagamento": "Payment method installments",
    "Indisponível": "Unavailable",
    "Valor líquido histórico indisponível": "Historical net amount unavailable",
    "Operações anteriores à preservação histórica podem não possuir detalhamento de taxas.": "Transactions predating historical preservation may not include fee details.",
    "Cartão de crédito à vista": "Credit card upfront",
    "Resumo do período": "Period summary",
    "Taxas/descontos": "Fees/discounts",
    "Observação opcional": "Optional note",
    "Total no período": "Total in period",
    "Atendimento - Paciente": "Appointment - Patient",
    "Nenhuma receita encontrada para este profissional no período selecionado.": "No revenue found for this professional in the selected period.",
    "Nenhuma receita encontrada com os filtros selecionados.": "No revenue found with the selected filters.",
    "Data da venda": "Sale date",
    "Descrição financeira": "Financial description",
    "Salvar plano": "Save plan",
    "Criar renovação": "Create renewal",
    "Cadastrar plano": "Create plan",
    "Profissionais": "Professionals",
    "Gestão de corpo clínico e especialidades": "Clinical staff and specialties management",
    "Novo Profissional": "New professional",
    "Editar Profissional": "Edit professional",
    "Profissional cadastrado com sucesso!": "Professional created successfully!",
    "Profissional atualizado com sucesso!": "Professional updated successfully!",
    "Erro ao carregar profissionais": "Error loading professionals",
    "Nenhum profissional encontrado": "No professionals found",
    "Catálogo de setores": "Sector catalog",
    "Use este cadastro para padronizar leads, profissionais e financeiro.": "Use this catalog to standardize leads, professionals, and finance.",
    "Setor removido com sucesso!": "Sector removed successfully!",
    "Setor adicionado com sucesso!": "Sector added successfully!",
    "Erro ao remover setor": "Error removing sector",
    "Erro ao adicionar setor": "Error adding sector",
    "Novo setor": "New sector",
    "Adicionar setor": "Add sector",
    "Buscar por nome, setor ou especialidade...": "Search by name, sector, or specialty...",
    "Especialidade": "Specialty",
    "Especialidades": "Specialties",
    "Registro": "Registration no.",
    "Cor na agenda": "Calendar color",
    "Duração da consulta": "Appointment duration",
    "A duração da consulta deve ser de pelo menos 15 minutos.": "The appointment duration must be at least 15 minutes.",
    "Limite de pacientes por horário": "Patient limit per time slot",
    "Limite por horário": "Limit per slot",
    "Minutos": "Minutes",
    "Ex.: Traumato, Ortopedia": "Ex.: Trauma, Orthopedics",
    "Horários / Agenda": "Working hours / Schedule",
    "Configure a capacidade semanal do profissional. Isso alimenta a ocupação percentual da agenda.": "Set the professional's weekly availability. This is used to calculate schedule occupancy.",
    "Segunda": "Monday",
    "Terça": "Tuesday",
    "Quarta": "Wednesday",
    "Quinta": "Thursday",
    "Sexta": "Friday",
    "Sábado": "Saturday",
    "Domingo": "Sunday",
    "Adicionar faixa": "Add time slot",
    "Sem horários configurados neste dia.": "No working hours configured for this day.",
    "Sem horários disponíveis": "No times available",
    "Agenda": "Calendar",
    "Gerencie os agendamentos da clínica": "Manage clinic appointments",
    "Novo Agendamento": "New appointment",
    "Hoje": "Today",
    "Ontem": "Yesterday",
    "Esta semana": "This week",
    "Últimos 7 dias": "Last 7 days",
    "Este mês": "This month",
    "Este ano": "This year",
    "Mês seguinte": "Next month",
    "Mês anterior": "Previous month",
    "Por período": "Custom period",
    "Todos profissionais": "All professionals",
    "Ver detalhes": "View details",
    "Carregando dados...": "Loading data...",
    "Erro ao carregar dados.": "Error loading data.",
    "Nenhum Lead encontrado": "No leads found",
    "Erro ao carregar agendamentos": "Error loading appointments",
    "Horário inválido": "Invalid time",
    "Não é possível criar agendamentos em datas anteriores à data atual.": "Appointments cannot be created for dates before today.",
    "Este profissional já atingiu o limite de pacientes para este horário. Escolha outro horário ou ajuste o limite no cadastro do profissional.": "This professional has already reached the patient limit for this time slot. Choose another time or adjust the limit in the professional record.",
    "Este profissional não possui faixa de horário cadastrada para o período selecionado. Ajuste a agenda do profissional ou escolha outro horário.": "This professional has no working hours configured for the selected period. Adjust the professional schedule or choose another time.",
    "Agendado com sucesso": "Scheduled successfully",
    "Agendamento atualizado": "Appointment updated",
    "Receita gerada com sucesso": "Revenue generated successfully",
    "Plano de tratamento": "Treatment plan",
    "Renovação do plano": "Plan renewal",
    "Selecionar plano": "Select plan",
    "Sem plano": "No plan",
    "sessões restantes": "sessions remaining",
    "validade até": "valid until",
    "Nenhum plano vendido ou ativo disponível para este paciente e profissional.": "No sold or active plan is available for this patient and professional.",
    "Selecione paciente e profissional para usar um plano.": "Select a patient and professional to use a plan.",
    "Início": "Start",
    "Término": "End",
    "Intervalos de": "Intervals of",
    "minutos": "minutes",
    "Atendimentos vinculados ao plano ficam com valor por sessão zerado. O faturamento entra na venda do plano.": "Appointments linked to a plan have a zero per-session amount. Billing is recorded through the plan sale.",
    "Agendar": "Schedule",
    "Detalhes do Agendamento": "Appointment details",
    "Criado em": "Created on",
    "Histórico de importação": "Import history",
    "A reversão deve seguir a ordem inversa da importação para preservar os vínculos.": "Reversal must follow the reverse import order to preserve links.",
    "Nenhuma importação registrada para esta clínica.": "No import has been recorded for this clinic.",
    "Revertida": "Rolled back",
    "Rastreáveis": "Traceable",
    "Revertido em": "Reverted on",
    "Reverter": "Revert",
    "Revertendo...": "Reverting...",
    "Reverter este lote removerá os registros importados por ele. Deseja continuar?": "Reverting this batch will remove the records it imported. Do you want to continue?",
    "CPFs inválidos legados": "Invalid legacy CPFs",
    "Reprocessados por ID externo": "Reprocessed by external ID",
    "Importação revertida com sucesso.": "Import reverted successfully.",
    "registros removidos": "records removed",
    "Erro ao carregar histórico de importação.": "Error loading import history.",
    "Erro ao reverter importação.": "Error reverting import.",
    "Importação bloqueada pela ordem de dependência. Consulte o histórico da clínica.": "Import blocked by dependency order. Check the clinic history.",
    "Remarcado": "Rescheduled",
    "Remarcar agendamento": "Reschedule appointment",
    "Selecione a nova data, horário e profissional para atualizar o agendamento.": "Select the new date, time, and professional to update the appointment.",
    "Nova data": "New date",
    "Informe a nova data, horário e profissional para remarcar.": "Enter the new date, time, and professional to reschedule.",
    "Não é possível remarcar para datas anteriores à data atual.": "Appointments cannot be rescheduled to dates before today.",
    "O plano vinculado pertence a outro profissional. Remova o plano ou mantenha o profissional atual.": "The linked plan belongs to another professional. Remove the plan or keep the current professional.",
    "Atual": "Current",
    "Motivo do cancelamento": "Cancellation reason",
    "Preencha quando o agendamento for cancelado": "Fill this in when the appointment is cancelled",
    "Disponível somente para status cancelado": "Available only for cancelled status",
    "Informe pelo menos 30 caracteres para registrar o cancelamento.": "Enter at least 30 characters to record the cancellation.",
    "Informe um motivo do cancelamento com pelo menos 30 caracteres.": "Enter a cancellation reason with at least 30 characters.",
    "Gerar receita": "Generate revenue",
    "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.": "Create a revenue entry linked to this appointment, patient, and professional.",
    "Agendamento cancelado": "Cancelled appointment",
    "Receita já gerada": "Revenue already generated",
    "Sessão coberta por plano": "Session covered by plan",
    "Não é permitido gerar receita para um agendamento com status cancelado.": "Revenue cannot be generated for a cancelled appointment.",
    "Este atendimento consome uma sessão do plano vinculado. O faturamento deve ser registrado na venda do plano, não por atendimento.": "This appointment uses one session from the linked plan. Billing should be recorded through the plan sale, not per appointment.",
    "Data do pagamento": "Payment date",
    "Descrição da receita": "Revenue description",
    "Gerando receita...": "Generating revenue...",
    "Receita indisponível": "Revenue not available",
    "Coberto por plano": "Covered by plan",
    "Receita gerada": "Revenue generated",
    "Fechar": "Close",
    "Avaliação": "Assessment",
    "Tratamento": "Treatment",
    "Retorno": "Follow-up",
    "Reavaliação": "Reassessment",
    "Confirmado": "Confirmed",
    "Em andamento": "In progress",
    "Não compareceu": "No-show",
    "Consulta": "Consultation",
    "Pacote": "Package",
    "Atendimento": "Care",
    "Eficiência assistencial, recorrência e satisfação do paciente em um único lugar.": "Care efficiency, repeat visits, and patient satisfaction in one place.",
    "Carregando indicadores assistenciais...": "Loading care indicators...",
    "Meta de retenção atualizada com sucesso!": "Retention goal updated successfully!",
    "NPS atualizado com sucesso!": "NPS updated successfully!",
    "NPS registrado com sucesso!": "NPS recorded successfully!",
    "Responder": "Answer",
    "Responder NPS": "Answer NPS",
    "Registre a resposta do NPS para o plano selecionado.": "Record the NPS response for the selected plan.",
    "Registros de NPS já respondidos pelo paciente.": "NPS records already answered by the patient.",
    "Link da pesquisa copiado.": "Survey link copied.",
    "Novo link gerado e copiado.": "New link generated and copied.",
    "Não foi possível copiar o link.": "Could not copy the link.",
    "Esta pesquisa já foi respondida. O link não pode ser copiado novamente.": "This survey has already been answered. The link cannot be copied again.",
    "Esta pesquisa expirou. Gere uma nova pesquisa para enviar um link válido.": "This survey has expired. Generate a new survey to send a valid link.",
    "Esta pesquisa expirou. Gere um novo link para continuar.": "This survey has expired. Generate a new link to continue.",
    "O link desta pesquisa já foi emitido de forma protegida. Use o envio automático ou gere uma nova pesquisa.": "This survey link has already been issued securely. Use automatic sending or generate a new survey.",
    "Não foi possível copiar o link porque a pesquisa não foi encontrada ou não pertence mais a esta clínica.": "The link could not be copied because the survey was not found or no longer belongs to this clinic.",
    "Não foi possível copiar o link por um erro inesperado. Tente novamente em instantes.": "The link could not be copied due to an unexpected error. Try again shortly.",
    "O link foi gerado, mas o navegador bloqueou a cópia automática. Permita o acesso à área de transferência e tente novamente.": "The link was generated, but the browser blocked automatic copying. Allow clipboard access and try again.",
    "Pacientes atendidos": "Patients seen",
    "Primeira consulta": "First appointment",
    "Avaliações no período selecionado": "Evaluations in the selected period",
    "Consultas avulsas": "Standalone appointments",
    "Atendimentos sem plano vinculado": "Appointments without a linked plan",
    "Pacientes em tratamento": "Patients in treatment",
    "Pacientes com plano no período": "Patients with a plan in the period",
    "Pacientes atendidos no período": "Patients seen in the period",
    "Resumo dos atendimentos concluídos entre": "Summary of completed appointments between",
    "Atendimentos": "Appointments",
    "Com retorno": "Returning",
    "Nenhum atendimento concluído no período selecionado.": "No completed appointments in the selected period.",
    "atendimento concluído": "completed appointment",
    "atendimentos concluídos": "completed appointments",
    "Paciente recorrente": "Returning patient",
    "Paciente novo no período": "New patient in the period",
    "Primeiro atendimento": "First appointment",
    "Último atendimento": "Last appointment",
    "Tipos de atendimento": "Appointment types",
    "com retorno no período": "with return visits in the period",
    "Taxa de retenção": "Retention rate",
    "NPS pós-tratamento": "Post-treatment NPS",
    "resposta": "response",
    "respostas": "responses",
    "Horas ocupadas": "Booked hours",
    "Horários ocupados": "Booked slots",
    "Horários": "Slots",
    "horários ativos": "active slots",
    "horários disponíveis": "available slots",
    "faltas no período": "no-shows in the period",
    "Novas x recorrentes": "New vs recurring",
    "Novas consultas x retornos": "New appointments vs follow-ups",
    "Novas consultas x recorrentes": "New appointments vs recurring",
    "Resumo do NPS": "NPS summary",
    "Absenteísmo": "Absenteeism",
    "faltas em": "no-shows in",
    "horários": "slots",
    "Mix assistencial": "Care mix",
    "Promotores": "Promoters",
    "Passivos": "Passives",
    "Detratores": "Detractors",
    "Sessões por paciente": "Sessions per patient",
    "Leitura operacional": "Operational reading",
    "Cruze eficiência, faltas e satisfação para agir mais rápido na rotina da clínica.": "Cross-check efficiency, no-shows, and satisfaction to act faster in the clinic's daily routine.",
    "Retornos por paciente": "Follow-ups per patient",
    "Média de sessões concluídas por paciente no período.": "Average completed sessions per patient in the period.",
    "Absenteísmo x conclusão": "Absenteeism vs completion",
    "Faltas comparadas aos atendimentos concluídos no mesmo recorte.": "No-shows compared with completed appointments in the same range.",
    "Pacientes fidelizados": "Retained patients",
    "Pacientes com 2 ou mais atendimentos concluídos no período.": "Patients with 2 or more completed appointments in the period.",
    "Agenda do dia": "Daily schedule",
    "Agenda da semana": "Weekly schedule",
    "Agenda do mês": "Monthly schedule",
    "horários ocupados": "booked slots",
    "Horas": "Hours",
    "Concluídos": "Completed",
    "de ocupação sobre": "occupancy over",
    "disponíveis": "available",
    "Sem carga horária configurada nos profissionais para calcular a taxa percentual.": "No working hours are configured for professionals, so the occupancy rate cannot be calculated.",
    "Meta de retenção": "Retention goal",
    "Meta": "Goal",
    "Pacientes com 2 ou mais atendimentos concluídos no período selecionado.": "Patients with 2 or more completed appointments in the selected period.",
    "Gap": "Gap",
    "de": "of",
    "pacientes retornaram no período.": "patients returned in the period.",
    "Defina uma meta percentual para acompanhar a fidelização da base.": "Set a percentage goal to track patient retention.",
    "Meta mensal (%)": "Monthly goal (%)",
    "Segmentação por setor": "Segmentation by sector",
    "Compare ocupação, retenção e satisfação para encontrar gargalos operacionais.": "Compare occupancy, retention, and satisfaction to find operational bottlenecks.",
    "Ocupação": "Occupancy",
    "Mix": "Mix",
    "Retenção": "Retention",
    "Respostas": "Responses",
    "Nenhum dado assistencial encontrado no período selecionado.": "No care data found in the selected period.",
    "concluídos": "completed",
    "faltas": "no-shows",
    "novas": "new",
    "recorrentes": "recurring",
    "Detalhes do setor": "Sector details",
    "Período considerado": "Period considered",
    "Agendamentos realizados": "Completed appointments",
    "Agendamentos cancelados": "Cancelled appointments",
    "Taxa de comparecimento": "Attendance rate",
    "Novos atendimentos": "New appointments",
    "Atendimentos recorrentes": "Recurring appointments",
    "Profissionais vinculados": "Linked professionals",
    "Nenhum profissional vinculado a este setor.": "No professional linked to this sector.",
    "Sem registro informado": "No registration provided",
    "Leitura do setor": "Sector reading",
    "Respostas NPS": "NPS responses",
    "Média de sessões por paciente": "Average sessions per patient",
    "Filtro de detalhes": "Details filter",
    "Selecione uma opção para listar paciente e profissional vinculados.": "Select an option to list the linked patient and professional.",
    "Total encontrado": "Total found",
    "Buscar nos detalhes": "Search details",
    "Ordenar por": "Sort by",
    "Mais recentes": "Most recent",
    "Nome do paciente": "Patient name",
    "Mais atendimentos": "Most appointments",
    "Carregando detalhes...": "Loading details...",
    "Erro ao carregar detalhes.": "Error loading details.",
    "Paciente não informado": "Patient not provided",
    "Profissional não informado": "Professional not provided",
    "Nenhum registro encontrado para este filtro.": "No record found for this filter.",
    "Fila de NPS automático": "Automatic NPS queue",
    "Todo plano concluído gera um link público para o paciente responder após o tratamento.": "Every completed plan generates a public link for the patient to respond after treatment.",
    "Pendentes": "Pending",
    "Respondidas": "Responded",
    "Ação": "Action",
    "Nenhuma pesquisa automática no período selecionado.": "No automatic surveys in the selected period.",
    "Respondida": "Responded",
    "Pendente": "Pending",
    "Expirada": "Expired",
    "Em": "On",
    "Criada em": "Created on",
    "Expirou em {date}": "Expired on {date}",
    "Copiar link": "Copy link",
    "Gerar novo link": "Generate new link",
    "O link público segue o formato": "The public link follows the format",
    "e pode ser enviado pela equipe no canal preferido.": "and can be sent by the team through the preferred channel.",
    "Feedbacks recentes": "Recent feedback",
    "Leitura rápida da satisfação capturada após os tratamentos.": "Quick view of satisfaction captured after treatment.",
    "Nota": "Score",
    "Comentário": "Comment",
    "Nenhum NPS registrado ainda.": "No NPS responses yet.",
    "Sem respostas ainda": "No responses yet",
    "Período inicial": "Start period",
    "Período final": "End period",
    "Mês atual": "Current month",
    "Excelência percebida": "Perceived excellence",
    "Boa satisfação": "Good satisfaction",
    "Atenção aos detratores": "Attention to detractors",
    "Experiência crítica": "Critical experience",
    "Metas": "Goals",
    "Usuários": "Users",
    "Acessos da clínica": "Clinic access",
    "Administrador": "Administrator",
    "Administradores": "Administrators",
    "Administradores ativos": "Active administrators",
    "Alterar função do usuário": "Change user role",
    "Ativar usuário": "Activate user",
    "Buscar por nome ou email": "Search by name or email",
    "Carregando usuários...": "Loading users...",
    "Confirmar": "Confirm",
    "Confirmar alteração": "Confirm change",
    "Criar usuário": "Create user",
    "Define se esse usuário pode acessar a clínica.": "Defines whether this user can access the clinic.",
    "Deixe em branco para manter": "Leave blank to keep the current password",
    "Desativar usuário": "Deactivate user",
    "Editar usuário": "Edit user",
    "Erro ao atualizar usuário": "Error updating user",
    "Erro ao carregar usuários": "Error loading users",
    "Erro ao criar usuário": "Error creating user",
    "Filtros aplicados a esta lista.": "Filters applied to this list.",
    "Função": "Role",
    "Nenhum usuário adicional encontrado para esta clínica.": "No additional users found for this clinic.",
    "Nenhum usuário encontrado com os filtros informados.": "No users found with the selected filters.",
    "Nenhum usuário encontrado.": "No users found.",
    "Nova senha": "New password",
    "Novo usuário": "New user",
    "Novo usuário da clínica": "New clinic user",
    "Processando...": "Processing...",
    "Reativar usuário": "Reactivate user",
    "Senha inicial": "Initial password",
    "Seu perfil atual não possui permissão para cadastrar ou editar usuários.": "Your current profile does not have permission to create or edit users.",
    "Somente administradores da clínica podem gerenciar acessos.": "Only clinic administrators can manage access.",
    "Todas as funções": "All roles",
    "Usuário": "User",
    "Usuário ativo": "Active user",
    "Usuário atualizado com sucesso.": "User updated successfully.",
    "Usuário criado com sucesso.": "User created successfully.",
    "Usuário desativado com sucesso.": "User deactivated successfully.",
    "Usuário reativado com sucesso.": "User reactivated successfully.",
    "usuario@clínica.com": "user@clinic.com",
    "Usuários ativos": "Active users",
    "Usuários da Clínica": "Clinic users",
    "Você": "You",
    "Clínicas": "Clinics",
    "Auditoria": "Audit",
    "Selecione a clínica": "Select the clinic",
    "Sair": "Sign out",
    "Workspace": "Workspace",
    "Control Tower": "Control Tower",
    "Tema claro": "Light theme",
    "Tema escuro": "Dark theme",
    "Ativar tema claro": "Enable light theme",
    "Ativar tema escuro": "Enable dark theme",
    "Entrar": "Sign in",
    "Criar clínica": "Create clinic",
    "Criando...": "Creating...",
    "Entrando...": "Signing in...",
    "Fazer login": "Sign in",
    "Voltar para a página inicial": "Back to homepage",
    "Criar conta": "Create account",
    "Bem-vindo de volta": "Welcome back",
    "Portal de acesso do super admin": "Super admin access portal",
    "Use suas credenciais para entrar na camada central da plataforma.": "Use your credentials to access the platform's central layer.",
    "Entre com suas credenciais para acessar a plataforma.": "Enter your credentials to access the platform.",
    "E-mail": "Email",
    "Mostrar senha": "Show password",
    "Ocultar senha": "Hide password",
    "Senha": "Password",
    "Não tem uma conta?": "Don't have an account?",
    "Já tem uma conta?": "Already have an account?",
    "Seu nome": "Your name",
    "Sua cidade": "Your city",
    "Mínimo 6 caracteres": "Minimum 6 characters",
    "Mínimo 8 caracteres": "Minimum 8 characters",
    "Repita sua senha": "Repeat your password",
    "Nome da clínica": "Clinic name",
    "Nome da sua clínica": "Your clinic name",
    "Telefone da clínica": "Clinic phone",
    "E-mail da clínica": "Clinic email",
    "Endereço": "Address",
    "Rua, avenida, etc.": "Street, avenue, etc.",
    "Número": "Number",
    "Cidade": "City",
    "Estado": "State",
    "Nome completo do responsável": "Primary admin full name",
    "E-mail do responsável": "Primary admin email",
    "Confirmar senha": "Confirm password",
    "Buscar endereço pelo CEP...": "Looking up address by ZIP code...",
    "Cadastro realizado!": "Registration completed!",
    "Logo você receberá um e-mail de confirmação com a liberação do seu acesso!": "You'll soon receive a confirmation email with your access approval.",
    "Desde já...": "Almost there...",
    "Seja bem-vindo!!!": "Welcome!",
    "Carregando métricas...": "Loading metrics...",
    "Selecione uma clínica para visualizar o dashboard.": "Select a clinic to view the dashboard.",
    "Visão geral": "Overview",
    "Visão geral de": "Overview of",
    "Indicadores exibidos para": "Indicators shown for",
    "Visão consolidada da clínica": "Consolidated clinic view",
    "Visão individual por setor": "Individual sector view",
    "Indicadores gerenciais": "Management indicators",
    "Indicadores com fonte técnica segura para o período e setor selecionados.": "Indicators with a reliable technical source for the selected period and sector.",
    "Primeiras consultas": "First appointments",
    "Agenda classificada como primeiro atendimento": "Schedule classified as first appointment",
    "Agendamentos sem plano de tratamento vinculado": "Appointments without a linked treatment plan",
    "Vendas confirmadas por data da venda": "Confirmed sales by sale date",
    "Despesas por competência / (agendamentos vinculados + lançamentos manuais)": "Accrual-basis expenses / (linked appointments + manual revenue entries)",
    "Resultado financeiro / (agendamentos vinculados + lançamentos manuais)": "Financial result / (linked appointments + manual revenue entries)",
    "Resumo por setor": "Sector summary",
    "Visão consolidada por setor com dados operacionais e financeiros permitidos para o seu perfil.": "Consolidated sector view with operational and financial data allowed for your profile.",
    "Visão individual do setor selecionado com dados permitidos para o seu perfil.": "Individual view of the selected sector with data allowed for your profile.",
    "Entradas financeiras": "Financial inflows",
    "Despesas reconhecidas por competência": "Accrual-basis expenses",
    "Despesas por competência do período": "Accrual expenses for the period",
    "Resultado financeiro": "Financial result",
    "Entradas realizadas menos despesas por competência, sem DRE contábil.": "Realized inflows minus accrual expenses, without accounting income statement treatment.",
    "Entradas financeiras por lançamento realizado": "Financial inflows per realized entry",
    "Sem dados setoriais confiáveis para o período selecionado.": "No reliable sector data for the selected period.",
    "Ano anterior": "Previous year",
    "Próximo ano": "Next year",
    "Mês Atual": "Current month",
    "no período": "in the period",
    "Receita Total": "Total revenue",
    "Receita": "Revenue",
    "Receita bruta": "Gross revenue",
    "Receita líquida": "Net revenue",
    "Despesas": "Expenses",
    "Lucro": "Profit",
    "Prejuízo": "Loss",
    "Equilíbrio": "Break-even",
    "Total de Entradas": "Total inflows",
    "Total de saídas": "Total outflows",
    "Resultado do Período": "Period result",
    "Soma de todas as receitas registradas no período selecionado.": "Sum of all revenue recorded in the selected period.",
    "Soma de todas as despesas registradas no período selecionado.": "Sum of all expenses recorded in the selected period.",
    "Diferença entre o total de entradas e o total de saídas no período selecionado.": "Difference between total inflows and total outflows in the selected period.",
    "Ticket Médio": "Average ticket",
    "Últimos 30 dias": "Last 30 days",
    "Leads convertidos": "Converted leads",
    "Atrasados": "Overdue",
    "Para hoje": "Due today",
    "Próximos": "Upcoming",
    "Configuração de follow-up": "Follow-up settings",
    "Defina em quantos dias a clínica costuma retomar um lead pendente.": "Set after how many days the clinic should follow up with a pending lead.",
    "Intervalo padrão (dias)": "Default interval (days)",
    "Salvar intervalo": "Save interval",
    "Nome": "Name",
    "Contato": "Contact",
    "Telefone": "Phone",
    "Origem": "Source",
    "Setor": "Sector",
    "Campanha": "Campaign",
    "Nova Campanha": "New campaign",
    "Nome da campanha": "Campaign name",
    "Fim": "End",
    "Canal": "Channel",
    "Publico alvo": "Target audience",
    "Público alvo": "Target audience",
    "Instagram, Google...": "Instagram, Google...",
    "Ex: Dor lombar": "Ex: Low back pain",
    "Criar campanha": "Create campaign",
    "Criar Campanha": "Create campaign",
    "Campanha criada com sucesso!": "Campaign created successfully!",
    "Mes atual": "Current month",
    "Filtrar por origem": "Filter by source",
    "Filtrar por campanha": "Filter by campaign",
    "Todas as origens": "All sources",
    "Todas as campanhas": "All campaigns",
    "Leads no período": "Leads in the period",
    "marcaram consulta": "booked an appointment",
    "convertidos": "converted",
    "Conversão comercial": "Sales conversion",
    "Investimento em campanhas": "Campaign spend",
    "Somatório das campanhas filtradas": "Total for filtered campaigns",
    "Somatorio das campanhas filtradas": "Total for filtered campaigns",
    "Detalhes da conversão comercial": "Sales conversion details",
    "Detalhes do investimento em campanhas": "Campaign spend details",
    "Período analisado": "Analyzed period",
    "Os dados abaixo respeitam os filtros atuais da página Marketing.": "The data below follows the current filters on the Marketing page.",
    "Leads considerados neste cálculo": "Leads included in this calculation",
    "Marcaram consulta": "Booked an appointment",
    "Leads que avançaram para agendamento": "Leads that moved to booking",
    "Leads convertidos em pacientes": "Leads converted into patients",
    "Taxa de conversão": "Conversion rate",
    "Percentual de leads convertidos": "Percentage of converted leads",
    "Leads considerados na conversão": "Leads included in the conversion",
    "Investimento total": "Total spend",
    "Campanhas filtradas": "Filtered campaigns",
    "Campanhas consideradas no período": "Campaigns included in the period",
    "Investimento médio": "Average spend",
    "Média por campanha filtrada": "Average per filtered campaign",
    "Custo por lead": "Cost per lead",
    "Investimento dividido pelos leads do período": "Spend divided by leads in the period",
    "Detalhamento de campanhas": "Campaign breakdown",
    "Período": "Period",
    "Nenhuma campanha encontrada com os filtros atuais.": "No campaign found with the current filters.",
    "Nenhum lead convertido no período selecionado.": "No lead converted in the selected period.",
    "Meta de agendamento": "Appointment goal",
    "Progresso:": "Progress:",
    "Custo por novo paciente no período": "Cost per new patient in the period",
    "Receita média por paciente no período": "Average revenue per patient in the period",
    "novos pacientes": "new patients",
    "Receita projetada": "Projected revenue",
    "Alertas operacionais": "Operational alerts",
    "Follow-ups vencidos / NPS pendentes": "Overdue follow-ups / pending NPS",
    "Fila unificada de follow-up vencido e NPS pendente.": "Unified list of overdue follow-ups and pending NPS surveys.",
    "Nenhum alerta operacional no momento.": "No operational alerts at the moment.",
    "Follow-up vencido": "Overdue follow-up",
    "NPS pendente": "Pending NPS",
    "Lead precisa de novo contato comercial.": "Lead needs another sales touchpoint.",
    "Plano": "Plan",
    "aguardando envio ou resposta.": "awaiting delivery or response.",
    "Programa Coluna Ativa": "Active Spine Program",
    "Recondicionamento Pos-corrida": "Post-run Reconditioning",
    "Recondicionamento Pós-corrida": "Post-run Reconditioning",
    "Acupuntura Sono e Stress": "Sleep and Stress Acupuncture",
    "Ombro Sem Travar": "Unlocked Shoulder",
    "Respirar Melhor com Pilates": "Breathe Better with Pilates",
    "Ritmo e Equilibrio Senior": "Senior Rhythm and Balance",
    "Ritmo e Equilíbrio Sênior": "Senior Rhythm and Balance",
    "Meta mensal de agendamento": "Monthly appointment goal",
    "Defina a meta da clínica e acompanhe o progresso do mês atual.": "Set the clinic goal and track progress for the current month.",
    "Quantidade de agendamentos": "Number of appointments",
    "Salvar meta": "Save goal",
    "agendamentos válidos no período atual.": "valid appointments in the current period.",
    "Meta mensal de agendamento atualizada!": "Monthly appointment goal updated!",
    "Erro ao atualizar meta": "Error updating goal",
    "Relatório por origem": "Report by source",
    "Relatório por campanha": "Report by campaign",
    "Relatório por setor": "Report by sector",
    "Marcou": "Booked",
    "Convertidos": "Converted",
    "Taxa": "Rate",
    "Investimento": "Investment",
    "Nenhum dado no período selecionado.": "No data in the selected period.",
    "Nenhuma campanha cadastrada.": "No campaign added.",
    "Sem canal": "No channel",
    "Campanhas cadastradas": "Campaigns added",
    "Público": "Audience",
    "Orçamento": "Budget",
    "Nenhuma campanha cadastrada ainda.": "No campaigns added yet.",
    "Ativa": "Active",
    "Inativa": "Inactive",
    "Editar campanha": "Edit campaign",
    "Ativar campanha": "Activate campaign",
    "Desativar campanha": "Deactivate campaign",
    "Remover campanha": "Remove campaign",
    "Campanha atualizada com sucesso!": "Campaign updated successfully!",
    "Status da campanha atualizado!": "Campaign status updated!",
    "Campanha removida da gestão ativa.": "Campaign removed from active management.",
    "Adultos de 30 a 55 anos com dor lombar recorrente e rotina de escritorio.": "Adults aged 30 to 55 with recurring low back pain and an office routine.",
    "Profissionais com pouco tempo e interesse em aulas no horário de almoço.": "Professionals with limited time and interest in lunchtime classes.",
    "Empresas de tecnologia e RH buscando palestras e triagens posturais.": "Technology companies and HR teams looking for talks and posture screenings.",
    "Ex-pacientes e familiares que ja haviam demonstrado interesse em retornar.": "Former patients and family members who had already shown interest in returning.",
    "leads": "leads",
    "marcaram": "booked",
    "Agendamentos": "Appointments",
    "Nenhum dado setorial no período selecionado.": "No sector data available in the selected period.",
    "Leads filtrados": "Filtered leads",
    "Lead": "Lead",
    "clínica": "clinic",
    "Resultado": "Result",
    "Data": "Date",
    "Nenhum lead encontrado com os filtros atuais.": "No leads found with the current filters.",
    "Follow-up": "Follow-up",
    "Status": "Status",
    "Editar Lead": "Edit lead",
    "Selecione o setor": "Select the sector",
    "Sem campanha": "No campaign",
    "Resultado comercial": "Sales outcome",
    "Marcou consulta": "Booked appointment",
    "Não marcou": "Did not book",
    "Próximo follow-up": "Next follow-up",
    "Hora": "Time",
    "Follow-up no horário": "Follow-up due now",
    "Existem leads com follow-up para agora.": "There are leads with follow-up due now.",
    "Motivo de não agendamento": "Reason for not scheduling",
    "Sem setor": "No sector",
    "Setor da venda": "Sale sector",
    "Setor do atendimento": "Care sector",
    "O setor informado diverge do setor histórico da origem": "The selected sector differs from the source's historical sector",
    "O setor histórico de um recebível originado não pode ser reclassificado": "The historical sector of a source-linked receivable cannot be reclassified",
    "Nenhum lead encontrado": "No leads found",
    "Nenhum lead neste grupo.": "No leads in this group.",
    "Abrir WhatsApp": "Open WhatsApp",
    "Abrir no WhatsApp": "Open in WhatsApp",
    "WhatsApp": "WhatsApp",
    "Telefone não informado": "Phone not provided",
    "Telefone inválido": "Invalid phone number",
    "Conversar pelo WhatsApp": "Chat on WhatsApp",
    "Informe um telefone válido para abrir o WhatsApp.": "Enter a valid phone number to open WhatsApp.",
    "Oi {name}, tudo bem com você? Aqui é da {clinicName}.": "Hi {name}, how are you? This is {clinicName}.",
    "Tudo bem?": "How are you?",
    "Tudo bem com você?": "How are you?",
    "Tudo bem? Estou entrando em contato sobre seu agendamento.": "How are you? I'm reaching out about your appointment.",
    "Converter em paciente": "Convert to patient",
    "Converter": "Convert",
    "Convertendo...": "Converting...",
    "Vincular a este paciente": "Link to this patient",
    "Criar novo paciente": "Create new patient",
    "Lead vinculado ao paciente existente.": "Lead linked to existing patient.",
    "O cadastro do paciente será criado a partir deste lead e a conversão ficará registrada no funil.": "The patient record will be created from this lead and the conversion will be recorded in the funnel.",
    "Agendar consulta": "Schedule appointment",
    "Agendar consulta do lead": "Schedule lead appointment",
    "Agendando...": "Scheduling...",
    "O agendamento será criado usando o paciente já convertido a partir deste lead.": "The appointment will use the patient already created from this lead.",
    "Para agendar uma consulta a partir do lead, primeiro converta esse lead em paciente.": "To schedule an appointment from this lead, convert it into a patient first.",
    "Limpar data": "Clear date",
    "Informe uma data de nascimento válida.": "Enter a valid birth date.",
    "A data de nascimento não pode ser futura.": "The birth date cannot be in the future.",
    "Informe uma data de nascimento a partir de 1900.": "Enter a birth date from 1900 onward.",
    "Bairro": "Neighborhood",
    "Prontuário do paciente": "Patient chart",
    "Módulo clínico": "Clinical module",
    "Ficha de Avaliação": "Assessment sheet",
    "Avaliações clínicas por paciente": "Clinical assessments by patient",
    "Abrir prontuário": "Open chart",
    "Paciente selecionado": "Selected patient",
    "Último registro": "Latest record",
    "Não foi possível carregar pacientes.": "Could not load patients.",
    "Não foi possível carregar a ficha de avaliação.": "Could not load the assessment sheet.",
    "Nenhum paciente encontrado para a busca.": "No patient found for the search.",
    "Selecione um paciente para abrir a ficha de avaliação.": "Select a patient to open the assessment sheet.",
    "Dados de identificação não informados": "Identification details not provided",
    "Evoluções": "Clinical notes",
    "Evolução": "Clinical note",
    "Alta": "Discharge",
    "Avaliações": "Assessments",
    "Avaliação inicial": "Initial assessment",
    "Avaliação padrão": "Default assessment",
    "Nova Avaliação": "New assessment",
    "Registrar avaliação clínica": "Record clinical assessment",
    "Tipo de avaliação": "Assessment type",
    "Título da avaliação": "Assessment title",
    "Objetivo clínico": "Clinical goal",
    "Achados principais": "Key findings",
    "Hipótese / diagnóstico funcional": "Hypothesis / functional diagnosis",
    "Plano terapêutico": "Therapeutic plan",
    "Salvar avaliação": "Save assessment",
    "Avaliação registrada com sucesso.": "Assessment recorded successfully.",
    "Não foi possível salvar a avaliação clínica.": "Could not save the clinical assessment.",
    "Informações do paciente": "Patient information",
    "Fluxo clínico": "Clinical flow",
    "Paciente → Avaliação → Evoluções": "Patient → Assessment → Clinical notes",
    "Cada avaliação organiza as evoluções clínicas vinculadas ao atendimento.": "Each assessment organizes the clinical notes linked to the appointment.",
    "Pesquisa clínica": "Clinical search",
    "Busque por sintomas, diagnóstico, profissional, data, conduta ou plano terapêutico.": "Search by symptoms, diagnosis, professional, date, intervention, or treatment plan.",
    "Buscar no prontuário clínico": "Search clinical record",
    "Selecione uma avaliação antes de criar uma evolução.": "Select an assessment before creating a clinical note.",
    "Buscar sintomas, diagnósticos, medicamentos ou palavras-chave": "Search symptoms, diagnoses, medications, or keywords",
    "Avaliações registradas": "Recorded assessments",
    "Cada avaliação agrupa as evoluções clínicas vinculadas.": "Each assessment groups its linked clinical notes.",
    "Carregando avaliações...": "Loading assessments...",
    "Nenhuma avaliação encontrada para a busca.": "No assessment found for the search.",
    "Nenhuma avaliação clínica registrada.": "No clinical assessment recorded.",
    "Evoluções vinculadas": "Linked clinical notes",
    "Criar evolução": "Create clinical note",
    "Editar evolução": "Edit clinical note",
    "Editar evolução clínica": "Edit clinical note",
    "Evolução atualizada com sucesso.": "Clinical note updated successfully.",
    "Nenhuma evolução vinculada a esta avaliação.": "No clinical note linked to this assessment.",
    "Esta avaliação reúne evoluções antigas sem vínculo direto, preservando o histórico clínico integral.": "This assessment groups older clinical notes without a direct link, preserving the full clinical history.",
    "Histórico preservado": "Preserved history",
    "Evoluções antigas sem avaliação vinculada.": "Older clinical notes without a linked assessment.",
    "Preservar histórico clínico registrado antes da estrutura Avaliação → Evoluções.": "Preserve clinical history recorded before the Assessment → Clinical notes structure.",
    "registro(s)": "record(s)",
    "Documentos": "Documents",
    "Plano de Tratamento": "Treatment plan",
    "Erro ao carregar histórico do paciente": "Error loading patient history",
    "Carregando histórico do paciente...": "Loading patient history...",
    "Resumo clínico": "Clinical summary",
    "Situação clínica do paciente": "Patient clinical status",
    "Informações essenciais para orientar o atendimento atual.": "Essential information to guide the current appointment.",
    "Editar cadastro": "Edit registration",
    "Sessões restantes": "Remaining sessions",
    "Próxima consulta": "Next appointment",
    "Última avaliação": "Latest assessment",
    "Pendência financeira": "Financial pending balance",
    "Timeline Clínica": "Clinical timeline",
    "Histórico cronológico dos principais eventos do paciente.": "Chronological history of the patient's main events.",
    "Nenhum evento clínico encontrado.": "No clinical event found.",
    "Abrir registro": "Open record",
    "Cadastro realizado": "Registration completed",
    "Consulta agendada": "Appointment scheduled",
    "Avaliação criada": "Assessment created",
    "Evolução registrada": "Clinical note recorded",
    "Plano vendido": "Plan sold",
    "Documento anexado": "Document attached",
    "Pagamento registrado": "Payment recorded",
    "Pesquisar por nome, categoria ou data": "Search by name, category, or date",
    "Todas as categorias": "All categories",
    "Relacionamento": "Relationship",
    "realizadas": "completed",
    "Sessões realizadas": "Completed sessions",
    "Objetivos alcançados": "Goals achieved",
    "Pendências": "Open items",
    "Finalizada": "Finished",
    "Ocultar detalhes": "Hide details",
    "URL da foto": "Photo URL",
    "Foto do paciente": "Patient photo",
    "Gerenciar foto do paciente": "Manage patient photo",
    "Inserir foto": "Add photo",
    "Alterar foto": "Change photo",
    "Selecionar imagem": "Select image",
    "Remover foto": "Remove photo",
    "Salvar foto": "Save photo",
    "Processando imagem...": "Processing image...",
    "Enviando foto...": "Uploading photo...",
    "Removendo foto...": "Removing photo...",
    "Pré-visualização da foto do paciente": "Patient photo preview",
    "Use uma imagem nítida e centralizada. A pré-visualização mostra o recorte final.": "Use a clear, centered image. The preview shows the final crop.",
    "Formatos aceitos: JPG, PNG e WEBP. Tamanho máximo:": "Accepted formats: JPG, PNG, and WEBP. Maximum size:",
    "Selecione uma imagem JPG, PNG ou WEBP.": "Select a JPG, PNG, or WEBP image.",
    "A imagem selecionada excede o tamanho máximo permitido.": "The selected image exceeds the maximum allowed size.",
    "Não foi possível processar a imagem selecionada.": "The selected image could not be processed.",
    "Foto do paciente atualizada com sucesso.": "Patient photo updated successfully.",
    "Foto do paciente removida com sucesso.": "Patient photo removed successfully.",
    "Alergias": "Allergies",
    "Medicamentos em uso": "Current medications",
    "Condições clínicas": "Clinical conditions",
    "Condições clínicas relevantes": "Relevant clinical conditions",
    "Alertas clínicos": "Clinical alerts",
    "Alertas clínicos importantes": "Important clinical alerts",
    "Plano atual": "Current plan",
    "Sem plano ativo": "No active plan",
    "Convênio não informado": "Insurance not provided",
    "Total de consultas": "Total appointments",
    "Última consulta": "Last appointment",
    "Acompanhamento": "Follow-up time",
    "anos": "years",
    "Plano ativo": "Active plan",
    "Nova Evolução": "New clinical note",
    "Registrar evolução clínica": "Record clinical note",
    "Template inteligente": "Smart template",
    "Selecionar modelo": "Select template",
    "Profissional responsável": "Responsible professional",
    "Não vincular": "Do not link",
    "Selecionar profissional": "Select professional",
    "Resumo rápido": "Quick summary",
    "Resumo para aparecer na timeline": "Summary shown in the timeline",
    "Escala de Dor": "Pain scale",
    "Testes Clínicos": "Clinical tests",
    "Conduta": "Intervention",
    "Registre o relato clínico da evolução do paciente.": "Record the patient's clinical progress note.",
    "Ex.: 0/10, 5/10 ou EVA 7": "E.g.: 0/10, 5/10, or VAS 7",
    "Registre testes clínicos realizados e seus resultados.": "Record clinical tests performed and their results.",
    "Registre a conduta adotada e orientações realizadas.": "Record the intervention applied and guidance provided.",
    "S - Subjetivo": "S - Subjective",
    "O - Objetivo": "O - Objective",
    "A - Avaliação": "A - Assessment",
    "P - Plano": "P - Plan",
    "Tags clínicas": "Clinical tags",
    "Separe por vírgula": "Separate with commas",
    "Anexos": "Attachments",
    "Imagem": "Image",
    "Exame": "Exam",
    "Documento": "Document",
    "Outros": "Other",
    "Remover": "Remove",
    "Salvar evolução": "Save clinical note",
    "Evolução registrada com sucesso.": "Clinical note recorded successfully.",
    "Não foi possível salvar a evolução clínica.": "Could not save the clinical note.",
    "Linha do tempo clínica": "Clinical timeline",
    "Carregando evoluções...": "Loading clinical notes...",
    "Nenhuma evolução encontrada para a busca.": "No clinical note found for this search.",
    "Nenhuma evolução clínica registrada.": "No clinical note recorded.",
    "Última alteração": "Last update",
    "Anexar documento": "Attach document",
    "Envie PDFs, exames, imagens ou fotos vinculadas ao prontuário do paciente.": "Upload PDFs, exams, images, or photos linked to the patient chart.",
    "Anexar": "Attach",
    "Selecionar arquivo": "Select file",
    "Selecionar arquivos": "Select files",
    "Nenhum arquivo selecionado": "No file selected",
    "Nenhum arquivo escolhido": "No file chosen",
    "Arquivo selecionado": "File selected",
    "{count} arquivos selecionados": "{count} files selected",
    "Remover arquivo": "Remove file",
    "Trocar arquivo": "Change file",
    "Formatos aceitos: {formats}": "Accepted formats: {formats}",
    "Limite de tamanho: {size}": "Size limit: {size}",
    "Enviando...": "Sending...",
    "Documento anexado com sucesso.": "Document attached successfully.",
    "Selecione um arquivo para anexar.": "Select a file to attach.",
    "Arquivo": "File",
    "Foto": "Photo",
    "O arquivo deve ter no máximo 15MB.": "The file must be at most 15 MB.",
    "O arquivo selecionado possui mais de {max} MB.\nSelecione um arquivo menor para continuar.": "The selected file is larger than {max} MB.\nSelect a smaller file to continue.",
    "O tamanho máximo permitido para upload é {max} MB.": "The maximum upload size is {max} MB.",
    "Limite de armazenamento atingido. Remova arquivos ou solicite ampliação da capacidade.": "Storage limit reached. Remove files or request a capacity increase.",
    "Tipo de arquivo incompatível com a categoria selecionada.": "File type is incompatible with the selected category.",
    "Não foi possível anexar o documento.": "Could not attach the document.",
    "Não foi possível abrir o documento.": "Could not open the document.",
    "Documentos do prontuário": "Chart documents",
    "Arquivos anexados diretamente ao prontuário do paciente.": "Files attached directly to the patient chart.",
    "Carregando documentos...": "Loading documents...",
    "Nenhum documento anexado ao prontuário.": "No document attached to the chart.",
    "Enviado em": "Sent on",
    "Visualizar": "View",
    "Baixar": "Download",
    "Documentos vinculados": "Linked documents",
    "selecionado(s)": "selected",
    "{count} documento vinculado": "{count} linked document",
    "{count} documentos vinculados": "{count} linked documents",
    "Nenhum documento vinculado a este registro.": "No document linked to this record.",
    "Documentos disponíveis para vincular": "Documents available to link",
    "Remover vínculo": "Remove link",
    "Vincular documento": "Link document",
    "Tipos aceitos: PDF, JPG, PNG e WEBP. Limite:": "Accepted types: PDF, JPG, PNG, and WEBP. Limit:",
    "Nenhum documento do paciente disponível para vincular.": "No patient document available to link.",
    "Documento removido com sucesso.": "Document removed successfully.",
    "Vinculado a": "Linked to",
    "registro clínico": "clinical record",
    "registros clínicos": "clinical records",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite: 15MB.": "Accepted types: PDF, JPG, PNG, WEBP, and GIF. Limit: 15 MB.",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite:": "Accepted types: PDF, JPG, PNG, WEBP, and GIF. Limit:",
    "Armazenamento": "Storage",
    "{used} de {total} utilizados": "{used} of {total} used",
    "Arquivos": "Files",
    "Utilizado": "Used",
    "Disponível": "Available",
    "Limite atingido": "Limit reached",
    "Próximo do limite": "Near limit",
    "Atenção": "Warning",
    "Normal": "Normal",
    "Sua clínica está próxima de atingir o limite de armazenamento.": "Your clinic is close to reaching its storage limit.",
    "Sua clínica está utilizando mais de 80% da capacidade contratada.": "Your clinic is using more than 80% of its contracted capacity.",
    "Evolução de": "Clinical note from",
    "Anexos vinculados às evoluções clínicas do paciente.": "Attachments linked to the patient's clinical notes.",
    "Receita do paciente": "Patient revenue",
    "Carregando financeiro do paciente...": "Loading patient finance...",
    "Nenhum lançamento financeiro encontrado.": "No financial entry found.",
    "Último pagamento": "Last payment",
    "Paciente desde": "Patient since",
    "Preferências e campanhas": "Preferences and campaigns",
    "Sem dados adicionais de marketing vinculados no cadastro atual.": "No additional marketing data linked to the current record.",
    "Dados comerciais e origem do relacionamento do paciente.": "Sales data and source of the patient relationship.",
    "Registrar queixa principal, objetivos do paciente e contexto clínico inicial.": "Record the main complaint, patient goals, and initial clinical context.",
    "Descrever achados relevantes da avaliação física, testes funcionais e observações clínicas.": "Describe relevant physical assessment findings, functional tests, and clinical observations.",
    "Definir hipótese clínica, diagnóstico funcional e prioridades terapêuticas.": "Define the clinical hypothesis, functional diagnosis, and therapeutic priorities.",
    "Definir plano terapêutico, frequência, metas e critérios de acompanhamento.": "Define the therapeutic plan, frequency, goals, and follow-up criteria.",
    "Comparar evolução clínica, adesão ao plano e novas demandas do paciente.": "Compare clinical progress, plan adherence, and new patient needs.",
    "Registrar mudanças de mobilidade, força, dor, função e desempenho.": "Record changes in mobility, strength, pain, function, and performance.",
    "Revisar interpretação clínica frente aos objetivos terapêuticos.": "Review the clinical interpretation against therapeutic goals.",
    "Atualizar condutas, metas, frequência e próximos passos.": "Update interventions, goals, frequency, and next steps.",
    "Registrar critérios clínicos de alta e percepção funcional do paciente.": "Record clinical discharge criteria and the patient's functional perception.",
    "Documentar achados finais, evolução funcional e pontos de atenção.": "Document final findings, functional progress, and points of attention.",
    "Concluir ciclo terapêutico conforme objetivos atingidos ou encaminhamentos necessários.": "Close the therapeutic cycle according to achieved goals or needed referrals.",
    "Orientar manutenção, prevenção de recidiva e sinais para retorno.": "Guide maintenance, recurrence prevention, and signs for return.",
    "Avaliação inicial e definição de conduta.": "Initial evaluation and care plan definition.",
    "Paciente relata queixa principal, início dos sintomas, fatores de melhora/piora e expectativas do tratamento.": "Patient reports main complaint, symptom onset, improvement/worsening factors, and treatment expectations.",
    "Realizada avaliação física, inspeção, testes funcionais e registro dos achados relevantes.": "Physical evaluation, inspection, functional tests, and relevant findings recorded.",
    "Hipótese clínica inicial compatível com o quadro apresentado. Definidas prioridades terapêuticas.": "Initial clinical hypothesis compatible with the presentation. Therapeutic priorities defined.",
    "Orientações iniciais, proposta de plano terapêutico e agendamento das próximas sessões.": "Initial guidance, therapeutic plan proposal, and scheduling of next sessions.",
    "Acompanhamento de retorno.": "Return follow-up.",
    "Paciente em retorno para acompanhamento da resposta clínica e evolução funcional.": "Patient returning for follow-up of clinical response and functional progress.",
    "Registrar testes clínicos ou funcionais realizados no retorno.": "Record clinical or functional tests performed at the return visit.",
    "Manter ou ajustar condutas conforme resposta clínica observada.": "Maintain or adjust interventions according to the observed clinical response.",
    "Paciente relata evolução desde a última consulta, adesão às orientações e sintomas atuais.": "Patient reports progress since the last appointment, adherence to guidance, and current symptoms.",
    "Reavaliados sinais clínicos, mobilidade, dor, função e resposta às condutas anteriores.": "Clinical signs, mobility, pain, function, and response to prior interventions reassessed.",
    "Quadro em acompanhamento, com ajustes conforme resposta clínica observada.": "Case under follow-up, with adjustments according to observed clinical response.",
    "Manter ou ajustar condutas, reforçar orientações e programar próximo retorno.": "Maintain or adjust interventions, reinforce guidance, and schedule the next return.",
    "Reavaliação clínica periódica.": "Periodic clinical reassessment.",
    "Paciente informa percepção de progresso, limitações persistentes e novas queixas, se houver.": "Patient reports perceived progress, persistent limitations, and new complaints, if any.",
    "Comparados indicadores funcionais, amplitude, força, dor e desempenho em relação à avaliação anterior.": "Functional indicators, range, strength, pain, and performance compared with the previous assessment.",
    "Evolução analisada frente aos objetivos terapêuticos e necessidade de ajustes.": "Progress analyzed against therapeutic goals and need for adjustments.",
    "Atualizar metas, condutas e frequência conforme evolução clínica.": "Update goals, interventions, and frequency according to clinical progress.",
    "Alta clínica": "Clinical discharge",
    "Evolução de alta clínica.": "Clinical discharge note.",
    "Evolução final do ciclo terapêutico, com critérios de alta e orientações registradas.": "Final note for the therapeutic cycle, with discharge criteria and guidance recorded.",
    "Registrar testes finais, medidas funcionais e critérios clínicos avaliados.": "Record final tests, functional measures, and assessed clinical criteria.",
    "Encerramento do ciclo terapêutico.": "Closure of the therapeutic cycle.",
    "Paciente relata melhora funcional e compreensão das orientações para manutenção.": "Patient reports functional improvement and understanding of maintenance guidance.",
    "Critérios clínicos e funcionais de alta avaliados e registrados.": "Clinical and functional discharge criteria evaluated and recorded.",
    "Paciente apto para alta clínica conforme evolução e objetivos atingidos.": "Patient eligible for clinical discharge according to progress and goals achieved.",
    "Orientações domiciliares, sinais de alerta e recomendação de retorno se necessário.": "Home guidance, warning signs, and recommendation to return if needed.",
    "Ajuste de tratamento": "Treatment adjustment",
    "Ajuste de conduta terapêutica.": "Therapeutic intervention adjustment.",
    "Evolução registrada para ajuste do plano terapêutico conforme resposta do paciente.": "Progress note recorded to adjust the therapeutic plan according to the patient's response.",
    "Registrar achados ou testes que justificam o ajuste terapêutico.": "Record findings or tests that support the therapeutic adjustment.",
    "Paciente relata resposta parcial, desconfortos ou necessidade de mudança no plano.": "Patient reports partial response, discomfort, or need to change the plan.",
    "Observados achados que indicam ajuste de carga, técnica, frequência ou estratégia.": "Findings observed indicating adjustment of load, technique, frequency, or strategy.",
    "Conduta revisada de acordo com tolerância, resposta clínica e objetivos.": "Intervention reviewed according to tolerance, clinical response, and goals.",
    "Aplicar ajustes definidos e monitorar resposta nas próximas sessões.": "Apply defined adjustments and monitor response in the next sessions.",
    "Ansiedade": "Anxiety",
    "Hipertensão": "Hypertension",
    "Diabetes": "Diabetes",
    "Obesidade": "Obesity",
    "Selecione": "Select",
    "Valor previsto": "Expected amount",
    "Apagar lead": "Delete lead",
    "Apagar": "Delete",
    "Esse lead será removido permanentemente do cadastro.": "This lead will be permanently deleted from the records.",
    "Novo": "New",
    "Em Contato": "Contacted",
    "Em contato": "Contacted",
    "Convertido": "Converted",
    "Não agendado": "Not scheduled",
    "Preço": "Price",
    "Sem retorno": "No response",
    "Sem interesse": "Not interested",
    "Conflito de horário": "Schedule conflict",
    "Já está em tratamento": "Already in treatment",
    "Distância": "Distance",
    "Conveniência": "Convenience",
    "Outro": "Other",
    "Indicação": "Referral",
    "Origens do Lead": "Lead sources",
    "Use este cadastro para padronizar as origens utilizadas no funil.": "Use this catalog to standardize the sources used in the funnel.",
    "Nova origem": "New source",
    "Adicionar origem": "Add source",
    "Origem adicionada com sucesso!": "Source added successfully!",
    "Origem removida com sucesso!": "Source removed successfully!",
    "Erro ao adicionar origem": "Error adding source",
    "Erro ao remover origem": "Error removing source",
    "Data de criação": "Creation date",
    "Todos os períodos": "All periods",
    "Site": "Website",
    "Presencial": "Walk-in",
    "Fisioterapia": "Physiotherapy",
    "Acupuntura": "Acupuncture",
    "Pilates": "Pilates",
    "Abril Retorno sem Dor": "April Pain-Free Return",
    "Pilates no Almoco": "Lunchtime Pilates",
    "Pilates no Almoço": "Lunchtime Pilates",
    "Semana da Postura Corporativa": "Corporate Posture Week",
    "Volta pra Casa": "Back Home",
    "Chegou por reels sobre dor ao acordar e queria avaliação rápida.": "Came through reels about waking up with pain and wanted a quick assessment.",
    "Corredor amador procurando retorno seguro apos dor no joelho.": "Amateur runner looking for a safe return after knee pain.",
    "Indicação do RH da empresa parceira para pilates no almoço.": "Referral from the partner company's HR team for lunchtime Pilates.",
    "Chegou por indicacao da esposa para melhorar sono e tensao cervical.": "Referred by his wife to improve sleep and cervical tension.",
    "Pediu plano para voltar a treinar sem piorar dor no quadril.": "Asked for a plan to train again without worsening hip pain.",
    "Ja saiu com aula teste marcada para a proxima semana.": "Left with a trial class scheduled for next week.",
    "RH gostou da proposta, mas o colaborador pediu mais prazo para decidir.": "HR liked the proposal, but the employee asked for more time to decide.",
    "Quer entender se a clínica atende no horário do fim do expediente.": "Wants to know if the clinic has appointments around the end of the workday.",
    "Preencheu formulário à noite e ainda não retornou ao WhatsApp.": "Filled out the form at night and has not replied on WhatsApp yet.",
    "Interesse em pilates para fortalecer depois da gestação.": "Interested in Pilates for strengthening after pregnancy.",
    "Parou no preco do pacote e pediu segunda opcao mais enxuta.": "Stopped at the package price and asked for a leaner second option.",
    "Mudou de cidade e desistiu do agendamento.": "Moved to another city and gave up on scheduling.",
    "Pacientes Ativos": "Active patients",
    "Consultas": "Appointments",
    "Status dos Agendamentos": "Appointment status",
    "Agendado": "Scheduled",
    "Em atendimento": "In care",
    "Atendimento realizado": "Care completed",
    "Faltou": "No-show",
    "Em Andamento": "In progress",
    "Concluído": "Completed",
    "Não Compareceu": "No-show",
    "Não foi possível marcar o agendamento como atendimento realizado.": "Could not mark the appointment as care completed.",
    "CAC": "CAC",
    "Custo de Aquisição": "Acquisition cost",
    "LTV": "LTV",
    "Valor por Paciente": "Value per patient",
    "ROI": "ROI",
    "Retorno sobre Investimento": "Return on investment",
    "Pesquisa de experiência": "Experience survey",
    "Carregando pesquisa...": "Loading survey...",
    "Esta pesquisa não está disponível.": "This survey is not available.",
    "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.": "Give a score from 0 to 10 and, if you'd like, leave a comment for the team.",
    "Sua nota": "Your score",
    "Conte para a clínica o que funcionou bem ou o que pode melhorar.": "Tell the clinic what worked well or what could be improved.",
    "Enviar resposta": "Send response",
    "Desenvolvido por": "Built by",
    "Operação inteligente para clínicas em crescimento": "Smart operations for growing clinics",
    "organiza a jornada da clínica do primeiro acesso até a operação diária.": "organizes the clinic journey from first access to daily operations.",
    "Uma camada de gestão criada para abrir clínicas, estruturar equipes e acompanhar marketing, agenda, atendimento e resultados com mais clareza.": "A management layer built to launch clinics, structure teams, and track marketing, scheduling, care, and results more clearly.",
    "Começar onboarding": "Start onboarding",
    "Acessar plataforma": "Open platform",
    "Portal Principal": "Main portal",
    "Onboarding guiado": "Guided onboarding",
    "Cadastre a clínica e o administrador principal em um fluxo único e direto, com acesso imediato para começar a operação no": "Create the clinic and primary administrator in one direct flow, with immediate access to start operating in",
    "Criar nova clínica": "Create new clinic",
    "Onboarding rápido para novas clínicas": "Quick onboarding for new clinics",
    "Operação financeira, agenda e atendimento em um só lugar": "Finance, scheduling, and care operations in one place",
    "Estrutura preparada para multi-clínica e administração central": "Designed for multi-clinic operations and centralized administration",
    "Entrada pública clara para login, cadastro e expansão do produto.": "A clear public entry point for login, registration, and product expansion.",
    "Clínicas organizadas com isolamento de dados e administração por unidade.": "Clinics organized with data isolation and per-unit administration.",
    "Visão integrada de operação, comercial, agenda, atendimento e financeiro.": "Integrated view of operations, sales, scheduling, care, and finance.",
    "Plataforma para operação de clínicas, equipes e crescimento com mais clareza.": "Platform for clinic operations, teams, and growth with more clarity.",
    "Estruture sua clínica e inicie a operação com governança desde o primeiro acesso.": "Structure your clinic and start operations with governance from the very first access.",
    "Configure a clínica e o administrador principal no mesmo fluxo.": "Set up the clinic and the primary administrator in the same flow.",
    "Selecione uma nota de 0 a 10.": "Select a score from 0 to 10.",
    "Resposta enviada com sucesso.": "Response sent successfully.",
    "Algo deu errado. Tente novamente.": "Something went wrong. Please try again.",
    "Revise os campos informados e tente novamente.": "Review the fields and try again.",
    "Preencha o campo obrigatório: {fieldLabel}.": "Fill in the required field: {fieldLabel}.",
    "Preencha todos os campos obrigatórios.": "Fill in all required fields.",
    "Revise o valor informado para o campo: {fieldLabel}.": "Review the value entered for the field: {fieldLabel}.",
    "Revise os campos clínicos informados.": "Review the clinical fields.",
    "O campo informado precisa ser maior ou igual a 15.": "The field must be greater than or equal to 15.",
    "Acesso em análise ou bloqueado!": "Access is pending review or blocked.",
    "Acesso bloqueado": "Access blocked",
    "Seu perfil atual não possui permissão para acessar esta página.": "Your current profile does not have permission to access this page.",
    "Você não tem permissão para executar esta ação.": "You do not have permission to perform this action.",
    "Você não tem permissão para editar a agenda.": "You do not have permission to edit the schedule.",
    "Você não tem permissão para registrar lançamentos financeiros.": "You do not have permission to record financial entries.",
    "Permissões do profissional": "Professional permissions",
    "Defina quais módulos este profissional poderá acessar.": "Choose which modules this professional can access.",
    "Prontuário": "Medical record",
    "Erro ao fazer login": "Error signing in",
    "Este portal é exclusivo para super admin.": "This portal is only available to super admins.",
    "As senhas não coincidem.": "Passwords do not match.",
    "Erro ao criar conta": "Error creating account",
    "CEP": "ZIP code",
    "Resumo": "Summary",
    "Agendamentos sem plano de tratamento": "Appointments without a treatment plan",
    "Atendimentos Avulsos": "Standalone appointments",
    "Ver histórico completo": "View full history",
    "Falha ao consultar CEP": "Failed to look up ZIP code",
    "CEP não encontrado.": "ZIP code not found.",
    "Não foi possível consultar o CEP.": "Could not look up the ZIP code.",
    "Aparência": "Appearance",
    "Alterne entre tema claro e escuro.": "Switch between light and dark theme.",
    "Escolha o idioma usado nos menus e páginas.": "Choose the language used in menus and pages.",
    "Moeda usada nos valores financeiros.": "Currency used for financial values.",
    "Termine a sessão neste dispositivo.": "Sign out on this device.",
    "Não foi possível carregar os modelos clínicos.": "Clinical templates could not be loaded.",
    "Modelo clínico salvo como rascunho.": "Clinical template saved as a draft.",
    "Modelo clínico criado como rascunho.": "Clinical template created as a draft.",
    "Não foi possível salvar o modelo clínico.": "The clinical template could not be saved.",
    "Não foi possível concluir a ação.": "The action could not be completed.",
    "Modelo duplicado como rascunho.": "Template duplicated as a draft.",
    "Modelo publicado com sucesso.": "Template published successfully.",
    "Modelo inativado com sucesso.": "Template deactivated successfully.",
    "Modelo reativado com sucesso.": "Template reactivated successfully.",
    "Gerencie modelos versionados para avaliações, evoluções e outros registros clínicos, preservando o histórico.": "Manage versioned templates for assessments, clinical notes, and other clinical records while preserving their history.",
    "Novo modelo clínico": "New clinical template",
    "Tipo": "Type",
    "Ativo": "Active",
    "Inativo": "Inactive",
    "Modelo clínico": "Clinical template",
    "Versões": "Versions",
    "Associações": "Associations",
    "Última atualização": "Last update",
    "Modelo padrão": "Default template",
    "Publicado": "Published",
    "Todos os serviços e procedimentos": "All services and procedures",
    "Pré-visualizar": "Preview",
    "Duplicar": "Duplicate",
    "Editar rascunho": "Edit draft",
    "Publicar": "Publish",
    "Nova versão": "New version",
    "Nenhum modelo clínico encontrado.": "No clinical templates found.",
    "Editar rascunho do modelo": "Edit template draft",
    "Criar modelo clínico": "Create clinical template",
    "Setores vinculados": "Linked departments",
    "Serviços e procedimentos vinculados": "Linked services and procedures",
    "Estrutura do modelo": "Template structure",
    "Organize as seções e os campos do modelo. Alterações estruturais ficam disponíveis somente após a publicação.": "Organize the template sections and fields. Structural changes become available only after publishing.",
    "Adicionar seção": "Add section",
    "Identificador técnico": "Technical identifier",
    "Título da seção": "Section title",
    "Descrição da seção": "Section description",
    "Identificador do campo": "Field identifier",
    "Rótulo": "Label",
    "Tipo de campo": "Field type",
    "Texto curto": "Short text",
    "Texto longo": "Long text",
    "Escala numérica": "Numeric scale",
    "Seleção única": "Single select",
    "Seleção múltipla": "Multiple select",
    "Caixa de seleção": "Checkbox",
    "Data civil": "Date",
    "Texto informativo": "Informational text",
    "Sim/Não": "Yes/No",
    "Medida com unidade": "Measurement with unit",
    "Obrigatório": "Required",
    "Opções": "Options",
    "Nova seção": "New section",
    "Novo campo": "New field",
    "Testes clínicos": "Clinical tests",
    "Mover seção para cima": "Move section up",
    "Mover seção para baixo": "Move section down",
    "Excluir seção": "Delete section",
    "Mover campo para cima": "Move field up",
    "Mover campo para baixo": "Move field down",
    "Excluir campo": "Delete field",
    "opcao_1:Opção 1, opcao_2:Opção 2": "opcao_1:Option 1, opcao_2:Option 2",
    "{count} vínculo selecionado": "{count} link selected",
    "{count} vínculos selecionados": "{count} links selected",
    "Adicionar campo": "Add field",
    "Após publicar, a versão fica imutável. Para alterar a estrutura depois disso, crie uma nova versão.": "After publishing, the version becomes immutable. To change the structure after that, create a new version.",
    "Cancelar": "Cancel",
    "Salvar rascunho": "Save draft",
    "Pré-visualizar modelo": "Preview template",
    "Nenhuma versão disponível para pré-visualização.": "No version available for preview.",
    "Selecionar": "Select",
    "Marcado": "Checked",
    "Selecionar modelo clínico": "Select clinical template",
    "Sem modelo versionado": "No versioned template",
    "Selecionar agendamento": "Select appointment",
    "Salvando...": "Saving...",
    "Personalizado": "Custom",
    "Leads por origem": "Leads by source",
    "Nenhum lead encontrado para o período selecionado.": "No leads were found for the selected period.",
    "Sem base suficiente": "Not enough base data",
    "Atribuição insuficiente": "Insufficient attribution",
    "Custo realizado ausente": "Missing realized cost",
    "Coorte longitudinal insuficiente": "Insufficient longitudinal cohort",
    "Dados insuficientes": "Insufficient data",
    "Dashboard Executivo": "Executive Dashboard",
    "Visão consolidada da clínica para decisões rápidas, sem duplicar os módulos detalhados.": "Consolidated clinic view for quick decisions without duplicating detailed modules.",
    "A data final não pode ser anterior à data inicial.": "The end date cannot be earlier than the start date.",
    "dia": "day",
    "Atualizando...": "Updating...",
    "Não foi possível carregar os indicadores executivos agora. Tente novamente em instantes.": "Executive indicators could not be loaded right now. Please try again shortly.",
    "Resumo executivo do período selecionado com os blocos permitidos para o seu perfil.": "Executive summary for the selected period with the blocks allowed for your profile.",
    "Leads criados": "Leads created",
    "Registros que compõem este indicador no período e setor selecionados.": "Records included in this indicator for the selected period and sector.",
    "Leads agendados": "Booked leads",
    "Agendado no período": "Booked during the period",
    "Não agendado no período": "Not booked during the period",
    "Convertido no período": "Converted during the period",
    "Não convertido no período": "Not converted during the period",
    "Atendimentos concluídos": "Completed appointments",
    "Atendimentos no período": "Appointments during the period",
    "Não concluído": "Not completed",
    "Faltas": "No-shows",
    "Falta": "No-show",
    "Sem falta": "Not a no-show",
    "Leads criados no período": "Leads created during the period",
    "Agendados": "Booked",
    "Não agendados": "Not booked",
    "Leads por status": "Leads by status",
    "Atendimentos realizados no período": "Completed visits during the period",
    "Agendamentos vinculados e lançamentos manuais": "Linked appointments and manual revenue entries",
    "Atendimentos realizados": "Completed visits",
    "consultas no período": "appointments in the period",
    "Pacientes ativos": "Active patients",
    "Cadastro canônico de pacientes": "Canonical patient registry",
    "Receita realizada": "Realized revenue",
    "lançamentos": "entries",
    "Seu perfil pode acessar o Dashboard, mas ainda não possui permissões para blocos executivos detalhados.": "Your profile can access the Dashboard but does not yet have permissions for detailed executive blocks.",
    "Captação e comercial": "Acquisition and sales",
    "Funil de entrada resumido; a análise detalhada continua nos módulos de Leads e Marketing.": "Summarized intake funnel; detailed analysis remains in the Leads and Marketing modules.",
    "Abrir Marketing": "Open Marketing",
    "Abrir Leads": "Open Leads",
    "Taxa de agendamento": "Booking rate",
    "agendados": "booked",
    "Conversão em paciente": "Conversion to patient",
    "Principal origem": "Main source",
    "Sem origem identificada": "No source identified",
    "Sem permissão para visualizar indicadores comerciais.": "No permission to view commercial indicators.",
    "Fonte oficial: métricas de CAC, LTV e ROI.": "Official source: CAC, LTV, and ROI metrics.",
    "Custo de aquisição atribuído.": "Attributed acquisition cost.",
    "ROI de marketing": "Marketing ROI",
    "Retorno atribuído ao investimento.": "Return attributed to investment.",
    "Operação clínica": "Clinical operations",
    "Agenda, comparecimento e continuidade do cuidado em visão resumida.": "Schedule, attendance, and continuity of care in summary view.",
    "Abrir Atendimento": "Open Care",
    "Abrir Agenda": "Open Schedule",
    "Consultas no período": "Appointments in the period",
    "Taxa de conclusão": "Completion rate",
    "Taxa de faltas": "No-show rate",
    "Somente atendimentos concluídos no período e setor selecionados.": "Only completed appointments in the selected period and sector.",
    "Somente faltas no período e setor selecionados.": "Only no-shows in the selected period and sector.",
    "Vendas confirmadas de planos no período e setor selecionados.": "Confirmed plan sales in the selected period and sector.",
    "Pacientes distintos": "Distinct patients",
    "Valor total vendido": "Total sold value",
    "Forma de pagamento recebida": "Received payment method",
    "Pacientes com atendimento concluído": "Patients with completed appointments",
    "Pacientes com falta": "Patients with no-shows",
    "Faltas no período": "No-shows in the period",
    "Detalhamento por profissional": "Breakdown by professional",
    "Tipo de atendimento não identificado": "Unidentified appointment type",
    "Profissional não identificado": "Unidentified professional",
    "Retenção de pacientes": "Patient retention",
    "pacientes com continuidade": "patients with continuity",
    "Sem permissão para visualizar indicadores da agenda.": "No permission to view schedule indicators.",
    "Entradas realizadas, despesas por competência e resultado do período com origem no serviço financeiro oficial.": "Realized inflows, accrual-basis expenses, and the period result sourced from the official financial service.",
    "Abrir Financeiro": "Open Finance",
    "Despesas realizadas": "Realized expenses",
    "Resultado do período": "Period result",
    "Por lançamento financeiro": "Per financial entry",
    "Sem permissão para visualizar indicadores financeiros.": "No permission to view financial indicators.",
    "Entradas financeiras no período": "Financial inflows during the period",
    "Entradas por setor": "Inflows by sector",
    "Despesas reconhecidas por competência no período": "Expenses recognized on an accrual basis during the period",
    "Valor líquido de planos vendidos": "Net value of plans sold",
    "Sem dados setoriais no período": "No sector data for the period",
    "Não foi possível carregar os repasses profissionais.": "Could not load professional payouts.",
    "Não foi possível carregar os filtros de repasse.": "Could not load payout filters.",
    "Repasses pendentes gerados com sucesso.": "Pending payouts generated successfully.",
    "Não foi possível gerar repasses pendentes.": "Could not generate pending payouts.",
    "Repasse aprovado com sucesso.": "Payout approved successfully.",
    "Não foi possível aprovar o repasse.": "Could not approve the payout.",
    "Pagamento de repasse registrado com sucesso.": "Payout payment recorded successfully.",
    "Não foi possível registrar o pagamento do repasse.": "Could not record the payout payment.",
    "Repasse cancelado com sucesso.": "Payout canceled successfully.",
    "Não foi possível cancelar o repasse.": "Could not cancel the payout.",
    "Repasses profissionais": "Professional payouts",
    "Repasses calculados a partir de produção clínica válida, separados de venda, recebível e caixa do paciente.": "Payouts calculated from valid clinical production, separate from patient sales, receivables, and cash.",
    "Aprovados": "Approved",
    "Valor a pagar": "Amount payable",
    "Valor pago": "Amount paid",
    "Bloqueados": "Blocked",
    "Cancelados": "Canceled",
    "Modalidade": "Mode",
    "Valor fixo": "Fixed amount",
    "Percentual da produção": "Production percentage",
    "Pagamento": "Payment",
    "Com saldo": "With balance",
    "Sem saldo": "No balance",
    "Produção base": "Base production",
    "Regra": "Rule",
    "Valor do repasse": "Payout amount",
    "Nenhum repasse profissional encontrado": "No professional payout found",
    "Aprovar": "Approve",
    "Pagar": "Pay",
    "Detalhes do repasse": "Payout details",
    "Produção de origem": "Source production",
    "Versão": "Version",
    "Saldo a pagar": "Balance payable",
    "Bloqueio": "Block",
    "Valor da produção não disponível para cálculo": "Production value unavailable for calculation",
    "Nenhuma regra de repasse aplicável": "No applicable payout rule",
    "Conflito entre regras de repasse": "Conflict between payout rules",
    "Produção clínica cancelada": "Clinical production canceled",
    "Repasse fixo não autorizado para produção de cortesia": "Fixed payout not authorized for courtesy production",
    "Moeda da regra de repasse incompatível com a produção": "Payout rule currency does not match the production currency",
    "Regra de repasse inválida": "Invalid payout rule",
    "Motivo do bloqueio não disponível": "Block reason unavailable",
    "Produção clínica de {date}": "Clinical production dated {date}",
    "Produção clínica vinculada": "Linked clinical production",
    "Status do repasse não disponível": "Payout status unavailable",
    "Regra de repasse não disponível": "Payout rule unavailable",
    "Registrar pagamento": "Record payment",
    "Cancelar repasse": "Cancel payout",
    "O repasse será cancelado sem alterar venda, recebível ou caixa do paciente.": "The payout will be cancelled without changing the patient's sale, receivable, or cash.",
    "Não foi possível carregar regras de repasse.": "Could not load payout rules.",
    "Regra de repasse criada com sucesso.": "Payout rule created successfully.",
    "Não foi possível criar a regra de repasse.": "Could not create the payout rule.",
    "Regra de repasse inativada com sucesso.": "Payout rule deactivated successfully.",
    "Não foi possível inativar a regra de repasse.": "Could not deactivate the payout rule.",
    "Nova regra de repasse": "New payout rule",
    "Quando serviço e setor são informados, esta regra só se aplica se ambos corresponderem.": "When both a service and department are specified, this rule applies only if both match.",
    "Prioridade das regras: serviço + setor, serviço, setor e regra geral.": "Rule priority: service + department, service, department, then general rule.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional, serviço e setor.": "An active or overlapping payout rule already exists for this professional, service, and department.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e serviço.": "An active or overlapping payout rule already exists for this professional and service.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e setor.": "An active or overlapping payout rule already exists for this professional and department.",
    "Já existe uma regra geral de repasse vigente ou com vigência sobreposta para este profissional.": "An active or overlapping general payout rule already exists for this professional.",
    "Setor opcional": "Optional sector",
    "Serviço opcional": "Optional service",
    "Percentual": "Percentage",
    "Vigência inicial": "Start date",
    "Vigência final": "End date",
    "Permitir cortesia": "Allow courtesy",
    "Criar regra": "Create rule",
    "Vigência": "Validity",
    "Nenhuma regra de repasse encontrada": "No payout rule found",
    "Bloco sem permissão": "Block without permission",
    "Oculto": "Hidden",
    "Fonte": "Source",
    "registros": "records",
    "Abrir origem": "Open source",
    "Nenhum dado gerencial encontrado": "No management data found",
    "Repasse calculado": "Calculated payout",
    "Repasse pago": "Paid payout",
    "Repasse a pagar": "Payout payable",
    "Não foi possível carregar a visão gerencial.": "Could not load the management view.",
    "Não foi possível exportar a visão gerencial.": "Could not export the management view.",
    "Visão gerencial": "Management view",
    "Vendido, produzido, recebido e repasses em fontes separadas, com comparações gerenciais temporais.": "Sold, produced, received, and payouts in separate sources, with temporal management comparisons.",
    "Exportar": "Export",
    "Exportar CSV": "Export CSV",
    "Excel": "Excel",
    "PDF": "PDF",
    "Gerando exportação": "Generating export",
    "Gerando relatório": "Generating report",
    "Gerar relatório": "Generate report",
    "Exportação gerencial concluída.": "Management export completed.",
    "Relatório gerencial gerado com sucesso.": "Management report generated successfully.",
    "Não foi possível gerar a exportação.": "Could not generate the export.",
    "Não foi possível gerar o relatório.": "Could not generate the report.",
    "Histórico de lotes": "Batch history",
    "Detalhamento dos snapshots": "Snapshot details",
    "Confiabilidade": "Reliability",
    "Somente confirmados": "Confirmed only",
    "Incluir inferidos": "Include inferred",
    "Incluir legados": "Include legacy",
    "Todos os dados": "All data",
    "Visão parcial": "Partial view",
    "Alguns blocos foram ocultados por permissão e não foram retornados no payload.": "Some blocks were hidden by permission and were not returned in the payload.",
    "Sem base": "No base",
    "Série temporal": "Timeline",
    "Pendências de qualidade dos dados": "Data quality pending items",
    "Planos e pacotes": "Plans and packages",
    "Planos vendidos": "Plans sold",
    "Valor vendido em planos": "Amount sold in plans",
    "Sessões produzidas": "Produced sessions",
    "Limitações": "Limitations",
    "Escala de dor": "Pain scale",
    "Rateio gerencial habilitado.": "Managerial allocation enabled.",
    "Rateio gerencial desabilitado.": "Managerial allocation disabled.",
    "Simulação de rateio concluída.": "Allocation simulation completed.",
    "Rateio gerencial aplicado.": "Managerial allocation applied.",
    "Aplicação de rateio revertida.": "Allocation application reversed.",
    "Elegibilidade da categoria atualizada.": "Category eligibility updated.",
    "Regra de rateio criada em rascunho.": "Allocation rule created as draft.",
    "Regra de rateio ativada.": "Allocation rule activated.",
    "Regra de rateio inativada.": "Allocation rule inactivated.",
    "Nova versão criada em rascunho.": "New version created as draft.",
    "Rateio gerencial por setor": "Managerial allocation by sector",
    "Configura regras percentuais por setor sem aplicar valores nesta etapa.": "Configure percentage rules by sector without applying values in this phase.",
    "Habilitar rateio": "Enable allocation",
    "Simular rateio": "Simulate allocation",
    "Inicio": "Start",
    "Simulando": "Simulating",
    "Simular": "Simulate",
    "Aviso: simulação de rateio não aplicada": "Warning: allocation simulation not applied",
    "Aplicação controlada do rateio": "Controlled allocation application",
    "Fingerprint da simulação": "Simulation fingerprint",
    "Fingerprint da aplicação": "Application fingerprint",
    "Aplicar rateio gerencial": "Apply managerial allocation",
    "Cancelar aplicação": "Cancel application",
    "Observação da aplicação": "Application note",
    "Chave de idempotência": "Idempotency key",
    "Aplicando": "Applying",
    "Confirmar aplicação": "Confirm application",
    "Aplicações de rateio": "Allocation applications",
    "Aplicada em": "Applied on",
    "Período da aplicação": "Application period",
    "Total aplicado": "Applied total",
    "Nenhuma aplicação de rateio encontrada": "No allocation application found",
    "Aplicada": "Applied",
    "Aplicação revertida": "Reversed application",
    "Detalhar aplicação": "View application details",
    "Preparar reversão": "Prepare reversal",
    "Itens aplicados": "Applied items",
    "Setores aplicados": "Applied sectors",
    "Motivo da reversão": "Reversal reason",
    "Revertendo": "Reversing",
    "Reverter aplicação": "Reverse application",
    "Despesas encontradas": "Expenses found",
    "Elegíveis": "Eligible",
    "Simuladas": "Simulated",
    "Total original": "Original total",
    "Total distribuído": "Distributed total",
    "Diferença de arredondamento": "Rounding difference",
    "Participação": "Participation",
    "Valor simulado": "Simulated amount",
    "Nenhum valor simulado por setor": "No amount simulated by sector",
    "Qtd.": "Qty.",
    "Nenhuma despesa fora da simulação": "No expense outside the simulation",
    "Despesa": "Expense",
    "Competência da despesa": "Expense accrual date",
    "Original": "Original",
    "Distribuído": "Distributed",
    "Despesa simulada": "Simulated expense",
    "Rateio desabilitado": "Allocation disabled",
    "Despesa não elegível": "Expense not eligible",
    "Categoria não elegível": "Category not eligible",
    "Sem regra aplicável": "No applicable rule",
    "Sem competência": "Missing accrual data",
    "Competência não confirmada": "Accrual classification not confirmed",
    "Moeda não compatível": "Currency not compatible",
    "Despesa direta": "Direct expense",
    "Regra inválida": "Invalid rule",
    "Não simulada": "Not simulated",
    "Categorias elegíveis": "Eligible categories",
    "Não elegível": "Not eligible",
    "Usar padrão da clínica": "Use clinic default",
    "Usar regra da categoria": "Use category rule",
    "Nova regra de rateio": "New allocation rule",
    "Nome da regra": "Rule name",
    "Escopo": "Scope",
    "Padrão da clínica": "Clinic default",
    "Categoria de despesa": "Expense category",
    "Sem categoria": "No category",
    "Soma dos percentuais": "Percentage total",
    "Criar rascunho": "Create draft",
    "Nenhuma regra de rateio encontrada": "No allocation rule found",
    "Ativar regra": "Activate rule",
    "Inativar regra": "Inactivate rule",
    "Histórico": "History",
    "Histórico de versões": "Version history",
    "Dinheiro": "Cash",
    "Cheque": "Check",
    "Cartão de crédito": "Credit card",
    "Cartão de débito": "Debit card",
    "Cartão de crédito parcelado": "Credit card installments",
    "Transferência": "Bank transfer",
    "Convênio": "Health insurance",
    "Aluguel": "Rent",
    "Contas": "Utilities",
    "Salários": "Salaries",
    "Materiais": "Supplies",
    "Equipamentos": "Equipment",
    "Impostos": "Taxes",
    "Manutenção": "Maintenance",
    "Fixa": "Fixed",
    "Variável": "Variable",
    "Próximo": "Next",
    "Modo de visualização": "Calendar view",
    "Nenhum agendamento encontrado para este período.": "No appointments found for this period.",
    "Sem agendamentos": "No appointments",
    "Planos vendidos no período": "Plans sold in the period",
    "Desempenho comercial e financeiro dos planos vendidos no intervalo selecionado.": "Commercial and financial performance of plans sold within the selected date range.",
    "Valor líquido total dos planos vendidos": "Total net value of plans sold",
    "Valor pendente": "Outstanding amount",
    "Plano mais vendido": "Best-selling plan",
    "Plano com maior valor líquido vendido": "Plan with the highest net value sold",
    "Buscar por nome do plano": "Search by plan name",
    "Digite o nome do plano": "Enter the plan name",
    "Tipo do plano": "Plan type",
    "Situação financeira": "Payment status",
    "Todas as situações": "All statuses",
    "Parcial": "Partially paid",
    "Qtde. vendida": "Qty sold",
    "Detalhes das vendas do plano": "Plan sales details",
    "Parcelamento": "Installments",
    "Profissional ou responsável": "Professional or sales owner",
    "Não informado": "Not provided",
    "à vista": "Paid in full",
    "parcelas": "installments",
    "Período do relatório": "Report period",
    "Relatório exportado com sucesso.": "Report exported successfully.",
    "Não foi possível abrir a exportação.": "The export could not be opened.",
    "Erro ao carregar o relatório de planos vendidos.": "Error loading the sold plans report.",
    "Ordenar em ordem crescente": "Sort in ascending order",
    "Ordenar em ordem decrescente": "Sort in descending order",
    "Meta de faturamento": "Revenue goal",
    "Acompanhe o progresso do período selecionado.": "Track progress for the selected period.",
    "Realizado": "Current",
    "Atingido": "Progress",
    "Meta atingida no período.": "Goal reached in the period.",
    "Nenhuma meta financeira vigente para o período.": "There is no active financial goal for this period.",
    "Indicadores por unidade de receita": "Per-revenue-unit indicators",
    "Não disponível": "Not available",
    "unidades": "units",
    "Receita realizada por agendamento vinculado ou lançamento manual": "Realized revenue per linked appointment or manual entry",
    "Base da métrica: atendimentos vinculados e lançamentos manuais de receita pagos no período.": "Metric basis: linked appointments and manual revenue entries paid in the period.",
    "Unidades consideradas": "Units considered",
    "Custo por unidade de receita": "Cost per revenue unit",
    "Resultado por unidade de receita": "Result per revenue unit",
    "Ponto de equilíbrio em unidades de receita": "Break-even point in revenue units",
    "Formas de pagamento": "Payment methods",
    "Cadastre meios de pagamento, adquirentes, prazos e taxas para preparar o cálculo automático financeiro.": "Register payment methods, acquirers, settlement terms, and fees to prepare automatic fee calculations.",
    "Nova forma de pagamento": "New payment method",
    "Editar forma de pagamento": "Edit payment method",
    "Todas as adquirentes": "All acquirers",
    "Todos os status": "All statuses",
    "Adquirente/maquininha": "Acquirer/card terminal",
    "Tipo aceito": "Accepted type",
    "Taxa percentual": "Percentage fee",
    "Taxa fixa": "Fixed fee",
    "Prazo de recebimento": "Settlement term",
    "Limite de parcelas": "Installment limit",
    "Taxas por parcela": "Fees by installment",
    "Taxas": "Fees",
    "Nenhuma forma de pagamento encontrada.": "No payment methods found.",
    "Forma de pagamento cadastrada.": "Payment method created.",
    "Forma de pagamento atualizada.": "Payment method updated.",
    "Forma de pagamento excluída.": "Payment method deleted.",
    "Não foi possível excluir a forma de pagamento.": "Could not delete the payment method.",
    "Preencha os campos obrigatérios.": "Fill in the required fields.",
    "dias": "days",
    "Informe a taxa percentual cobrada em cada quantidade de parcelas.": "Enter the percentage fee charged for each installment count.",
    "Essas configurações ainda não alteram lançamentos existentes; elas deixam a clínica pronta para cálculo automático de taxas.": "These settings do not change existing entries yet; they prepare the clinic for automatic fee calculations.",
    "Carregando relatórios financeiros...": "Loading financial reports...",
    "Visão gerencial para acompanhar entradas, saídas e saldo de caixa.": "Management view of cash inflows, outflows, and balance.",
    "Erro ao excluir receita": "Error deleting revenue",
    "Erro ao excluir despesa": "Error deleting expense",
    "Erro ao carregar dados financeiros": "Error loading financial data",
    "Excluir receita": "Delete revenue",
    "Excluir despesa": "Delete expense",
    "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.": "This revenue entry will be deleted and will no longer appear in lists and indicators.",
    "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.": "This expense entry will be deleted and will no longer appear in lists and indicators.",
    "Não vinculado": "Unlinked",
    "Paciente removido": "Removed patient",
    "Profissional removido": "Removed professional",
    "Fixas": "Fixed",
    "Variáveis": "Variable",
    "Leitura gerencial": "Management overview",
    "Um resumo rápido do comportamento financeiro no recorte atual.": "A quick summary of financial performance in the current range.",
    "Recebimento médio de caixa": "Average cash receipt",
    "em pacotes": "from packages",
    "Indicadores de competência permanecem indisponíveis até haver produção canônica.": "Accrual indicators remain unavailable until canonical production records are available.",
    "Onde fatura": "Revenue by sector",
    "Receita distribuída por setor.": "Revenue distributed by sector.",
    "Nenhuma receita com setor vinculado no período.": "No revenue linked to sectors in the period.",
    "Quem fatura": "Revenue by professional",
    "Receita distribuída por profissional.": "Revenue distributed by professional.",
    "Nenhuma receita vinculada a profissional no período.": "No revenue linked to professionals in the period.",
    "Como entra": "Payment methods",
    "Receita distribuída por forma de pagamento.": "Revenue distributed by payment method.",
    "Nenhuma receita registrada no período.": "No revenue recorded in the period.",
    "CSV": "CSV",
    "Data final": "End date",
    "Data inicial": "Start date",
    "Despesa excluída com sucesso!": "Expense deleted successfully!",
    "Despesa registrada com sucesso!": "Expense recorded successfully!",
    "Despesas fixas": "Fixed expenses",
    "Despesas variáveis": "Variable expenses",
    "Dia": "Day",
    "Lançamentos": "Transactions",
    "Limpar filtros": "Clear filters",
    "Mês": "Month",
    "Nenhum plano encontrado com os filtros selecionados.": "No plans match the selected filters.",
    "Não alocada": "Unassigned",
    "OK": "OK",
    "Observações": "Notes",
    "Por profissional": "By professional",
    "Quantidade vendida": "Quantity sold",
    "Receita excluída com sucesso!": "Revenue deleted successfully!",
    "Registrar": "Record",
    "Salvar": "Save",
    "Selecione uma clínica.": "Select a clinic.",
    "Sem dados no período": "No data for this period",
    "Semana": "Week",
    "Situação": "Status",
    "Situação do pagamento": "Payment status",
    "Ticket médio": "Average ticket",
    "Totais consolidados": "Consolidated totals",
    "Total": "Total",
    "Total de planos vendidos": "Total plans sold",
    "Valor unit.": "Unit price",
    "Valor unitário": "Unit price",
    "Visão Geral": "Overview",
    "Últimas Despesas": "Latest expenses",
    "Últimas Receitas": "Latest revenues",
    "Anterior": "Previous",
    "min": "min",
    "Salvar alterações": "Save changes",
    "Selecione uma clínica": "Select a clinic",
    "Acréscimo": "Surcharge",
    "Classificar setor": "Assign sector",
    "Dado": "Data",
    "Confiabilidade do dado": "Data reliability",
    "Desconto": "Discount",
    "Preencha os campos obrigatórios.": "Fill in the required fields.",
    "Recorrente": "Recurring",
    "Registros": "Records",
    "Sem vínculo": "Not linked",
    "Vincular paciente": "Link patient",
    "Vincular profissional": "Link professional",
    "Importado": "Imported",
    "Sem regra": "No payout rule",
    "Receita gerencial líquida": "Net managerial revenue",
    "Resultado gerencial após custos diretos": "Managerial result after direct costs",
    "Nome completo": "Full name",
    "Histórico do paciente": "Patient history",
    "Atendimentos e financeiro do paciente.": "Patient visits and financial activity.",
    "Faltas registradas": "Recorded no-shows",
    "Histórico de atendimentos": "Visit history",
    "Nenhum atendimento encontrado para este paciente.": "No visits found for this patient.",
    "Vínculo": "Linked to",
    "Agendamento sem plano de tratamento": "Appointment without a treatment plan",
    "Ativos": "Active",
    "Inativos": "Inactive",
    "Exames": "Exams",
    "Imagens": "Images",
    "Fotos": "Photos",
    "Agendamentos do paciente sem vínculo com plano de tratamento.": "Patient appointments not linked to a treatment plan.",
    "Nenhum agendamento sem plano de tratamento encontrado para este paciente.": "No appointment without a treatment plan was found for this patient.",
    "Erro ao carregar pacientes": "Error loading patients",
    "Lead convertido em paciente com sucesso!": "Lead successfully converted to a patient!",
    "Lead criado com sucesso!": "Lead created successfully!",
    "Criar": "Create",
    "Lead atualizado com sucesso!": "Lead updated successfully!",
    "Pendências de follow-up": "Pending follow-ups",
    "Priorize os leads com retorno mais urgente.": "Prioritize leads requiring the most urgent follow-up.",
    "Remover origem": "Remove source",
    "Buscar por nome ou telefone...": "Search by name or phone...",
    "Gerencie seus leads e conversões": "Manage your leads and conversions",
    "Total de registros": "Total records",
    "Página {currentPage} de {totalPages}": "Page {currentPage} of {totalPages}",
    "Consulta agendada a partir do lead!": "Appointment scheduled from the lead!",
    "Tentar novamente": "Try again",
    "Atualizando dados de marketing...": "Refreshing marketing data...",
    "Apenas perfis de super admin podem acessar esta área.": "Only super admin accounts can access this area.",
    "Seu usuário não possui permissão para visualizar clínicas globais ou controlar acessos centrais.": "Your user account does not have permission to view all clinics or manage central access.",
    "Governança central do ecossistema": "Central ecosystem governance",
    "Gerencie clínicas, super admins e trilhas de auditoria a partir de um único painel global.": "Manage clinics, super admins, and audit trails from a single global dashboard.",
    "Clínicas da plataforma": "Platform clinics",
    "Busque clínicas, confira seus usuários e controle liberação ou bloqueio operacional.": "Search clinics, review their users, and manage operational access or blocking.",
    "Buscar clínica, cidade, usuário ou e-mail": "Search by clinic, city, user, or email",
    "Carregando clínicas...": "Loading clinics...",
    "Nenhuma clínica encontrada com o filtro informado.": "No clinics match the filter.",
    "Bloqueada": "Blocked",
    "Sem e-mail cadastrado": "No email address provided",
    "Sem telefone cadastrado": "No phone number provided",
    "Cidade não informada": "City not provided",
    "Bloquear clínica": "Block clinic",
    "Liberar clínica": "Unblock clinic",
    "Usuários cadastrados": "Registered users",
    "Nenhum usuário vinculado a esta clínica.": "No users are linked to this clinic.",
    "Bloqueado": "Blocked",
    "Redefinir acesso": "Reset access",
    "Nova senha temporária": "New temporary password",
    "Reativar acesso": "Reactivate access",
    "Se ativado, a conta volta a acessar o painel global após a redefinição.": "When enabled, the account regains access to the global dashboard after the reset.",
    "Salvar nova senha": "Save new password",
    "Convide novos perfis globais, revogue acessos quando necessário e redefina credenciais com auditoria.": "Invite new global accounts, revoke access when necessary, and reset credentials with audit tracking.",
    "Buscar super admin por nome ou email": "Search for a super admin by name or email",
    "Convidar super admin": "Invite super admin",
    "Carregando super admins...": "Loading super admins...",
    "Nenhum super admin encontrado com o filtro informado.": "No super admins match the filter.",
    "Desativado": "Deactivated",
    "Revogar": "Revoke",
    "Redefinir acesso do super admin": "Reset super admin access",
    "Acesso redefinido com sucesso.": "Access reset successfully.",
    "Erro ao redefinir acesso": "Error resetting access",
    "Auditoria recente": "Recent audit activity",
    "Últimas ações sensíveis executadas por super admins na plataforma.": "Latest sensitive actions performed by super admins on the platform.",
    "Carregando trilha de auditoria...": "Loading audit trail...",
    "Nenhum evento de auditoria registrado ainda.": "No audit events have been recorded yet.",
    "Quando": "When",
    "Responsável": "Actor",
    "Alvo": "Target",
    "Clínica bloqueada": "Clinic blocked",
    "Clínica liberada": "Clinic unblocked",
    "Super admin convidado": "Super admin invited",
    "Super admin desativado": "Super admin deactivated",
    "Super admin reativado": "Super admin reactivated",
    "Acesso redefinido": "Access reset",
    "Importação Zen Fisio - pacientes": "Zen Fisio patient import",
    "Importação Zen Fisio - profissionais": "Zen Fisio professional import",
    "Importação Zen Fisio - agenda": "Zen Fisio appointment import",
    "Importação Zen Fisio - financeiro": "Zen Fisio financial import",
    "Importação Zen Fisio - prontuário": "Zen Fisio clinical record import",
    "Importação Zen Fisio revertida": "Zen Fisio import rolled back",
    "Importa Zen Fisio": "Zen Fisio Import",
    "Migração por clínica": "Per-clinic migration",
    "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.": "Select the destination clinic before importing patients, professionals, appointments, financial data, or clinical records from Zen Fisio.",
    "Clínica de destino": "Destination clinic",
    "Selecione o arquivo": "Select the file",
    "Nenhuma clínica selecionada": "No clinic selected",
    "Importados": "Imported",
    "Importe o arquivo pacientes.csv preservando o isolamento multi-tenant e gerando alertas de CPF e nascimento.": "Import the pacientes.csv file while preserving multi-tenant isolation and generating alerts for CPF numbers and dates of birth.",
    "Importar pacientes": "Import patients",
    "Importando pacientes...": "Importing patients...",
    "Importe profissionais com setor automático, agenda padrão das 08:00 às 18:00, duração de 1 hora e limite de 1 paciente por horário.": "Import professionals with automatic sector assignment, a default schedule from 08:00 to 18:00, one-hour appointment duration, and a limit of one patient per time slot.",
    "Importar profissionais": "Import professionals",
    "Importando profissionais...": "Importing professionals...",
    "Importe agendamentos históricos preservando o AgendamentoId, vinculando pacientes pelo id legado e profissionais pelo nome importado anteriormente.": "Import historical appointments while preserving the AgendamentoId, linking patients by legacy ID and professionals by the name imported previously.",
    "Importar agenda": "Import appointments",
    "Importando agenda...": "Importing appointments...",
    "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.": "Import historical revenue, expenses, and receivables, grouping transactions with the same AgendamentoID and preserving partial payments.",
    "Importar financeiro": "Import financial data",
    "Importando financeiro...": "Importing financial data...",
    "Avaliações clínicas": "Clinical evaluations",
    "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.": "Import Zen Fisio evaluations as legacy Markdown records linked to the patient, professional, and appointment.",
    "Importar avaliações": "Import evaluations",
    "Importando avaliações...": "Importing evaluations...",
    "Evoluções clínicas": "Clinical notes",
    "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.": "Import Zen Fisio clinical notes by consolidating lines for each visit and ignoring empty responses.",
    "Importar evoluções": "Import clinical notes",
    "Importando evoluções...": "Importing clinical notes...",
    "Selecione um arquivo CSV.": "Select a CSV file.",
    "Aguarde a importação em andamento terminar antes de iniciar outra.": "Wait for the import in progress to finish before starting another one.",
    "Selecione a clínica de destino.": "Select the destination clinic.",
    "Importação finalizada com sucesso.": "Import completed successfully.",
    "A importação não foi concluída. Tente novamente.": "The import could not be completed. Try again.",
    "Ignorados": "Skipped",
    "Registros com erro/alerta": "Records with errors or alerts",
    "Importação finalizada": "Import completed",
    "Importação não concluída": "Import not completed",
    "Importação em andamento": "Import in progress",
    "Progresso da importação": "Import progress",
    "Não foi possível concluir a importação. Tente novamente.": "The import could not be completed. Try again.",
    "Os dados estão sendo processados.": "The data is being processed.",
    "Nova importação": "New import",
    "Alertas": "Alerts",
    "CPFs duplicados": "Duplicate CPF numbers",
    "Nascimentos bloqueados": "Blocked dates of birth",
    "Setores criados": "Sectors created",
    "Agenda padrão": "Default schedule",
    "Horários inválidos": "Invalid times",
    "Duplicados": "Duplicates",
    "Pagamentos": "Payments",
    "Agrupados": "Grouped",
    "Sem agendamento": "No appointment",
    "Grupos vazios": "Empty groups",
    "Datas inválidas": "Invalid dates",
    "Arquivo de alertas gerado": "Alert file generated",
    "Baixe o CSV para revisar CPFs duplicados, nascimentos futuros e datas inválidas bloqueadas.": "Download the CSV to review duplicate CPF numbers, future dates of birth, and blocked invalid dates.",
    "Baixe o CSV para revisar setores criados automaticamente e registros ignorados.": "Download the CSV to review automatically created sectors and skipped records.",
    "Baixe o CSV para revisar vínculos ausentes, horários inválidos, duplicidades e setores criados.": "Download the CSV to review missing links, invalid times, duplicates, and created sectors.",
    "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.": "Download the CSV to review missing links, unmatched payment methods, groupings, and skipped transactions.",
    "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.": "Download the CSV to review missing appointments, empty groups, duplicates, and invalid dates.",
    "Baixar alertas": "Download alerts",
    "{count} usuário": "{count} user",
    "{count} usuários": "{count} users",
    "{count} ativo": "{count} active",
    "{count} ativos": "{count} active",
    "{count} admin": "{count} admin",
    "{count} admins": "{count} admins",
    "A senha de {name} será redefinida e o acesso será reativado.": "The password for {name} will be reset and access will be reactivated.",
    "A senha de {name} será redefinida mantendo o status atual da conta.": "The password for {name} will be reset while keeping the account's current status.",
    "Nova senha para {name}.": "New password for {name}.",
    "Destino selecionado: {clinic}": "Selected destination: {clinic}",
    "Destino selecionado: nenhuma clínica selecionada.": "Selected destination: no clinic selected.",
    "Autenticação necessária.": "Authentication required.",
    "Esta operação exige o perfil de super admin.": "This operation requires the super admin role.",
    "Clínica não encontrada.": "Clinic not found.",
    "Este e-mail já está cadastrado.": "This email address is already registered.",
    "Super admin não encontrado.": "Super admin not found.",
    "Deve existir ao menos um super admin ativo.": "At least one super admin must remain active.",
    "Informe a clínica de destino.": "Select the destination clinic.",
    "Informe o conteúdo CSV.": "Provide the CSV content.",
    "O histórico de importação está temporariamente indisponível.": "Import history is temporarily unavailable.",
    "Lote de importação não encontrado.": "Import batch not found.",
    "Somente lotes ativos podem ser revertidos.": "Only active batches can be rolled back.",
    "O nome deve ter pelo menos {minimum} caracteres.": "The name must contain at least {minimum} characters.",
    "Informe um e-mail válido.": "Enter a valid email address.",
    "A senha deve ter pelo menos {minimum} caracteres.": "The password must contain at least {minimum} characters.",
    "A senha deve ter no máximo {maximum} caracteres.": "The password must contain at most {maximum} characters.",
    "A senha deve conter ao menos uma letra minúscula.": "The password must contain at least one lowercase letter.",
    "A senha deve conter ao menos uma letra maiúscula.": "The password must contain at least one uppercase letter.",
    "A senha deve conter ao menos um número.": "The password must contain at least one number.",
    "O CSV não contém as colunas obrigatórias: {missing_headers}.": "The CSV is missing required columns: {missing_headers}.",
    "Já existe uma importação ativa de {import_type} para esta clínica.": "There is already an active {import_type} import for this clinic.",
    "Importação bloqueada. Execute primeiro: {missing_prerequisites}.": "Import blocked. Run these first: {missing_prerequisites}.",
    "Importação bloqueada pela etapa posterior {blocking_import_type}.": "Import blocked by the later {blocking_import_type} stage.",
    "Reverta primeiro o lote posterior de {blocking_import_type}.": "Roll back the later {blocking_import_type} batch first.",
    "Não é possível reverter {entity_type}: existem {dependency_count} dependências.": "Cannot roll back {entity_type}: {dependency_count} dependencies exist.",
    "pacientes": "patients",
    "profissionais": "professionals",
    "agendamentos": "appointments",
    "A clínica": "The clinic",
    "Bloquear acesso": "Block access",
    "Clínica bloqueada com sucesso.": "Clinic blocked successfully.",
    "Clínica liberada com sucesso.": "Clinic unblocked successfully.",
    "Clínicas ativas": "Active clinics",
    "Clínicas cadastradas": "Registered clinics",
    "Conceder acesso de super admin": "Grant super admin access",
    "Confirmar convite": "Confirm invitation",
    "Convidar novo super admin": "Invite new super admin",
    "Desativar super admin": "Deactivate super admin",
    "Email": "Email",
    "Erro ao atualizar status da clínica": "Error updating clinic status",
    "Erro ao atualizar super admin": "Error updating super admin",
    "Erro ao carregar painel de super admin": "Error loading the super admin panel",
    "Erro ao convidar super admin": "Error inviting super admin",
    "Liberar acesso": "Grant access",
    "Reativar super admin": "Reactivate super admin",
    "Revogar acesso": "Revoke access",
    "Super Admin": "Super Admin",
    "Super admin convidado com sucesso.": "Super admin invited successfully.",
    "Super admin desativado com sucesso.": "Super admin deactivated successfully.",
    "Super admin reativado com sucesso.": "Super admin reactivated successfully.",
    "Super admins ativos": "Active super admins",
    "Usuários monitorados": "Monitored users",
    "com o email": "with the email",
    "passará a ter acesso global ao sistema": "will have global access to the system",
    "perderá o acesso ao painel global até nova liberação.": "will lose access to the global panel until access is granted again.",
    "terá o acesso ao sistema interrompido até nova liberação.": "will have system access suspended until the clinic is unblocked.",
    "voltará a acessar a plataforma normalmente.": "will regain normal access to the platform.",
    "voltará a acessar o painel global normalmente.": "will regain normal access to the global panel.",
  },
  es: {
    "Categorias financeiras": "Categorías financieras",
    "Gerencie separadamente as categorias usadas em receitas e despesas.": "Gestiona por separado las categorías utilizadas en ingresos y gastos.",
    "Tipo de categoria financeira": "Tipo de categoría financiera",
    "Nova categoria": "Nueva categoría",
    "Renomear uma categoria não altera o nome preservado nos lançamentos anteriores. Categorias inativas continuam no histórico, mas não podem ser usadas em novos lançamentos.": "Cambiar el nombre de una categoría no modifica el nombre conservado en registros anteriores. Las categorías inactivas permanecen en el historial, pero no pueden usarse en nuevos registros.",
    "Carregando categorias...": "Cargando categorías...",
    "Não foi possível carregar as categorias.": "No se pudieron cargar las categorías.",
    "Nenhuma categoria de receita cadastrada.": "No hay categorías de ingresos registradas.",
    "Nenhuma categoria de despesa cadastrada.": "No hay categorías de gastos registradas.",
    "Editar categoria": "Editar categoría",
    "Nome da categoria": "Nombre de la categoría",
    "Informe o nome da categoria.": "Introduce el nombre de la categoría.",
    "Categoria atualizada com sucesso.": "Categoría actualizada correctamente.",
    "Categoria cadastrada com sucesso.": "Categoría registrada correctamente.",
    "Categoria desativada com sucesso.": "Categoría desactivada correctamente.",
    "Categoria reativada com sucesso.": "Categoría reactivada correctamente.",
    "Desativar categoria": "Desactivar categoría",
    "Reativar categoria": "Reactivar categoría",
    "A categoria deixará de aparecer em novos lançamentos. O histórico será preservado.": "La categoría dejará de aparecer en nuevos registros. Se conservará el historial.",
    "A categoria voltará a aparecer em novos lançamentos.": "La categoría volverá a aparecer en nuevos registros.",
    "Categoria de receita": "Categoría de ingresos",
    "Nenhuma categoria de receita ativa cadastrada.": "No hay categorías de ingresos activas registradas.",
    "Nenhuma categoria de despesa ativa cadastrada.": "No hay categorías de gastos activas registradas.",
    "Selecione uma categoria": "Selecciona una categoría",
    "Configurar categorias": "Configurar categorías",
    "Selecione uma categoria de receita.": "Selecciona una categoría de ingresos.",
    "Selecione uma categoria de despesa.": "Selecciona una categoría de gastos.",
    "Avaliação vinculada": "Evaluación vinculada",
    "Modelo clínico registrado": "Modelo clínico registrado",
    "Editar registros de outros profissionais": "Editar registros de otros profesionales",
    "Adicionar correções a registros finalizados": "Añadir correcciones a registros finalizados",
    "Autorizações clínicas explícitas": "Autorizaciones clínicas explícitas",
    "Estas autorizações clínicas sensíveis não são concedidas automaticamente ao administrador.": "Estas autorizaciones clínicas sensibles no se conceden automáticamente al administrador.",
    "Existem alterações não salvas. Deseja descartá-las?": "Hay cambios sin guardar. ¿Desea descartarlos?",
    "Avaliação atualizada com sucesso.": "Evaluación actualizada correctamente.",
    "Este registro foi alterado por outro usuário enquanto você o editava. Atualize os dados antes de salvar novamente.": "Otro usuario modificó este registro mientras usted lo editaba. Actualice los datos antes de volver a guardarlos.",
    "Atualizar registro": "Actualizar registro",
    "Editar avaliação clínica": "Editar evaluación clínica",
    "Finalizado": "Finalizado",
    "Finalizar registro clínico": "Finalizar registro clínico",
    "Após finalizar, este registro não poderá mais ser alterado diretamente. Se for necessário corrigir alguma informação depois, será criada uma correção mantendo o conteúdo original no histórico.": "Después de finalizar, este registro ya no podrá modificarse directamente. Si es necesario corregir información, se añadirá una corrección y el contenido original permanecerá en el historial.",
    "Finalizando...": "Finalizando...",
    "Finalizar registro": "Finalizar registro",
    "Adicionar correção": "Añadir corrección",
    "O conteúdo original será preservado. A correção será acrescentada ao histórico do registro.": "El contenido original se conservará. La corrección se añadirá al historial del registro.",
    "Motivo da correção": "Motivo de la corrección",
    "Informe pelo menos 10 caracteres.": "Introduzca al menos 10 caracteres.",
    "Conteúdo da correção": "Contenido de la corrección",
    "Correção adicionada ao histórico.": "Corrección añadida al historial.",
    "Informe um motivo com pelo menos 10 caracteres.": "Introduzca un motivo de al menos 10 caracteres.",
    "Informe o conteúdo da correção.": "Introduzca el contenido de la corrección.",
    "Registro finalizado com sucesso.": "Registro finalizado correctamente.",
    "Criado por": "Creado por",
    "Autoria não disponível": "Autoría no disponible",
    "Última alteração por": "Última modificación por",
    "Finalizado por": "Finalizado por",
    "Editar avaliação": "Editar evaluación",
    "Finalizar": "Finalizar",
    "Ver histórico": "Ver historial",
    "Histórico do registro clínico": "Historial del registro clínico",
    "Linha do tempo": "Cronología",
    "Carregando histórico...": "Cargando historial...",
    "Correção adicionada": "Corrección añadida",
    "Primeiro estado disponível no histórico": "Primer estado disponible en el historial",
    "Registro criado": "Registro creado",
    "Registro finalizado": "Registro finalizado",
    "Revisão salva": "Revisión guardada",
    "Revisão": "Revisión",
    "Carregar mais": "Cargar más",
    "Este é o primeiro estado disponível após a adoção do histórico. Não há versões anteriores reconstruíveis.": "Este es el primer estado disponible tras adoptar el historial. No se pueden reconstruir versiones anteriores.",
    "versão": "versión",
    "Sem modelo clínico registrado": "Sin modelo clínico registrado",
    "Documentos desta revisão": "Documentos de esta revisión",
    "O conteúdo original foi preservado. Esta correção foi acrescentada ao histórico.": "El contenido original se conservó. Esta corrección se añadió al historial.",
    "Correção adicionada por": "Corrección añadida por",
    "Escolha uma revisão ou correção para consultar o conteúdo preservado.": "Elija una revisión o corrección para consultar el contenido conservado.",
    "Selecione um item do histórico": "Seleccione un elemento del historial",
    "Plano cancelado com segurança.": "Plan cancelado de forma segura.",
    "Plano reativado com segurança.": "Plan reactivado de forma segura.",
    "Cancelar Plano de Tratamento": "Cancelar Plan de Tratamiento",
    "Reativar Plano de Tratamento": "Reactivar Plan de Tratamiento",
    "Sessões utilizadas": "Sesiones utilizadas",
    "{count} sessão restante": "{count} sesión restante",
    "{count} sessões restantes": "{count} sesiones restantes",
    "{count} recebível": "{count} cuenta por cobrar",
    "{count} recebíveis": "{count} cuentas por cobrar",
    "{count} pagamento": "{count} pago",
    "{count} pagamentos": "{count} pagos",
    "{count} receita": "{count} ingreso",
    "{count} receitas": "{count} ingresos",
    "Verificando efeitos do cancelamento...": "Comprobando los efectos de la cancelación...",
    "Não foi possível verificar os efeitos do cancelamento. Feche o diálogo e tente novamente.": "No se pudieron comprobar los efectos de la cancelación. Cierre el diálogo e inténtelo de nuevo.",
    "Consequências do cancelamento": "Consecuencias de la cancelación",
    "Cancelar o Plano impede seu uso futuro, mas não apaga atendimentos, pagamentos, produções ou repasses já registrados. Pendências financeiras serão encaminhadas para revisão.": "Cancelar el Plan impide su uso futuro, pero no elimina atenciones, pagos, producciones ni pagos profesionales ya registrados. Las incidencias financieras se enviarán a revisión.",
    "Agendamentos futuros": "Citas futuras",
    "Saldo financeiro": "Saldo financiero",
    "Situação do NPS": "Estado del NPS",
    "Sem solicitação": "Sin solicitud",
    "Estes agendamentos serão cancelados": "Estas citas se cancelarán",
    "Financeiro preservado e enviado para revisão": "Información financiera conservada y enviada a revisión",
    "recebíveis": "cuentas por cobrar",
    "pagamentos": "pagos",
    "receitas": "ingresos",
    "pago": "pagado",
    "Efeitos da reativação": "Efectos de la reactivación",
    "Agendamentos cancelados não serão restaurados automaticamente.": "Las citas canceladas no se restaurarán automáticamente.",
    "A reativação não cria uma nova venda nem resolve pendências financeiras.": "La reactivación no crea una nueva venta ni resuelve incidencias financieras.",
    "Tokens NPS revogados não serão restaurados automaticamente.": "Los tokens NPS revocados no se restaurarán automáticamente.",
    "O status será recalculado conforme validade, venda e uso efetivo do Plano.": "El estado se recalculará según la vigencia, la venta y el uso efectivo del Plan.",
    "Motivo da reativação": "Motivo de la reactivación",
    "Descreva o motivo com detalhes": "Describa el motivo detalladamente",
    "Informe pelo menos 30 caracteres.": "Introduzca al menos 30 caracteres.",
    "Cancelando Plano...": "Cancelando Plan...",
    "Reativando Plano...": "Reactivando Plan...",
    "Confirmar cancelamento": "Confirmar cancelación",
    "Confirmar reativação": "Confirmar reactivación",
    "Reativar Plano": "Reactivar Plan",
    "Cancelar Plano": "Cancelar Plan",
    "Ciclo do atendimento": "Ciclo de la atención",
    "Este atendimento possui efeitos operacionais materializados.": "Esta atención tiene efectos operativos materializados.",
    "A conclusão registra produção, repasse e os demais efeitos aplicáveis.": "La finalización registra producción, pago profesional y los demás efectos aplicables.",
    "Concluir atendimento": "Finalizar atención",
    "Reverter conclusão": "Revertir finalización",
    "Você não possui as permissões necessárias para reverter esta conclusão.": "No tiene los permisos necesarios para revertir esta finalización.",
    "Concluir atendimento?": "¿Finalizar la atención?",
    "A conclusão registrará produção, repasse, progresso do plano e financeiro quando aplicável.": "La finalización registrará producción, pago profesional, avance del plan y efectos financieros cuando corresponda.",
    "Concluindo...": "Finalizando...",
    "Efeitos da reversão": "Efectos de la reversión",
    "A produção ativa será cancelada e preservada no histórico.": "La producción activa se cancelará y se conservará en el historial.",
    "O repasse relacionado será cancelado ou marcado para tratamento administrativo.": "El pago profesional relacionado se cancelará o se marcará para tratamiento administrativo.",
    "As sessões utilizadas e o status do plano serão recalculados.": "Se recalcularán las sesiones utilizadas y el estado del plan.",
    "Uma entrega NPS ativa poderá ser invalidada até uma nova conclusão.": "Una entrega NPS activa podrá invalidarse hasta una nueva finalización.",
    "A reversão do atendimento não cancela nem estorna automaticamente os registros financeiros existentes. Eles serão sinalizados para revisão.": "La reversión de la atención no cancela ni reembolsa automáticamente los registros financieros existentes. Se marcarán para revisión.",
    "Se existir repasse relacionado, a operação exigirá a permissão para cancelar repasses.": "Si existe un pago profesional relacionado, la operación requerirá permiso para cancelar pagos profesionales.",
    "Novo estado operacional": "Nuevo estado operativo",
    "Para remarcar, reverta primeiro e depois utilize a ação específica de remarcação.": "Para reprogramar, revierta primero la finalización y después utilice la acción específica de reprogramación.",
    "Explique por que a conclusão precisa ser revertida": "Explique por qué debe revertirse la finalización",
    "Informe pelo menos 30 caracteres para registrar a reversão.": "Introduzca al menos 30 caracteres para registrar la reversión.",
    "Confirmar reversão": "Confirmar reversión",
    "Atendimento concluído com sucesso.": "Atención finalizada correctamente.",
    "Não foi possível concluir o atendimento.": "No se pudo finalizar la atención.",
    "Conclusão revertida. Os registros financeiros relacionados foram sinalizados para revisão.": "Finalización revertida. Los registros financieros relacionados se marcaron para revisión.",
    "Conclusão revertida com sucesso.": "Finalización revertida correctamente.",
    "O repasse possui pagamentos preservados e requer tratamento administrativo.": "El pago profesional conserva pagos y requiere tratamiento administrativo.",
    "Não foi possível reverter a conclusão.": "No se pudo revertir la finalización.",
    "Utilize a ação Concluir atendimento para registrar a conclusão.": "Utilice Finalizar atención para registrar la finalización.",
    "Este atendimento já foi concluído. Utilize Reverter conclusão para alterar seu estado.": "Esta atención ya está finalizada. Utilice Revertir finalización para cambiar su estado.",
    "Restaure primeiro o agendamento para um estado operacional antes de executar esta ação.": "Restaure primero la cita a un estado operativo antes de realizar esta acción.",
    "Status atual": "Estado actual",
    "Destino após a reversão": "Destino después de la reversión",
    "Verificando efeitos da reversão...": "Verificando los efectos de la reversión...",
    "Não foi possível verificar os efeitos da reversão. Feche o diálogo e tente novamente.": "No fue posible verificar los efectos de la reversión. Cierre el diálogo e inténtelo de nuevo.",
    "Existem pagamentos de repasse relacionados. Eles serão preservados e o repasse será marcado para tratamento administrativo.": "Existen pagos profesionales relacionados. Se conservarán y el pago profesional se marcará para tratamiento administrativo.",
    "Revisão financeira necessária": "Revisión financiera necesaria",
    "Este registro financeiro foi preservado e precisa de uma decisão financeira explícita.": "Este registro financiero se conservó y requiere una decisión financiera explícita.",
    "A conclusão do atendimento foi revertida. Este registro foi preservado e precisa de decisão financeira.": "La finalización de la atención fue revertida. Este registro fue preservado y requiere una decisión financiera.",
    "Confirmar manutenção do recebível": "Confirmar mantenimiento de la cuenta por cobrar",
    "Confirme somente se a cobrança deve permanecer válida após a reversão clínica.": "Confirme únicamente si el cobro debe seguir siendo válido después de la reversión clínica.",
    "Justificativa financeira": "Justificación financiera",
    "Registre por que o recebível deve ser mantido": "Registre por qué debe mantenerse la cuenta por cobrar",
    "Confirmar manutenção": "Confirmar mantenimiento",
    "Manutenção do recebível confirmada.": "Mantenimiento de la cuenta por cobrar confirmado.",
    "Trilha de navegação": "Ruta de navegación",
    "Mais": "Más",
    "Slide anterior": "Diapositiva anterior",
    "Próximo slide": "Diapositiva siguiente",
    "Paginação": "Paginación",
    "Ir para a página anterior": "Ir a la página anterior",
    "Ir para a próxima página": "Ir a la página siguiente",
    "Mais páginas": "Más páginas",
    "Rotina clínica": "Rutina clínica",
    "Gestão": "Gestión",
    "Alternar menu Financeiro": "Alternar menú Finanzas",
    "Navegação principal": "Navegación principal",
    "Trocar paciente": "Cambiar paciente",
    "Selecione uma forma de pagamento para registrar o recebimento.": "Seleccione una forma de pago para registrar el cobro.",
    "Acompanhe recebimentos, valores a receber e despesas sem misturar caixa e competência.": "Siga cobros, cuentas por cobrar y gastos sin mezclar caja y devengo.",
    "Onde recebeu": "Dónde se cobró",
    "Recebimentos distribuídos por setor, conforme a data de pagamento.": "Cobros distribuidos por sector, según la fecha de pago.",
    "Nenhum recebimento com setor vinculado no período.": "No hay cobro con sector vinculado en el período.",
    "Quem recebeu": "Quién recibió el cobro",
    "Recebimentos distribuídos por profissional, conforme a data de pagamento.": "Cobros distribuidos por profesional, según la fecha de pago.",
    "Nenhum recebimento vinculado a profissional no período.": "No hay cobro vinculado a un profesional en el período.",
    "Leitura dos indicadores financeiros": "Cómo leer los indicadores financieros",
    "Os cards de recebimento usam a data de pagamento. As despesas usam a data de competência e não compõem resultado de caixa nesta tela.": "Las tarjetas de cobros usan la fecha de pago. Los gastos usan la fecha de devengo y no componen un resultado de caja en esta pantalla.",
    "Valor comercial confirmado; não representa recebimento.": "Valor comercial confirmado; no representa un cobro.",
    "Entrada registrada por data de pagamento.": "Entrada registrada por fecha de pago.",
    "Saldo pendente de cobrança; não representa entrada em caixa.": "Saldo pendiente de cobro; no representa una entrada de caja.",
    "Serviço executado, separado de venda e recebimento.": "Servicio realizado, separado de la venta y del cobro.",
    "A competência reconhece cada evento no período correspondente; ela é analisada separadamente do caixa.": "El devengo reconoce cada evento en el período correspondiente; se analiza por separado de la caja.",
    "A DRE e o rateio gerencial analisam competência; o rateio não altera o caixa.": "El estado de resultados gerencial y el prorrateo analizan el devengo; el prorrateo no altera la caja.",
    "Caixa bruto": "Caja bruta",
    "Caixa líquido": "Caja neta",
    "Saídas efetivas": "Salidas efectivas",
    "Resultado de caixa": "Resultado de caja",
    "Caixa líquido menos saídas efetivas, sem apuração contábil.": "Caixa líquido menos saídas efetivas, sem apuração contábil.",
    "Ticket médio de caixa": "Ticket medio de caja",
    "Recebimentos líquidos por evento de caixa": "Cobros netos por evento de caja",
    "Indicadores contábeis indisponíveis": "Indicadores contables no disponibles",
    "Relatórios de competência e indicadores unitários dependem de produção canônica, competência e custos confiáveis.": "Los informes por devengo y los indicadores unitarios dependen de registros canónicos de producción, datos de devengo fiables y costes fiables.",
    "Valor vendido/cobrado": "Valor vendido/cobrado",
    "Total estornado": "Total reembolsado",
    "Saldo vencido": "Saldo vencido",
    "Não foi possível carregar as despesas.": "No fue posible cargar los gastos.",
    "Despesa registrada.": "Gasto registrado.",
    "Recorrência criada.": "Recurrencia creada.",
    "Pagamento registrado.": "Pago registrado.",
    "Reversão operacional": "Reversión operativa",
    "Reversão registrada.": "Reversión registrada.",
    "Cancelamento operacional": "Cancelacion operativa",
    "Despesa cancelada.": "Gasto cancelado.",
    "Despesas e saídas": "Gastos y salidas",
    "Obrigações, pagamentos e resultado de caixa sem rateio automático.": "Obligaciones, pagos y resultado de caja sin asignación automática.",
    "Aberta": "Abierta",
    "Pagamento parcial": "Pago parcial",
    "Paga": "Pagada",
    "Vencida": "Vencida",
    "Direta do setor": "Directa del sector",
    "Direta do profissional": "Directa del profesional",
    "Corporativa não alocada": "Corporativa no asignada",
    "Nova despesa": "Nuevo gasto",
    "Outras": "Otras",
    "Pagamento imediato": "Pago inmediato",
    "Nova recorrência": "Nueva recurrencia",
    "Dia de vencimento": "Dia de vencimiento",
    "Criar recorrência": "Crear recurrencia",
    "Nenhuma despesa encontrada": "No se encontraron gastos",
    "Próxima": "Siguiente",
    "Produção clínica": "Producción clínica",
    "Produções geradas por atendimento concluído, separadas do financeiro e prontas para conferência operacional.": "Producciones generadas por atenciones concluidas, separadas de finanzas y listas para revisión operativa.",
    "Não foi possível carregar a produção clínica.": "No fue posible cargar la producción clínica.",
    "Não foi possível carregar os filtros de produção clínica.": "No fue posible cargar los filtros de producción clínica.",
    "Não foi possível gerar a produção clínica.": "No fue posible generar la producción clínica.",
    "Não foi possível cancelar a produção clínica.": "No fue posible cancelar la producción clínica.",
    "Produção clínica gerada com sucesso.": "Producción clínica generada correctamente.",
    "Produção clínica cancelada com sucesso.": "Producción clínica cancelada correctamente.",
    "Produções para revisar": "Producciones para revisar",
    "Produzido": "Producido",
    "Avulso": "Individual",
    "Revisar valor": "Revisar valor",
    "Filtros": "Filtros",
    "Todas": "Todas",
    "Todos": "Todos",
    "Cancelada": "Cancelada",
    "Classificação": "Clasificacion",
    "Venda do atendimento": "Venta de la cita",
    "Valor registrado no atendimento": "Valor registrado en la cita",
    "Cortesia": "Cortesía",
    "Valor do atendimento não determinado": "Valor de la cita no determinado",
    "Sessão adicional sem aditivo comercial": "Sesión adicional sin adenda comercial",
    "Venda de plano de tratamento": "Venta de plan de tratamiento",
    "Valor registrado no plano de tratamento": "Valor registrado en el plan de tratamiento",
    "Venda comercial": "Venta comercial",
    "Atendimento avulso": "Cita individual",
    "Atendimento avulso para revisão": "Cita individual para revisión",
    "Sessão adicional de plano": "Sesión adicional del plan",
    "Valor confirmado": "Valor confirmado",
    "Valor estimado": "Valor estimado",
    "Valor a revisar": "Valor para revisar",
    "Origem não identificada": "Origen no identificado",
    "Tipo não identificado": "Tipo no identificado",
    "Classificação não identificada": "Clasificación no identificada",
    "Sem venda, serviço ou valor operacional determinístico": "Sin venta, servicio ni importe operativo determinístico",
    "Motivo de revisão não identificado": "Motivo de revisión no identificado",
    "Recebimento": "Cobro",
    "Estorno": "Reembolso",
    "Ajuste": "Ajuste",
    "Lançamento manual de caixa": "Registro manual de caja",
    "Tipo de lançamento não identificado": "Tipo de movimiento no identificado",
    "Forma de pagamento não identificada": "Forma de pago no identificada",
    "confirmed": "confirmado",
    "inferred": "inferido",
    "legacy": "legado",
    "unknown": "desconocido",
    "commercial sale": "venta comercial",
    "treatment plan": "plan de tratamiento",
    "appointment snapshot": "snapshot de la atencion",
    "courtesy": "cortesia",
    "additional session requires review": "sesion adicional requiere revision",
    "Ações": "Acciones",
    "Detalhes": "Detalles",
    "Nenhuma produção clínica encontrada": "No se encontró producción clínica",
    "Detalhes da produção": "Detalles de la producción",
    "Competência": "Fecha de devengo",
    "Sessão": "Sesión",
    "Cancelar produção clínica": "Cancelar producción clínica",
    "A produção será marcada como cancelada sem alterar vendas, recebíveis ou caixa.": "La producción se marcará como cancelada sin cambiar ventas, cuentas por cobrar ni caja.",
    "Cancelar produção": "Cancelar producción",
    "Cancelando...": "Cancelando...",
    "Gerar pendentes": "Generar pendientes",
    "Atualizar": "Actualizar",
    "Configurações": "Configuraciones",
    "Central Administrativa": "Central administrativa",
    "Administração": "Administración",
    "Categorias": "Categorías",
    "Carregando configurações...": "Cargando configuraciones...",
    "Navegação de configurações": "Navegación de configuraciones",
    "Seção": "Sección",
    "Voltar": "Volver",
    "Voltar às configurações": "Volver a configuraciones",
    "Seção não encontrada": "Sección no encontrada",
    "Nenhuma configuração encontrada para este endereço.": "No se encontró ninguna configuración para esta dirección.",
    "Centralize as configurações administrativas da clínica sem duplicar cadastros ou fontes de dados.": "Centralice las configuraciones administrativas de la clínica sin duplicar registros ni fuentes de datos.",
    "Clínica": "Clínica",
    "Configurações da clínica": "Configuraciones de la clínica",
    "Dados institucionais, moeda e fuso horário da clínica.": "Datos institucionales, moneda y zona horaria de la clínica.",
    "Operação": "Operación",
    "Organize setores usados por profissionais, agenda, filtros e relatórios.": "Organice sectores usados por profesionales, agenda, filtros e informes.",
    "Status da Agenda": "Estados de la agenda",
    "Configure nomes, cores, ordem e visibilidade dos status sem alterar os estados técnicos.": "Configure nombres, colores, orden y visibilidad sin alterar los estados técnicos.",
    "Não foi possível carregar os status configurados da agenda.": "No fue posible cargar los estados configurados de la agenda.",
    "Catálogo visual de status": "Catálogo visual de estados",
    "A configuração visual altera rótulos, cores, ordem e visibilidade sem mudar os estados técnicos usados nas regras da Agenda.": "La configuración visual cambia etiquetas, colores, orden y visibilidad sin cambiar los estados técnicos usados por las reglas de la agenda.",
    "Restaurar padrão": "Restaurar predeterminado",
    "Salvar status": "Guardar estados",
    "Status da agenda salvos.": "Estados de la agenda guardados.",
    "Seu perfil não possui permissão para alterar os status da Agenda.": "Su perfil no tiene permiso para alterar los estados de la agenda.",
    "Revise nomes vazios, cores inválidas ou ordens negativas antes de salvar.": "Revise nombres vacíos, colores inválidos u órdenes negativas antes de guardar.",
    "Estado técnico": "Estado técnico",
    "Nome exibido": "Nombre mostrado",
    "Cor": "Color",
    "Ordem": "Orden",
    "Prévia": "Vista previa",
    "Serviços e Procedimentos": "Servicios y Procedimientos",
    "Gerencie o catálogo canônico usado na agenda, planos e financeiro.": "Gestione el catálogo canónico usado en agenda, planes y finanzas.",
    "Atendimento clínico": "Atención clínica",
    "Modelos clínicos": "Plantillas clínicas",
    "Crie e gerencie modelos reutilizáveis para avaliações, evoluções e outros registros clínicos.": "Cree y gestione plantillas reutilizables para evaluaciones, evoluciones y otros registros clínicos.",
    "Comercial": "Comercial",
    "Origens de Leads": "Orígenes de leads",
    "Padronize as origens usadas no funil comercial e nos filtros.": "Estandarice los orígenes usados en el embudo comercial y en los filtros.",
    "Motivos de não agendamento": "Motivos de no agendamiento",
    "Mantenha o catálogo oficial usado nos desfechos comerciais.": "Mantenga el catálogo oficial usado en los desenlaces comerciales.",
    "Financeiro": "Finanzas",
    "DRE Gerencial": "Estado de Resultados Gerencial",
    "Demonstrativo gerencial por competência, com caixa exibido em bloco comparativo separado.": "Estado gerencial por devengo, con caja mostrada en un bloque comparativo separado.",
    "Não foi possível carregar a DRE gerencial.": "No fue posible cargar el estado de resultados gerencial.",
    "Não foi possível carregar os filtros da DRE gerencial.": "No fue posible cargar los filtros del estado de resultados gerencial.",
    "Não foi possível carregar a origem da linha.": "No fue posible cargar el origen de la linea.",
    "Não foi possível carregar os detalhes da linha.": "No fue posible cargar los detalles de la línea.",
    "Não foi possível exportar a DRE gerencial.": "No fue posible exportar el estado de resultados gerencial.",
    "Aviso gerencial": "Aviso de gestión",
    "Linha": "Linea",
    "Origem dos dados": "Origen de los datos",
    "Data considerada": "Fecha considerada",
    "Detalhes de {line}": "Detalles de {line}",
    "Entender {line}": "Entender {line}",
    "Ver detalhes de {line}": "Ver detalles de {line}",
    "Nenhum detalhe encontrado": "No se encontraron detalles",
    "Despesas consideradas no rateio": "Gastos incluidos en el prorrateo",
    "Competência versus caixa": "Devengo versus caja",
    "Receita reconhecida": "Ingreso reconocido",
    "Caixa recebido": "Caja recibida",
    "Custos e despesas reconhecidos": "Costos y gastos reconocidos",
    "Qualidade dos dados": "Calidad de datos",
    "Origem da linha": "Origen de la linea",
    "Nenhuma origem encontrada": "No se encontró ningún origen",
    "Nenhum demonstrativo encontrado": "No se encontró ningún estado",
    "Visão": "Vista",
    "Visão original": "Vista original",
    "Visão pós-rateio": "Vista posterior al prorrateo",
    "Comparar": "Comparar",
    "Impacto do rateio gerencial": "Impacto del prorrateo gerencial",
    "Análise exclusivamente gerencial": "Analisis exclusivamente gerencial",
    "Análise por setor": "Análisis por sector",
    "Análise por profissional": "Análisis por profesional",
    "Análise por serviço": "Análisis por servicio",
    "DRE original": "Estado original",
    "DRE pós-rateio": "Estado posterior al prorrateo",
    "DRE comparativa": "Estado comparativo",
    "Histórico de rateios": "Historial de prorrateos",
    "Não calculável": "No calculable",
    "Cobertura do rateio": "Cobertura del prorrateo",
    "Setores impactados": "Sectores impactados",
    "Comparativo consolidado": "Comparativo consolidado",
    "Maiores impactos por setor": "Mayores impactos por sector",
    "Evolução do rateio": "Evolucion del prorrateo",
    "Período sem movimento": "Periodo sin movimiento",
    "Revisar rateios gerenciais": "Revisar prorrateos gerenciales",
    "Esta análise redistribui despesas corporativas entre setores para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa é alterado.": "Este analisis redistribuye gastos corporativos entre sectores con fines gerenciales. No se modifica ningun asiento financiero, pago ni valor de caja.",
    "Nenhum rateio gerencial ativo foi encontrado neste período.": "No se encontró ningún prorrateo gerencial activo en este período.",
    "Todas as despesas corporativas elegíveis do período foram rateadas.": "Todos los gastos corporativos elegibles del periodo fueron prorrateados.",
    "Este setor não recebeu despesas por rateio no período selecionado.": "Este sector no recibio gastos por prorrateo en el periodo seleccionado.",
    "O rateio redistribui despesas entre setores e não altera o resultado consolidado da clínica.": "El prorrateo redistribuye gastos entre sectores y no altera el resultado consolidado de la clinica.",
    "APPLIED": "Aplicado",
    "Resultado antes de rateios": "Resultado gerencial antes del prorrateo",
    "Resultado pós-rateio": "Resultado posterior al prorrateo",
    "Despesas redistribuídas": "Gastos redistribuidos",
    "Despesas rateadas": "Gastos prorrateados",
    "Despesas ainda não rateadas": "Gastos aún no prorrateados",
    "Lotes considerados": "Lotes considerados",
    "Snapshots gerenciais": "Snapshots gerenciales",
    "Diferença consolidada": "Diferencia consolidada",
    "Resultado consolidado preservado": "Resultado consolidado preservado",
    "Lote revertido": "Lote revertido",
    "Nenhum rateio aplicado no período": "Ningún prorrateo aplicado en el período",
    "Nenhum rateio ativo no período": "Ningún prorrateo activo en el período",
    "Despesa original": "Gasto original",
    "Rateio recebido": "Prorrateo recibido",
    "Despesa pós-rateio": "Gasto posterior al prorrateo",
    "Resultado original": "Resultado original",
    "Diferença": "Diferencia",
    "Despesas recebidas por rateio": "Gastos recibidos por prorrateo",
    "Esta visão redistribui despesas corporativas para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa foi alterado.": "Esta vista redistribuye gastos corporativos con fines gerenciales. No se modifico ningun asiento financiero, pago ni valor de caja.",
    "A comparação demonstra o impacto setorial do rateio. O resultado consolidado da clínica permanece inalterado.": "La comparacion muestra el impacto sectorial del prorrateo. El resultado consolidado de la clinica permanece sin cambios.",
    "Visão original sem efeitos de snapshots de rateio gerencial.": "Vista original sin efectos de snapshots de prorrateo gerencial.",
    "Despesas corporativas redistribuídas na leitura gerencial a partir de snapshots aplicados.": "Gastos corporativos redistribuidos en la lectura gerencial a partir de snapshots aplicados.",
    "Resultado gerencial por competência após redistribuição analítica de despesas corporativas aplicadas.": "Resultado gerencial por devengo despues de la redistribucion analitica de gastos corporativos aplicados.",
    "Métodos de pagamento": "Métodos de pago",
    "Configure meios, adquirentes, prazos e taxas existentes.": "Configure medios, adquirentes, plazos y tasas existentes.",
    "Regras de repasse": "Reglas de liquidación profesional",
    "Configure regras versionadas de repasse por profissional, setor ou serviço.": "Configure reglas versionadas para las liquidaciones por profesional, sector o servicio.",
    "Rateio gerencial": "Prorrateo gerencial de gastos",
    "Configure regras versionadas de rateio percentual por setor.": "Configure reglas versionadas de prorrateo porcentual de gastos por sector.",
    "Configurações comerciais": "Configuraciones comerciales",
    "Gerencie origens e motivos administrativos na Central de Configurações.": "Gestione orígenes y motivos administrativos en la Central de Configuraciones.",
    "Gerenciar origens": "Gestionar orígenes",
    "Gerenciar motivos": "Gestionar motivos",
    "Gerenciar catálogo": "Gestionar catálogo",
    "Gerenciar templates": "Gestionar plantillas",
    "Gerenciar métodos": "Gestionar métodos",
    "Use este cadastro para padronizar as origens utilizadas no funil comercial.": "Use este registro para estandarizar los orígenes utilizados en el embudo comercial.",
    "Selecione uma clínica para continuar.": "Seleccione una clínica para continuar.",
    "Dados cadastrais": "Datos de registro",
    "Estes dados vêm do cadastro canônico da clínica e não são duplicados nesta central.": "Estos datos vienen del registro canónico de la clínica y no se duplican en esta central.",
    "CNPJ": "CNPJ",
    "Preferências regionais": "Preferencias regionales",
    "A moeda e o fuso horário são compartilhados por toda a clínica.": "La moneda y la zona horaria se comparten en toda la clínica.",
    "Alterações não salvas": "Cambios sin guardar",
    "Salve as alterações antes de sair desta seção.": "Guarde los cambios antes de salir de esta sección.",
    "Salvar configurações": "Guardar configuraciones",
    "Categoria": "Categoría",
    "Não alocado": "No asignado",
    "até": "hasta",
    "Filtros avançados": "Filtros avanzados",
    "Filtros ativos": "Filtros activos",
    "Indicadores comerciais": "Indicadores comerciales",
    "Lista de leads": "Lista de leads",
    "Carregando leads...": "Cargando leads...",
    "Nenhum resultado encontrado": "No se encontraron resultados",
    "Nenhum lead cadastrado": "No hay leads registrados",
    "Ajuste ou limpe os filtros para ampliar a busca.": "Ajuste o limpie los filtros para ampliar la búsqueda.",
    "Crie um lead para começar o acompanhamento comercial.": "Cree un lead para iniciar el seguimiento comercial.",
    "Acompanhe, priorize e avance as oportunidades comerciais.": "Siga, priorice y avance las oportunidades comerciales.",
    "Localização": "Ubicación",
    "Filtrar por cidade": "Filtrar por ciudad",
    "Filtrar por bairro": "Filtrar por barrio",
    "Gênero": "Género",
    "Data de cadastro do paciente": "Fecha de registro del paciente",
    "Cadastro a partir de": "Registro desde",
    "Cadastro até antes de": "Registro hasta antes de",
    "Direção": "Dirección",
    "Crescente": "Ascendente",
    "Decrescente": "Descendente",
    "Catálogo": "Catálogo",
    "Catálogo de Serviços e Procedimentos": "Catálogo de Servicios y Procedimientos",
    "Gerencie serviços e procedimentos utilizados na agenda, no prontuário clínico, nos planos de tratamento e no financeiro, preservando os dados históricos.": "Gestione los servicios y procedimientos utilizados en la agenda, las historias clínicas, los planes de tratamiento y el área financiera, preservando los datos históricos.",
    "Novo item do catálogo": "Nuevo ítem del catálogo",
    "Item do catálogo atualizado com sucesso.": "Ítem del catálogo actualizado correctamente.",
    "Item do catálogo cadastrado com sucesso.": "Ítem del catálogo creado correctamente.",
    "Item do catálogo inativado com sucesso.": "Ítem del catálogo desactivado correctamente.",
    "Item do catálogo reativado com sucesso.": "Ítem del catálogo reactivado correctamente.",
    "Não foi possível salvar o item do catálogo.": "No fue posible guardar el ítem del catálogo.",
    "Não foi possível alterar o status do item do catálogo.": "No fue posible cambiar el estado del ítem del catálogo.",
    "Não foi possível carregar o catálogo.": "No fue posible cargar el catálogo.",
    "Serviço": "Servicio",
    "Procedimento": "Procedimiento",
    "Serviço ou procedimento": "Servicio o procedimiento",
    "Serviço não informado": "Servicio no informado",
    "Sem item do catálogo": "Sin ítem del catálogo",
    "Outro lançamento": "Otro movimiento",
    "Todos os tipos": "Todos los tipos",
    "Buscar por nome ou descrição": "Buscar por nombre o descripción",
    "Duração": "Duración",
    "Preço padrão": "Precio predeterminado",
    "Uso histórico": "Uso histórico",
    "Toda a clínica": "Toda la clínica",
    "Nenhum item do catálogo encontrado.": "No se encontró ningún ítem del catálogo.",
    "Editar item do catálogo": "Editar ítem del catálogo",
    "Cadastrar item do catálogo": "Crear ítem del catálogo",
    "Alterações no catálogo não modificam agendamentos, planos de tratamento ou lançamentos financeiros já registrados.": "Los cambios en el catálogo no afectan las citas, los planes de tratamiento ni los movimientos financieros ya registrados.",
    "Carregando serviços...": "Cargando servicios...",
    "Sugestão": "Sugerencia",
    "Agendamentos antigos podem permanecer sem serviço.": "Las citas antiguas pueden permanecer sin servicio.",
    "O preço padrão é apenas uma sugestão; o valor efetivo fica salvo no lançamento.": "El precio predeterminado es solo una sugerencia; el valor efectivo queda guardado en el movimiento.",
    "Buscar": "Buscar",
    "Agendamento": "Cita",
    "Sim": "Sí",
    "Não": "No",
    "Recebido": "Recibido",
    "Receber": "Cobrar",
    "Em aberto": "Abierto",
    "Quantidade": "Cantidad",
    "Histórico financeiro": "Historial financiero",
    "Nenhuma receita encontrada para este paciente.": "No se encontraron ingresos para este paciente.",
    "Todos os setores": "Todas las áreas",
    "Filtrar por setor": "Filtrar por sector",
    "Setor inativo": "Sector inactivo",
    "Setor selecionado": "Sector seleccionado",
    "Resultado direto do setor": "Resultado directo del sector",
    "Despesas atribuídas": "Gastos asignados",
    "Despesas a pagar": "Gastos por pagar",
    "Despesas vencidas": "Gastos vencidos",
    "Despesas pagas": "Gastos pagados",
    "Despesas corporativas": "Gastos corporativos",
    "Despesas diretas": "Gastos directos",
    "Repasses pagos": "Liquidaciones pagadas",
    "Dados não atribuídos": "Datos no asignados",
    "Nenhum setor encontrado": "No se encontró ningún sector",
    "Setores": "Áreas",
    "Ticket médio por lançamento": "Ingreso medio por asiento",
    "Requer custo realizado atribuído às aquisições": "Requiere coste realizado atribuido a las adquisiciones",
    "Receita média por paciente pagante": "Ingreso medio por paciente pagador",
    "Receita realizada no período; não representa LTV": "Ingreso realizado del período; no representa LTV",
    "Requer custo e receita atribuídos à campanha": "Requiere coste e ingresos atribuidos a la campaña",
    "Erro ao carregar setores": "Error al cargar las áreas",
    "Já existe um setor com este nome": "Ya existe un área con este nombre",
    "O nome do setor deve ter entre 2 e 80 caracteres": "El nombre del área debe tener entre 2 y 80 caracteres",
    "Selecione um setor ativo da clínica": "Seleccione un área activa de la clínica",
    "Selecione um setor da clínica": "Seleccione un área de la clínica",
    "Setor não encontrado": "Área no encontrada",
    "Informe ao menos uma alteração": "Introduzca al menos un cambio",
    "Idioma": "Idioma",
    "Idioma da interface": "Idioma de la interfaz",
    "Moeda": "Moneda",
    "Moeda do sistema": "Moneda del sistema",
    "Configuração regional da clínica": "Configuración regional de la clínica",
    "Configuração regional salva": "Configuración regional guardada",
    "A moeda e o fuso são compartilhados por toda a clínica.": "La moneda y la zona horaria se comparten en toda la clínica.",
    "Somente administradores podem alterar esta configuração.": "Solo los administradores pueden cambiar esta configuración.",
    "A moeda está bloqueada porque já existe histórico financeiro.": "La moneda está bloqueada porque ya existe historial financiero.",
    "Alterar a moeda não converte valores existentes.": "Cambiar la moneda no convierte los valores existentes.",
    "Fuso horário": "Zona horaria",
    "São Paulo (América)": "São Paulo (América)",
    "Nova York (América)": "Nueva York (América)",
    "Lisboa (Europa)": "Lisboa (Europa)",
    "Madri (Europa)": "Madrid (Europa)",
    "Roma (Europa)": "Roma (Europa)",
    "Buscar cidade ou fuso": "Buscar ciudad o zona horaria",
    "O fuso define dias, horários da agenda e períodos dos indicadores; instantes salvos não são reescritos.": "La zona horaria define días, citas y periodos de indicadores; los instantes guardados no se reescriben.",
    "Preferências": "Preferencias",
    "Abrir preferências": "Abrir preferencias",
    "Ajuste idioma, moeda e tema da interface.": "Ajuste el idioma, la moneda y el tema de la interfaz.",
    "Ajuste idioma e tema da interface.": "Ajuste el idioma y el tema de la interfaz.",
    "Tema": "Tema",
    "Tema da interface": "Tema de la interfaz",
    "América Latina": "América Latina",
    "Europa": "Europa",
    "Português": "Portugués",
    "Inglês": "Inglés",
    "Espanhol": "Español",
    "Dashboard": "Dashboard",
    "Leads": "Leads",
    "Abrir lead": "Abrir lead",
    "Abrir próximo follow-up": "Abrir próximo follow-up",
    "Campanhas, agendamentos e desempenho comercial.": "Campañas, citas y desempeño comercial.",
    "Data do Pagamento": "Fecha de pago",
    "Editar": "Editar",
    "Erro ao carregar dados de marketing": "Error al cargar los datos de marketing",
    "Erro ao carregar leads": "Error al cargar los leads",
    "Erro ao excluir lead": "Error al eliminar el lead",
    "Excluir": "Eliminar",
    "Excluir lead": "Eliminar lead",
    "Filtrar status": "Filtrar estado",
    "Leads por Origem": "Leads por origen",
    "Nenhum dado disponível": "Sin datos disponibles",
    "Nenhum follow-up pendente no momento.": "No hay follow-ups pendientes en este momento.",
    "Nova Despesa": "Nuevo gasto",
    "Nova Receita": "Nuevo ingreso",
    "Novo Lead": "Nuevo lead",
    "Paciente": "Paciente",
    "Receita registrada com sucesso!": "Ingreso registrado correctamente.",
    "Receitas": "Ingresos",
    "Resumo Financeiro": "Resumen financiero",
    "Central Financeira do Paciente": "Centro financiero del paciente",
    "Visão filtrada dos lançamentos financeiros vinculados ao paciente.": "Vista filtrada de los movimientos financieros vinculados al paciente.",
    "Novo lançamento": "Nuevo movimiento",
    "Receita total do paciente": "Ingresos totales del paciente",
    "Total recebido": "Total recibido",
    "Total em aberto": "Total pendiente",
    "Próximo vencimento": "Próximo vencimiento",
    "Planos ativos": "Planes activos",
    "Pendências financeiras": "Pendientes financieros",
    "Lançamentos em aberto, vencidos ou parcialmente pagos.": "Movimientos abiertos, vencidos o parcialmente pagados.",
    "pendência(s)": "pendiente(s)",
    "Nenhuma pendência financeira para este paciente.": "No hay pendientes financieros para este paciente.",
    "Saldo pendente": "Saldo pendiente",
    "Histórico financeiro do paciente": "Historial financiero del paciente",
    "Receitas e valores a receber filtrados pelo paciente aberto.": "Ingresos y cuentas por cobrar filtrados por el paciente abierto.",
    "Buscar por descrição, profissional, valor, data ou status": "Buscar por descripción, profesional, importe, fecha o estado",
    "Pagos": "Pagados",
    "Editar lançamento": "Editar movimiento",
    "Tipo do lançamento": "Tipo de movimiento",
    "Consulta avulsa": "Consulta individual",
    "Outro lançamento personalizado": "Otro movimiento personalizado",
    "Selecionar plano existente": "Seleccionar plan existente",
    "Selecione um plano": "Seleccione un plan",
    "Sem plano vinculado": "Sin plan vinculado",
    "Sem profissional vinculado": "Sin profesional vinculado",
    "Sem agendamento vinculado": "Sin cita vinculada",
    "Receber pagamento": "Registrar pago",
    "Valor recebido": "Valor recibido",
    "Data do recebimento": "Fecha de recepción",
    "Detalhes do lançamento": "Detalles del movimiento",
    "Plano vinculado": "Plan vinculado",
    "Agendamento vinculado": "Cita vinculada",
    "Cancelar lançamento": "Cancelar movimiento",
    "Este agendamento já possui lançamento financeiro vinculado.": "Esta cita ya tiene un movimiento financiero vinculado.",
    "Este plano já possui lançamento financeiro vinculado.": "Este plan ya tiene un movimiento financiero vinculado.",
    "Informe um valor válido.": "Informe un valor válido.",
    "Status do lead atualizado!": "Estado del lead actualizado.",
    "Todos os profissionais": "Todos los profesionales",
    "Marketing": "Marketing",
    "Pacientes": "Pacientes",
    "Cadastro e prontuário de pacientes": "Registro e historial clínico de pacientes",
    "Cadastro e prontuário do paciente": "Registro e historial clínico del paciente",
    "Novo Paciente": "Nuevo paciente",
    "Tipo de tratamento": "Tipo de tratamiento",
    "Tipo de atendimento": "Tipo de atención",
    "Tipo não informado": "Tipo no indicado",
    "Todos os tipos de tratamento": "Todos los tipos de tratamiento",
    "Iniciar atendimento": "Iniciar atención",
    "Cadastre uma avaliação antes de iniciar uma evolução.": "Registre una evaluación antes de iniciar una evolución clínica.",
    "Data da avaliação": "Fecha de la evaluación",
    "Última evolução": "Última evolución",
    "Status do paciente": "Estado del paciente",
    "Paciente ativado com sucesso!": "Paciente activado correctamente.",
    "Paciente inativado com sucesso!": "Paciente desactivado correctamente.",
    "Motivos de Não Agendamento": "Motivos de no agendamiento",
    "Principais objeções dos leads que não realizaram um agendamento.": "Principales objeciones que impiden que los leads agenden una cita.",
    "Leads não agendados": "Leads sin cita",
    "Leads não agendados por origem": "Leads sin cita por origen",
    "Distribuição server-side dos leads com desfecho não agendado.": "Distribución server-side de los leads con resultado no agendado.",
    "Principal motivo": "Motivo principal",
    "Representatividade": "Porcentaje",
    "Nenhum motivo registrado": "Ningún motivo registrado",
    "Nenhum motivo de não agendamento encontrado para os filtros selecionados.": "No se encontraron motivos de no agendamiento para los filtros seleccionados.",
    "Leads não agendados por motivo": "Leads sin cita por motivo",
    "Detalhamento dos leads não agendados": "Detalle de los leads sin cita",
    "Data do desfecho": "Fecha del resultado",
    "Desfecho registrado em": "Resultado registrado el",
    "Desfechos sem data definida": "Resultados sin fecha definida",
    "Cobertura histórica incompleta": "Cobertura histórica incompleta",
    "Registros legados": "Registros heredados",
    "Dados anteriores não possuem data confiável.": "Los datos anteriores no tienen una fecha confiable.",
    "Não foi possível determinar a data": "No fue posible determinar la fecha",
    "Sem origem": "Sin origen",
    "Sem motivo": "Sin motivo",
    "Origem inativa": "Origen inactivo",
    "Comercial responsável": "Responsable comercial",
    "Último follow-up": "Último seguimiento",
    "Status comercial": "Estado comercial",
    "Nenhum lead encontrado para este motivo.": "No se encontraron leads para este motivo.",
    "Catálogo de Motivos de Não Agendamento": "Catálogo de motivos de no agendamiento",
    "Padronize as objeções utilizadas no acompanhamento comercial dos leads.": "Estandariza las objeciones utilizadas en el seguimiento comercial de los leads.",
    "Novo motivo de não agendamento": "Nuevo motivo de no agendamiento",
    "Motivo adicionado com sucesso!": "Motivo añadido correctamente.",
    "Motivo atualizado com sucesso!": "Motivo actualizado correctamente.",
    "Motivo ativado com sucesso!": "Motivo activado correctamente.",
    "Motivo inativado com sucesso!": "Motivo desactivado correctamente.",
    "Adicionar motivo": "Añadir motivo",
    "Motivo": "Motivo",
    "Ativar": "Activar",
    "Inativar": "Desactivar",
    "Editar motivo de não agendamento": "Editar motivo de no agendamiento",
    "Inativar motivo?": "¿Desactivar el motivo?",
    "O motivo deixará de aparecer em novos cadastros, mas continuará preservado no histórico.": "El motivo dejará de aparecer en nuevos registros, pero se conservará en el historial.",
    "O motivo deve ter entre 2 e 100 caracteres": "El motivo debe tener entre 2 y 100 caracteres",
    "Já existe um motivo de não agendamento com este nome": "Ya existe un motivo de no agendamiento con este nombre",
    "Motivo de não agendamento não encontrado": "No se encontró el motivo de no agendamiento",
    "Selecione um motivo de não agendamento ativo": "Selecciona un motivo de no agendamiento activo",
    "Não respondeu": "No respondió",
    "Preço / Achou caro": "Precio / Le pareció caro",
    "Queria convênio": "Quería usar el seguro médico",
    "Sem horário compatível": "Sin horario compatible",
    "Vai pensar": "Lo va a pensar",
    "Já fechou em outra clínica": "Eligió otra clínica",
    "Nenhum atendimento encontrado para o tipo e período selecionados.": "No se encontraron atenciones para el tipo y el período seleccionados.",
    "Buscar paciente existente": "Buscar paciente existente",
    "Cadastrar novo paciente": "Registrar nuevo paciente",
    "Buscar paciente por nome, telefone ou CPF": "Buscar paciente por nombre, telefono o CPF",
    "Buscar paciente": "Buscar paciente",
    "Digite o nome do paciente": "Escriba el nombre del paciente",
    "Buscando pacientes...": "Buscando pacientes...",
    "Não foi possível buscar os pacientes. Tente novamente.": "No se pudieron buscar los pacientes. Inténtelo de nuevo.",
    "Selecione um paciente e um profissional.": "Seleccione un paciente y un profesional.",
    "Busca por nome, telefone ou CPF": "Buscar por nombre, teléfono o CPF",
    "Busca por nome, telefone, CPF, gênero, cidade ou bairro": "Buscar por nombre, teléfono, CPF, género, ciudad o barrio",
    "Erro ao carregar dados do paciente": "Error al cargar los datos del paciente",
    "Carregando...": "Cargando...",
    "Carregando pacientes...": "Cargando pacientes...",
    "Nenhuma despesa encontrada no período selecionado.": "No se encontraron gastos en el período seleccionado.",
    "Nenhum paciente": "No hay pacientes",
    "Nenhum paciente encontrado": "No se encontraron pacientes",
    "Nascimento": "Fecha de nacimiento",
    "Cadastro": "Registro",
    "Ver": "Ver",
    "Paciente criado com sucesso!": "¡Paciente creado con éxito!",
    "Paciente existente selecionado.": "Paciente existente seleccionado.",
    "Encontramos um ou mais pacientes com dados semelhantes nesta clínica. Revise os cadastros antes de continuar.": "Encontramos uno o más pacientes con datos similares en esta clínica. Revise los registros antes de continuar.",
    "Já existe um cadastro com este CPF nesta clínica. Como bases históricas podem conter duplicidades, você pode selecionar o cadastro existente ou continuar a criação após revisar os dados.": "Ya existe un registro con este CPF en esta clínica. Como las bases históricas pueden contener duplicados, puede seleccionar el registro existente o continuar la creación tras revisar los datos.",
    "Confiança": "Confianza",
    "Critérios": "Criterios",
    "Usar este cadastro": "Usar este registro",
    "Revisei e preciso criar um novo cadastro": "Lo revisé y necesito crear un nuevo registro",
    "Continuar criação": "Continuar creación",
    "Paciente atualizado com sucesso!": "¡Paciente actualizado con éxito!",
    "Editar Paciente": "Editar paciente",
    "RG": "Documento secundario",
    "Estado civil": "Estado civil",
    "Selecione o estado civil": "Seleccione el estado civil",
    "Solteiro(a)": "Soltero(a)",
    "Casado(a)": "Casado(a)",
    "União Estável": "Unión de hecho",
    "Divorciado(a)": "Divorciado(a)",
    "Viúvo(a)": "Viudo(a)",
    "Separado(a)": "Separado(a)",
    "Prefiro não informar": "Prefiero no informar",
    "Profissão": "Profesión",
    "Convênio e filiação": "Seguro médico y filiación",
    "Número do convênio": "Número del seguro médico",
    "CNS": "Tarjeta nacional de salud",
    "Nome da mãe": "Nombre de la madre",
    "Documento da mãe": "Documento de la madre",
    "Nome do pai": "Nombre del padre",
    "Documento do pai": "Documento del padre",
    "Filiação": "Filiación",
    "Texto livre. Use vírgulas, espaços ou uma linha por item.": "Texto libre. Use comas, espacios o una línea por elemento.",
    "Masculino": "Masculino",
    "Feminino": "Femenino",
    "Logradouro": "Calle",
    "Complemento": "Complemento",
    "Contato de Emergência": "Contacto de emergencia",
    "Histórico Médico / Observações": "Historia clínica / Observaciones",
    "Cadastrar": "Registrar",
    "Erro ao carregar planos de tratamento": "Error al cargar los planes de tratamiento",
    "Carregando planos de tratamento...": "Cargando planes de tratamiento...",
    "Buscando endereço pelo CEP...": "Buscando dirección por código postal...",
    "Plano cancelado com sucesso!": "¡Plan cancelado con éxito!",
    "Plano reativado com sucesso!": "¡Plan reactivado con éxito!",
    "Planos de tratamento": "Planes de tratamiento",
    "Acompanhe venda, execução e saldo de sessões do paciente.": "Acompañe la venta, la ejecución y el saldo de sesiones del paciente.",
    "Novo plano": "Nuevo plan",
    "Nenhum plano cadastrado para este paciente ainda.": "Aún no hay planes registrados para este paciente.",
    "Profissional": "Profesional",
    "Venda em": "Venta realizada el",
    "Venda ainda não registrada": "Venta aún no registrada",
    "Sessões": "Sesiones",
    "Saldo": "Saldo",
    "restantes": "restantes",
    "Valor": "Importe",
    "Valor bruto": "Importe bruto",
    "A receber": "Cuentas por cobrar",
    "À Receber": "Cuentas por cobrar",
    "Novo valor a receber": "Nueva cuenta por cobrar",
    "Novo à receber": "Nueva cuenta por cobrar",
    "Editar valor a receber": "Editar cuenta por cobrar",
    "Editar à receber": "Editar cuenta por cobrar",
    "Gerar valor a receber": "Crear cuenta por cobrar",
    "Gerar à receber": "Crear cuenta por cobrar",
    "Valor a receber gerado com sucesso": "Cuenta por cobrar creada con éxito",
    "À receber gerado com sucesso": "Cuenta por cobrar creada con éxito",
    "À receber gerado": "Cuenta por cobrar creada",
    "À receber já gerado": "Cuenta por cobrar ya creada",
    "Valor a receber criado com sucesso.": "Cuenta por cobrar creada con éxito.",
    "À receber criado com sucesso.": "Cuenta por cobrar creada con éxito.",
    "Valor a receber atualizado com sucesso.": "Cuenta por cobrar actualizada con éxito.",
    "À receber atualizado com sucesso.": "Cuenta por cobrar actualizada con éxito.",
    "Valor a receber cancelado com sucesso.": "Cuenta por cobrar cancelada con éxito.",
    "À receber cancelado com sucesso.": "Cuenta por cobrar cancelada con éxito.",
    "Controle valores pendentes, vencidos e recebimentos parciais sem misturar com receitas já recebidas.": "Controle valores pendientes, vencidos y pagos parciales sin mezclarlos con ingresos ya recibidos.",
    "Total a receber": "Total por cobrar",
    "Total à receber": "Total por cobrar",
    "Total vencido": "Total vencido",
    "Recebido no período": "Recibido en el período",
    "Total parcial": "Total parcial",
    "Pacientes com pendência": "Pacientes con saldo pendiente",
    "Todos os pacientes": "Todos los pacientes",
    "Todos status": "Todos los estados",
    "Todos pacientes": "Todos los pacientes",
    "Todas as formas": "Todas las formas",
    "Forma de pagamento prevista": "Forma de pago prevista",
    "Pagamento previsto": "Pago previsto",
    "Sem forma prevista": "Sin forma prevista",
    "Sem forma definida": "Sin forma definida",
    "Data do pagamento ou vencimento": "Fecha de pago o vencimiento",
    "Valor total": "Importe total",
    "Saldo restante": "Saldo restante",
    "Marcar como recebido": "Marcar como recibido",
    "Registrar pagamento parcial": "Registrar pago parcial",
    "Registrar baixa": "Registrar pago",
    "Recebimento registrado com sucesso.": "Pago registrado con éxito.",
    "Recebimento parcial registrado com sucesso.": "Pago parcial registrado con éxito.",
    "Nenhum valor a receber encontrado com os filtros selecionados.": "No se encontró ninguna cuenta por cobrar con los filtros seleccionados.",
    "Nenhum valor à receber encontrado com os filtros selecionados.": "No se encontró ninguna cuenta por cobrar con los filtros seleccionados.",
    "Detalhes do valor a receber": "Detalles de la cuenta por cobrar",
    "Detalhes do à receber": "Detalles de la cuenta por cobrar",
    "Nenhuma baixa registrada.": "No hay pagos registrados.",
    "Valor a receber indisponível": "Cuenta por cobrar no disponible",
    "À receber indisponível": "Cuenta por cobrar no disponible",
    "Gerando...": "Creando...",
    "Vencimento": "Vencimiento",
    "Vencido": "Vencido",
    "Pago": "Pagado",
    "Cancelado": "Cancelada",
    "Valor líquido": "Importe neto",
    "Valor da receita": "Importe del ingreso",
    "Validade": "Validez",
    "Sem prazo": "Sin plazo",
    "Venda registrada no financeiro.": "Venta registrada en Finanzas.",
    "Plano ainda sem receita vinculada no financeiro.": "El plan aún no tiene un ingreso vinculado en Finanzas.",
    "Rascunho": "Borrador",
    "Vendido": "Vendido",
    "Em execução": "En ejecución",
    "Renovar": "Renovar",
    "Reativar": "Reactivar",
    "Plano de tratamento atualizado com sucesso!": "¡Plan de tratamiento actualizado con éxito!",
    "Renovação do plano criada com sucesso!": "¡Renovación del plan creada con éxito!",
    "Plano de tratamento cadastrado com sucesso!": "¡Plan de tratamiento registrado con éxito!",
    "Editar Plano de Tratamento": "Editar plan de tratamiento",
    "Renovar Plano de Tratamento": "Renovar plan de tratamiento",
    "Novo Plano de Tratamento": "Nuevo plan de tratamiento",
    "Ex.: Reabilitação lombar": "Ej.: Rehabilitación lumbar",
    "Selecione o profissional": "Seleccione el profesional",
    "Quantidade de sessões": "Cantidad de sesiones",
    "Validade inicial": "Inicio de vigencia",
    "Validade final": "Fin de vigencia",
    "Descrição": "Descripción",
    "Objetivo do tratamento, observações e combinados.": "Objetivo del tratamiento, observaciones y acuerdos.",
    "Registrar nova venda na renovação": "Registrar nueva venta en la renovación",
    "Registrar venda do plano agora": "Registrar la venta del plan ahora",
    "Quando marcado, o financeiro recebe uma receita de pacote vinculada ao plano.": "Cuando está marcado, se registra en Finanzas un ingreso de paquete vinculado al plan.",
    "Forma de pagamento": "Forma de pago",
    "Selecione uma forma de pagamento": "Seleccione una forma de pago",
    "Selecione uma forma de pagamento.": "Seleccione una forma de pago.",
    "Nenhuma forma de pagamento ativa": "No hay formas de pago activas",
    "Cadastre uma forma de pagamento ativa antes de registrar receitas.": "Registre una forma de pago activa antes de registrar ingresos.",
    "Forma de pagamento não cadastrada ({method})": "Forma de pago no registrada ({method})",
    "{name} (inativa)": "{name} (inactiva)",
    "Registro histórico": "Registro histórico",
    "Forma inativa": "Forma inactiva",
    "Vínculo histórico indisponível": "Vínculo histórico no disponible",
    "Forma de pagamento histórica não vinculada": "Forma de pago histórica no vinculada",
    "Convênio/cobertura": "Convenio/cobertura",
    "Identificador técnico: {identifier}": "Identificador técnico: {identifier}",
    "Parcela {current} de {total} da obrigação": "Cuota {current} de {total} de la obligación",
    "Parcelas": "Cuotas",
    "Parcelas do meio de pagamento": "Cuotas del medio de pago",
    "Indisponível": "No disponible",
    "Valor líquido histórico indisponível": "Valor neto histórico no disponible",
    "Operações anteriores à preservação histórica podem não possuir detalhamento de taxas.": "Las operaciones anteriores a la preservación histórica pueden no incluir el detalle de las tasas.",
    "Cartão de crédito à vista": "Tarjeta de crédito al contado",
    "Resumo do período": "Resumen del período",
    "Taxas/descontos": "Tasas/descuentos",
    "Observação opcional": "Observación opcional",
    "Total no período": "Total del período",
    "Atendimento - Paciente": "Cita - Paciente",
    "Nenhuma receita encontrada para este profissional no período selecionado.": "No se encontraron ingresos para este profesional en el período seleccionado.",
    "Nenhuma receita encontrada com os filtros selecionados.": "No se encontraron ingresos con los filtros seleccionados.",
    "Data da venda": "Fecha de venta",
    "Descrição financeira": "Descripción financiera",
    "Salvar plano": "Guardar plan",
    "Criar renovação": "Crear renovación",
    "Cadastrar plano": "Registrar plan",
    "Profissionais": "Profesionales",
    "Gestão de corpo clínico e especialidades": "Gestión del equipo clínico y especialidades",
    "Novo Profissional": "Nuevo profesional",
    "Editar Profissional": "Editar profesional",
    "Profissional cadastrado com sucesso!": "¡Profesional registrado con éxito!",
    "Profissional atualizado com sucesso!": "¡Profesional actualizado con éxito!",
    "Erro ao carregar profissionais": "Error al cargar profesionales",
    "Nenhum profissional encontrado": "No se encontró ningún profesional",
    "Catálogo de setores": "Catálogo de sectores",
    "Use este cadastro para padronizar leads, profissionais e financeiro.": "Use este registro para estandarizar leads, profesionales y finanzas.",
    "Setor removido com sucesso!": "¡Sector eliminado con éxito!",
    "Setor adicionado com sucesso!": "¡Sector agregado con éxito!",
    "Erro ao remover setor": "Error al eliminar sector",
    "Erro ao adicionar setor": "Error al agregar sector",
    "Novo setor": "Nuevo sector",
    "Adicionar setor": "Agregar sector",
    "Buscar por nome, setor ou especialidade...": "Buscar por nombre, sector o especialidad...",
    "Especialidade": "Especialidad",
    "Especialidades": "Especialidades",
    "Registro": "Registro",
    "Cor na agenda": "Color en el calendario",
    "Duração da consulta": "Duración de la consulta",
    "A duração da consulta deve ser de pelo menos 15 minutos.": "La duración de la consulta debe ser de al menos 15 minutos.",
    "Limite de pacientes por horário": "Límite de pacientes por horario",
    "Limite por horário": "Límite por horario",
    "Minutos": "Minutos",
    "Ex.: Traumato, Ortopedia": "Ej.: Trauma, Ortopedia",
    "Horários / Agenda": "Horarios / Calendario",
    "Configure a capacidade semanal do profissional. Isso alimenta a ocupação percentual da agenda.": "Configure la capacidad semanal del profesional. Esto alimenta el porcentaje de ocupación del calendario.",
    "Segunda": "Lunes",
    "Terça": "Martes",
    "Quarta": "Miércoles",
    "Quinta": "Jueves",
    "Sexta": "Viernes",
    "Sábado": "Sábado",
    "Domingo": "Domingo",
    "Adicionar faixa": "Agregar franja",
    "Sem horários configurados neste dia.": "No hay horarios configurados para este día.",
    "Sem horários disponíveis": "No hay horarios disponibles",
    "Agenda": "Calendario",
    "Gerencie os agendamentos da clínica": "Gestione el calendario de citas de la clínica",
    "Novo Agendamento": "Nueva cita",
    "Hoje": "Hoy",
    "Ontem": "Ayer",
    "Esta semana": "Esta semana",
    "Últimos 7 dias": "Últimos 7 días",
    "Este mês": "Este mes",
    "Este ano": "Este año",
    "Mês seguinte": "Mes siguiente",
    "Mês anterior": "Mes anterior",
    "Por período": "Por período",
    "Todos profissionais": "Todos los profesionales",
    "Ver detalhes": "Ver detalles",
    "Carregando dados...": "Cargando datos...",
    "Erro ao carregar dados.": "Error al cargar los datos.",
    "Nenhum Lead encontrado": "No se encontraron Leads",
    "Erro ao carregar agendamentos": "Error al cargar las citas",
    "Horário inválido": "Horario inválido",
    "Não é possível criar agendamentos em datas anteriores à data atual.": "No es posible crear citas en fechas anteriores a la fecha actual.",
    "Este profissional já atingiu o limite de pacientes para este horário. Escolha outro horário ou ajuste o limite no cadastro do profissional.": "Este profesional ya alcanzó el límite de pacientes para este horario. Elija otro horario o ajuste el límite en el registro del profesional.",
    "Este profissional não possui faixa de horário cadastrada para o período selecionado. Ajuste a agenda do profissional ou escolha outro horário.": "Este profesional no tiene horarios configurados para el período seleccionado. Ajuste la agenda del profesional o elija otro horario.",
    "Agendado com sucesso": "¡Cita programada con éxito!",
    "Agendamento atualizado": "¡Cita actualizada!",
    "Receita gerada com sucesso": "¡Ingreso registrado con éxito!",
    "Plano de tratamento": "Plan de tratamiento",
    "Renovação do plano": "Renovación del plan",
    "Selecionar plano": "Seleccionar plan",
    "Sem plano": "Sin plan",
    "sessões restantes": "sesiones restantes",
    "validade até": "vigente hasta",
    "Nenhum plano vendido ou ativo disponível para este paciente e profissional.": "No hay planes vendidos o activos disponibles para este paciente y este profesional.",
    "Selecione paciente e profissional para usar um plano.": "Seleccione un paciente y un profesional para usar un plan.",
    "Início": "Inicio",
    "Término": "Fin",
    "Intervalos de": "Intervalos de",
    "minutos": "minutos",
    "Atendimentos vinculados ao plano ficam com valor por sessão zerado. O faturamento entra na venda do plano.": "Las citas vinculadas al plan quedan con importe por sesión igual a cero. La facturación se registra en la venta del plan.",
    "Agendar": "Programar",
    "Detalhes do Agendamento": "Detalles de la cita",
    "Criado em": "Creado el",
    "Histórico de importação": "Historial de importación",
    "A reversão deve seguir a ordem inversa da importação para preservar os vínculos.": "La reversión debe seguir el orden inverso de importación para preservar los vínculos.",
    "Nenhuma importação registrada para esta clínica.": "No hay importaciones registradas para esta clínica.",
    "Aplicação revertida": "Aplicación revertida",
    "Rastreáveis": "Rastreables",
    "Revertido em": "Revertido el",
    "Reverter": "Revertir",
    "Revertendo...": "Revirtiendo...",
    "Reverter este lote removerá os registros importados por ele. Deseja continuar?": "Revertir este lote eliminará los registros que importó. ¿Desea continuar?",
    "CPFs inválidos legados": "CPF heredados inválidos",
    "Reprocessados por ID externo": "Reprocesados por ID externo",
    "Importação revertida com sucesso.": "Importación revertida con éxito.",
    "registros removidos": "registros eliminados",
    "Erro ao carregar histórico de importação.": "Error al cargar el historial de importación.",
    "Erro ao reverter importação.": "Error al revertir la importación.",
    "Importação bloqueada pela ordem de dependência. Consulte o histórico da clínica.": "Importación bloqueada por el orden de dependencias. Consulte el historial de la clínica.",
    "Remarcado": "Reprogramada",
    "Remarcar agendamento": "Reprogramar cita",
    "Selecione a nova data, horário e profissional para atualizar o agendamento.": "Seleccione la nueva fecha, horario y profesional para actualizar la cita.",
    "Nova data": "Nueva fecha",
    "Informe a nova data, horário e profissional para remarcar.": "Informe la nueva fecha, horario y profesional para reprogramar.",
    "Não é possível remarcar para datas anteriores à data atual.": "No es posible reprogramar para fechas anteriores a la fecha actual.",
    "O plano vinculado pertence a outro profissional. Remova o plano ou mantenha o profissional atual.": "El plan vinculado pertenece a otro profesional. Quite el plan o mantenga el profesional actual.",
    "Atual": "Actual",
    "Motivo do cancelamento": "Motivo de la cancelación",
    "Preencha quando o agendamento for cancelado": "Complete este campo cuando se cancele la cita",
    "Disponível somente para status cancelado": "Disponible solo para estado cancelado",
    "Informe pelo menos 30 caracteres para registrar o cancelamento.": "Informe al menos 30 caracteres para registrar la cancelación.",
    "Informe um motivo do cancelamento com pelo menos 30 caracteres.": "Informe un motivo de cancelación con al menos 30 caracteres.",
    "Gerar receita": "Registrar ingreso",
    "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.": "Registre un ingreso vinculado a esta cita, al paciente y al profesional.",
    "Agendamento cancelado": "Cita cancelada",
    "Receita já gerada": "Ingreso ya registrado",
    "Sessão coberta por plano": "Sesión cubierta por el plan",
    "Não é permitido gerar receita para um agendamento com status cancelado.": "No se permite registrar un ingreso para una cita cancelada.",
    "Este atendimento consome uma sessão do plano vinculado. O faturamento deve ser registrado na venda do plano, não por atendimento.": "Esta cita consume una sesión del plan vinculado. La facturación debe registrarse en la venta del plan, no por cita.",
    "Data do pagamento": "Fecha de pago",
    "Descrição da receita": "Descripción del ingreso",
    "Gerando receita...": "Registrando ingreso...",
    "Receita indisponível": "Ingreso no disponible",
    "Coberto por plano": "Cubierto por el plan",
    "Receita gerada": "Ingreso registrado",
    "Fechar": "Cerrar",
    "Avaliação": "Evaluación",
    "Tratamento": "Tratamiento",
    "Retorno": "Seguimiento",
    "Reavaliação": "Reevaluación",
    "Confirmado": "Confirmada",
    "Em andamento": "En curso",
    "Não compareceu": "No asistió",
    "Consulta": "Consulta",
    "Pacote": "Paquete",
    "Atendimento": "Atención",
    "Eficiência assistencial, recorrência e satisfação do paciente em um único lugar.": "Atención eficiente, continuidad y satisfacción del paciente en un solo lugar.",
    "Carregando indicadores assistenciais...": "Cargando indicadores asistenciales...",
    "Meta de retenção atualizada com sucesso!": "¡Meta de retención actualizada con éxito!",
    "NPS atualizado com sucesso!": "¡NPS actualizado con éxito!",
    "NPS registrado com sucesso!": "¡NPS registrado con éxito!",
    "Responder": "Responder",
    "Responder NPS": "Responder NPS",
    "Registre a resposta do NPS para o plano selecionado.": "Registre la respuesta NPS para el plan seleccionado.",
    "Registros de NPS já respondidos pelo paciente.": "Registros de NPS ya respondidos por el paciente.",
    "Link da pesquisa copiado.": "Enlace de la encuesta copiado.",
    "Novo link gerado e copiado.": "Nuevo enlace generado y copiado.",
    "Não foi possível copiar o link.": "No fue posible copiar el enlace.",
    "Esta pesquisa já foi respondida. O link não pode ser copiado novamente.": "Esta encuesta ya fue respondida. El enlace no puede copiarse nuevamente.",
    "Esta pesquisa expirou. Gere uma nova pesquisa para enviar um link válido.": "Esta encuesta expiró. Genere una nueva encuesta para enviar un enlace válido.",
    "Esta pesquisa expirou. Gere um novo link para continuar.": "Esta encuesta ha caducado. Genere un nuevo enlace para continuar.",
    "O link desta pesquisa já foi emitido de forma protegida. Use o envio automático ou gere uma nova pesquisa.": "El enlace de esta encuesta ya fue emitido de forma protegida. Use el envío automático o genere una nueva encuesta.",
    "Não foi possível copiar o link porque a pesquisa não foi encontrada ou não pertence mais a esta clínica.": "No fue posible copiar el enlace porque la encuesta no fue encontrada o ya no pertenece a esta clínica.",
    "Não foi possível copiar o link por um erro inesperado. Tente novamente em instantes.": "No fue posible copiar el enlace por un error inesperado. Inténtelo nuevamente en unos instantes.",
    "O link foi gerado, mas o navegador bloqueou a cópia automática. Permita o acesso à área de transferência e tente novamente.": "El enlace fue generado, pero el navegador bloqueó la copia automática. Permita el acceso al portapapeles e inténtelo nuevamente.",
    "Pacientes atendidos": "Pacientes atendidos",
    "Primeira consulta": "Primera consulta",
    "Avaliações no período selecionado": "Evaluaciones en el período seleccionado",
    "Consultas avulsas": "Citas individuales",
    "Atendimentos sem plano vinculado": "Citas sin plan vinculado",
    "Pacientes em tratamento": "Pacientes en tratamiento",
    "Pacientes com plano no período": "Pacientes con plan en el período",
    "Pacientes atendidos no período": "Pacientes atendidos en el período",
    "Resumo dos atendimentos concluídos entre": "Resumen de citas completadas entre",
    "Atendimentos": "Citas",
    "Com retorno": "Con retorno",
    "Nenhum atendimento concluído no período selecionado.": "No hay citas completadas en el período seleccionado.",
    "atendimento concluído": "cita completada",
    "atendimentos concluídos": "citas completadas",
    "Paciente recorrente": "Paciente recurrente",
    "Paciente novo no período": "Paciente nuevo en el período",
    "Primeiro atendimento": "Primera cita",
    "Último atendimento": "Última cita",
    "Tipos de atendimento": "Tipos de cita",
    "com retorno no período": "con seguimiento en el período",
    "Taxa de retenção": "Tasa de retención",
    "NPS pós-tratamento": "NPS pos-tratamiento",
    "resposta": "respuesta",
    "respostas": "respuestas",
    "Horas ocupadas": "Horas ocupadas",
    "Horários ocupados": "Horarios ocupados",
    "Horários": "Horarios",
    "horários ativos": "horarios activos",
    "horários disponíveis": "horarios disponibles",
    "faltas no período": "ausencias en el período",
    "Novas x recorrentes": "Nuevas vs recurrentes",
    "Novas consultas x retornos": "Consultas nuevas vs seguimientos",
    "Novas consultas x recorrentes": "Consultas nuevas vs recurrentes",
    "Resumo do NPS": "Resumen de NPS",
    "Absenteísmo": "Ausentismo",
    "faltas em": "ausencias en",
    "horários": "horarios",
    "Mix assistencial": "Combinación asistencial",
    "Promotores": "Promotores",
    "Passivos": "Pasivos",
    "Detratores": "Detractores",
    "Sessões por paciente": "Sesiones por paciente",
    "Leitura operacional": "Análisis operativo",
    "Cruze eficiência, faltas e satisfação para agir mais rápido na rotina da clínica.": "Combine eficiencia, ausencias y satisfacción para actuar con más rapidez en la rutina de la clínica.",
    "Retornos por paciente": "Sesiones por paciente",
    "Média de sessões concluídas por paciente no período.": "Promedio de sesiones completadas por paciente en el período.",
    "Absenteísmo x conclusão": "Ausentismo vs finalización",
    "Faltas comparadas aos atendimentos concluídos no mesmo recorte.": "Ausencias comparadas con las citas completadas en el mismo período.",
    "Pacientes fidelizados": "Pacientes retenidos",
    "Pacientes com 2 ou mais atendimentos concluídos no período.": "Pacientes con 2 o más citas completadas en el período.",
    "Agenda do dia": "Calendario del día",
    "Agenda da semana": "Calendario de la semana",
    "Agenda do mês": "Calendario del mes",
    "horários ocupados": "horarios ocupados",
    "Horas": "Horas",
    "Concluídos": "Completadas",
    "de ocupação sobre": "de ocupación de",
    "disponíveis": "disponibles",
    "Sem carga horária configurada nos profissionais para calcular a taxa percentual.": "No hay carga horaria configurada para los profesionales para calcular el porcentaje.",
    "Meta de retenção": "Meta de retención",
    "Meta": "Meta",
    "Pacientes com 2 ou mais atendimentos concluídos no período selecionado.": "Pacientes con 2 o más citas completadas en el período seleccionado.",
    "Gap": "Brecha",
    "de": "de",
    "pacientes retornaram no período.": "pacientes volvieron durante el período.",
    "Defina uma meta percentual para acompanhar a fidelização da base.": "Defina una meta porcentual para monitorear la retención de la base de pacientes.",
    "Meta mensal (%)": "Meta mensual (%)",
    "Segmentação por setor": "Segmentación por sector",
    "Compare ocupação, retenção e satisfação para encontrar gargalos operacionais.": "Compare ocupación, retención y satisfacción para detectar cuellos de botella operativos.",
    "Ocupação": "Ocupación",
    "Mix": "Mix",
    "Retenção": "Retención",
    "Respostas": "Respuestas",
    "Nenhum dado assistencial encontrado no período selecionado.": "No hay datos asistenciales en el período seleccionado.",
    "concluídos": "completadas",
    "faltas": "ausencias",
    "novas": "nuevas",
    "recorrentes": "recurrentes",
    "Detalhes do setor": "Detalles del sector",
    "Período considerado": "Período considerado",
    "Agendamentos realizados": "Citas realizadas",
    "Agendamentos cancelados": "Citas canceladas",
    "Taxa de comparecimento": "Tasa de asistencia",
    "Novos atendimentos": "Nuevas citas",
    "Atendimentos recorrentes": "Citas recurrentes",
    "Profissionais vinculados": "Profesionales vinculados",
    "Nenhum profissional vinculado a este setor.": "Ningún profesional vinculado a este sector.",
    "Sem registro informado": "Sin registro informado",
    "Leitura do setor": "Lectura del sector",
    "Respostas NPS": "Respuestas NPS",
    "Média de sessões por paciente": "Promedio de sesiones por paciente",
    "Filtro de detalhes": "Filtro de detalles",
    "Selecione uma opção para listar paciente e profissional vinculados.": "Seleccione una opción para listar el paciente y el profesional vinculados.",
    "Total encontrado": "Total encontrado",
    "Buscar nos detalhes": "Buscar en los detalles",
    "Ordenar por": "Ordenar por",
    "Mais recentes": "Más recientes",
    "Nome do paciente": "Nombre del paciente",
    "Mais atendimentos": "Más citas",
    "Carregando detalhes...": "Cargando detalles...",
    "Erro ao carregar detalhes.": "Error al cargar los detalles.",
    "Paciente não informado": "Paciente no informado",
    "Profissional não informado": "Profesional no informado",
    "Nenhum registro encontrado para este filtro.": "Ningún registro encontrado para este filtro.",
    "Fila de NPS automático": "Lista de NPS automático",
    "Todo plano concluído gera um link público para o paciente responder após o tratamento.": "Cada plan de tratamiento completado genera un enlace público para que el paciente responda después del tratamiento.",
    "Pendentes": "Pendientes",
    "Respondidas": "Respondidas",
    "Ação": "Acción",
    "Nenhuma pesquisa automática no período selecionado.": "No hay encuestas automáticas en el período seleccionado.",
    "Respondida": "Respondida",
    "Pendente": "Pendiente",
    "Expirada": "Caducada",
    "Em": "El",
    "Criada em": "Creada el",
    "Expirou em {date}": "Caducó el {date}",
    "Copiar link": "Copiar enlace",
    "Gerar novo link": "Generar nuevo enlace",
    "O link público segue o formato": "El enlace público tiene el formato",
    "e pode ser enviado pela equipe no canal preferido.": "y el equipo puede enviarlo por el canal preferido.",
    "Feedbacks recentes": "Feedback reciente",
    "Leitura rápida da satisfação capturada após os tratamentos.": "Resumen rápido de la satisfacción capturada después de los tratamientos.",
    "Nota": "Puntuación",
    "Comentário": "Comentário",
    "Nenhum NPS registrado ainda.": "Aún no hay registros de NPS.",
    "Período inicial": "Período inicial",
    "Período final": "Período final",
    "Mês atual": "Mes actual",
    "Sem respostas ainda": "Aún no hay respuestas",
    "Excelência percebida": "Excelencia percibida",
    "Boa satisfação": "Buena satisfacción",
    "Atenção aos detratores": "Atención a los detractores",
    "Experiência crítica": "Experiencia crítica",
    "Metas": "Metas",
    "Usuários": "Usuarios",
    "Acessos da clínica": "Acceso a la clínica",
    "Administrador": "Administrador",
    "Administradores": "Administradores",
    "Administradores ativos": "Administradores activos",
    "Alterar função do usuário": "Cambiar el rol del usuario",
    "Ativar usuário": "Activar usuario",
    "Buscar por nome ou email": "Buscar por nombre o correo electrónico",
    "Carregando usuários...": "Cargando usuarios...",
    "Confirmar": "Confirmar",
    "Confirmar alteração": "Confirmar cambio",
    "Criar usuário": "Crear usuario",
    "Define se esse usuário pode acessar a clínica.": "Indica si este usuario puede acceder a la clínica.",
    "Deixe em branco para manter": "Deje este campo en blanco para conservar la contraseña actual",
    "Desativar usuário": "Desactivar usuario",
    "Editar usuário": "Editar usuario",
    "Erro ao atualizar usuário": "Error al actualizar el usuario",
    "Erro ao carregar usuários": "Error al cargar los usuarios",
    "Erro ao criar usuário": "Error al crear el usuario",
    "Filtros aplicados a esta lista.": "Hay filtros aplicados a esta lista.",
    "Função": "Rol",
    "Nenhum usuário adicional encontrado para esta clínica.": "No hay usuarios adicionales en esta clínica.",
    "Nenhum usuário encontrado com os filtros informados.": "No hay usuarios con los filtros seleccionados.",
    "Nenhum usuário encontrado.": "No se encontraron usuarios.",
    "Nova senha": "Nueva contraseña",
    "Novo usuário": "Nuevo usuario",
    "Novo usuário da clínica": "Nuevo usuario de la clínica",
    "Processando...": "Procesando...",
    "Reativar usuário": "Reactivar usuario",
    "Senha inicial": "Contraseña inicial",
    "Seu perfil atual não possui permissão para cadastrar ou editar usuários.": "Su perfil actual no tiene permiso para crear o editar usuarios.",
    "Somente administradores da clínica podem gerenciar acessos.": "Solo los administradores de la clínica pueden gestionar el acceso.",
    "Todas as funções": "Todos los roles",
    "Usuário": "Usuario",
    "Usuário ativo": "Usuario activo",
    "Usuário atualizado com sucesso.": "Usuario actualizado correctamente.",
    "Usuário criado com sucesso.": "Usuario creado correctamente.",
    "Usuário desativado com sucesso.": "Usuario desactivado correctamente.",
    "Usuário reativado com sucesso.": "Usuario reactivado correctamente.",
    "usuario@clínica.com": "usuario@clinica.com",
    "Usuários ativos": "Usuarios activos",
    "Usuários da Clínica": "Usuarios de la clínica",
    "Você": "Tú",
    "Clínicas": "Clínicas",
    "Auditoria": "Auditoría",
    "Selecione a clínica": "Seleccione la clínica",
    "Sair": "Cerrar sesión",
    "Workspace": "Espacio de trabajo",
    "Control Tower": "Centro de control",
    "Tema claro": "Tema claro",
    "Tema escuro": "Tema oscuro",
    "Ativar tema claro": "Activar tema claro",
    "Ativar tema escuro": "Activar tema oscuro",
    "Entrar": "Iniciar sesión",
    "Criar clínica": "Crear clínica",
    "Criando...": "Creando...",
    "Entrando...": "Ingresando...",
    "Fazer login": "Iniciar sesión",
    "Voltar para a página inicial": "Volver a la página inicial",
    "Criar conta": "Crear cuenta",
    "Bem-vindo de volta": "Bienvenido de nuevo",
    "Portal de acesso do super admin": "Portal de acceso del super admin",
    "Use suas credenciais para entrar na camada central da plataforma.": "Use sus credenciales para acceder a la capa central de la plataforma.",
    "Entre com suas credenciais para acessar a plataforma.": "Ingrese sus credenciales para acceder a la plataforma.",
    "E-mail": "E-mail",
    "Mostrar senha": "Mostrar contraseña",
    "Ocultar senha": "Ocultar contraseña",
    "Senha": "Contraseña",
    "Não tem uma conta?": "¿No tiene una cuenta?",
    "Já tem uma conta?": "¿Ya tiene una cuenta?",
    "Seu nome": "Su nombre",
    "Sua cidade": "Su ciudad",
    "Mínimo 6 caracteres": "Mínimo 6 caracteres",
    "Mínimo 8 caracteres": "Mínimo 8 caracteres",
    "Repita sua senha": "Repita su contraseña",
    "Nome da clínica": "Nombre de la clínica",
    "Nome da sua clínica": "Nombre de su clínica",
    "Telefone da clínica": "Teléfono de la clínica",
    "E-mail da clínica": "E-mail de la clínica",
    "Endereço": "Dirección",
    "Rua, avenida, etc.": "Calle, avenida, etc.",
    "Número": "Número",
    "Cidade": "Ciudad",
    "Estado": "Estado",
    "Nome completo do responsável": "Nombre completo del responsable",
    "E-mail do responsável": "E-mail del responsable",
    "Confirmar senha": "Confirmar contraseña",
    "Buscar endereço pelo CEP...": "Buscando dirección por código postal...",
    "Cadastro realizado!": "¡Registro realizado!",
    "Logo você receberá um e-mail de confirmação com a liberação do seu acesso!": "Pronto recibirá un e-mail de confirmación con la liberación de su acceso.",
    "Desde já...": "Desde ahora...",
    "Seja bem-vindo!!!": "¡¡¡Bienvenido!!!",
    "Carregando métricas...": "Cargando indicadores...",
    "Selecione uma clínica para visualizar o dashboard.": "Seleccione una clínica para visualizar el Dashboard.",
    "Visão geral": "Vista general",
    "Visão geral de": "Vista general de",
    "Indicadores exibidos para": "Indicadores mostrados para",
    "Visão consolidada da clínica": "Vista consolidada de la clínica",
    "Visão individual por setor": "Vista individual por sector",
    "Indicadores gerenciais": "Indicadores de gestión",
    "Indicadores com fonte técnica segura para o período e setor selecionados.": "Indicadores con fuente técnica fiable para el período y sector seleccionados.",
    "Primeiras consultas": "Primeras consultas",
    "Agenda classificada como primeiro atendimento": "Agenda clasificada como primera consulta",
    "Agendamentos sem plano de tratamento vinculado": "Citas sin plan de tratamiento vinculado",
    "Vendas confirmadas por data da venda": "Ventas confirmadas por fecha de venta",
    "Despesas por competência / (agendamentos vinculados + lançamentos manuais)": "Gastos por devengo / (citas vinculadas + registros manuales de ingresos)",
    "Resultado financeiro / (agendamentos vinculados + lançamentos manuais)": "Resultado financiero / (citas vinculadas + registros manuales de ingresos)",
    "Resumo por setor": "Resumen por sector",
    "Visão consolidada por setor com dados operacionais e financeiros permitidos para o seu perfil.": "Vista consolidada por sector con datos operativos y financieros permitidos para tu perfil.",
    "Visão individual do setor selecionado com dados permitidos para o seu perfil.": "Vista individual del sector seleccionado con datos permitidos para tu perfil.",
    "Entradas financeiras": "Entradas financieras",
    "Despesas reconhecidas por competência": "Gastos reconocidos por devengo",
    "Despesas por competência do período": "Gastos por devengo del período",
    "Resultado financeiro": "Resultado financiero",
    "Entradas realizadas menos despesas por competência, sem DRE contábil.": "Entradas realizadas menos gastos por devengo, sin tratamiento de estado de resultados contable.",
    "Entradas financeiras por lançamento realizado": "Entradas financieras por asiento realizado",
    "Sem dados setoriais confiáveis para o período selecionado.": "No hay datos sectoriales fiables para el período seleccionado.",
    "Ano anterior": "Año anterior",
    "Próximo ano": "Próximo año",
    "Mês Atual": "Mes actual",
    "no período": "en el período",
    "Receita Total": "Ingresos totales",
    "Receita": "Ingresos",
    "Receita bruta": "Ingresos brutos",
    "Receita líquida": "Ingresos netos",
    "Despesas": "Gastos",
    "Lucro": "Ganancia",
    "Prejuízo": "Pérdida",
    "Equilíbrio": "Equilibrio",
    "Total de Entradas": "Total de entradas",
    "Total de saídas": "Total de salidas",
    "Resultado do Período": "Resultado del período",
    "Soma de todas as receitas registradas no período selecionado.": "Suma de todos los ingresos registrados en el período seleccionado.",
    "Soma de todas as despesas registradas no período selecionado.": "Suma de todos los gastos registrados en el período seleccionado.",
    "Diferença entre o total de entradas e o total de saídas no período selecionado.": "Diferencia entre el total de entradas y el total de salidas en el período seleccionado.",
    "Ticket Médio": "Valor promedio por paciente",
    "Últimos 30 dias": "Últimos 30 días",
    "Leads convertidos": "Leads convertidos",
    "Atrasados": "Vencidos",
    "Para hoje": "Para hoy",
    "Próximos": "Próximos",
    "Configuração de follow-up": "Configuración de Follow-up",
    "Defina em quantos dias a clínica costuma retomar um lead pendente.": "Defina en cuántos días la clínica suele retomar contacto con un Lead pendiente.",
    "Intervalo padrão (dias)": "Intervalo predeterminado (días)",
    "Salvar intervalo": "Guardar intervalo",
    "Nome": "Nombre",
    "Contato": "Contacto",
    "Telefone": "Teléfono",
    "Origem": "Origen",
    "Setor": "Sector",
    "Campanha": "Campaña",
    "Nova Campanha": "Nueva campaña",
    "Nome da campanha": "Nombre de la campaña",
    "Fim": "Fin",
    "Canal": "Canal",
    "Publico alvo": "Público objetivo",
    "Público alvo": "Público objetivo",
    "Instagram, Google...": "Instagram, Google...",
    "Ex: Dor lombar": "Ej: Dolor lumbar",
    "Criar campanha": "Crear campaña",
    "Criar Campanha": "Crear campaña",
    "Campanha criada com sucesso!": "¡Campaña creada con éxito!",
    "Mes atual": "Mes actual",
    "Filtrar por origem": "Filtrar por origen",
    "Filtrar por campanha": "Filtrar por campaña",
    "Todas as origens": "Todos los orígenes",
    "Todas as campanhas": "Todas las campañas",
    "Leads no período": "Leads en el período",
    "marcaram consulta": "agendaron consulta",
    "convertidos": "convertidos",
    "Conversão comercial": "Conversión comercial",
    "Investimento em campanhas": "Inversión en campañas",
    "Somatório das campanhas filtradas": "Suma de las campañas filtradas",
    "Somatorio das campanhas filtradas": "Suma de las campañas filtradas",
    "Detalhes da conversão comercial": "Detalles de la conversión comercial",
    "Detalhes do investimento em campanhas": "Detalles de la inversión en campañas",
    "Período analisado": "Período analizado",
    "Os dados abaixo respeitam os filtros atuais da página Marketing.": "Los datos siguientes respetan los filtros actuales de la página Marketing.",
    "Leads considerados neste cálculo": "Leads considerados en este cálculo",
    "Marcaram consulta": "Agendaron consulta",
    "Leads que avançaram para agendamento": "Leads que avanzaron a cita",
    "Leads convertidos em pacientes": "Leads convertidos en pacientes",
    "Taxa de conversão": "Tasa de conversión",
    "Percentual de leads convertidos": "Porcentaje de leads convertidos",
    "Leads considerados na conversão": "Leads considerados en la conversión",
    "Investimento total": "Inversión total",
    "Campanhas filtradas": "Campañas filtradas",
    "Campanhas consideradas no período": "Campañas consideradas en el período",
    "Investimento médio": "Inversión promedio",
    "Média por campanha filtrada": "Promedio por campaña filtrada",
    "Custo por lead": "Costo por lead",
    "Investimento dividido pelos leads do período": "Inversión dividida por los leads del período",
    "Detalhamento de campanhas": "Detalle de campañas",
    "Período": "Período",
    "Nenhuma campanha encontrada com os filtros atuais.": "No se encontró ninguna campaña con los filtros actuales.",
    "Nenhum lead convertido no período selecionado.": "Ningún lead convertido en el período seleccionado.",
    "Meta de agendamento": "Meta de citas",
    "Progresso:": "Progreso:",
    "Custo por novo paciente no período": "Costo por nuevo paciente en el período",
    "Receita média por paciente no período": "Valor promedio por paciente en el período",
    "novos pacientes": "nuevos pacientes",
    "Receita projetada": "Ingresos proyectados",
    "Alertas operacionais": "Alertas operativos",
    "Follow-ups vencidos / NPS pendentes": "Follow-ups vencidos / NPS pendientes",
    "Fila unificada de follow-up vencido e NPS pendente.": "Fila unificada de Follow-up vencido y NPS pendiente.",
    "Nenhum alerta operacional no momento.": "No hay alertas operativos por el momento.",
    "Follow-up vencido": "Follow-up vencido",
    "NPS pendente": "NPS pendiente",
    "Lead precisa de novo contato comercial.": "El Lead necesita un nuevo contacto comercial.",
    "Plano": "Plan",
    "aguardando envio ou resposta.": "a la espera de envío o respuesta.",
    "Programa Coluna Ativa": "Programa Columna Activa",
    "Recondicionamento Pos-corrida": "Reacondicionamiento poscarrera",
    "Recondicionamento Pós-corrida": "Reacondicionamiento poscarrera",
    "Acupuntura Sono e Stress": "Acupuntura sueño y estrés",
    "Ombro Sem Travar": "Hombro sin bloqueo",
    "Respirar Melhor com Pilates": "Respirar mejor con Pilates",
    "Ritmo e Equilibrio Senior": "Ritmo y equilibrio sénior",
    "Ritmo e Equilíbrio Sênior": "Ritmo y equilibrio sénior",
    "Meta mensal de agendamento": "Meta mensual de citas",
    "Defina a meta da clínica e acompanhe o progresso do mês atual.": "Defina la meta de la clínica y acompañe el progreso del mes actual.",
    "Quantidade de agendamentos": "Cantidad de citas",
    "Salvar meta": "Guardar meta",
    "agendamentos válidos no período atual.": "citas válidas en el período actual.",
    "Meta mensal de agendamento atualizada!": "¡Meta mensual de citas actualizada!",
    "Erro ao atualizar meta": "Error al actualizar la meta",
    "Relatório por origem": "Informe por origen",
    "Relatório por campanha": "Informe por campaña",
    "Relatório por setor": "Informe por sector",
    "Marcou": "Agendó",
    "Convertidos": "Convertidos",
    "Taxa": "Tasa",
    "Investimento": "Inversión",
    "Nenhum dado no período selecionado.": "No hay datos en el período seleccionado.",
    "Nenhuma campanha cadastrada.": "Ninguna campaña registrada.",
    "Sem canal": "Sin canal",
    "Campanhas cadastradas": "Campañas registradas",
    "Público": "Público",
    "Orçamento": "Presupuesto",
    "Nenhuma campanha cadastrada ainda.": "Ninguna campaña registrada todavía.",
    "Ativa": "Activa",
    "Inativa": "Inactiva",
    "Editar campanha": "Editar campaña",
    "Ativar campanha": "Activar campaña",
    "Desativar campanha": "Desactivar campaña",
    "Remover campanha": "Eliminar campaña",
    "Campanha atualizada com sucesso!": "¡Campaña actualizada con éxito!",
    "Status da campanha atualizado!": "¡Estado de la campaña actualizado!",
    "Campanha removida da gestão ativa.": "Campaña eliminada de la gestión activa.",
    "Adultos de 30 a 55 anos com dor lombar recorrente e rotina de escritorio.": "Adultos de 30 a 55 años con dolor lumbar recurrente y rutina de oficina.",
    "Profissionais com pouco tempo e interesse em aulas no horário de almoço.": "Profesionales con poco tiempo e interés en clases a la hora del almuerzo.",
    "Empresas de tecnologia e RH buscando palestras e triagens posturais.": "Empresas de tecnología y RR. HH. que buscan charlas y evaluaciones posturales.",
    "Ex-pacientes e familiares que ja haviam demonstrado interesse em retornar.": "Ex pacientes y familiares que ya habían mostrado interés en volver.",
    "leads": "Leads",
    "marcaram": "agendaron",
    "Agendamentos": "Citas",
    "Nenhum dado setorial no período selecionado.": "No hay datos sectoriales en el período seleccionado.",
    "Leads filtrados": "Leads filtrados",
    "Lead": "Lead",
    "clínica": "clínica",
    "Resultado": "Resultado",
    "Data": "Fecha",
    "Nenhum lead encontrado com os filtros atuais.": "No se encontraron Leads con los filtros actuales.",
    "Follow-up": "Follow-up",
    "Status": "Estado",
    "Editar Lead": "Editar Lead",
    "Selecione o setor": "Seleccione el sector",
    "Sem campanha": "Sin campaña",
    "Resultado comercial": "Resultado comercial",
    "Marcou consulta": "Agendó consulta",
    "Não marcou": "No agendó",
    "Próximo follow-up": "Próximo Follow-up",
    "Hora": "Hora",
    "Follow-up no horário": "Follow-up en horario",
    "Existem leads com follow-up para agora.": "Hay Leads con Follow-up para ahora.",
    "Motivo de não agendamento": "Motivo por el que no agendó",
    "Sem setor": "Sin sector",
    "Setor da venda": "Sector de la venta",
    "Setor do atendimento": "Sector de la atención",
    "O setor informado diverge do setor histórico da origem": "El sector indicado difiere del sector histórico del origen",
    "O setor histórico de um recebível originado não pode ser reclassificado": "El sector histórico de una cuenta por cobrar vinculada a un origen no puede reclasificarse",
    "Nenhum lead encontrado": "No se encontraron Leads",
    "Nenhum lead neste grupo.": "No hay leads en este grupo.",
    "Abrir WhatsApp": "Abrir WhatsApp",
    "Abrir no WhatsApp": "Abrir en WhatsApp",
    "WhatsApp": "WhatsApp",
    "Telefone não informado": "Teléfono no informado",
    "Telefone inválido": "Teléfono inválido",
    "Conversar pelo WhatsApp": "Conversar por WhatsApp",
    "Informe um telefone válido para abrir o WhatsApp.": "Informe un teléfono válido para abrir WhatsApp.",
    "Oi {name}, tudo bem com você? Aqui é da {clinicName}.": "Hola {name}, ¿todo bien? Te escribimos de {clinicName}.",
    "Tudo bem?": "¿Todo bien?",
    "Tudo bem com você?": "¿Todo bien contigo?",
    "Tudo bem? Estou entrando em contato sobre seu agendamento.": "¿Todo bien? Me pongo en contacto sobre tu cita.",
    "Converter em paciente": "Convertir en paciente",
    "Converter": "Convertir",
    "Convertendo...": "Convirtiendo...",
    "Vincular a este paciente": "Vincular a este paciente",
    "Criar novo paciente": "Crear nuevo paciente",
    "Lead vinculado ao paciente existente.": "Lead vinculado al paciente existente.",
    "Lead atualizado com sucesso!": "¡Lead actualizado con éxito!",
    "Lead convertido em paciente com sucesso!": "¡Lead convertido en paciente con éxito!",
    "Consulta agendada a partir do lead!": "¡Cita programada a partir del lead!",
    "O cadastro do paciente será criado a partir deste lead e a conversão ficará registrada no funil.": "El registro del paciente se creará a partir de este Lead y la conversión quedará registrada en el Funnel.",
    "Agendar consulta": "Agendar consulta",
    "Agendar consulta do lead": "Agendar consulta del Lead",
    "Agendando...": "Programando...",
    "O agendamento será criado usando o paciente já convertido a partir deste lead.": "La cita se creará usando el paciente ya convertido a partir de este Lead.",
    "Para agendar uma consulta a partir do lead, primeiro converta esse lead em paciente.": "Para agendar una consulta a partir del Lead, primero convierta este Lead en paciente.",
    "Limpar data": "Borrar fecha",
    "Informe uma data de nascimento válida.": "Informe una fecha de nacimiento válida.",
    "A data de nascimento não pode ser futura.": "La fecha de nacimiento no puede ser futura.",
    "Informe uma data de nascimento a partir de 1900.": "Informe una fecha de nacimiento a partir de 1900.",
    "Bairro": "Barrio",
    "Prontuário do paciente": "Historia clínica del paciente",
    "Módulo clínico": "Módulo clínico",
    "Ficha de Avaliação": "Ficha de evaluación",
    "Avaliações clínicas por paciente": "Evaluaciones clínicas por paciente",
    "Abrir prontuário": "Abrir historia clínica",
    "Paciente selecionado": "Paciente seleccionado",
    "Último registro": "Último registro",
    "Não foi possível carregar pacientes.": "No fue posible cargar pacientes.",
    "Não foi possível carregar a ficha de avaliação.": "No fue posible cargar la ficha de evaluación.",
    "Nenhum paciente encontrado para a busca.": "No se encontro ningun paciente para la busqueda.",
    "Selecione um paciente para abrir a ficha de avaliação.": "Seleccione un paciente para abrir la ficha de evaluación.",
    "Dados de identificação não informados": "Datos de identificación no informados",
    "Evoluções": "Evoluciones",
    "Evolução": "Evolución",
    "Alta": "Alta",
    "Avaliações": "Evaluaciones",
    "Avaliação inicial": "Evaluación inicial",
    "Avaliação padrão": "Evaluación predeterminada",
    "Nova Avaliação": "Nueva evaluación",
    "Registrar avaliação clínica": "Registrar evaluación clínica",
    "Tipo de avaliação": "Tipo de evaluación",
    "Título da avaliação": "Título de la evaluación",
    "Objetivo clínico": "Objetivo clínico",
    "Achados principais": "Hallazgos principales",
    "Hipótese / diagnóstico funcional": "Hipótesis / diagnóstico funcional",
    "Plano terapêutico": "Plan terapéutico",
    "Salvar avaliação": "Guardar evaluación",
    "Avaliação registrada com sucesso.": "Evaluación registrada con éxito.",
    "Não foi possível salvar a avaliação clínica.": "No fue posible guardar la evaluación clínica.",
    "Informações do paciente": "Información del paciente",
    "Fluxo clínico": "Flujo clínico",
    "Paciente → Avaliação → Evoluções": "Paciente → Evaluación → Evoluciones",
    "Cada avaliação organiza as evoluções clínicas vinculadas ao atendimento.": "Cada evaluación organiza las evoluciones clínicas vinculadas a la atención.",
    "Pesquisa clínica": "Búsqueda clínica",
    "Busque por sintomas, diagnóstico, profissional, data, conduta ou plano terapêutico.": "Busque por síntomas, diagnóstico, profesional, fecha, conducta o plan terapéutico.",
    "Buscar no prontuário clínico": "Buscar en la historia clínica",
    "Selecione uma avaliação antes de criar uma evolução.": "Seleccione una evaluación antes de crear una evolución.",
    "Buscar sintomas, diagnósticos, medicamentos ou palavras-chave": "Buscar síntomas, diagnósticos, medicamentos o palabras clave",
    "Avaliações registradas": "Evaluaciones registradas",
    "Cada avaliação agrupa as evoluções clínicas vinculadas.": "Cada evaluación agrupa las evoluciones clínicas vinculadas.",
    "Carregando avaliações...": "Cargando evaluaciones...",
    "Nenhuma avaliação encontrada para a busca.": "No se encontró ninguna evaluación para la búsqueda.",
    "Nenhuma avaliação clínica registrada.": "No hay evaluación clínica registrada.",
    "Evoluções vinculadas": "Evoluciones vinculadas",
    "Criar evolução": "Crear evolución",
    "Editar evolução": "Editar evolución",
    "Editar evolução clínica": "Editar evolución clínica",
    "Evolução atualizada com sucesso.": "Evolución actualizada con éxito.",
    "Nenhuma evolução vinculada a esta avaliação.": "No hay evolución vinculada a esta evaluación.",
    "Esta avaliação reúne evoluções antigas sem vínculo direto, preservando o histórico clínico integral.": "Esta evaluación reúne evoluciones antiguas sin vínculo directo, preservando el historial clínico completo.",
    "Histórico preservado": "Historial preservado",
    "Evoluções antigas sem avaliação vinculada.": "Evoluciones antiguas sin evaluación vinculada.",
    "Preservar histórico clínico registrado antes da estrutura Avaliação → Evoluções.": "Preservar el historial clínico registrado antes de la estructura Evaluación → Evoluciones.",
    "registro(s)": "registro(s)",
    "Documentos": "Documentos",
    "Plano de Tratamento": "Plan de tratamiento",
    "Erro ao carregar histórico do paciente": "Error al cargar el historial del paciente",
    "Carregando histórico do paciente...": "Cargando historial del paciente...",
    "Resumo clínico": "Resumen clínico",
    "Situação clínica do paciente": "Situación clínica del paciente",
    "Informações essenciais para orientar o atendimento atual.": "Información esencial para orientar la atención actual.",
    "Editar cadastro": "Editar registro",
    "Sessões restantes": "Sesiones restantes",
    "Próxima consulta": "Próxima consulta",
    "Última avaliação": "Última evaluación",
    "Pendência financeira": "Pendiente financiero",
    "Timeline Clínica": "Timeline clínica",
    "Histórico cronológico dos principais eventos do paciente.": "Historial cronológico de los principales eventos del paciente.",
    "Nenhum evento clínico encontrado.": "No se encontró ningún evento clínico.",
    "Abrir registro": "Abrir registro",
    "Cadastro realizado": "Registro realizado",
    "Consulta agendada": "Cita programada",
    "Avaliação criada": "Evaluación creada",
    "Evolução registrada": "Evolución registrada",
    "Plano vendido": "Plan vendido",
    "Documento anexado": "Documento adjuntado",
    "Pagamento registrado": "Pago registrado",
    "Pesquisar por nome, categoria ou data": "Buscar por nombre, categoría o fecha",
    "Todas as categorias": "Todas las categorías",
    "Relacionamento": "Relación",
    "realizadas": "realizadas",
    "Sessões realizadas": "Sesiones realizadas",
    "Objetivos alcançados": "Objetivos alcanzados",
    "Pendências": "Pendientes",
    "Finalizada": "Finalizada",
    "Ocultar detalhes": "Ocultar detalles",
    "URL da foto": "URL de la foto",
    "Foto do paciente": "Foto del paciente",
    "Gerenciar foto do paciente": "Gestionar foto del paciente",
    "Inserir foto": "Añadir foto",
    "Alterar foto": "Cambiar foto",
    "Selecionar imagem": "Seleccionar imagen",
    "Remover foto": "Eliminar foto",
    "Salvar foto": "Guardar foto",
    "Processando imagem...": "Procesando imagen...",
    "Enviando foto...": "Subiendo foto...",
    "Removendo foto...": "Eliminando foto...",
    "Pré-visualização da foto do paciente": "Vista previa de la foto del paciente",
    "Use uma imagem nítida e centralizada. A pré-visualização mostra o recorte final.": "Utilice una imagen nítida y centrada. La vista previa muestra el recorte final.",
    "Formatos aceitos: JPG, PNG e WEBP. Tamanho máximo:": "Formatos admitidos: JPG, PNG y WEBP. Tamaño máximo:",
    "Selecione uma imagem JPG, PNG ou WEBP.": "Seleccione una imagen JPG, PNG o WEBP.",
    "A imagem selecionada excede o tamanho máximo permitido.": "La imagen seleccionada supera el tamaño máximo permitido.",
    "Não foi possível processar a imagem selecionada.": "No se pudo procesar la imagen seleccionada.",
    "Foto do paciente atualizada com sucesso.": "La foto del paciente se ha actualizado correctamente.",
    "Foto do paciente removida com sucesso.": "La foto del paciente se ha eliminado correctamente.",
    "Alergias": "Alergias",
    "Medicamentos em uso": "Medicamentos en uso",
    "Condições clínicas": "Condiciones clínicas",
    "Condições clínicas relevantes": "Condiciones clínicas relevantes",
    "Alertas clínicos": "Alertas clínicos",
    "Alertas clínicos importantes": "Alertas clínicos importantes",
    "Plano atual": "Plan actual",
    "Sem plano ativo": "Sin plan activo",
    "Convênio não informado": "Seguro no informado",
    "Total de consultas": "Total de consultas",
    "Última consulta": "Última consulta",
    "Acompanhamento": "Seguimiento",
    "anos": "años",
    "Plano ativo": "Plan activo",
    "Nova Evolução": "Nueva evolución",
    "Registrar evolução clínica": "Registrar evolución clínica",
    "Template inteligente": "Plantilla inteligente",
    "Selecionar modelo": "Seleccionar modelo",
    "Profissional responsável": "Profesional responsable",
    "Não vincular": "No vincular",
    "Selecionar profissional": "Seleccionar profesional",
    "Resumo rápido": "Resumen rápido",
    "Resumo para aparecer na timeline": "Resumen que aparecerá en la línea de tiempo",
    "Escala de Dor": "Escala de dolor",
    "Testes Clínicos": "Pruebas clínicas",
    "Conduta": "Conducta",
    "Registre o relato clínico da evolução do paciente.": "Registre el relato clínico de la evolución del paciente.",
    "Ex.: 0/10, 5/10 ou EVA 7": "Ej.: 0/10, 5/10 o EVA 7",
    "Registre testes clínicos realizados e seus resultados.": "Registre las pruebas clínicas realizadas y sus resultados.",
    "Registre a conduta adotada e orientações realizadas.": "Registre la conducta adoptada y las orientaciones realizadas.",
    "S - Subjetivo": "S - Subjetivo",
    "O - Objetivo": "O - Objetivo",
    "A - Avaliação": "A - Evaluación",
    "P - Plano": "P - Plan",
    "Tags clínicas": "Etiquetas clínicas",
    "Separe por vírgula": "Separe con comas",
    "Anexos": "Adjuntos",
    "Imagem": "Imagen",
    "Exame": "Examen",
    "Documento": "Documento",
    "Outros": "Otros",
    "Remover": "Eliminar",
    "Salvar evolução": "Guardar evolución",
    "Evolução registrada com sucesso.": "Evolución registrada con éxito.",
    "Não foi possível salvar a evolução clínica.": "No fue posible guardar la evolución clínica.",
    "Linha do tempo clínica": "Línea de tiempo clínica",
    "Carregando evoluções...": "Cargando evoluciones...",
    "Nenhuma evolução encontrada para a busca.": "No se encontró ninguna evolución para la búsqueda.",
    "Nenhuma evolução clínica registrada.": "No hay evolución clínica registrada.",
    "Última alteração": "Última modificación",
    "Anexar documento": "Adjuntar documento",
    "Envie PDFs, exames, imagens ou fotos vinculadas ao prontuário do paciente.": "Envíe PDF, exámenes, imágenes o fotos vinculadas a la historia clínica del paciente.",
    "Anexar": "Adjuntar",
    "Selecionar arquivo": "Seleccionar archivo",
    "Selecionar arquivos": "Seleccionar archivos",
    "Nenhum arquivo selecionado": "No se ha seleccionado ningún archivo",
    "Nenhum arquivo escolhido": "No se ha elegido ningún archivo",
    "Arquivo selecionado": "Archivo seleccionado",
    "{count} arquivos selecionados": "{count} archivos seleccionados",
    "Remover arquivo": "Quitar archivo",
    "Trocar arquivo": "Cambiar archivo",
    "Formatos aceitos: {formats}": "Formatos admitidos: {formats}",
    "Limite de tamanho: {size}": "Límite de tamaño: {size}",
    "Enviando...": "Enviando...",
    "Documento anexado com sucesso.": "Documento adjuntado con éxito.",
    "Selecione um arquivo para anexar.": "Seleccione un archivo para adjuntar.",
    "Arquivo": "Archivo",
    "Foto": "Foto",
    "O arquivo deve ter no máximo 15MB.": "El archivo debe tener como máximo 15 MB.",
    "O arquivo selecionado possui mais de {max} MB.\nSelecione um arquivo menor para continuar.": "El archivo seleccionado supera los {max} MB.\nSeleccione un archivo más pequeño para continuar.",
    "O tamanho máximo permitido para upload é {max} MB.": "El tamaño máximo permitido para la carga es de {max} MB.",
    "Limite de armazenamento atingido. Remova arquivos ou solicite ampliação da capacidade.": "Límite de almacenamiento alcanzado. Elimine archivos o solicite ampliación de capacidad.",
    "Tipo de arquivo incompatível com a categoria selecionada.": "El tipo de archivo no es compatible con la categoría seleccionada.",
    "Não foi possível anexar o documento.": "No fue posible adjuntar el documento.",
    "Não foi possível abrir o documento.": "No fue posible abrir el documento.",
    "Documentos do prontuário": "Documentos de la historia clínica",
    "Arquivos anexados diretamente ao prontuário do paciente.": "Archivos adjuntados directamente a la historia clínica del paciente.",
    "Carregando documentos...": "Cargando documentos...",
    "Nenhum documento anexado ao prontuário.": "No hay documentos adjuntos a la historia clínica.",
    "Enviado em": "Enviado el",
    "Visualizar": "Visualizar",
    "Baixar": "Descargar",
    "Documentos vinculados": "Documentos vinculados",
    "selecionado(s)": "seleccionado(s)",
    "{count} documento vinculado": "{count} documento vinculado",
    "{count} documentos vinculados": "{count} documentos vinculados",
    "Nenhum documento vinculado a este registro.": "No hay documentos vinculados a este registro.",
    "Documentos disponíveis para vincular": "Documentos disponibles para vincular",
    "Remover vínculo": "Quitar vínculo",
    "Vincular documento": "Vincular documento",
    "Tipos aceitos: PDF, JPG, PNG e WEBP. Limite:": "Tipos aceptados: PDF, JPG, PNG y WEBP. Límite:",
    "Nenhum documento do paciente disponível para vincular.": "No hay documentos del paciente disponibles para vincular.",
    "Documento removido com sucesso.": "Documento eliminado con éxito.",
    "Vinculado a": "Vinculado a",
    "registro clínico": "registro clínico",
    "registros clínicos": "registros clínicos",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite: 15MB.": "Tipos aceptados: PDF, JPG, PNG, WEBP y GIF. Límite: 15 MB.",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite:": "Tipos aceptados: PDF, JPG, PNG, WEBP y GIF. Límite:",
    "Armazenamento": "Almacenamiento",
    "{used} de {total} utilizados": "{used} de {total} utilizados",
    "Arquivos": "Archivos",
    "Utilizado": "Utilizado",
    "Disponível": "Disponible",
    "Limite atingido": "Límite alcanzado",
    "Próximo do limite": "Cerca del límite",
    "Atenção": "Atención",
    "Normal": "Normal",
    "Sua clínica está próxima de atingir o limite de armazenamento.": "Su clínica está cerca de alcanzar el límite de almacenamiento.",
    "Sua clínica está utilizando mais de 80% da capacidade contratada.": "Su clínica está utilizando más del 80 % de la capacidad contratada.",
    "Evolução de": "Evolución de",
    "Anexos vinculados às evoluções clínicas do paciente.": "Adjuntos vinculados a las evoluciones clínicas del paciente.",
    "Receita do paciente": "Ingresos del paciente",
    "Carregando financeiro do paciente...": "Cargando financiero del paciente...",
    "Nenhum lançamento financeiro encontrado.": "No se encontró ningún movimiento financiero.",
    "Último pagamento": "Último pago",
    "Paciente desde": "Paciente desde",
    "Preferências e campanhas": "Preferencias y campañas",
    "Sem dados adicionais de marketing vinculados no cadastro atual.": "No hay datos adicionales de marketing vinculados al registro actual.",
    "Dados comerciais e origem do relacionamento do paciente.": "Datos comerciales y origen de la relación con el paciente.",
    "Registrar queixa principal, objetivos do paciente e contexto clínico inicial.": "Registrar la queja principal, los objetivos del paciente y el contexto clínico inicial.",
    "Descrever achados relevantes da avaliação física, testes funcionais e observações clínicas.": "Describir hallazgos relevantes de la evaluación física, pruebas funcionales y observaciones clínicas.",
    "Definir hipótese clínica, diagnóstico funcional e prioridades terapêuticas.": "Definir hipótesis clínica, diagnóstico funcional y prioridades terapéuticas.",
    "Definir plano terapêutico, frequência, metas e critérios de acompanhamento.": "Definir plan terapéutico, frecuencia, metas y criterios de seguimiento.",
    "Comparar evolução clínica, adesão ao plano e novas demandas do paciente.": "Comparar evolución clínica, adherencia al plan y nuevas necesidades del paciente.",
    "Registrar mudanças de mobilidade, força, dor, função e desempenho.": "Registrar cambios de movilidad, fuerza, dolor, función y desempeño.",
    "Revisar interpretação clínica frente aos objetivos terapêuticos.": "Revisar la interpretación clínica frente a los objetivos terapéuticos.",
    "Atualizar condutas, metas, frequência e próximos passos.": "Actualizar conductas, metas, frecuencia y próximos pasos.",
    "Registrar critérios clínicos de alta e percepção funcional do paciente.": "Registrar criterios clínicos de alta y percepción funcional del paciente.",
    "Documentar achados finais, evolução funcional e pontos de atenção.": "Documentar hallazgos finales, evolución funcional y puntos de atención.",
    "Concluir ciclo terapêutico conforme objetivos atingidos ou encaminhamentos necessários.": "Concluir el ciclo terapéutico según objetivos alcanzados o derivaciones necesarias.",
    "Orientar manutenção, prevenção de recidiva e sinais para retorno.": "Orientar mantenimiento, prevención de recurrencia y señales para volver.",
    "Avaliação inicial e definição de conduta.": "Evaluación inicial y definición de conducta.",
    "Paciente relata queixa principal, início dos sintomas, fatores de melhora/piora e expectativas do tratamento.": "El paciente relata queja principal, inicio de los síntomas, factores de mejora/empeoramiento y expectativas del tratamiento.",
    "Realizada avaliação física, inspeção, testes funcionais e registro dos achados relevantes.": "Se realizó evaluación física, inspección, pruebas funcionales y registro de hallazgos relevantes.",
    "Hipótese clínica inicial compatível com o quadro apresentado. Definidas prioridades terapêuticas.": "Hipótesis clínica inicial compatible con el cuadro presentado. Prioridades terapéuticas definidas.",
    "Orientações iniciais, proposta de plano terapêutico e agendamento das próximas sessões.": "Orientaciones iniciales, propuesta de plan terapéutico y programación de las próximas sesiones.",
    "Acompanhamento de retorno.": "Seguimiento de retorno.",
    "Paciente em retorno para acompanhamento da resposta clínica e evolução funcional.": "Paciente en retorno para seguimiento de la respuesta clínica y evolución funcional.",
    "Registrar testes clínicos ou funcionais realizados no retorno.": "Registrar pruebas clínicas o funcionales realizadas en el retorno.",
    "Manter ou ajustar condutas conforme resposta clínica observada.": "Mantener o ajustar conductas según la respuesta clínica observada.",
    "Paciente relata evolução desde a última consulta, adesão às orientações e sintomas atuais.": "El paciente relata evolución desde la última consulta, adhesión a las orientaciones y síntomas actuales.",
    "Reavaliados sinais clínicos, mobilidade, dor, função e resposta às condutas anteriores.": "Se reevaluaron signos clínicos, movilidad, dolor, función y respuesta a conductas anteriores.",
    "Quadro em acompanhamento, com ajustes conforme resposta clínica observada.": "Cuadro en seguimiento, con ajustes según la respuesta clínica observada.",
    "Manter ou ajustar condutas, reforçar orientações e programar próximo retorno.": "Mantener o ajustar conductas, reforzar orientaciones y programar próximo retorno.",
    "Reavaliação clínica periódica.": "Reevaluación clínica periódica.",
    "Paciente informa percepção de progresso, limitações persistentes e novas queixas, se houver.": "El paciente informa percepción de progreso, limitaciones persistentes y nuevas quejas, si las hubiera.",
    "Comparados indicadores funcionais, amplitude, força, dor e desempenho em relação à avaliação anterior.": "Se compararon indicadores funcionales, amplitud, fuerza, dolor y desempeño con la evaluación anterior.",
    "Evolução analisada frente aos objetivos terapêuticos e necessidade de ajustes.": "Evolución analizada frente a los objetivos terapéuticos y la necesidad de ajustes.",
    "Atualizar metas, condutas e frequência conforme evolução clínica.": "Actualizar metas, conductas y frecuencia según la evolución clínica.",
    "Alta clínica": "Alta clínica",
    "Evolução de alta clínica.": "Evolución de alta clínica.",
    "Evolução final do ciclo terapêutico, com critérios de alta e orientações registradas.": "Evolución final del ciclo terapéutico, con criterios de alta y orientaciones registradas.",
    "Registrar testes finais, medidas funcionais e critérios clínicos avaliados.": "Registrar pruebas finales, medidas funcionales y criterios clínicos evaluados.",
    "Encerramento do ciclo terapêutico.": "Cierre del ciclo terapéutico.",
    "Paciente relata melhora funcional e compreensão das orientações para manutenção.": "El paciente relata mejora funcional y comprensión de las orientaciones de mantenimiento.",
    "Critérios clínicos e funcionais de alta avaliados e registrados.": "Criterios clínicos y funcionales de alta evaluados y registrados.",
    "Paciente apto para alta clínica conforme evolução e objetivos atingidos.": "Paciente apto para alta clínica según evolución y objetivos alcanzados.",
    "Orientações domiciliares, sinais de alerta e recomendação de retorno se necessário.": "Orientaciones domiciliarias, señales de alerta y recomendación de retorno si es necesario.",
    "Ajuste de tratamento": "Ajuste de tratamiento",
    "Ajuste de conduta terapêutica.": "Ajuste de conducta terapéutica.",
    "Evolução registrada para ajuste do plano terapêutico conforme resposta do paciente.": "Evolución registrada para ajustar el plan terapéutico según la respuesta del paciente.",
    "Registrar achados ou testes que justificam o ajuste terapêutico.": "Registrar hallazgos o pruebas que justifiquen el ajuste terapéutico.",
    "Paciente relata resposta parcial, desconfortos ou necessidade de mudança no plano.": "El paciente relata respuesta parcial, molestias o necesidad de cambio en el plan.",
    "Observados achados que indicam ajuste de carga, técnica, frequência ou estratégia.": "Se observaron hallazgos que indican ajuste de carga, técnica, frecuencia o estrategia.",
    "Conduta revisada de acordo com tolerância, resposta clínica e objetivos.": "Conducta revisada de acuerdo con tolerancia, respuesta clínica y objetivos.",
    "Aplicar ajustes definidos e monitorar resposta nas próximas sessões.": "Aplicar ajustes definidos y monitorear respuesta en las próximas sesiones.",
    "Ansiedade": "Ansiedad",
    "Hipertensão": "Hipertensión",
    "Diabetes": "Diabetes",
    "Obesidade": "Obesidad",
    "Selecione": "Seleccione",
    "Valor previsto": "Valor previsto",
    "Apagar lead": "Eliminar Lead",
    "Apagar": "Eliminar",
    "Esse lead será removido permanentemente do cadastro.": "Este Lead será eliminado permanentemente del registro.",
    "Novo": "Nuevo",
    "Em Contato": "En contacto",
    "Em contato": "En contacto",
    "Convertido": "Convertido",
    "Não agendado": "No agendado",
    "Preço": "Precio",
    "Sem retorno": "Sin respuesta",
    "Sem interesse": "Sin interés",
    "Conflito de horário": "Conflicto de horario",
    "Já está em tratamento": "Ya está en tratamiento",
    "Distância": "Distancia",
    "Conveniência": "Conveniencia",
    "Outro": "Otro",
    "Indicação": "Indicación",
    "Origens do Lead": "Orígenes del Lead",
    "Use este cadastro para padronizar as origens utilizadas no funil.": "Use este registro para estandarizar los orígenes utilizados en el funnel.",
    "Nova origem": "Nuevo origen",
    "Adicionar origem": "Agregar origen",
    "Origem adicionada com sucesso!": "¡Origen agregado con éxito!",
    "Origem removida com sucesso!": "¡Origen eliminado con éxito!",
    "Erro ao adicionar origem": "Error al agregar origen",
    "Erro ao remover origem": "Error al eliminar origen",
    "Data de criação": "Fecha de creación",
    "Todos os períodos": "Todos los períodos",
    "Site": "Sitio web",
    "Presencial": "Presencial",
    "Fisioterapia": "Fisioterapia",
    "Acupuntura": "Acupuntura",
    "Pilates": "Pilates",
    "Abril Retorno sem Dor": "Abril regreso sin dolor",
    "Pilates no Almoco": "Pilates al mediodía",
    "Pilates no Almoço": "Pilates al mediodía",
    "Semana da Postura Corporativa": "Semana de postura corporativa",
    "Volta pra Casa": "Vuelta a casa",
    "Chegou por reels sobre dor ao acordar e queria avaliação rápida.": "Llegó por reels sobre dolor al despertar y quería una evaluación rápida.",
    "Corredor amador procurando retorno seguro apos dor no joelho.": "Corredor amateur buscando un retorno seguro después de dolor en la rodilla.",
    "Indicação do RH da empresa parceira para pilates no almoço.": "Indicación de RR. HH. de la empresa asociada para pilates al mediodía.",
    "Chegou por indicacao da esposa para melhorar sono e tensao cervical.": "Llegó por indicación de su esposa para mejorar el sueño y la tensión cervical.",
    "Pediu plano para voltar a treinar sem piorar dor no quadril.": "Pidió un plan para volver a entrenar sin empeorar el dolor de cadera.",
    "Ja saiu com aula teste marcada para a proxima semana.": "Salió con una clase de prueba agendada para la próxima semana.",
    "RH gostou da proposta, mas o colaborador pediu mais prazo para decidir.": "RR. HH. aprobó la propuesta, pero el colaborador pidió más tiempo para decidir.",
    "Quer entender se a clínica atende no horário do fim do expediente.": "Quiere saber si la clínica atiende al final de la jornada laboral.",
    "Preencheu formulário à noite e ainda não retornou ao WhatsApp.": "Completó el formulario por la noche y todavía no respondió por WhatsApp.",
    "Interesse em pilates para fortalecer depois da gestação.": "Interés en pilates para fortalecerse después de la gestación.",
    "Parou no preco do pacote e pediu segunda opcao mais enxuta.": "Se detuvo por el precio del paquete y pidió una segunda opción más económica.",
    "Mudou de cidade e desistiu do agendamento.": "Se mudó de ciudad y desistió de la cita.",
    "Pacientes Ativos": "Pacientes activos",
    "Consultas": "Consultas",
    "Status dos Agendamentos": "Estado de las citas",
    "Agendado": "Agendada",
    "Em atendimento": "En atención",
    "Atendimento realizado": "Atención realizada",
    "Faltou": "No asistió",
    "Em Andamento": "En curso",
    "Concluído": "Completada",
    "Não Compareceu": "No asistió",
    "Não foi possível marcar o agendamento como atendimento realizado.": "No fue posible marcar la cita como atención realizada.",
    "CAC": "CAC",
    "Custo de Aquisição": "Costo de adquisición",
    "LTV": "LTV",
    "Valor por Paciente": "Valor por paciente",
    "ROI": "ROI",
    "Retorno sobre Investimento": "Retorno de la inversión",
    "Pesquisa de experiência": "Encuesta de experiencia",
    "Carregando pesquisa...": "Cargando encuesta...",
    "Esta pesquisa não está disponível.": "Esta encuesta no está disponible.",
    "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.": "Dé una calificación de 0 a 10 y, si quiere, deje un comentario para el equipo.",
    "Sua nota": "Su calificación",
    "Conte para a clínica o que funcionou bem ou o que pode melhorar.": "Cuéntele a la clínica qué funcionó bien o qué puede mejorar.",
    "Enviar resposta": "Enviar respuesta",
    "Desenvolvido por": "Desarrollado por",
    "Operação inteligente para clínicas em crescimento": "Gestión inteligente para clínicas en crecimiento",
    "organiza a jornada da clínica do primeiro acesso até a operação diária.": "organiza la jornada de la clínica desde el primer acceso hasta la gestión diaria.",
    "Uma camada de gestão criada para abrir clínicas, estruturar equipes e acompanhar marketing, agenda, atendimento e resultados com mais clareza.": "Una capa de gestión creada para poner clínicas en marcha, estructurar equipos y acompañar marketing, calendario, atención y resultados con más claridad.",
    "Começar onboarding": "Iniciar onboarding",
    "Acessar plataforma": "Acceder a la plataforma",
    "Portal Principal": "Portal principal",
    "Onboarding guiado": "Onboarding guiado",
    "Cadastre a clínica e o administrador principal em um fluxo único e direto, com acesso imediato para começar a operação no": "Registre la clínica y el administrador principal en un flujo único y directo, con acceso inmediato para empezar a trabajar en",
    "Criar nova clínica": "Crear nueva clínica",
    "Onboarding rápido para novas clínicas": "Onboarding rápido para nuevas clínicas",
    "Operação financeira, agenda e atendimento em um só lugar": "Finanzas, calendario y atención en una sola plataforma",
    "Estrutura preparada para multi-clínica e administração central": "Estructura preparada para multiclínica y administración central",
    "Entrada pública clara para login, cadastro e expansão do produto.": "Entrada pública clara para inicio de sesión, registro y expansión del producto.",
    "Clínicas organizadas com isolamento de dados e administração por unidade.": "Clínicas organizadas con aislamiento de datos y administración por unidad.",
    "Visão integrada de operação, comercial, agenda, atendimento e financeiro.": "Visión integrada de gestión, comercial, calendario, atención y finanzas.",
    "Plataforma para operação de clínicas, equipes e crescimento com mais clareza.": "Plataforma para gestionar clínicas, equipos y crecimiento con más claridad.",
    "Estruture sua clínica e inicie a operação com governança desde o primeiro acesso.": "Estructure su clínica e inicie la gestión con gobernanza desde el primer acceso.",
    "Configure a clínica e o administrador principal no mesmo fluxo.": "Configure la clínica y el administrador principal en el mismo flujo.",
    "Selecione uma nota de 0 a 10.": "Seleccione una calificación de 0 a 10.",
    "Resposta enviada com sucesso.": "Respuesta enviada con éxito.",
    "Algo deu errado. Tente novamente.": "Algo salió mal. Inténtelo nuevamente.",
    "Revise os campos informados e tente novamente.": "Revise los campos informados e inténtelo nuevamente.",
    "Preencha o campo obrigatório: {fieldLabel}.": "Complete el campo obligatorio: {fieldLabel}.",
    "Preencha todos os campos obrigatórios.": "Complete todos los campos obligatorios.",
    "Revise o valor informado para o campo: {fieldLabel}.": "Revise el valor introducido en el campo: {fieldLabel}.",
    "Revise os campos clínicos informados.": "Revise los campos clínicos introducidos.",
    "O campo informado precisa ser maior ou igual a 15.": "El campo debe ser mayor o igual a 15.",
    "Acesso em análise ou bloqueado!": "¡Acceso en análisis o bloqueado!",
    "Acesso bloqueado": "Acceso bloqueado",
    "Seu perfil atual não possui permissão para acessar esta página.": "Su perfil actual no tiene permiso para acceder a esta página.",
    "Você não tem permissão para executar esta ação.": "No tiene permiso para ejecutar esta acción.",
    "Você não tem permissão para editar a agenda.": "No tiene permiso para editar la agenda.",
    "Você não tem permissão para registrar lançamentos financeiros.": "No tiene permiso para registrar movimientos financieros.",
    "Permissões do profissional": "Permisos del profesional",
    "Defina quais módulos este profissional poderá acessar.": "Defina a qué módulos podrá acceder este profesional.",
    "Prontuário": "Historia clínica",
    "Erro ao fazer login": "Error al iniciar sesión",
    "Este portal é exclusivo para super admin.": "Este portal es exclusivo para super admins.",
    "As senhas não coincidem.": "Las contraseñas no coinciden.",
    "Erro ao criar conta": "Error al crear la cuenta",
    "CEP": "Código postal",
    "Resumo": "Resumen",
    "Agendamentos sem plano de tratamento": "Citas sin plan de tratamiento",
    "Atendimentos Avulsos": "Atenciones sueltas",
    "Ver histórico completo": "Ver historial completo",
    "Falha ao consultar CEP": "Error al consultar el código postal",
    "CEP não encontrado.": "Código postal no encontrado.",
    "Não foi possível consultar o CEP.": "No fue posible consultar el código postal.",
    "Aparência": "Apariencia",
    "Alterne entre tema claro e escuro.": "Cambie entre tema claro y oscuro.",
    "Escolha o idioma usado nos menus e páginas.": "Elija el idioma utilizado en menús y páginas.",
    "Moeda usada nos valores financeiros.": "Moneda utilizada en los valores financieros.",
    "Termine a sessão neste dispositivo.": "Cierre sesión en este dispositivo.",
    "Não foi possível carregar os modelos clínicos.": "No se pudieron cargar las plantillas clínicas.",
    "Modelo clínico salvo como rascunho.": "Plantilla clínica guardada como borrador.",
    "Modelo clínico criado como rascunho.": "Plantilla clínica creada como borrador.",
    "Não foi possível salvar o modelo clínico.": "No se pudo guardar la plantilla clínica.",
    "Não foi possível concluir a ação.": "No se pudo completar la acción.",
    "Modelo duplicado como rascunho.": "Plantilla duplicada como borrador.",
    "Modelo publicado com sucesso.": "Plantilla publicada correctamente.",
    "Modelo inativado com sucesso.": "Plantilla desactivada correctamente.",
    "Modelo reativado com sucesso.": "Plantilla reactivada correctamente.",
    "Gerencie modelos versionados para avaliações, evoluções e outros registros clínicos, preservando o histórico.": "Gestione plantillas versionadas para evaluaciones, evoluciones y otros registros clínicos, conservando el historial.",
    "Novo modelo clínico": "Nueva plantilla clínica",
    "Tipo": "Tipo",
    "Ativo": "Activo",
    "Inativo": "Inactivo",
    "Modelo clínico": "Plantilla clínica",
    "Versões": "Versiones",
    "Associações": "Asociaciones",
    "Última atualização": "Última actualización",
    "Modelo padrão": "Plantilla predeterminada",
    "Publicado": "Publicado",
    "Todos os serviços e procedimentos": "Todos los servicios y procedimientos",
    "Pré-visualizar": "Previsualizar",
    "Duplicar": "Duplicar",
    "Editar rascunho": "Editar borrador",
    "Publicar": "Publicar",
    "Nova versão": "Nueva versión",
    "Nenhum modelo clínico encontrado.": "No se encontró ninguna plantilla clínica.",
    "Editar rascunho do modelo": "Editar borrador de la plantilla",
    "Criar modelo clínico": "Crear plantilla clínica",
    "Setores vinculados": "Áreas vinculadas",
    "Serviços e procedimentos vinculados": "Servicios y procedimientos vinculados",
    "Estrutura do modelo": "Estructura de la plantilla",
    "Organize as seções e os campos do modelo. Alterações estruturais ficam disponíveis somente após a publicação.": "Organice las secciones y los campos de la plantilla. Los cambios estructurales solo estarán disponibles después de la publicación.",
    "Adicionar seção": "Añadir sección",
    "Identificador técnico": "Identificador técnico",
    "Título da seção": "Título de la sección",
    "Descrição da seção": "Descripción de la sección",
    "Identificador do campo": "Identificador del campo",
    "Rótulo": "Etiqueta",
    "Tipo de campo": "Tipo de campo",
    "Texto curto": "Texto corto",
    "Texto longo": "Texto largo",
    "Escala numérica": "Escala numérica",
    "Seleção única": "Selección única",
    "Seleção múltipla": "Selección múltiple",
    "Caixa de seleção": "Casilla de verificación",
    "Data civil": "Fecha",
    "Texto informativo": "Texto informativo",
    "Sim/Não": "Sí/No",
    "Medida com unidade": "Medida con unidad",
    "Obrigatório": "Obligatorio",
    "Opções": "Opciones",
    "Nova seção": "Nueva sección",
    "Novo campo": "Nuevo campo",
    "Testes clínicos": "Pruebas clínicas",
    "Mover seção para cima": "Mover sección hacia arriba",
    "Mover seção para baixo": "Mover sección hacia abajo",
    "Excluir seção": "Eliminar sección",
    "Mover campo para cima": "Mover campo hacia arriba",
    "Mover campo para baixo": "Mover campo hacia abajo",
    "Excluir campo": "Eliminar campo",
    "opcao_1:Opção 1, opcao_2:Opção 2": "opcao_1:Opción 1, opcao_2:Opción 2",
    "{count} vínculo selecionado": "{count} vínculo seleccionado",
    "{count} vínculos selecionados": "{count} vínculos seleccionados",
    "Adicionar campo": "Agregar campo",
    "Após publicar, a versão fica imutável. Para alterar a estrutura depois disso, crie uma nova versão.": "Después de publicar, la versión queda inmutable. Para cambiar la estructura después, cree una nueva versión.",
    "Cancelar": "Cancelar",
    "Salvar rascunho": "Guardar borrador",
    "Pré-visualizar modelo": "Previsualizar plantilla",
    "Nenhuma versão disponível para pré-visualização.": "No hay versión disponible para previsualización.",
    "Selecionar": "Seleccionar",
    "Marcado": "Marcado",
    "Selecionar modelo clínico": "Seleccionar plantilla clínica",
    "Sem modelo versionado": "Sin plantilla versionada",
    "Selecionar agendamento": "Seleccionar cita",
    "Salvando...": "Guardando...",
    "Personalizado": "Personalizado",
    "Leads por origem": "Leads por origen",
    "Nenhum lead encontrado para o período selecionado.": "No se encontraron leads en el período seleccionado.",
    "Sem base suficiente": "Sin base suficiente",
    "Atribuição insuficiente": "Atribución insuficiente",
    "Custo realizado ausente": "Coste realizado ausente",
    "Coorte longitudinal insuficiente": "Cohorte longitudinal insuficiente",
    "Dados insuficientes": "Datos insuficientes",
    "Dashboard Executivo": "Dashboard ejecutivo",
    "Visão consolidada da clínica para decisões rápidas, sem duplicar os módulos detalhados.": "Vista consolidada de la clínica para decisiones rápidas, sin duplicar los módulos detallados.",
    "A data final não pode ser anterior à data inicial.": "La fecha final no puede ser anterior a la fecha inicial.",
    "dia": "día",
    "Atualizando...": "Actualizando...",
    "Não foi possível carregar os indicadores executivos agora. Tente novamente em instantes.": "No se pudieron cargar los indicadores ejecutivos ahora. Inténtelo de nuevo en unos instantes.",
    "Resumo executivo do período selecionado com os blocos permitidos para o seu perfil.": "Resumen ejecutivo del período seleccionado con los bloques permitidos para su perfil.",
    "Leads criados": "Leads creados",
    "Registros que compõem este indicador no período e setor selecionados.": "Registros incluidos en este indicador para el período y sector seleccionados.",
    "Leads agendados": "Leads agendados",
    "Agendado no período": "Agendado en el período",
    "Não agendado no período": "No agendado en el período",
    "Convertido no período": "Convertido en el período",
    "Não convertido no período": "No convertido en el período",
    "Atendimentos concluídos": "Citas completadas",
    "Atendimentos no período": "Citas en el período",
    "Não concluído": "No completado",
    "Faltas": "Inasistencias",
    "Falta": "Inasistencia",
    "Sem falta": "Sin inasistencia",
    "Leads criados no período": "Leads creados en el período",
    "Agendados": "Programados",
    "Não agendados": "No programados",
    "Leads por status": "Leads por estado",
    "Atendimentos realizados no período": "Atenciones realizadas en el período",
    "Agendamentos vinculados e lançamentos manuais": "Citas vinculadas y registros manuales de ingresos",
    "Atendimentos realizados": "Atenciones realizadas",
    "consultas no período": "consultas en el período",
    "Pacientes ativos": "Pacientes activos",
    "Cadastro canônico de pacientes": "Registro canónico de pacientes",
    "Receita realizada": "Ingresos realizados",
    "lançamentos": "registros",
    "Seu perfil pode acessar o Dashboard, mas ainda não possui permissões para blocos executivos detalhados.": "Su perfil puede acceder al Dashboard, pero aún no tiene permisos para bloques ejecutivos detallados.",
    "Captação e comercial": "Captación y comercial",
    "Funil de entrada resumido; a análise detalhada continua nos módulos de Leads e Marketing.": "Embudo de entrada resumido; el análisis detallado continúa en los módulos de Leads y Marketing.",
    "Abrir Marketing": "Abrir Marketing",
    "Abrir Leads": "Abrir Leads",
    "Taxa de agendamento": "Tasa de agendamiento",
    "agendados": "programados",
    "Conversão em paciente": "Conversión en paciente",
    "Principal origem": "Origen principal",
    "Sem origem identificada": "Sin origen identificado",
    "Sem permissão para visualizar indicadores comerciais.": "Sin permiso para ver indicadores comerciales.",
    "Fonte oficial: métricas de CAC, LTV e ROI.": "Fuente oficial: métricas de CAC, LTV y ROI.",
    "Custo de aquisição atribuído.": "Coste de adquisición atribuido.",
    "ROI de marketing": "ROI de marketing",
    "Retorno atribuído ao investimento.": "Retorno atribuido a la inversión.",
    "Operação clínica": "Operación clínica",
    "Agenda, comparecimento e continuidade do cuidado em visão resumida.": "Agenda, asistencia y continuidad del cuidado en vista resumida.",
    "Abrir Atendimento": "Abrir Atención",
    "Abrir Agenda": "Abrir Agenda",
    "Consultas no período": "Consultas en el período",
    "Taxa de conclusão": "Tasa de finalización",
    "Taxa de faltas": "Tasa de inasistencias",
    "Somente atendimentos concluídos no período e setor selecionados.": "Solo citas completadas en el período y sector seleccionados.",
    "Somente faltas no período e setor selecionados.": "Solo inasistencias en el período y sector seleccionados.",
    "Vendas confirmadas de planos no período e setor selecionados.": "Ventas confirmadas de planes en el período y sector seleccionados.",
    "Pacientes distintos": "Pacientes distintos",
    "Valor total vendido": "Valor total vendido",
    "Forma de pagamento recebida": "Forma de pago recibida",
    "Pacientes com atendimento concluído": "Pacientes con citas completadas",
    "Pacientes com falta": "Pacientes con inasistencias",
    "Faltas no período": "Inasistencias en el período",
    "Detalhamento por profissional": "Desglose por profesional",
    "Tipo de atendimento não identificado": "Tipo de cita no identificado",
    "Profissional não identificado": "Profesional no identificado",
    "Retenção de pacientes": "Retención de pacientes",
    "pacientes com continuidade": "pacientes con continuidad",
    "Sem permissão para visualizar indicadores da agenda.": "Sin permiso para ver indicadores de agenda.",
    "Entradas realizadas, despesas por competência e resultado do período com origem no serviço financeiro oficial.": "Entradas realizadas, gastos por devengo y resultado del período procedentes del servicio financiero oficial.",
    "Abrir Financeiro": "Abrir Finanzas",
    "Despesas realizadas": "Gastos realizados",
    "Resultado do período": "Resultado del período",
    "Por lançamento financeiro": "Por registro financiero",
    "Sem permissão para visualizar indicadores financeiros.": "Sin permiso para ver indicadores financieros.",
    "Entradas financeiras no período": "Entradas financieras en el período",
    "Entradas por setor": "Entradas por sector",
    "Despesas reconhecidas por competência no período": "Gastos reconocidos por devengo en el período",
    "Valor líquido de planos vendidos": "Valor neto de los planes vendidos",
    "Sem dados setoriais no período": "Sin datos por sector en el período",
    "Não foi possível carregar os repasses profissionais.": "No se pudieron cargar los pagos profesionales.",
    "Não foi possível carregar os filtros de repasse.": "No se pudieron cargar los filtros de pagos.",
    "Repasses pendentes gerados com sucesso.": "Pagos pendientes generados correctamente.",
    "Não foi possível gerar repasses pendentes.": "No se pudieron generar los pagos pendientes.",
    "Repasse aprovado com sucesso.": "Pago aprobado correctamente.",
    "Não foi possível aprovar o repasse.": "No se pudo aprobar el pago.",
    "Pagamento de repasse registrado com sucesso.": "Pago profesional registrado correctamente.",
    "Não foi possível registrar o pagamento do repasse.": "No se pudo registrar el pago profesional.",
    "Repasse cancelado com sucesso.": "Pago cancelado correctamente.",
    "Não foi possível cancelar o repasse.": "No se pudo cancelar el pago.",
    "Repasses profissionais": "Pagos profesionales",
    "Repasses calculados a partir de produção clínica válida, separados de venda, recebível e caixa do paciente.": "Pagos calculados a partir de producción clínica válida, separados de venta, cuenta por cobrar y caja del paciente.",
    "Aprovados": "Aprobados",
    "Valor a pagar": "Importe a pagar",
    "Valor pago": "Importe pagado",
    "Bloqueados": "Bloqueados",
    "Cancelados": "Cancelados",
    "Modalidade": "Modalidad",
    "Valor fixo": "Valor fijo",
    "Percentual da produção": "Porcentaje de producción",
    "Pagamento": "Pago",
    "Com saldo": "Con saldo",
    "Sem saldo": "Sin saldo",
    "Produção base": "Producción base",
    "Regra": "Regla",
    "Valor do repasse": "Valor del pago",
    "Nenhum repasse profissional encontrado": "No se encontró ningún pago profesional",
    "Aprovar": "Aprobar",
    "Pagar": "Pagar",
    "Detalhes do repasse": "Detalles del pago",
    "Produção de origem": "Producción de origen",
    "Versão": "Versión",
    "Saldo a pagar": "Saldo a pagar",
    "Bloqueio": "Bloqueo",
    "Valor da produção não disponível para cálculo": "Valor de la producción no disponible para el cálculo",
    "Nenhuma regra de repasse aplicável": "No hay ninguna regla de liquidación profesional aplicable",
    "Conflito entre regras de repasse": "Conflicto entre reglas de liquidación profesional",
    "Produção clínica cancelada": "Producción clínica cancelada",
    "Repasse fixo não autorizado para produção de cortesia": "Liquidación fija no autorizada para una producción de cortesía",
    "Moeda da regra de repasse incompatível com a produção": "La moneda de la regla de liquidación no coincide con la de la producción",
    "Regra de repasse inválida": "Regla de liquidación profesional no válida",
    "Motivo do bloqueio não disponível": "Motivo del bloqueo no disponible",
    "Produção clínica de {date}": "Producción clínica del {date}",
    "Produção clínica vinculada": "Producción clínica vinculada",
    "Status do repasse não disponível": "Estado de la liquidación no disponible",
    "Regra de repasse não disponível": "Regla de liquidación no disponible",
    "Registrar pagamento": "Registrar pago",
    "Cancelar repasse": "Cancelar pago",
    "O repasse será cancelado sem alterar venda, recebível ou caixa do paciente.": "La liquidación profesional se cancelará sin alterar la venta, la cuenta por cobrar ni la caja del paciente.",
    "Não foi possível carregar regras de repasse.": "No se pudieron cargar las reglas de pago.",
    "Regra de repasse criada com sucesso.": "Regla de pago creada correctamente.",
    "Não foi possível criar a regra de repasse.": "No se pudo crear la regla de pago.",
    "Regra de repasse inativada com sucesso.": "Regla de pago inactivada correctamente.",
    "Não foi possível inativar a regra de repasse.": "No se pudo inactivar la regla de pago.",
    "Nova regra de repasse": "Nueva regla de pago",
    "Quando serviço e setor são informados, esta regra só se aplica se ambos corresponderem.": "Cuando se indican un servicio y un sector, esta regla solo se aplica si ambos coinciden.",
    "Prioridade das regras: serviço + setor, serviço, setor e regra geral.": "Prioridad de las reglas: servicio + sector, servicio, sector y regla general.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional, serviço e setor.": "Ya existe una regla de liquidación vigente o con un periodo de vigencia superpuesto para este mismo profesional, servicio y sector.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e serviço.": "Ya existe una regla de liquidación vigente o con un periodo de vigencia superpuesto para este mismo profesional y servicio.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e setor.": "Ya existe una regla de liquidación vigente o con un periodo de vigencia superpuesto para este mismo profesional y sector.",
    "Já existe uma regra geral de repasse vigente ou com vigência sobreposta para este profissional.": "Ya existe una regla general de liquidación vigente o con un periodo de vigencia superpuesto para este profesional.",
    "Setor opcional": "Sector opcional",
    "Serviço opcional": "Servicio opcional",
    "Percentual": "Porcentaje",
    "Vigência inicial": "Inicio de vigencia",
    "Vigência final": "Fin de vigencia",
    "Permitir cortesia": "Permitir cortesía",
    "Criar regra": "Crear regla",
    "Vigência": "Vigencia",
    "Nenhuma regra de repasse encontrada": "No se encontró ninguna regla de pago",
    "Bloco sem permissão": "Bloque sin permiso",
    "Oculto": "Oculto",
    "Fonte": "Fuente",
    "registros": "registros",
    "Abrir origem": "Abrir origen",
    "Nenhum dado gerencial encontrado": "No se encontraron datos gerenciales",
    "Repasse calculado": "Pago calculado",
    "Repasse pago": "Pago realizado",
    "Repasse a pagar": "Pago pendiente",
    "Não foi possível carregar a visão gerencial.": "No se pudo cargar la vista gerencial.",
    "Não foi possível exportar a visão gerencial.": "No se pudo exportar la vista gerencial.",
    "Visão gerencial": "Vista gerencial",
    "Vendido, produzido, recebido e repasses em fontes separadas, com comparações gerenciais temporais.": "Vendido, producido, cobrado y pagos en fuentes separadas, con comparaciones gerenciales temporales.",
    "Exportar": "Exportar",
    "Exportar CSV": "Exportar CSV",
    "Excel": "Excel",
    "PDF": "PDF",
    "Gerando exportação": "Generando exportación",
    "Gerando relatório": "Generando informe",
    "Gerar relatório": "Generar informe",
    "Exportação gerencial concluída.": "Exportación gerencial concluida.",
    "Relatório gerencial gerado com sucesso.": "Informe gerencial generado con éxito.",
    "Não foi possível gerar a exportação.": "No se pudo generar la exportación.",
    "Não foi possível gerar o relatório.": "No se pudo generar el informe.",
    "Histórico de lotes": "Historial de lotes",
    "Detalhamento dos snapshots": "Detalle de snapshots",
    "Confiabilidade": "Fiabilidad",
    "Somente confirmados": "Solo confirmados",
    "Incluir inferidos": "Incluir inferidos",
    "Incluir legados": "Incluir legados",
    "Todos os dados": "Todos los datos",
    "Visão parcial": "Vista parcial",
    "Alguns blocos foram ocultados por permissão e não foram retornados no payload.": "Algunos bloques se ocultaron por permiso y no se devolvieron en el payload.",
    "Sem base": "Sin base",
    "Série temporal": "Serie temporal",
    "Pendências de qualidade dos dados": "Pendientes de calidad de datos",
    "Planos e pacotes": "Planes y paquetes",
    "Planos vendidos": "Planes vendidos",
    "Valor vendido em planos": "Valor vendido en planes",
    "Sessões produzidas": "Sesiones producidas",
    "Limitações": "Limitaciones",
    "Escala de dor": "Escala de dolor",
    "Rateio gerencial habilitado.": "Prorrateo gerencial habilitado.",
    "Rateio gerencial desabilitado.": "Prorrateo gerencial deshabilitado.",
    "Simulação de rateio concluída.": "Simulación de prorrateo concluida.",
    "Rateio gerencial aplicado.": "Prorrateo gerencial aplicado.",
    "Aplicação de rateio revertida.": "Aplicación de prorrateo revertida.",
    "Elegibilidade da categoria atualizada.": "Elegibilidad de la categoría actualizada.",
    "Regra de rateio criada em rascunho.": "Regla de prorrateo creada como borrador.",
    "Regra de rateio ativada.": "Regla de prorrateo activada.",
    "Regra de rateio inativada.": "Regla de prorrateo inactivada.",
    "Nova versão criada em rascunho.": "Nueva versión creada como borrador.",
    "Rateio gerencial por setor": "Prorrateo gerencial por sector",
    "Configura regras percentuais por setor sem aplicar valores nesta etapa.": "Configura reglas porcentuales por sector sin aplicar valores en esta etapa.",
    "Habilitar rateio": "Habilitar prorrateo",
    "Simular rateio": "Simular prorrateo",
    "Inicio": "Inicio",
    "Simulando": "Simulando",
    "Simular": "Simular",
    "Aviso: simulação de rateio não aplicada": "Aviso: simulación de prorrateo no aplicada",
    "Aplicação controlada do rateio": "Aplicación controlada del prorrateo",
    "Fingerprint da simulação": "Fingerprint de la simulación",
    "Fingerprint da aplicação": "Fingerprint de la aplicación",
    "Aplicar rateio gerencial": "Aplicar prorrateo gerencial",
    "Cancelar aplicação": "Cancelar aplicación",
    "Observação da aplicação": "Observación de la aplicación",
    "Chave de idempotência": "Clave de idempotencia",
    "Aplicando": "Aplicando",
    "Confirmar aplicação": "Confirmar aplicación",
    "Aplicações de rateio": "Aplicaciones de prorrateo",
    "Aplicada em": "Aplicada el",
    "Período da aplicação": "Período de la aplicación",
    "Total aplicado": "Total aplicado",
    "Nenhuma aplicação de rateio encontrada": "No se encontró ninguna aplicación de prorrateo",
    "Aplicada": "Aplicada",
    "Revertida": "Revertida",
    "Detalhar aplicação": "Detallar aplicación",
    "Preparar reversão": "Preparar reversión",
    "Itens aplicados": "Elementos aplicados",
    "Setores aplicados": "Sectores aplicados",
    "Motivo da reversão": "Motivo de la reversión",
    "Revertendo": "Revirtiendo",
    "Reverter aplicação": "Revertir aplicación",
    "Despesas encontradas": "Gastos encontrados",
    "Elegíveis": "Elegibles",
    "Simuladas": "Simuladas",
    "Total original": "Total original",
    "Total distribuído": "Total distribuído",
    "Diferença de arredondamento": "Diferencia de redondeo",
    "Participação": "Participación",
    "Valor simulado": "Valor simulado",
    "Nenhum valor simulado por setor": "Ningún valor simulado por sector",
    "Qtd.": "Cant.",
    "Nenhuma despesa fora da simulação": "Ningún gasto fuera de la simulación",
    "Despesa": "Gasto",
    "Competência da despesa": "Fecha de devengo del gasto",
    "Original": "Original",
    "Distribuído": "Distribuido",
    "Despesa simulada": "Gasto simulado",
    "Rateio desabilitado": "Prorrateo deshabilitado",
    "Despesa não elegível": "Gasto no elegible",
    "Categoria não elegível": "Categoría no elegible",
    "Sem regra aplicável": "Sin regla aplicable",
    "Sem competência": "Faltan datos de devengo",
    "Competência não confirmada": "Clasificación por devengo no confirmada",
    "Moeda não compatível": "Moneda no compatible",
    "Despesa direta": "Gasto directo",
    "Regra inválida": "Regla inválida",
    "Não simulada": "No simulada",
    "Categorias elegíveis": "Categorías elegibles",
    "Não elegível": "No elegible",
    "Usar padrão da clínica": "Usar el patrón de la clínica",
    "Usar regra da categoria": "Usar la regla de la categoría",
    "Nova regra de rateio": "Nueva regla de prorrateo",
    "Nome da regra": "Nombre de la regla",
    "Escopo": "Alcance",
    "Padrão da clínica": "Patrón de la clínica",
    "Categoria de despesa": "Categoría de gasto",
    "Sem categoria": "Sin categoría",
    "Soma dos percentuais": "Suma de porcentajes",
    "Criar rascunho": "Crear borrador",
    "Nenhuma regra de rateio encontrada": "No se encontró ninguna regla de prorrateo",
    "Ativar regra": "Activar regla",
    "Inativar regra": "Inactivar regla",
    "Histórico": "Historial",
    "Histórico de versões": "Historial de versiones",
    "Dinheiro": "Efectivo",
    "Cheque": "Cheque",
    "Cartão de crédito": "Tarjeta de crédito",
    "Cartão de débito": "Tarjeta de débito",
    "Cartão de crédito parcelado": "Tarjeta de crédito en cuotas",
    "Transferência": "Transferencia bancaria",
    "Convênio": "Seguro médico",
    "Aluguel": "Alquiler",
    "Contas": "Servicios",
    "Salários": "Salarios",
    "Materiais": "Materiales",
    "Equipamentos": "Equipos",
    "Impostos": "Impuestos",
    "Manutenção": "Mantenimiento",
    "Fixa": "Fija",
    "Variável": "Variable",
    "Próximo": "Siguiente",
    "Modo de visualização": "Vista del calendario",
    "Nenhum agendamento encontrado para este período.": "No se encontraron citas para este período.",
    "Sem agendamentos": "Sin citas",
    "Planos vendidos no período": "Planes vendidos en el período",
    "Desempenho comercial e financeiro dos planos vendidos no intervalo selecionado.": "Rendimiento comercial y financiero de los planes vendidos en el intervalo seleccionado.",
    "Valor líquido total dos planos vendidos": "Valor neto total de los planes vendidos",
    "Valor pendente": "Importe pendiente",
    "Plano mais vendido": "Plan más vendido",
    "Plano com maior valor líquido vendido": "Plan con el mayor valor neto vendido",
    "Buscar por nome do plano": "Buscar por nombre del plan",
    "Digite o nome do plano": "Introduzca el nombre del plan",
    "Tipo do plano": "Tipo de plan",
    "Situação financeira": "Situación financiera",
    "Todas as situações": "Todas las situaciones",
    "Parcial": "Pago parcial",
    "Qtde. vendida": "Cant. vendida",
    "Detalhes das vendas do plano": "Detalles de las ventas del plan",
    "Parcelamento": "Pago en cuotas",
    "Profissional ou responsável": "Profesional o responsable",
    "Não informado": "No informado",
    "à vista": "Al contado",
    "parcelas": "cuotas",
    "Período do relatório": "Período del informe",
    "Relatório exportado com sucesso.": "Informe exportado correctamente.",
    "Não foi possível abrir a exportação.": "No se pudo abrir la exportación.",
    "Erro ao carregar o relatório de planos vendidos.": "Error al cargar el informe de planes vendidos.",
    "Ordenar em ordem crescente": "Ordenar de forma ascendente",
    "Ordenar em ordem decrescente": "Ordenar de forma descendente",
    "Meta de faturamento": "Meta de facturación",
    "Acompanhe o progresso do período selecionado.": "Monitoree el progreso del período seleccionado.",
    "Realizado": "Facturado",
    "Atingido": "Avance",
    "Meta atingida no período.": "Meta alcanzada en el período.",
    "Nenhuma meta financeira vigente para o período.": "No hay una meta financiera vigente para el período.",
    "Indicadores por unidade de receita": "Indicadores por unidad de ingreso",
    "Não disponível": "No disponible",
    "unidades": "unidades",
    "Receita realizada por agendamento vinculado ou lançamento manual": "Ingreso realizado por cita vinculada o registro manual",
    "Base da métrica: atendimentos vinculados e lançamentos manuais de receita pagos no período.": "Base de la métrica: citas vinculadas y registros manuales de ingresos pagados en el período.",
    "Unidades consideradas": "Unidades consideradas",
    "Custo por unidade de receita": "Coste por unidad de ingreso",
    "Resultado por unidade de receita": "Resultado por unidad de ingreso",
    "Ponto de equilíbrio em unidades de receita": "Punto de equilibrio en unidades de ingreso",
    "Formas de pagamento": "Formas de pago",
    "Cadastre meios de pagamento, adquirentes, prazos e taxas para preparar o cálculo automático financeiro.": "Registre medios de pago, adquirentes, plazos y tasas para preparar el cálculo automático de comisiones.",
    "Nova forma de pagamento": "Nueva forma de pago",
    "Editar forma de pagamento": "Editar forma de pago",
    "Todas as adquirentes": "Todas las adquirentes",
    "Todos os status": "Todos los estados",
    "Adquirente/maquininha": "Adquirente/terminal",
    "Tipo aceito": "Tipo aceptado",
    "Taxa percentual": "Tasa porcentual",
    "Taxa fixa": "Tasa fija",
    "Prazo de recebimento": "Plazo de cobro",
    "Limite de parcelas": "Límite de cuotas",
    "Taxas por parcela": "Tasas por cuota",
    "Taxas": "Tasas",
    "Nenhuma forma de pagamento encontrada.": "No se encontraron formas de pago.",
    "Forma de pagamento cadastrada.": "Forma de pago creada.",
    "Forma de pagamento atualizada.": "Forma de pago actualizada.",
    "Forma de pagamento excluída.": "Forma de pago eliminada.",
    "Não foi possível excluir a forma de pagamento.": "No fue posible eliminar la forma de pago.",
    "Preencha os campos obrigatérios.": "Complete los campos obligatorios.",
    "dias": "días",
    "Informe a taxa percentual cobrada em cada quantidade de parcelas.": "Informe la tasa porcentual cobrada para cada cantidad de cuotas.",
    "Essas configurações ainda não alteram lançamentos existentes; elas deixam a clínica pronta para cálculo automático de taxas.": "Estas configuraciones aún no modifican movimientos existentes; preparan la clínica para el cálculo automático de tasas.",
    "Carregando relatórios financeiros...": "Cargando reportes financieros...",
    "Visão gerencial para acompanhar entradas, saídas e saldo de caixa.": "Vista de gestión para acompañar entradas, salidas y saldo de caja.",
    "Erro ao excluir receita": "Error al eliminar el ingreso",
    "Erro ao excluir despesa": "Error al eliminar el gasto",
    "Erro ao carregar dados financeiros": "Error al cargar los datos financieros",
    "Excluir receita": "Eliminar ingreso",
    "Excluir despesa": "Eliminar gasto",
    "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.": "Este ingreso será eliminado y dejará de aparecer en las listas e indicadores.",
    "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.": "Este gasto será eliminado y dejará de aparecer en las listas e indicadores.",
    "Não vinculado": "Sin vínculo",
    "Paciente removido": "Paciente eliminado",
    "Profissional removido": "Profesional eliminado",
    "Fixas": "Fijos",
    "Variáveis": "Variables",
    "Leitura gerencial": "Análisis de gestión",
    "Um resumo rápido do comportamento financeiro no recorte atual.": "Un resumen rápido del comportamiento financiero en el período seleccionado.",
    "Recebimento médio de caixa": "Cobro medio de caja",
    "em pacotes": "en paquetes",
    "Indicadores de competência permanecem indisponíveis até haver produção canônica.": "Los indicadores de devengo permanecen no disponibles hasta que haya registros canónicos de producción.",
    "Onde fatura": "Ingresos por sector",
    "Receita distribuída por setor.": "Ingresos distribuidos por sector.",
    "Nenhuma receita com setor vinculado no período.": "No hay ingresos con sector vinculado en el período.",
    "Quem fatura": "Ingresos por profesional",
    "Receita distribuída por profissional.": "Ingresos distribuidos por profesional.",
    "Nenhuma receita vinculada a profissional no período.": "No hay ingresos vinculados a un profesional en el período.",
    "Como entra": "Formas de pago",
    "Receita distribuída por forma de pagamento.": "Ingresos distribuidos por forma de pago.",
    "Nenhuma receita registrada no período.": "No hay ingresos registrados en el período.",
    "CSV": "CSV",
    "Data final": "Fecha final",
    "Data inicial": "Fecha inicial",
    "Despesa excluída com sucesso!": "¡Gasto eliminado con éxito!",
    "Despesa registrada com sucesso!": "¡Gasto registrado con éxito!",
    "Despesas fixas": "Gastos fijos",
    "Despesas variáveis": "Gastos variables",
    "Dia": "Día",
    "Lançamentos": "Movimientos",
    "Limpar filtros": "Limpiar filtros",
    "Mês": "Mes",
    "Nenhum plano encontrado com os filtros selecionados.": "No se encontraron planes con los filtros seleccionados.",
    "Não alocada": "Sin asignar",
    "OK": "OK",
    "Observações": "Observaciones",
    "Por profissional": "Por profesional",
    "Quantidade vendida": "Cantidad vendida",
    "Receita excluída com sucesso!": "¡Ingreso eliminado con éxito!",
    "Registrar": "Registrar",
    "Salvar": "Guardar",
    "Selecione uma clínica.": "Seleccione una clínica.",
    "Sem dados no período": "Sin datos en el período",
    "Semana": "Semana",
    "Situação": "Situación",
    "Situação do pagamento": "Situación del pago",
    "Ticket médio": "Importe medio",
    "Totais consolidados": "Totales consolidados",
    "Total": "Total",
    "Total de planos vendidos": "Total de planes vendidos",
    "Valor unit.": "Valor unit.",
    "Valor unitário": "Valor unitario",
    "Visão Geral": "Vista general",
    "Últimas Despesas": "Últimos gastos",
    "Últimas Receitas": "Últimos ingresos",
    "Anterior": "Anterior",
    "min": "min",
    "Salvar alterações": "Guardar cambios",
    "Selecione uma clínica": "Seleccione una clínica",
    "Acréscimo": "Recargo",
    "Classificar setor": "Asignar sector",
    "Dado": "Dato",
    "Confiabilidade do dado": "Fiabilidad de los datos",
    "Desconto": "Descuento",
    "Preencha os campos obrigatórios.": "Complete los campos obligatorios.",
    "Recorrente": "Recurrente",
    "Registros": "Registros",
    "Sem vínculo": "Sin vínculo",
    "Vincular paciente": "Vincular paciente",
    "Vincular profissional": "Vincular profesional",
    "Importado": "Importado",
    "Sem regra": "Sin regla de liquidación profesional",
    "Receita gerencial líquida": "Ingreso gerencial neto",
    "Resultado gerencial após custos diretos": "Resultado gerencial tras costes directos",
    "Nome completo": "Nombre completo",
    "Histórico do paciente": "Historial del paciente",
    "Atendimentos e financeiro do paciente.": "Atenciones y actividad financiera del paciente.",
    "Faltas registradas": "Inasistencias registradas",
    "Histórico de atendimentos": "Historial de atenciones",
    "Nenhum atendimento encontrado para este paciente.": "No se encontraron atenciones para este paciente.",
    "Vínculo": "Vínculo",
    "Agendamento sem plano de tratamento": "Cita sin plan de tratamiento",
    "Ativos": "Activos",
    "Inativos": "Inactivos",
    "Exames": "Exámenes",
    "Imagens": "Imágenes",
    "Fotos": "Fotos",
    "Agendamentos do paciente sem vínculo com plano de tratamento.": "Citas del paciente sin vínculo con un plan de tratamiento.",
    "Nenhum agendamento sem plano de tratamento encontrado para este paciente.": "No se encontraron citas sin plan de tratamiento para este paciente.",
    "Erro ao carregar pacientes": "Error al cargar los pacientes",
    "Lead criado com sucesso!": "¡Lead creado con éxito!",
    "Criar": "Crear",
    "Pendências de follow-up": "Seguimientos pendientes",
    "Priorize os leads com retorno mais urgente.": "Priorice los leads que requieren un seguimiento más urgente.",
    "Remover origem": "Eliminar origen",
    "Buscar por nome ou telefone...": "Buscar por nombre o teléfono...",
    "Gerencie seus leads e conversões": "Gestione sus leads y conversiones",
    "Total de registros": "Total de registros",
    "Página {currentPage} de {totalPages}": "Página {currentPage} de {totalPages}",
    "Tentar novamente": "Intentar de nuevo",
    "Atualizando dados de marketing...": "Actualizando los datos de marketing...",
    "Apenas perfis de super admin podem acessar esta área.": "Solo los perfiles de superadministrador pueden acceder a esta área.",
    "Seu usuário não possui permissão para visualizar clínicas globais ou controlar acessos centrais.": "Su usuario no tiene permiso para ver las clínicas globales ni gestionar los accesos centrales.",
    "Governança central do ecossistema": "Gobernanza central del ecosistema",
    "Gerencie clínicas, super admins e trilhas de auditoria a partir de um único painel global.": "Gestione clínicas, superadministradores y registros de auditoría desde un único panel global.",
    "Clínicas da plataforma": "Clínicas de la plataforma",
    "Busque clínicas, confira seus usuários e controle liberação ou bloqueio operacional.": "Busque clínicas, consulte sus usuarios y gestione la habilitación o el bloqueo operativo.",
    "Buscar clínica, cidade, usuário ou e-mail": "Buscar por clínica, ciudad, usuario o correo electrónico",
    "Carregando clínicas...": "Cargando clínicas...",
    "Nenhuma clínica encontrada com o filtro informado.": "No se encontraron clínicas con el filtro indicado.",
    "Bloqueada": "Bloqueada",
    "Sem e-mail cadastrado": "Sin correo electrónico registrado",
    "Sem telefone cadastrado": "Sin teléfono registrado",
    "Cidade não informada": "Ciudad no indicada",
    "Bloquear clínica": "Bloquear clínica",
    "Liberar clínica": "Desbloquear clínica",
    "Usuários cadastrados": "Usuarios registrados",
    "Nenhum usuário vinculado a esta clínica.": "No hay usuarios vinculados a esta clínica.",
    "Bloqueado": "Bloqueado",
    "Redefinir acesso": "Restablecer acceso",
    "Nova senha temporária": "Nueva contraseña temporal",
    "Reativar acesso": "Reactivar acceso",
    "Se ativado, a conta volta a acessar o painel global após a redefinição.": "Si se activa, la cuenta recuperará el acceso al panel global después del restablecimiento.",
    "Salvar nova senha": "Guardar nueva contraseña",
    "Convide novos perfis globais, revogue acessos quando necessário e redefina credenciais com auditoria.": "Invite nuevos perfiles globales, revoque accesos cuando sea necesario y restablezca credenciales con registro de auditoría.",
    "Buscar super admin por nome ou email": "Buscar un superadministrador por nombre o correo electrónico",
    "Convidar super admin": "Invitar superadministrador",
    "Carregando super admins...": "Cargando superadministradores...",
    "Nenhum super admin encontrado com o filtro informado.": "No se encontraron superadministradores con el filtro indicado.",
    "Desativado": "Desactivado",
    "Revogar": "Revocar",
    "Redefinir acesso do super admin": "Restablecer el acceso del superadministrador",
    "Acesso redefinido com sucesso.": "Acceso restablecido correctamente.",
    "Erro ao redefinir acesso": "Error al restablecer el acceso",
    "Auditoria recente": "Auditoría reciente",
    "Últimas ações sensíveis executadas por super admins na plataforma.": "Últimas acciones sensibles realizadas por superadministradores en la plataforma.",
    "Carregando trilha de auditoria...": "Cargando el registro de auditoría...",
    "Nenhum evento de auditoria registrado ainda.": "Aún no se ha registrado ningún evento de auditoría.",
    "Quando": "Cuándo",
    "Responsável": "Responsable",
    "Alvo": "Objetivo",
    "Clínica bloqueada": "Clínica bloqueada",
    "Clínica liberada": "Clínica desbloqueada",
    "Super admin convidado": "Superadministrador invitado",
    "Super admin desativado": "Superadministrador desactivado",
    "Super admin reativado": "Superadministrador reactivado",
    "Acesso redefinido": "Acceso restablecido",
    "Importação Zen Fisio - pacientes": "Importación de pacientes de Zen Fisio",
    "Importação Zen Fisio - profissionais": "Importación de profesionales de Zen Fisio",
    "Importação Zen Fisio - agenda": "Importación de agenda de Zen Fisio",
    "Importação Zen Fisio - financeiro": "Importación de datos financieros de Zen Fisio",
    "Importação Zen Fisio - prontuário": "Importación de historia clínica de Zen Fisio",
    "Importação Zen Fisio revertida": "Importación de Zen Fisio revertida",
    "Importa Zen Fisio": "Importación de Zen Fisio",
    "Migração por clínica": "Migración por clínica",
    "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.": "Seleccione la clínica de destino antes de importar pacientes, profesionales, agenda, datos financieros o historias clínicas de Zen Fisio.",
    "Clínica de destino": "Clínica de destino",
    "Selecione o arquivo": "Seleccione el archivo",
    "Nenhuma clínica selecionada": "Ninguna clínica seleccionada",
    "Importados": "Importados",
    "Importe o arquivo pacientes.csv preservando o isolamento multi-tenant e gerando alertas de CPF e nascimento.": "Importe el archivo pacientes.csv preservando el aislamiento multi-tenant y generando alertas sobre CPF y fechas de nacimiento.",
    "Importar pacientes": "Importar pacientes",
    "Importando pacientes...": "Importando pacientes...",
    "Importe profissionais com setor automático, agenda padrão das 08:00 às 18:00, duração de 1 hora e limite de 1 paciente por horário.": "Importe profesionales con asignación automática de sector, agenda predeterminada de 08:00 a 18:00, citas de 1 hora y un límite de 1 paciente por franja horaria.",
    "Importar profissionais": "Importar profesionales",
    "Importando profissionais...": "Importando profesionales...",
    "Importe agendamentos históricos preservando o AgendamentoId, vinculando pacientes pelo id legado e profissionais pelo nome importado anteriormente.": "Importe citas históricas preservando el AgendamentoId, vinculando a los pacientes por el id heredado y a los profesionales por el nombre importado anteriormente.",
    "Importar agenda": "Importar agenda",
    "Importando agenda...": "Importando agenda...",
    "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.": "Importe ingresos, gastos y cuentas por cobrar históricos agrupando movimientos del mismo AgendamentoID y preservando los pagos parciales.",
    "Importar financeiro": "Importar datos financieros",
    "Importando financeiro...": "Importando datos financieros...",
    "Avaliações clínicas": "Evaluaciones clínicas",
    "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.": "Importe evaluaciones de Zen Fisio como registros heredados en Markdown vinculados al paciente, al profesional y a la cita.",
    "Importar avaliações": "Importar evaluaciones",
    "Importando avaliações...": "Importando evaluaciones...",
    "Evoluções clínicas": "Evoluciones clínicas",
    "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.": "Importe evoluciones de Zen Fisio consolidando las líneas por atención e ignorando las respuestas vacías.",
    "Importar evoluções": "Importar evoluciones",
    "Importando evoluções...": "Importando evoluciones...",
    "Selecione um arquivo CSV.": "Seleccione un archivo CSV.",
    "Aguarde a importação em andamento terminar antes de iniciar outra.": "Espere a que finalice la importación en curso antes de iniciar otra.",
    "Selecione a clínica de destino.": "Seleccione la clínica de destino.",
    "Importação finalizada com sucesso.": "Importación finalizada correctamente.",
    "A importação não foi concluída. Tente novamente.": "No se pudo completar la importación. Inténtelo de nuevo.",
    "Ignorados": "Ignorados",
    "Registros com erro/alerta": "Registros con errores o alertas",
    "Importação finalizada": "Importación finalizada",
    "Importação não concluída": "Importación no completada",
    "Importação em andamento": "Importación en curso",
    "Progresso da importação": "Progreso de la importación",
    "Não foi possível concluir a importação. Tente novamente.": "No se pudo completar la importación. Inténtelo de nuevo.",
    "Os dados estão sendo processados.": "Los datos se están procesando.",
    "Nova importação": "Nueva importación",
    "Alertas": "Alertas",
    "CPFs duplicados": "CPF duplicados",
    "Nascimentos bloqueados": "Fechas de nacimiento bloqueadas",
    "Setores criados": "Sectores creados",
    "Agenda padrão": "Agenda predeterminada",
    "Horários inválidos": "Horarios no válidos",
    "Duplicados": "Duplicados",
    "Pagamentos": "Pagos",
    "Agrupados": "Agrupados",
    "Sem agendamento": "Sin cita",
    "Grupos vazios": "Grupos vacíos",
    "Datas inválidas": "Fechas no válidas",
    "Arquivo de alertas gerado": "Archivo de alertas generado",
    "Baixe o CSV para revisar CPFs duplicados, nascimentos futuros e datas inválidas bloqueadas.": "Descargue el CSV para revisar CPF duplicados, fechas de nacimiento futuras y fechas no válidas bloqueadas.",
    "Baixe o CSV para revisar setores criados automaticamente e registros ignorados.": "Descargue el CSV para revisar los sectores creados automáticamente y los registros ignorados.",
    "Baixe o CSV para revisar vínculos ausentes, horários inválidos, duplicidades e setores criados.": "Descargue el CSV para revisar vínculos ausentes, horarios no válidos, duplicados y sectores creados.",
    "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.": "Descargue el CSV para revisar vínculos ausentes, formas de pago sin correspondencia, agrupaciones y movimientos ignorados.",
    "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.": "Descargue el CSV para revisar citas ausentes, grupos vacíos, duplicados y fechas no válidas.",
    "Baixar alertas": "Descargar alertas",
    "{count} usuário": "{count} usuario",
    "{count} usuários": "{count} usuarios",
    "{count} ativo": "{count} activo",
    "{count} ativos": "{count} activos",
    "{count} admin": "{count} administrador",
    "{count} admins": "{count} administradores",
    "A senha de {name} será redefinida e o acesso será reativado.": "La contraseña de {name} se restablecerá y el acceso se reactivará.",
    "A senha de {name} será redefinida mantendo o status atual da conta.": "La contraseña de {name} se restablecerá manteniendo el estado actual de la cuenta.",
    "Nova senha para {name}.": "Nueva contraseña para {name}.",
    "Destino selecionado: {clinic}": "Destino seleccionado: {clinic}",
    "Destino selecionado: nenhuma clínica selecionada.": "Destino seleccionado: ninguna clínica seleccionada.",
    "Autenticação necessária.": "Se requiere autenticación.",
    "Esta operação exige o perfil de super admin.": "Esta operación requiere el perfil de superadministrador.",
    "Clínica não encontrada.": "Clínica no encontrada.",
    "Este e-mail já está cadastrado.": "Este correo electrónico ya está registrado.",
    "Super admin não encontrado.": "Superadministrador no encontrado.",
    "Deve existir ao menos um super admin ativo.": "Debe permanecer activo al menos un superadministrador.",
    "Informe a clínica de destino.": "Indique la clínica de destino.",
    "Informe o conteúdo CSV.": "Proporcione el contenido CSV.",
    "O histórico de importação está temporariamente indisponível.": "El historial de importación no está disponible temporalmente.",
    "Lote de importação não encontrado.": "Lote de importación no encontrado.",
    "Somente lotes ativos podem ser revertidos.": "Solo se pueden revertir lotes activos.",
    "O nome deve ter pelo menos {minimum} caracteres.": "El nombre debe tener al menos {minimum} caracteres.",
    "Informe um e-mail válido.": "Introduzca un correo electrónico válido.",
    "A senha deve ter pelo menos {minimum} caracteres.": "La contraseña debe tener al menos {minimum} caracteres.",
    "A senha deve ter no máximo {maximum} caracteres.": "La contraseña debe tener como máximo {maximum} caracteres.",
    "A senha deve conter ao menos uma letra minúscula.": "La contraseña debe contener al menos una letra minúscula.",
    "A senha deve conter ao menos uma letra maiúscula.": "La contraseña debe contener al menos una letra mayúscula.",
    "A senha deve conter ao menos um número.": "La contraseña debe contener al menos un número.",
    "O CSV não contém as colunas obrigatórias: {missing_headers}.": "Al CSV le faltan las columnas obligatorias: {missing_headers}.",
    "Já existe uma importação ativa de {import_type} para esta clínica.": "Ya existe una importación activa de {import_type} para esta clínica.",
    "Importação bloqueada. Execute primeiro: {missing_prerequisites}.": "Importación bloqueada. Ejecute primero: {missing_prerequisites}.",
    "Importação bloqueada pela etapa posterior {blocking_import_type}.": "Importación bloqueada por la etapa posterior {blocking_import_type}.",
    "Reverta primeiro o lote posterior de {blocking_import_type}.": "Revierta primero el lote posterior de {blocking_import_type}.",
    "Não é possível reverter {entity_type}: existem {dependency_count} dependências.": "No se puede revertir {entity_type}: existen {dependency_count} dependencias.",
    "pacientes": "pacientes",
    "profissionais": "profesionales",
    "agendamentos": "citas",
    "A clínica": "La clínica",
    "Bloquear acesso": "Bloquear acceso",
    "Clínica bloqueada com sucesso.": "Clínica bloqueada correctamente.",
    "Clínica liberada com sucesso.": "Clínica desbloqueada correctamente.",
    "Clínicas ativas": "Clínicas activas",
    "Clínicas cadastradas": "Clínicas registradas",
    "Conceder acesso de super admin": "Conceder acceso de superadministrador",
    "Confirmar convite": "Confirmar invitación",
    "Convidar novo super admin": "Invitar a un nuevo superadministrador",
    "Desativar super admin": "Desactivar superadministrador",
    "Email": "Correo electrónico",
    "Erro ao atualizar status da clínica": "Error al actualizar el estado de la clínica",
    "Erro ao atualizar super admin": "Error al actualizar el superadministrador",
    "Erro ao carregar painel de super admin": "Error al cargar el panel de superadministrador",
    "Erro ao convidar super admin": "Error al invitar al superadministrador",
    "Liberar acesso": "Conceder acceso",
    "Reativar super admin": "Reactivar superadministrador",
    "Revogar acesso": "Revocar acceso",
    "Super Admin": "Superadministrador",
    "Super admin convidado com sucesso.": "Superadministrador invitado correctamente.",
    "Super admin desativado com sucesso.": "Superadministrador desactivado correctamente.",
    "Super admin reativado com sucesso.": "Superadministrador reactivado correctamente.",
    "Super admins ativos": "Superadministradores activos",
    "Usuários monitorados": "Usuarios supervisados",
    "com o email": "con el correo electrónico",
    "passará a ter acesso global ao sistema": "tendrá acceso global al sistema",
    "perderá o acesso ao painel global até nova liberação.": "perderá el acceso al panel global hasta que se le vuelva a conceder.",
    "terá o acesso ao sistema interrompido até nova liberação.": "tendrá el acceso al sistema suspendido hasta que se desbloquee la clínica.",
    "voltará a acessar a plataforma normalmente.": "volverá a acceder a la plataforma con normalidad.",
    "voltará a acessar o painel global normalmente.": "volverá a acceder al panel global con normalidad.",
  },
  it: {
    "Categorias financeiras": "Categorie finanziarie",
    "Gerencie separadamente as categorias usadas em receitas e despesas.": "Gestisci separatamente le categorie utilizzate per entrate e spese.",
    "Tipo de categoria financeira": "Tipo di categoria finanziaria",
    "Nova categoria": "Nuova categoria",
    "Renomear uma categoria não altera o nome preservado nos lançamentos anteriores. Categorias inativas continuam no histórico, mas não podem ser usadas em novos lançamentos.": "Rinominare una categoria non modifica il nome conservato nelle registrazioni precedenti. Le categorie inattive restano nello storico, ma non possono essere usate per nuove registrazioni.",
    "Carregando categorias...": "Caricamento delle categorie...",
    "Não foi possível carregar as categorias.": "Impossibile caricare le categorie.",
    "Nenhuma categoria de receita cadastrada.": "Non è stata registrata alcuna categoria di entrata.",
    "Nenhuma categoria de despesa cadastrada.": "Non è stata registrata alcuna categoria di spesa.",
    "Editar categoria": "Modifica categoria",
    "Nome da categoria": "Nome della categoria",
    "Informe o nome da categoria.": "Inserisci il nome della categoria.",
    "Categoria atualizada com sucesso.": "Categoria aggiornata correttamente.",
    "Categoria cadastrada com sucesso.": "Categoria registrata correttamente.",
    "Categoria desativada com sucesso.": "Categoria disattivata correttamente.",
    "Categoria reativada com sucesso.": "Categoria riattivata correttamente.",
    "Desativar categoria": "Disattiva categoria",
    "Reativar categoria": "Riattiva categoria",
    "A categoria deixará de aparecer em novos lançamentos. O histórico será preservado.": "La categoria non apparirà più nelle nuove registrazioni. Lo storico sarà conservato.",
    "A categoria voltará a aparecer em novos lançamentos.": "La categoria apparirà di nuovo nelle nuove registrazioni.",
    "Categoria de receita": "Categoria di entrata",
    "Nenhuma categoria de receita ativa cadastrada.": "Non è stata registrata alcuna categoria di entrata attiva.",
    "Nenhuma categoria de despesa ativa cadastrada.": "Non è stata registrata alcuna categoria di spesa attiva.",
    "Selecione uma categoria": "Seleziona una categoria",
    "Configurar categorias": "Configura categorie",
    "Selecione uma categoria de receita.": "Seleziona una categoria di entrata.",
    "Selecione uma categoria de despesa.": "Seleziona una categoria di spesa.",
    "Avaliação vinculada": "Valutazione collegata",
    "Modelo clínico registrado": "Modello clinico registrato",
    "Editar registros de outros profissionais": "Modifica le registrazioni di altri professionisti",
    "Adicionar correções a registros finalizados": "Aggiungi correzioni alle registrazioni finalizzate",
    "Autorizações clínicas explícitas": "Autorizzazioni cliniche esplicite",
    "Estas autorizações clínicas sensíveis não são concedidas automaticamente ao administrador.": "Queste autorizzazioni cliniche sensibili non vengono concesse automaticamente all'amministratore.",
    "Existem alterações não salvas. Deseja descartá-las?": "Sono presenti modifiche non salvate. Vuoi scartarle?",
    "Avaliação atualizada com sucesso.": "Valutazione aggiornata correttamente.",
    "Este registro foi alterado por outro usuário enquanto você o editava. Atualize os dados antes de salvar novamente.": "Questa registrazione è stata modificata da un altro utente mentre la stavi aggiornando. Ricarica i dati prima di salvare di nuovo.",
    "Atualizar registro": "Ricarica registrazione",
    "Editar avaliação clínica": "Modifica valutazione clinica",
    "Finalizado": "Finalizzato",
    "Finalizar registro clínico": "Finalizza registrazione clinica",
    "Após finalizar, este registro não poderá mais ser alterado diretamente. Se for necessário corrigir alguma informação depois, será criada uma correção mantendo o conteúdo original no histórico.": "Dopo la finalizzazione, questa registrazione non potrà più essere modificata direttamente. Se sarà necessario correggere un'informazione, verrà aggiunta una correzione mantenendo il contenuto originale nello storico.",
    "Finalizando...": "Finalizzazione...",
    "Finalizar registro": "Finalizza registrazione",
    "Adicionar correção": "Aggiungi correzione",
    "O conteúdo original será preservado. A correção será acrescentada ao histórico do registro.": "Il contenuto originale verrà preservato. La correzione sarà aggiunta allo storico della registrazione.",
    "Motivo da correção": "Motivo della correzione",
    "Informe pelo menos 10 caracteres.": "Inserisci almeno 10 caratteri.",
    "Conteúdo da correção": "Contenuto della correzione",
    "Correção adicionada ao histórico.": "Correzione aggiunta allo storico.",
    "Informe um motivo com pelo menos 10 caracteres.": "Inserisci un motivo di almeno 10 caratteri.",
    "Informe o conteúdo da correção.": "Inserisci il contenuto della correzione.",
    "Registro finalizado com sucesso.": "Registrazione finalizzata correttamente.",
    "Criado por": "Creato da",
    "Autoria não disponível": "Autore non disponibile",
    "Última alteração por": "Ultima modifica di",
    "Finalizado por": "Finalizzato da",
    "Editar avaliação": "Modifica valutazione",
    "Finalizar": "Finalizza",
    "Ver histórico": "Vedi storico",
    "Histórico do registro clínico": "Storico della registrazione clinica",
    "Linha do tempo": "Cronologia",
    "Carregando histórico...": "Caricamento dello storico...",
    "Correção adicionada": "Correzione aggiunta",
    "Primeiro estado disponível no histórico": "Primo stato disponibile nello storico",
    "Registro criado": "Registrazione creata",
    "Registro finalizado": "Registrazione finalizzata",
    "Revisão salva": "Revisione salvata",
    "Revisão": "Revisione",
    "Carregar mais": "Carica altro",
    "Este é o primeiro estado disponível após a adoção do histórico. Não há versões anteriores reconstruíveis.": "Questo è il primo stato disponibile dopo l'introduzione dello storico. Le versioni precedenti non possono essere ricostruite.",
    "versão": "versione",
    "Sem modelo clínico registrado": "Nessun modello clinico registrato",
    "Documentos desta revisão": "Documenti di questa revisione",
    "O conteúdo original foi preservado. Esta correção foi acrescentada ao histórico.": "Il contenuto originale è stato preservato. Questa correzione è stata aggiunta allo storico.",
    "Correção adicionada por": "Correzione aggiunta da",
    "Escolha uma revisão ou correção para consultar o conteúdo preservado.": "Scegli una revisione o correzione per consultarne il contenuto preservato.",
    "Selecione um item do histórico": "Seleziona un elemento dello storico",
    "Plano cancelado com segurança.": "Piano annullato in sicurezza.",
    "Plano reativado com segurança.": "Piano riattivato in sicurezza.",
    "Cancelar Plano de Tratamento": "Annulla Piano di Trattamento",
    "Reativar Plano de Tratamento": "Riattiva Piano di Trattamento",
    "Sessões utilizadas": "Sessioni utilizzate",
    "{count} sessão restante": "{count} sessione rimanente",
    "{count} sessões restantes": "{count} sessioni rimanenti",
    "{count} recebível": "{count} credito",
    "{count} recebíveis": "{count} crediti",
    "{count} pagamento": "{count} pagamento",
    "{count} pagamentos": "{count} pagamenti",
    "{count} receita": "{count} ricavo",
    "{count} receitas": "{count} ricavi",
    "Verificando efeitos do cancelamento...": "Verifica degli effetti dell'annullamento...",
    "Não foi possível verificar os efeitos do cancelamento. Feche o diálogo e tente novamente.": "Non è stato possibile verificare gli effetti dell'annullamento. Chiudi la finestra e riprova.",
    "Consequências do cancelamento": "Conseguenze dell'annullamento",
    "Cancelar o Plano impede seu uso futuro, mas não apaga atendimentos, pagamentos, produções ou repasses já registrados. Pendências financeiras serão encaminhadas para revisão.": "L'annullamento del Piano ne impedisce l'uso futuro, ma non elimina appuntamenti, pagamenti, produzioni o compensi professionali già registrati. Le pendenze finanziarie saranno inviate alla revisione.",
    "Agendamentos futuros": "Appuntamenti futuri",
    "Saldo financeiro": "Saldo finanziario",
    "Situação do NPS": "Stato NPS",
    "Sem solicitação": "Nessuna richiesta",
    "Estes agendamentos serão cancelados": "Questi appuntamenti saranno annullati",
    "Financeiro preservado e enviado para revisão": "Dati finanziari conservati e inviati alla revisione",
    "recebíveis": "crediti",
    "pagamentos": "pagamenti",
    "receitas": "ricavi",
    "pago": "pagato",
    "Efeitos da reativação": "Effetti della riattivazione",
    "Agendamentos cancelados não serão restaurados automaticamente.": "Gli appuntamenti annullati non verranno ripristinati automaticamente.",
    "A reativação não cria uma nova venda nem resolve pendências financeiras.": "La riattivazione non crea una nuova vendita né risolve le pendenze finanziarie.",
    "Tokens NPS revogados não serão restaurados automaticamente.": "I token NPS revocati non verranno ripristinati automaticamente.",
    "O status será recalculado conforme validade, venda e uso efetivo do Plano.": "Lo stato verrà ricalcolato in base alla validità, alla vendita e all'uso effettivo del Piano.",
    "Motivo da reativação": "Motivo della riattivazione",
    "Descreva o motivo com detalhes": "Descrivi il motivo in dettaglio",
    "Informe pelo menos 30 caracteres.": "Inserisci almeno 30 caratteri.",
    "Cancelando Plano...": "Annullamento del Piano...",
    "Reativando Plano...": "Riattivazione del Piano...",
    "Confirmar cancelamento": "Conferma annullamento",
    "Confirmar reativação": "Conferma riattivazione",
    "Reativar Plano": "Riattiva Piano",
    "Cancelar Plano": "Annulla Piano",
    "Ciclo do atendimento": "Ciclo dell'appuntamento",
    "Este atendimento possui efeitos operacionais materializados.": "Questo appuntamento ha effetti operativi già registrati.",
    "A conclusão registra produção, repasse e os demais efeitos aplicáveis.": "Il completamento registra produzione, compenso professionale e gli altri effetti applicabili.",
    "Concluir atendimento": "Completa appuntamento",
    "Reverter conclusão": "Annulla completamento",
    "Você não possui as permissões necessárias para reverter esta conclusão.": "Non disponi delle autorizzazioni necessarie per annullare questo completamento.",
    "Concluir atendimento?": "Completare l'appuntamento?",
    "A conclusão registrará produção, repasse, progresso do plano e financeiro quando aplicável.": "Il completamento registrerà produzione, compenso professionale, avanzamento del piano ed effetti finanziari quando applicabile.",
    "Concluindo...": "Completamento...",
    "Efeitos da reversão": "Effetti dell'annullamento",
    "A produção ativa será cancelada e preservada no histórico.": "La produzione attiva verrà annullata e conservata nello storico.",
    "O repasse relacionado será cancelado ou marcado para tratamento administrativo.": "Il compenso professionale correlato verrà annullato o segnalato per la gestione amministrativa.",
    "As sessões utilizadas e o status do plano serão recalculados.": "Le sessioni utilizzate e lo stato del piano verranno ricalcolati.",
    "Uma entrega NPS ativa poderá ser invalidada até uma nova conclusão.": "Un invio NPS attivo potrà essere invalidato fino a un nuovo completamento.",
    "A reversão do atendimento não cancela nem estorna automaticamente os registros financeiros existentes. Eles serão sinalizados para revisão.": "L'annullamento dell'appuntamento non cancella né rimborsa automaticamente i record finanziari esistenti. Verranno segnalati per la revisione.",
    "Se existir repasse relacionado, a operação exigirá a permissão para cancelar repasses.": "Se esiste un compenso correlato, l'operazione richiederà l'autorizzazione ad annullare i compensi.",
    "Novo estado operacional": "Nuovo stato operativo",
    "Para remarcar, reverta primeiro e depois utilize a ação específica de remarcação.": "Per riprogrammare, annulla prima il completamento e poi utilizza l'azione specifica di riprogrammazione.",
    "Explique por que a conclusão precisa ser revertida": "Spiega perché il completamento deve essere annullato",
    "Informe pelo menos 30 caracteres para registrar a reversão.": "Inserisci almeno 30 caratteri per registrare l'annullamento.",
    "Confirmar reversão": "Conferma annullamento",
    "Atendimento concluído com sucesso.": "Appuntamento completato correttamente.",
    "Não foi possível concluir o atendimento.": "Non è stato possibile completare l'appuntamento.",
    "Conclusão revertida. Os registros financeiros relacionados foram sinalizados para revisão.": "Completamento annullato. I record finanziari correlati sono stati segnalati per la revisione.",
    "Conclusão revertida com sucesso.": "Completamento annullato correttamente.",
    "O repasse possui pagamentos preservados e requer tratamento administrativo.": "Il compenso conserva i pagamenti e richiede una gestione amministrativa.",
    "Não foi possível reverter a conclusão.": "Non è stato possibile annullare il completamento.",
    "Utilize a ação Concluir atendimento para registrar a conclusão.": "Usa Completa appuntamento per registrare il completamento.",
    "Este atendimento já foi concluído. Utilize Reverter conclusão para alterar seu estado.": "Questo appuntamento è già completato. Usa Annulla completamento per modificarne lo stato.",
    "Restaure primeiro o agendamento para um estado operacional antes de executar esta ação.": "Ripristina prima l'appuntamento a uno stato operativo, quindi esegui questa azione.",
    "Status atual": "Stato attuale",
    "Destino após a reversão": "Destinazione dopo l'annullamento",
    "Verificando efeitos da reversão...": "Verifica degli effetti dell'annullamento...",
    "Não foi possível verificar os efeitos da reversão. Feche o diálogo e tente novamente.": "Non è stato possibile verificare gli effetti dell'annullamento. Chiudi la finestra e riprova.",
    "Existem pagamentos de repasse relacionados. Eles serão preservados e o repasse será marcado para tratamento administrativo.": "Sono presenti pagamenti del compenso professionale correlati. Saranno conservati e il compenso sarà contrassegnato per la gestione amministrativa.",
    "Revisão financeira necessária": "Revisione finanziaria necessaria",
    "Este registro financeiro foi preservado e precisa de uma decisão financeira explícita.": "Questa registrazione finanziaria è stata conservata e richiede una decisione finanziaria esplicita.",
    "A conclusão do atendimento foi revertida. Este registro foi preservado e precisa de decisão financeira.": "Il completamento dell'appuntamento è stato annullato. Questo record è stato conservato e richiede una decisione finanziaria.",
    "Confirmar manutenção do recebível": "Conferma mantenimento del credito",
    "Confirme somente se a cobrança deve permanecer válida após a reversão clínica.": "Conferma solo se l'addebito deve restare valido dopo l'annullamento clinico.",
    "Justificativa financeira": "Giustificazione finanziaria",
    "Registre por que o recebível deve ser mantido": "Indica perché il credito deve essere mantenuto",
    "Confirmar manutenção": "Conferma mantenimento",
    "Manutenção do recebível confirmada.": "Mantenimento del credito confermato.",
    "Trilha de navegação": "Percorso di navigazione",
    "Mais": "Altro",
    "Slide anterior": "Diapositiva precedente",
    "Próximo slide": "Diapositiva successiva",
    "Paginação": "Paginazione",
    "Ir para a página anterior": "Vai alla pagina precedente",
    "Ir para a próxima página": "Vai alla pagina successiva",
    "Mais páginas": "Altre pagine",
    "Rotina clínica": "Attività clinica",
    "Gestão": "Gestione",
    "Alternar menu Financeiro": "Apri o chiudi il menu Finanza",
    "Navegação principal": "Navigazione principale",
    "Trocar paciente": "Cambia paziente",
    "Selecione uma forma de pagamento para registrar o recebimento.": "Seleziona un metodo di pagamento per registrare l'incasso.",
    "Acompanhe recebimentos, valores a receber e despesas sem misturar caixa e competência.": "Monitora incassi, crediti e spese senza mescolare cassa e competenza.",
    "Onde recebeu": "Dove sono stati ricevuti gli incassi",
    "Recebimentos distribuídos por setor, conforme a data de pagamento.": "Incassi distribuiti per settore, in base alla data di pagamento.",
    "Nenhum recebimento com setor vinculado no período.": "Nessun incasso con settore collegato nel periodo.",
    "Quem recebeu": "Chi ha ricevuto l'incasso",
    "Recebimentos distribuídos por profissional, conforme a data de pagamento.": "Incassi distribuiti per professionista, in base alla data di pagamento.",
    "Nenhum recebimento vinculado a profissional no período.": "Nessun incasso collegato a un professionista nel periodo.",
    "Leitura dos indicadores financeiros": "Come leggere gli indicatori finanziari",
    "Os cards de recebimento usam a data de pagamento. As despesas usam a data de competência e não compõem resultado de caixa nesta tela.": "Le schede degli incassi usano la data di pagamento. Le spese usano la data di competenza e non compongono un risultato di cassa in questa schermata.",
    "Valor comercial confirmado; não representa recebimento.": "Valore commerciale confermato; non rappresenta un incasso.",
    "Entrada registrada por data de pagamento.": "Entrata registrata in base alla data di pagamento.",
    "Saldo pendente de cobrança; não representa entrada em caixa.": "Saldo di incasso in sospeso; non rappresenta un'entrata di cassa.",
    "Serviço executado, separado de venda e recebimento.": "Servizio eseguito, separato da vendita e incasso.",
    "A competência reconhece cada evento no período correspondente; ela é analisada separadamente do caixa.": "La competenza riconosce ogni evento nel periodo corrispondente; viene analizzata separatamente dalla cassa.",
    "A DRE e o rateio gerencial analisam competência; o rateio não altera o caixa.": "Il conto economico gestionale e la ripartizione analizzano la competenza; la ripartizione non modifica la cassa.",
    "Caixa bruto": "Cassa lorda",
    "Caixa líquido": "Cassa netta",
    "Saídas efetivas": "Uscite effettive",
    "Resultado de caixa": "Risultato di cassa",
    "Caixa líquido menos saídas efetivas, sem apuração contábil.": "Caixa líquido menos saídas efetivas, sem apuração contábil.",
    "Ticket médio de caixa": "Ticket medio di cassa",
    "Recebimentos líquidos por evento de caixa": "Incassi netti per evento di cassa",
    "Indicadores contábeis indisponíveis": "Indicatori contabili non disponibili",
    "Relatórios de competência e indicadores unitários dependem de produção canônica, competência e custos confiáveis.": "I report per competenza e gli indicatori unitari dipendono da registrazioni canoniche della produzione e da dati di competenza e costi affidabili.",
    "Valor vendido/cobrado": "Valore venduto/addebbitato",
    "Total estornado": "Totale rimborsato",
    "Saldo vencido": "Saldo scaduto",
    "Não foi possível carregar as despesas.": "Non è stato possibile caricare le spese.",
    "Despesa registrada.": "Spesa registrata.",
    "Recorrência criada.": "Ricorrenza creata.",
    "Pagamento registrado.": "Pagamento registrato.",
    "Reversão operacional": "Storno operativo",
    "Reversão registrada.": "Storno registrato.",
    "Cancelamento operacional": "Cancellazione operativa",
    "Despesa cancelada.": "Spesa cancellata.",
    "Despesas e saídas": "Spese e uscite",
    "Obrigações, pagamentos e resultado de caixa sem rateio automático.": "Obblighi, pagamenti e risultato di cassa senza allocazione automatica.",
    "Aberta": "Aperta",
    "Pagamento parcial": "Pagamento parziale",
    "Paga": "Pagata",
    "Vencida": "Scaduta",
    "Direta do setor": "Diretta al settore",
    "Direta do profissional": "Diretta al professionista",
    "Corporativa não alocada": "Corporate non allocata",
    "Nova despesa": "Nuova spesa",
    "Outras": "Altre",
    "Pagamento imediato": "Pagamento immediato",
    "Nova recorrência": "Nuova ricorrenza",
    "Dia de vencimento": "Giorno di scadenza",
    "Criar recorrência": "Crea ricorrenza",
    "Nenhuma despesa encontrada": "Nessuna spesa trovata",
    "Produção clínica": "Produzione clinica",
    "Produções geradas por atendimento concluído, separadas do financeiro e prontas para conferência operacional.": "Produzioni generate da prestazioni concluse, separate dalla finanza e pronte per la revisione operativa.",
    "Não foi possível carregar a produção clínica.": "Non è stato possibile caricare la produzione clinica.",
    "Não foi possível carregar os filtros de produção clínica.": "Non è stato possibile caricare i filtri di produzione clinica.",
    "Não foi possível gerar a produção clínica.": "Non è stato possibile generare la produzione clinica.",
    "Não foi possível cancelar a produção clínica.": "Non è stato possibile annullare la produzione clinica.",
    "Produção clínica gerada com sucesso.": "Produzione clinica generata correttamente.",
    "Produção clínica cancelada com sucesso.": "Produzione clinica annullata correttamente.",
    "Produções para revisar": "Produzioni da rivedere",
    "Produzido": "Prodotto",
    "Avulso": "Singolo",
    "Revisar valor": "Rivedi valore",
    "Filtros": "Filtri",
    "Todas": "Tutte",
    "Todos": "Tutti",
    "Cancelada": "Annullata",
    "Classificação": "Classificazione",
    "Venda do atendimento": "Vendita della visita",
    "Valor registrado no atendimento": "Valore registrato per la visita",
    "Cortesia": "Prestazione gratuita",
    "Valor do atendimento não determinado": "Valore della visita non determinato",
    "Sessão adicional sem aditivo comercial": "Seduta aggiuntiva senza integrazione commerciale",
    "Venda de plano de tratamento": "Vendita del piano di trattamento",
    "Valor registrado no plano de tratamento": "Valore registrato nel piano di trattamento",
    "Venda comercial": "Vendita commerciale",
    "Atendimento avulso": "Visita singola",
    "Atendimento avulso para revisão": "Visita singola da verificare",
    "Sessão adicional de plano": "Seduta aggiuntiva del piano",
    "Valor confirmado": "Valore confermato",
    "Valor estimado": "Valore stimato",
    "Valor a revisar": "Valore da verificare",
    "Origem não identificada": "Origine non identificata",
    "Tipo não identificado": "Tipo non identificato",
    "Classificação não identificada": "Classificazione non identificata",
    "Sem venda, serviço ou valor operacional determinístico": "Nessuna vendita, prestazione o importo operativo deterministico",
    "Motivo de revisão não identificado": "Motivo della verifica non identificato",
    "Recebimento": "Incasso",
    "Estorno": "Rimborso",
    "Ajuste": "Rettifica",
    "Lançamento manual de caixa": "Registrazione manuale di cassa",
    "Tipo de lançamento não identificado": "Tipo di movimento non identificato",
    "Forma de pagamento não identificada": "Metodo di pagamento non identificato",
    "confirmed": "confermato",
    "inferred": "inferito",
    "legacy": "legacy",
    "unknown": "sconosciuto",
    "commercial sale": "vendita commerciale",
    "treatment plan": "piano di trattamento",
    "appointment snapshot": "snapshot appuntamento",
    "courtesy": "cortesia",
    "additional session requires review": "sessione aggiuntiva da rivedere",
    "Ações": "Azioni",
    "Detalhes": "Dettagli",
    "Nenhuma produção clínica encontrada": "Nessuna produzione clinica trovata",
    "Detalhes da produção": "Dettagli produzione",
    "Sessão": "Sessione",
    "Cancelar produção clínica": "Annulla produzione clinica",
    "A produção será marcada como cancelada sem alterar vendas, recebíveis ou caixa.": "La produzione sarà contrassegnata come annullata senza modificare vendite, crediti o cassa.",
    "Cancelar produção": "Annulla produzione",
    "Cancelando...": "Annullamento...",
    "Gerar pendentes": "Genera pendenti",
    "Configurações": "Impostazioni",
    "Central Administrativa": "Centro amministrativo",
    "Administração": "Amministrazione",
    "Categorias": "Categorie",
    "Carregando configurações...": "Caricamento impostazioni...",
    "Navegação de configurações": "Navigazione impostazioni",
    "Seção": "Sezione",
    "Voltar": "Indietro",
    "Voltar às configurações": "Torna alle impostazioni",
    "Seção não encontrada": "Sezione non trovata",
    "Nenhuma configuração encontrada para este endereço.": "Nessuna impostazione trovata per questo indirizzo.",
    "Centralize as configurações administrativas da clínica sem duplicar cadastros ou fontes de dados.": "Centralizza le impostazioni amministrative della clinica senza duplicare registri o fonti dati.",
    "Clínica": "Clinica",
    "Configurações da clínica": "Impostazioni della clinica",
    "Dados institucionais, moeda e fuso horário da clínica.": "Dati istituzionali, valuta e fuso orario della clinica.",
    "Operação": "Operazioni",
    "Organize setores usados por profissionais, agenda, filtros e relatórios.": "Organizza reparti usati da professionisti, agenda, filtri e report.",
    "Status da Agenda": "Stati dell'agenda",
    "Configure nomes, cores, ordem e visibilidade dos status sem alterar os estados técnicos.": "Configura nomi, colori, ordine e visibilità senza modificare gli stati tecnici.",
    "Não foi possível carregar os status configurados da agenda.": "Non è stato possibile caricare gli stati configurati dell'agenda.",
    "Catálogo visual de status": "Catalogo visuale degli stati",
    "A configuração visual altera rótulos, cores, ordem e visibilidade sem mudar os estados técnicos usados nas regras da Agenda.": "La configurazione visuale modifica etichette, colori, ordine e visibilità senza cambiare gli stati tecnici usati dalle regole dell'agenda.",
    "Restaurar padrão": "Ripristina predefiniti",
    "Salvar status": "Salva stati",
    "Status da agenda salvos.": "Stati dell'agenda salvati.",
    "Seu perfil não possui permissão para alterar os status da Agenda.": "Il tuo profilo non ha il permesso di modificare gli stati dell'agenda.",
    "Revise nomes vazios, cores inválidas ou ordens negativas antes de salvar.": "Controlla nomi vuoti, colori non validi o ordini negativi prima di salvare.",
    "Estado técnico": "Stato tecnico",
    "Nome exibido": "Nome visualizzato",
    "Cor": "Colore",
    "Ordem": "Ordine",
    "Prévia": "Anteprima",
    "Serviços e Procedimentos": "Servizi e Procedure",
    "Gerencie o catálogo canônico usado na agenda, planos e financeiro.": "Gestisci il catalogo canonico usato in agenda, piani e finanze.",
    "Atendimento clínico": "Assistenza clinica",
    "Modelos clínicos": "Modelli clinici",
    "Crie e gerencie modelos reutilizáveis para avaliações, evoluções e outros registros clínicos.": "Crea e gestisci modelli riutilizzabili per valutazioni, evoluzioni e altri documenti clinici.",
    "Comercial": "Commerciale",
    "Origens de Leads": "Origini dei lead",
    "Padronize as origens usadas no funil comercial e nos filtros.": "Standardizza le origini usate nel funnel commerciale e nei filtri.",
    "Motivos de não agendamento": "Motivi di mancata prenotazione",
    "Mantenha o catálogo oficial usado nos desfechos comerciais.": "Mantieni il catalogo ufficiale usato negli esiti commerciali.",
    "Financeiro": "Finanziario",
    "DRE Gerencial": "Conto Economico Gestionale",
    "Demonstrativo gerencial por competência, com caixa exibido em bloco comparativo separado.": "Prospetto gestionale per competenza, con cassa mostrata in un blocco comparativo separato.",
    "Não foi possível carregar a DRE gerencial.": "Non è stato possibile caricare il conto economico gestionale.",
    "Não foi possível carregar os filtros da DRE gerencial.": "Non è stato possibile caricare i filtri del conto economico gestionale.",
    "Não foi possível carregar a origem da linha.": "Non è stato possibile caricare l'origine della riga.",
    "Não foi possível carregar os detalhes da linha.": "Non è stato possibile caricare i dettagli della riga.",
    "Não foi possível exportar a DRE gerencial.": "Non è stato possibile esportare il conto economico gestionale.",
    "Aviso gerencial": "Avviso gestionale",
    "Linha": "Riga",
    "Origem dos dados": "Origine dei dati",
    "Data considerada": "Data considerata",
    "Detalhes de {line}": "Dettagli di {line}",
    "Entender {line}": "Informazioni su {line}",
    "Ver detalhes de {line}": "Vedi i dettagli di {line}",
    "Nenhum detalhe encontrado": "Nessun dettaglio trovato",
    "Despesas consideradas no rateio": "Spese incluse nella ripartizione",
    "Competência versus caixa": "Competenza versus cassa",
    "Receita reconhecida": "Ricavo riconosciuto",
    "Caixa recebido": "Cassa ricevuta",
    "Custos e despesas reconhecidos": "Costi e spese riconosciuti",
    "Qualidade dos dados": "Qualita dei dati",
    "Origem da linha": "Origine della riga",
    "Nenhuma origem encontrada": "Nessuna origine trovata",
    "Nenhum demonstrativo encontrado": "Nessun prospetto trovato",
    "Visão": "Vista",
    "Visão original": "Vista originale",
    "Visão pós-rateio": "Vista post-ripartizione",
    "Comparar": "Confronta",
    "Impacto do rateio gerencial": "Impatto della ripartizione gestionale",
    "Análise exclusivamente gerencial": "Analisi esclusivamente gestionale",
    "Análise por setor": "Analisi per settore",
    "Análise por profissional": "Analisi per professionista",
    "Análise por serviço": "Analisi per servizio",
    "DRE original": "Prospetto originale",
    "DRE pós-rateio": "Prospetto post-ripartizione",
    "DRE comparativa": "Prospetto comparativo",
    "Histórico de rateios": "Storico ripartizioni",
    "Não calculável": "Non calcolabile",
    "Cobertura do rateio": "Copertura della ripartizione",
    "Setores impactados": "Settori impattati",
    "Comparativo consolidado": "Confronto consolidato",
    "Maiores impactos por setor": "Maggiori impatti per settore",
    "Evolução do rateio": "Andamento della ripartizione",
    "Período sem movimento": "Periodo senza movimento",
    "Revisar rateios gerenciais": "Rivedi ripartizioni gestionali",
    "Esta análise redistribui despesas corporativas entre setores para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa é alterado.": "Questa analisi redistribuisce le spese corporate tra settori per finalita gestionali. Nessuna registrazione finanziaria, pagamento o valore di cassa viene modificato.",
    "Nenhum rateio gerencial ativo foi encontrado neste período.": "Nessuna ripartizione gestionale attiva trovata in questo periodo.",
    "Todas as despesas corporativas elegíveis do período foram rateadas.": "Tutte le spese corporate eleggibili del periodo sono state ripartite.",
    "Este setor não recebeu despesas por rateio no período selecionado.": "Questo settore non ha ricevuto spese per ripartizione nel periodo selezionato.",
    "O rateio redistribui despesas entre setores e não altera o resultado consolidado da clínica.": "La ripartizione redistribuisce le spese tra settori e non modifica il risultato consolidato della clinica.",
    "APPLIED": "Applicato",
    "Resultado antes de rateios": "Risultato gestionale prima delle ripartizioni",
    "Resultado pós-rateio": "Risultato post-ripartizione",
    "Despesas redistribuídas": "Spese redistribuite",
    "Despesas rateadas": "Spese ripartite",
    "Despesas ainda não rateadas": "Spese non ancora ripartite",
    "Lotes considerados": "Lotti considerati",
    "Snapshots gerenciais": "Snapshot gestionali",
    "Diferença consolidada": "Differenza consolidata",
    "Resultado consolidado preservado": "Risultato consolidato preservato",
    "Lote revertido": "Lotto stornato",
    "Nenhum rateio aplicado no período": "Nessuna ripartizione applicata nel periodo",
    "Nenhum rateio ativo no período": "Nessuna ripartizione attiva nel periodo",
    "Despesa original": "Spesa originale",
    "Rateio recebido": "Ripartizione ricevuta",
    "Despesa pós-rateio": "Spesa post-ripartizione",
    "Resultado original": "Risultato originale",
    "Diferença": "Differenza",
    "Despesas recebidas por rateio": "Spese ricevute per ripartizione",
    "Esta visão redistribui despesas corporativas para fins gerenciais. Nenhum lançamento financeiro, pagamento ou valor de caixa foi alterado.": "Questa vista redistribuisce le spese corporate per finalita gestionali. Nessuna registrazione finanziaria, pagamento o valore di cassa e stato modificato.",
    "A comparação demonstra o impacto setorial do rateio. O resultado consolidado da clínica permanece inalterado.": "Il confronto mostra l'impatto settoriale della ripartizione. Il risultato consolidato della clinica resta invariato.",
    "Visão original sem efeitos de snapshots de rateio gerencial.": "Vista originale senza effetti dagli snapshot di ripartizione gestionale.",
    "Despesas corporativas redistribuídas na leitura gerencial a partir de snapshots aplicados.": "Spese corporate redistribuite nella lettura gestionale dagli snapshot applicati.",
    "Resultado gerencial por competência após redistribuição analítica de despesas corporativas aplicadas.": "Risultato gestionale per competenza dopo la redistribuzione analitica delle spese corporate applicate.",
    "Métodos de pagamento": "Metodi di pagamento",
    "Configure meios, adquirentes, prazos e taxas existentes.": "Configura metodi, acquirer, termini e commissioni esistenti.",
    "Regras de repasse": "Regole per i compensi professionali",
    "Configure regras versionadas de repasse por profissional, setor ou serviço.": "Configura regole versionate per determinare i compensi in base al professionista, al settore o al servizio.",
    "Rateio gerencial": "Ripartizione gestionale delle spese",
    "Configure regras versionadas de rateio percentual por setor.": "Configura regole versionate di ripartizione percentuale delle spese per settore.",
    "Configurações comerciais": "Impostazioni commerciali",
    "Gerencie origens e motivos administrativos na Central de Configurações.": "Gestisci origini e motivi amministrativi nel Centro impostazioni.",
    "Gerenciar origens": "Gestisci origini",
    "Gerenciar motivos": "Gestisci motivi",
    "Gerenciar catálogo": "Gestisci catalogo",
    "Gerenciar templates": "Gestisci template",
    "Gerenciar métodos": "Gestisci metodi",
    "Use este cadastro para padronizar as origens utilizadas no funil comercial.": "Usa questo registro per standardizzare le origini utilizzate nel funnel commerciale.",
    "Selecione uma clínica para continuar.": "Seleziona una clinica per continuare.",
    "Dados cadastrais": "Dati di registrazione",
    "Estes dados vêm do cadastro canônico da clínica e não são duplicados nesta central.": "Questi dati provengono dal registro canonico della clinica e non sono duplicati in questo centro.",
    "CNPJ": "CNPJ",
    "Preferências regionais": "Preferenze regionali",
    "A moeda e o fuso horário são compartilhados por toda a clínica.": "Valuta e fuso orario sono condivisi in tutta la clinica.",
    "Alterações não salvas": "Modifiche non salvate",
    "Salve as alterações antes de sair desta seção.": "Salva le modifiche prima di uscire da questa sezione.",
    "Salvar configurações": "Salva impostazioni",
    "Não alocado": "Non assegnato",
    "até": "fino a",
    "Filtros avançados": "Filtri avanzati",
    "Filtros ativos": "Filtri attivi",
    "Indicadores comerciais": "Indicatori commerciali",
    "Lista de leads": "Elenco dei lead",
    "Carregando leads...": "Caricamento lead...",
    "Nenhum resultado encontrado": "Nessun risultato trovato",
    "Nenhum lead cadastrado": "Nessun lead registrato",
    "Ajuste ou limpe os filtros para ampliar a busca.": "Modifica o cancella i filtri per ampliare la ricerca.",
    "Crie um lead para começar o acompanhamento comercial.": "Crea un lead per iniziare il follow-up commerciale.",
    "Acompanhe, priorize e avance as oportunidades comerciais.": "Monitora, dai priorità e fai avanzare le opportunità commerciali.",
    "Localização": "Posizione",
    "Filtrar por cidade": "Filtra per città",
    "Filtrar por bairro": "Filtra per quartiere",
    "Gênero": "Genere",
    "Data de cadastro do paciente": "Data di registrazione del paziente",
    "Cadastro a partir de": "Registrazione dal",
    "Cadastro até antes de": "Registrazione fino a prima di",
    "Direção": "Direzione",
    "Crescente": "Crescente",
    "Decrescente": "Decrescente",
    "Catálogo": "Catalogo",
    "Catálogo de Serviços e Procedimentos": "Catalogo di Servizi e Procedure",
    "Gerencie serviços e procedimentos utilizados na agenda, no prontuário clínico, nos planos de tratamento e no financeiro, preservando os dados históricos.": "Gestisci i servizi e le procedure utilizzati nel calendario, nelle cartelle cliniche, nei piani di trattamento e nell'area finanziaria, preservando i dati storici.",
    "Novo item do catálogo": "Nuova voce del catalogo",
    "Item do catálogo atualizado com sucesso.": "Voce del catalogo aggiornata correttamente.",
    "Item do catálogo cadastrado com sucesso.": "Voce del catalogo creata correttamente.",
    "Item do catálogo inativado com sucesso.": "Voce del catalogo disattivata correttamente.",
    "Item do catálogo reativado com sucesso.": "Voce del catalogo riattivata correttamente.",
    "Não foi possível salvar o item do catálogo.": "Non è stato possibile salvare la voce del catalogo.",
    "Não foi possível alterar o status do item do catálogo.": "Non è stato possibile modificare lo stato della voce del catalogo.",
    "Não foi possível carregar o catálogo.": "Non è stato possibile caricare il catalogo.",
    "Serviço": "Servizio",
    "Procedimento": "Procedura",
    "Serviço ou procedimento": "Servizio o procedura",
    "Serviço não informado": "Servizio non indicato",
    "Sem item do catálogo": "Senza voce del catalogo",
    "Outro lançamento": "Altro movimento",
    "Todos os tipos": "Tutti i tipi",
    "Buscar por nome ou descrição": "Cerca per nome o descrizione",
    "Duração": "Durata",
    "Preço padrão": "Prezzo predefinito",
    "Uso histórico": "Uso storico",
    "Toda a clínica": "Tutta la clinica",
    "Nenhum item do catálogo encontrado.": "Nessuna voce del catalogo trovata.",
    "Editar item do catálogo": "Modifica voce del catalogo",
    "Cadastrar item do catálogo": "Crea voce del catalogo",
    "Alterações no catálogo não modificam agendamentos, planos de tratamento ou lançamentos financeiros já registrados.": "Le modifiche al catalogo non influiscono su appuntamenti, piani di trattamento o movimenti finanziari già registrati.",
    "Carregando serviços...": "Caricamento servizi...",
    "Sugestão": "Suggerimento",
    "Agendamentos antigos podem permanecer sem serviço.": "Gli appuntamenti precedenti possono restare senza servizio.",
    "O preço padrão é apenas uma sugestão; o valor efetivo fica salvo no lançamento.": "Il prezzo predefinito è solo un suggerimento; l'importo effettivo viene salvato nel movimento.",
    "Buscar": "Cerca",
    "Agendamento": "Appuntamento",
    "Sim": "Sì",
    "Não": "No",
    "Recebido": "Ricevuto",
    "Receber": "Incassare",
    "Em aberto": "Aperto",
    "Quantidade": "Quantità",
    "Histórico financeiro": "Storico finanziario",
    "Nenhuma receita encontrada para este paciente.": "Nessun ricavo trovato per questo paziente.",
    "Todos os setores": "Tutti i reparti",
    "Filtrar por setor": "Filtra per reparto",
    "Setor inativo": "Settore inattivo",
    "Setor selecionado": "Settore selezionato",
    "Resultado direto do setor": "Risultato diretto del reparto",
    "Despesas atribuídas": "Spese attribuite",
    "Despesas a pagar": "Spese da pagare",
    "Despesas vencidas": "Spese scadute",
    "Despesas pagas": "Spese pagate",
    "Despesas corporativas": "Spese aziendali",
    "Despesas diretas": "Spese dirette",
    "Repasses pagos": "Compensi pagati",
    "Dados não atribuídos": "Dati non attribuiti",
    "Nenhum setor encontrado": "Nessun reparto trovato",
    "Setores": "Reparti",
    "Ticket médio por lançamento": "Ricavo medio per registrazione",
    "Requer custo realizado atribuído às aquisições": "Richiede costi effettivi attribuiti alle acquisizioni",
    "Receita média por paciente pagante": "Ricavo medio per paziente pagante",
    "Receita realizada no período; não representa LTV": "Ricavo effettivo del periodo; non rappresenta l'LTV",
    "Requer custo e receita atribuídos à campanha": "Richiede costi e ricavi attribuiti alla campagna",
    "Erro ao carregar setores": "Errore durante il caricamento dei reparti",
    "Já existe um setor com este nome": "Esiste già un reparto con questo nome",
    "O nome do setor deve ter entre 2 e 80 caracteres": "Il nome del reparto deve contenere da 2 a 80 caratteri",
    "Selecione um setor ativo da clínica": "Seleziona un reparto attivo della clinica",
    "Selecione um setor da clínica": "Seleziona un reparto della clinica",
    "Setor não encontrado": "Reparto non trovato",
    "Informe ao menos uma alteração": "Inserisci almeno una modifica",
    "Idioma": "Lingua",
    "Idioma da interface": "Lingua dell'interfaccia",
    "Moeda": "Valuta",
    "Moeda do sistema": "Valuta del sistema",
    "Configuração regional da clínica": "Impostazioni regionali della clinica",
    "Configuração regional salva": "Impostazioni regionali salvate",
    "A moeda e o fuso são compartilhados por toda a clínica.": "La valuta e il fuso orario sono condivisi in tutta la clinica.",
    "Somente administradores podem alterar esta configuração.": "Solo gli amministratori possono modificare questa impostazione.",
    "A moeda está bloqueada porque já existe histórico financeiro.": "La valuta è bloccata perché esiste già uno storico finanziario.",
    "Alterar a moeda não converte valores existentes.": "Cambiare valuta non converte i valori esistenti.",
    "Fuso horário": "Fuso orario",
    "São Paulo (América)": "San Paolo (Americhe)",
    "Nova York (América)": "New York (Americhe)",
    "Lisboa (Europa)": "Lisbona (Europa)",
    "Madri (Europa)": "Madrid (Europa)",
    "Roma (Europa)": "Roma (Europa)",
    "Buscar cidade ou fuso": "Cerca città o fuso orario",
    "O fuso define dias, horários da agenda e períodos dos indicadores; instantes salvos não são reescritos.": "Il fuso definisce giorni, orari dell'agenda e periodi degli indicatori; gli istanti salvati non vengono riscritti.",
    "Preferências": "Preferenze",
    "Abrir preferências": "Apri preferenze",
    "Ajuste idioma, moeda e tema da interface.": "Regola lingua, valuta e tema dell'interfaccia.",
    "Ajuste idioma e tema da interface.": "Regola lingua e tema dell'interfaccia.",
    "Tema": "Tema",
    "Tema da interface": "Tema dell'interfaccia",
    "América Latina": "America Latina",
    "Europa": "Europa",
    "Português": "Portoghese",
    "Inglês": "Inglese",
    "Espanhol": "Spagnolo",
    "Dashboard": "Dashboard",
    "Leads": "Lead",
    "Abrir lead": "Apri lead",
    "Abrir próximo follow-up": "Apri prossimo follow-up",
    "Erro ao carregar leads": "Errore nel caricamento dei lead",
    "Erro ao excluir lead": "Errore nell'eliminazione del lead",
    "Excluir lead": "Elimina lead",
    "Filtrar status": "Filtra stato",
    "Leads por Origem": "Lead per origine",
    "Nenhum dado disponível": "Nessun dato disponibile",
    "Nenhum follow-up pendente no momento.": "Nessun follow-up in sospeso al momento.",
    "Status do lead atualizado!": "Stato del lead aggiornato.",
    "Todos os profissionais": "Tutti i professionisti",
    "Marketing": "Marketing",
    "Pacientes": "Pazienti",
    "Profissionais": "Professionisti",
    "Agenda": "Calendario",
    "Atendimento": "Assistenza",
    "Metas": "Obiettivi",
    "Usuários": "Utenti",
    "Acessos da clínica": "Accessi alla clinica",
    "Administradores": "Amministratori",
    "Administradores ativos": "Amministratori attivi",
    "Alterar função do usuário": "Modifica il ruolo dell'utente",
    "Ativar usuário": "Attiva utente",
    "Buscar por nome ou email": "Cerca per nome o e-mail",
    "Carregando usuários...": "Caricamento utenti...",
    "Confirmar alteração": "Conferma modifica",
    "Define se esse usuário pode acessar a clínica.": "Indica se questo utente può accedere alla clinica.",
    "Deixe em branco para manter": "Lascia vuoto per mantenere la password attuale",
    "Desativar usuário": "Disattiva utente",
    "Editar usuário": "Modifica utente",
    "Erro ao atualizar usuário": "Errore durante l'aggiornamento dell'utente",
    "Erro ao carregar usuários": "Errore durante il caricamento degli utenti",
    "Erro ao criar usuário": "Errore durante la creazione dell'utente",
    "Filtros aplicados a esta lista.": "Filtri applicati a questo elenco.",
    "Nenhum usuário adicional encontrado para esta clínica.": "Nessun altro utente trovato per questa clinica.",
    "Nenhum usuário encontrado com os filtros informados.": "Nessun utente trovato con i filtri selezionati.",
    "Nenhum usuário encontrado.": "Nessun utente trovato.",
    "Nova senha": "Nuova password",
    "Reativar usuário": "Riattiva utente",
    "Senha inicial": "Password iniziale",
    "Seu perfil atual não possui permissão para cadastrar ou editar usuários.": "Il tuo profilo attuale non dispone dei permessi per creare o modificare utenti.",
    "Somente administradores da clínica podem gerenciar acessos.": "Solo gli amministratori della clinica possono gestire gli accessi.",
    "Todas as funções": "Tutti i ruoli",
    "Usuário ativo": "Utente attivo",
    "Usuário atualizado com sucesso.": "Utente aggiornato correttamente.",
    "Usuário criado com sucesso.": "Utente creato correttamente.",
    "Usuário desativado com sucesso.": "Utente disattivato correttamente.",
    "Usuário reativado com sucesso.": "Utente riattivato correttamente.",
    "usuario@clínica.com": "utente@clinica.com",
    "Usuários ativos": "Utenti attivi",
    "Usuários da Clínica": "Utenti della clinica",
    "Você": "Tu",
    "Clínicas": "Cliniche",
    "Auditoria": "Audit",
    "Sair": "Esci",
    "Entrar": "Accedi",
    "Fazer login": "Accedi",
    "Criar conta": "Crea account",
    "Bem-vindo de volta": "Bentornato",
    "E-mail": "E-mail",
    "Senha": "Password",
    "Mostrar senha": "Mostra password",
    "Ocultar senha": "Nascondi password",
    "Criar clínica": "Crea clinica",
    "Criando...": "Creazione...",
    "Entrando...": "Accesso...",
    "Criar": "Crea",
    "Cadastrar": "Registra",
    "Editar": "Modifica",
    "Excluir": "Elimina",
    "Atualizar": "Aggiorna",
    "Cancelar": "Annulla",
    "Confirmar": "Conferma",
    "Total de registros": "Totale record",
    "Página": "Pagina",
    "Página {currentPage} de {totalPages}": "Pagina {currentPage} di {totalPages}",
    "Registros por página": "Record per pagina",
    "Anterior": "Precedente",
    "Próxima": "Successiva",
    "Fechar": "Chiudi",
    "Processando...": "Elaborazione...",
    "Salvando...": "Salvataggio...",
    "Carregando...": "Caricamento...",
    "Nome": "Nome",
    "Contato": "Contatto",
    "Telefone": "Telefono",
    "Origem": "Origine",
    "Setor": "Settore",
    "Campanha": "Campagna",
    "Status": "Stato",
    "Data": "Data",
    "Tipo": "Tipo",
    "Tipo de tratamento": "Tipo di trattamento",
    "Tipo de atendimento": "Tipo di prestazione",
    "Tipo não informado": "Tipo non indicato",
    "Todos os tipos de tratamento": "Tutti i tipi di trattamento",
    "Iniciar atendimento": "Avvia prestazione",
    "Cadastre uma avaliação antes de iniciar uma evolução.": "Registra una valutazione prima di iniziare un'evoluzione clinica.",
    "Data da avaliação": "Data della valutazione",
    "Última evolução": "Ultima evoluzione",
    "Status do paciente": "Stato del paziente",
    "Paciente ativado com sucesso!": "Paziente attivato correttamente.",
    "Paciente inativado com sucesso!": "Paziente disattivato correttamente.",
    "Motivos de Não Agendamento": "Motivi di mancata prenotazione",
    "Principais objeções dos leads que não realizaram um agendamento.": "Principali obiezioni che impediscono ai Lead di prenotare un appuntamento.",
    "Leads não agendados": "Lead senza appuntamento",
    "Leads não agendados por origem": "Lead senza appuntamento per origine",
    "Distribuição server-side dos leads com desfecho não agendado.": "Distribuzione server-side dei Lead con esito mancata prenotazione.",
    "Principal motivo": "Motivo principale",
    "Representatividade": "Incidenza",
    "Nenhum motivo registrado": "Nessun motivo registrato",
    "Nenhum motivo de não agendamento encontrado para os filtros selecionados.": "Nessun motivo di mancata prenotazione trovato per i filtri selezionati.",
    "Leads não agendados por motivo": "Lead senza appuntamento per motivo",
    "Detalhamento dos leads não agendados": "Dettaglio dei Lead senza appuntamento",
    "Data do desfecho": "Data dell'esito",
    "Desfecho registrado em": "Esito registrato il",
    "Desfechos sem data definida": "Esiti senza data definita",
    "Cobertura histórica incompleta": "Copertura storica incompleta",
    "Registros legados": "Record legacy",
    "Dados anteriores não possuem data confiável.": "I dati precedenti non hanno una data affidabile.",
    "Não foi possível determinar a data": "Non è stato possibile determinare la data",
    "Sem origem": "Nessuna origine",
    "Sem motivo": "Nessun motivo",
    "Origem inativa": "Origine inattiva",
    "Comercial responsável": "Responsabile commerciale",
    "Último follow-up": "Ultimo follow-up",
    "Status comercial": "Stato commerciale",
    "Nenhum lead encontrado para este motivo.": "Nessun Lead trovato per questo motivo.",
    "Catálogo de Motivos de Não Agendamento": "Catalogo dei motivi di mancata prenotazione",
    "Padronize as objeções utilizadas no acompanhamento comercial dos leads.": "Standardizza le obiezioni utilizzate nel follow-up commerciale dei Lead.",
    "Novo motivo de não agendamento": "Nuovo motivo di mancata prenotazione",
    "Motivo adicionado com sucesso!": "Motivo aggiunto correttamente.",
    "Motivo atualizado com sucesso!": "Motivo aggiornato correttamente.",
    "Motivo ativado com sucesso!": "Motivo attivato correttamente.",
    "Motivo inativado com sucesso!": "Motivo disattivato correttamente.",
    "Adicionar motivo": "Aggiungi motivo",
    "Motivo": "Motivo",
    "Ativar": "Attiva",
    "Inativar": "Disattiva",
    "Editar motivo de não agendamento": "Modifica motivo di mancata prenotazione",
    "Inativar motivo?": "Disattivare il motivo?",
    "O motivo deixará de aparecer em novos cadastros, mas continuará preservado no histórico.": "Il motivo non comparirà più nei nuovi inserimenti, ma resterà nello storico.",
    "O motivo deve ter entre 2 e 100 caracteres": "Il motivo deve contenere da 2 a 100 caratteri",
    "Já existe um motivo de não agendamento com este nome": "Esiste già un motivo di mancata prenotazione con questo nome",
    "Motivo de não agendamento não encontrado": "Motivo di mancata prenotazione non trovato",
    "Selecione um motivo de não agendamento ativo": "Seleziona un motivo di mancata prenotazione attivo",
    "Não respondeu": "Non ha risposto",
    "Preço / Achou caro": "Prezzo / Troppo caro",
    "Queria convênio": "Desiderava usare l'assicurazione sanitaria",
    "Sem horário compatível": "Nessun orario compatibile",
    "Vai pensar": "Ci penserà",
    "Já fechou em outra clínica": "Ha scelto un'altra clinica",
    "Nenhum atendimento encontrado para o tipo e período selecionados.": "Nessuna seduta trovata per il tipo e il periodo selezionati.",
    "Categoria": "Categoria",
    "Pagamento": "Pagamento",
    "Pago": "Pagato",
    "Não Pago": "Non pagato",
    "Valor": "Importo",
    "Valor bruto": "Importo lordo",
    "A receber": "Crediti da incassare",
    "À Receber": "Crediti da incassare",
    "Novo valor a receber": "Nuovo credito",
    "Novo à receber": "Nuovo credito",
    "Editar valor a receber": "Modifica credito",
    "Editar à receber": "Modifica credito",
    "Gerar valor a receber": "Crea credito",
    "Gerar à receber": "Crea credito",
    "Valor a receber gerado com sucesso": "Credito creato con successo",
    "À receber gerado com sucesso": "Credito creato con successo",
    "À receber gerado": "Credito creato",
    "À receber já gerado": "Credito già creato",
    "Valor a receber criado com sucesso.": "Credito creato con successo.",
    "À receber criado com sucesso.": "Credito creato con successo.",
    "Valor a receber atualizado com sucesso.": "Credito aggiornato con successo.",
    "À receber atualizado com sucesso.": "Credito aggiornato con successo.",
    "Valor a receber cancelado com sucesso.": "Credito annullato con successo.",
    "À receber cancelado com sucesso.": "Credito annullato con successo.",
    "Controle valores pendentes, vencidos e recebimentos parciais sem misturar com receitas já recebidas.": "Controlla importi pendenti, scaduti e incassi parziali senza mescolarli con ricavi già ricevuti.",
    "Total a receber": "Totale da incassare",
    "Total à receber": "Totale da incassare",
    "Total vencido": "Totale scaduto",
    "Recebido no período": "Incassato nel periodo",
    "Total parcial": "Totale parziale",
    "Pacientes com pendência": "Pazienti con pendenze",
    "Todos os pacientes": "Tutti i pazienti",
    "Todos status": "Tutti gli stati",
    "Todos pacientes": "Tutti i pazienti",
    "Todas as formas": "Tutti i metodi",
    "Forma de pagamento prevista": "Metodo previsto",
    "Pagamento previsto": "Pagamento previsto",
    "Sem forma prevista": "Nessun metodo previsto",
    "Sem forma definida": "Nessun metodo definito",
    "Data do pagamento ou vencimento": "Data pagamento o scadenza",
    "Data do recebimento": "Data di incasso",
    "Valor total": "Valore totale",
    "Valor recebido": "Importo incassato",
    "Saldo restante": "Saldo residuo",
    "Marcar como recebido": "Segna come ricevuto",
    "Registrar pagamento parcial": "Registra pagamento parziale",
    "Registrar baixa": "Registra incasso",
    "Recebimento registrado com sucesso.": "Incasso registrato con successo.",
    "Recebimento parcial registrado com sucesso.": "Incasso parziale registrato con successo.",
    "Nenhum valor a receber encontrado com os filtros selecionados.": "Nessun credito trovato con i filtri selezionati.",
    "Nenhum valor à receber encontrado com os filtros selecionados.": "Nessun credito trovato con i filtri selezionati.",
    "Detalhes do valor a receber": "Dettagli credito",
    "Detalhes do à receber": "Dettagli credito",
    "Nenhuma baixa registrada.": "Nessun incasso registrato.",
    "Valor a receber indisponível": "Credito non disponibile",
    "À receber indisponível": "Credito non disponibile",
    "Gerando...": "Creazione...",
    "Vencimento": "Scadenza",
    "Vencido": "Scaduto",
    "Cancelado": "Annullato",
    "Valor líquido": "Importo netto",
    "Descrição": "Descrizione",
    "Endereço": "Indirizzo",
    "Número": "Numero",
    "Cidade": "Città",
    "Estado": "Stato",
    "Paciente": "Paziente",
    "Profissional": "Professionista",
    "Novo Paciente": "Nuovo paziente",
    "Buscar paciente existente": "Cerca paziente esistente",
    "Cadastrar novo paciente": "Registra nuovo paziente",
    "Novo Profissional": "Nuovo professionista",
    "Novo Agendamento": "Nuovo appuntamento",
    "Novo Lead": "Nuovo lead",
    "Nova Campanha": "Nuova campagna",
    "Novo Usuário": "Nuovo utente",
    "Novo usuário": "Nuovo utente",
    "Novo usuário da clínica": "Nuovo utente della clinica",
    "Criar usuário": "Crea utente",
    "Hoje": "Oggi",
    "Ontem": "Ieri",
    "Esta semana": "Questa settimana",
    "Últimos 7 dias": "Ultimi 7 giorni",
    "Este mês": "Questo mese",
    "Este ano": "Quest'anno",
    "Mês seguinte": "Mese successivo",
    "Mês anterior": "Mese precedente",
    "Por período": "Periodo personalizzato",
    "Início": "Inizio",
    "Fim": "Fine",
    "Término": "Fine",
    "Período inicial": "Periodo iniziale",
    "Período final": "Período finale",
    "Mês atual": "Mese corrente",
    "Visão geral": "Panoramica",
    "Receita": "Ricavi",
    "Receita bruta": "Ricavi lordi",
    "Receita líquida": "Ricavi netti",
    "Receita Total": "Ricavi totali",
    "Receitas": "Ricavi",
    "Despesas": "Spese",
    "Lucro": "Utile",
    "Prejuízo": "Perdita",
    "Equilíbrio": "Pareggio",
    "Total de Entradas": "Totale entrate",
    "Total de saídas": "Totale uscite",
    "Resultado do Período": "Risultato del periodo",
    "Soma de todas as receitas registradas no período selecionado.": "Somma di tutti i ricavi registrati nel periodo selezionato.",
    "Soma de todas as despesas registradas no período selecionado.": "Somma di tutte le spese registrate nel periodo selezionato.",
    "Diferença entre o total de entradas e o total de saídas no período selecionado.": "Differenza tra il totale delle entrate e il totale delle uscite nel periodo selezionato.",
    "Faturamento": "Fatturato",
    "Ticket Médio": "Valore medio per cliente",
    "Resumo Financeiro": "Riepilogo finanziario",
    "Central Financeira do Paciente": "Centro finanziario del paziente",
    "Visão filtrada dos lançamentos financeiros vinculados ao paciente.": "Vista filtrata dei movimenti finanziari collegati al paziente.",
    "Novo lançamento": "Nuovo movimento",
    "Receita total do paciente": "Ricavi totali del paziente",
    "Total recebido": "Totale incassato",
    "Total em aberto": "Totale aperto",
    "Próximo vencimento": "Prossima scadenza",
    "Planos ativos": "Piani attivi",
    "Pendências financeiras": "Pendenze finanziarie",
    "Lançamentos em aberto, vencidos ou parcialmente pagos.": "Movimenti aperti, scaduti o pagati parzialmente.",
    "pendência(s)": "pendenza/e",
    "Nenhuma pendência financeira para este paciente.": "Nessuna pendenza finanziaria per questo paziente.",
    "Saldo pendente": "Saldo pendente",
    "Histórico financeiro do paciente": "Storico finanziario del paziente",
    "Receitas e valores a receber filtrados pelo paciente aberto.": "Entrate e crediti filtrati per il paziente aperto.",
    "Buscar por descrição, profissional, valor, data ou status": "Cerca per descrizione, professionista, importo, data o stato",
    "Pagos": "Pagati",
    "Editar lançamento": "Modifica movimento",
    "Tipo do lançamento": "Tipo di movimento",
    "Consulta avulsa": "Visita singola",
    "Outro lançamento personalizado": "Altro movimento personalizzato",
    "Selecionar plano existente": "Seleziona piano esistente",
    "Selecione um plano": "Seleziona un piano",
    "Sem plano vinculado": "Nessun piano collegato",
    "Sem profissional vinculado": "Nessun professionista collegato",
    "Sem agendamento vinculado": "Nessun appuntamento collegato",
    "Receber pagamento": "Registra pagamento",
    "Detalhes do lançamento": "Dettagli del movimento",
    "Plano vinculado": "Piano collegato",
    "Agendamento vinculado": "Appuntamento collegato",
    "Cancelar lançamento": "Annulla movimento",
    "Este agendamento já possui lançamento financeiro vinculado.": "Questo appuntamento ha già un movimento finanziario collegato.",
    "Este plano já possui lançamento financeiro vinculado.": "Questo piano ha già un movimento finanziario collegato.",
    "Informe um valor válido.": "Inserisci un importo valido.",
    "Nova Receita": "Nuova entrata",
    "Nova Despesa": "Nuova spesa",
    "Receita registrada com sucesso!": "Entrata registrata con successo.",
    "Data do Pagamento": "Data di pagamento",
    "Competência": "Data di competenza",
    "Margem": "Margine",
    "Meta": "Obiettivo",
    "Gap": "Scostamento",
    "Novo plano": "Nuovo piano",
    "Plano de tratamento": "Piano di trattamento",
    "Renovação do plano": "Rinnovo del piano",
    "Planos de tratamento": "Piani di trattamento",
    "Sessões": "Sedute",
    "sessões restantes": "sedute rimanenti",
    "Saldo": "Saldo",
    "restantes": "rimanenti",
    "Validade": "Validità",
    "Sem prazo": "Senza scadenza",
    "Rascunho": "Bozza",
    "Vendido": "Venduto",
    "Em execução": "In corso",
    "Renovar": "Rinnova",
    "Reativar": "Riattiva",
    "Forma de pagamento": "Metodo di pagamento",
    "Selecione uma forma de pagamento": "Seleziona un metodo di pagamento",
    "Selecione uma forma de pagamento.": "Seleziona un metodo di pagamento.",
    "Nenhuma forma de pagamento ativa": "Nessun metodo di pagamento attivo",
    "Cadastre uma forma de pagamento ativa antes de registrar receitas.": "Registra un metodo di pagamento attivo prima di registrare entrate.",
    "Forma de pagamento não cadastrada ({method})": "Metodo di pagamento non registrato ({method})",
    "{name} (inativa)": "{name} (inattiva)",
    "Registro histórico": "Record storico",
    "Forma inativa": "Metodo inattivo",
    "Vínculo histórico indisponível": "Collegamento storico non disponibile",
    "Forma de pagamento histórica não vinculada": "Metodo di pagamento storico non collegato",
    "Convênio/cobertura": "Convenzione/copertura",
    "Identificador técnico: {identifier}": "Identificatore tecnico: {identifier}",
    "Parcela {current} de {total} da obrigação": "Rata {current} di {total} dell'obbligazione",
    "Parcelas": "Rate",
    "Parcelas do meio de pagamento": "Rate del metodo di pagamento",
    "Indisponível": "Non disponibile",
    "Valor líquido histórico indisponível": "Valore netto storico non disponibile",
    "Operações anteriores à preservação histórica podem não possuir detalhamento de taxas.": "Le operazioni precedenti alla conservazione storica potrebbero non includere il dettaglio delle commissioni.",
    "Cartão de crédito à vista": "Carta di credito in unica soluzione",
    "Resumo do período": "Riepilogo del periodo",
    "Taxas/descontos": "Commissioni/sconti",
    "Observação opcional": "Nota opzionale",
    "Total no período": "Totale nel periodo",
    "Atendimento - Paciente": "Appuntamento - Paziente",
    "Nenhuma receita encontrada para este profissional no período selecionado.": "Nessun ricavo trovato per questo professionista nel periodo selezionato.",
    "Nenhuma receita encontrada com os filtros selecionados.": "Nessun ricavo trovato con i filtri selezionati.",
    "Avaliação": "Valutazione",
    "Tratamento": "Trattamento",
    "Retorno": "Visita di controllo",
    "Reavaliação": "Rivalutazione",
    "Confirmado": "Confermato",
    "Em andamento": "In corso",
    "Não compareceu": "Non si è presentato",
    "Consulta": "Visita",
    "Pacote": "Pacchetto",
    "Masculino": "Maschile",
    "Feminino": "Femminile",
    "Logradouro": "Indirizzo",
    "Complemento": "Complemento",
    "Contato de Emergência": "Contatto di emergenza",
    "Histórico Médico / Observações": "Anamnesi / Osservazioni",
    "Função": "Ruolo",
    "Permissões": "Permessi",
    "Administrador": "Amministratore",
    "Ativo": "Attivo",
    "Inativo": "Inattivo",
    "Usuário": "Utente",
    "Erro ao fazer login": "Errore durante l'accesso",
    "Erro ao criar conta": "Errore durante la creazione dell'account",
    "As senhas não coincidem.": "Le password non coincidono.",
    "Cadastro e prontuário de pacientes": "Anagrafica e cartella clinica dei pazienti",
    "Cadastro e prontuário do paciente": "Anagrafica e cartella clinica del paziente",
    "Busca por nome, telefone ou CPF": "Cerca per nome, telefono o CPF",
    "Busca por nome, telefone, CPF, gênero, cidade ou bairro": "Cerca per nome, telefono, CPF, genere, città o quartiere",
    "Buscar paciente": "Cerca paziente",
    "Buscar paciente por nome, telefone ou CPF": "Cerca paziente per nome, telefono o CPF",
    "Digite o nome do paciente": "Inserisci il nome del paziente",
    "Buscando pacientes...": "Ricerca pazienti...",
    "Não foi possível buscar os pacientes. Tente novamente.": "Non è stato possibile cercare i pazienti. Riprova.",
    "Selecione um paciente e um profissional.": "Seleziona un paziente e un professionista.",
    "Erro ao carregar dados do paciente": "Errore nel caricamento dei dati del paziente",
    "Carregando pacientes...": "Caricamento pazienti...",
    "Nenhuma despesa encontrada no período selecionado.": "Nessuna spesa trovata nel periodo selezionato.",
    "Nenhum paciente": "Nessun paziente",
    "Nenhum paciente encontrado": "Nessun paziente trovato",
    "Nascimento": "Nascita",
    "Cadastro": "Anagrafica",
    "Ver": "Visualizza",
    "Paciente criado com sucesso!": "Paziente creato con successo.",
    "Paciente existente selecionado.": "Paziente esistente selezionato.",
    "Encontramos um ou mais pacientes com dados semelhantes nesta clínica. Revise os cadastros antes de continuar.": "Abbiamo trovato uno o più pazienti con dati simili in questa clinica. Rivedi i record prima di continuare.",
    "Já existe um cadastro com este CPF nesta clínica. Como bases históricas podem conter duplicidades, você pode selecionar o cadastro existente ou continuar a criação após revisar os dados.": "Esiste già una scheda con questo CPF in questa clinica. Poiché gli archivi storici possono contenere duplicati, puoi selezionare la scheda esistente o continuare la creazione dopo aver verificato i dati.",
    "Confiança": "Affidabilità",
    "Critérios": "Criteri",
    "Usar este cadastro": "Usa questa scheda",
    "Revisei e preciso criar um novo cadastro": "Ho verificato e devo creare una nuova scheda",
    "Continuar criação": "Continua creazione",
    "Paciente atualizado com sucesso!": "Paziente aggiornato con successo.",
    "Editar Paciente": "Modifica paziente",
    "RG": "Documento secondario",
    "Estado civil": "Stato civile",
    "Selecione o estado civil": "Seleziona lo stato civile",
    "Solteiro(a)": "Celibe/Nubile",
    "Casado(a)": "Sposato/a",
    "União Estável": "Unione stabile",
    "Divorciado(a)": "Divorziato/a",
    "Viúvo(a)": "Vedovo/a",
    "Separado(a)": "Separato/a",
    "Prefiro não informar": "Preferisco non indicarlo",
    "Profissão": "Professione",
    "Convênio e filiação": "Assicurazione sanitaria e filiazione",
    "Número do convênio": "Numero assicurazione sanitaria",
    "CNS": "Tessera sanitaria nazionale",
    "Nome da mãe": "Nome della madre",
    "Documento da mãe": "Documento della madre",
    "Nome do pai": "Nome del padre",
    "Documento do pai": "Documento del padre",
    "Filiação": "Filiazione",
    "Texto livre. Use vírgulas, espaços ou uma linha por item.": "Testo libero. Usa virgole, spazi o una riga per elemento.",
    "Erro ao carregar planos de tratamento": "Errore nel caricamento dei piani di trattamento",
    "Carregando planos de tratamento...": "Caricamento piani di trattamento...",
    "Buscando endereço pelo CEP...": "Ricerca dell'indirizzo tramite CAP...",
    "Plano cancelado com sucesso!": "Piano annullato con successo.",
    "Plano reativado com sucesso!": "Piano riattivato con successo.",
    "Acompanhe venda, execução e saldo de sessões do paciente.": "Monitora vendita, utilizzo e saldo delle sedute del paziente.",
    "Nenhum plano cadastrado para este paciente ainda.": "Nessun piano ancora registrato per questo paziente.",
    "Venda em": "Vendita il",
    "Venda ainda não registrada": "Vendita non ancora registrata",
    "Valor da receita": "Importo del ricavo",
    "Venda registrada no financeiro.": "Vendita registrata nel modulo finanziario.",
    "Plano ainda sem receita vinculada no financeiro.": "Piano ancora senza entrata collegata nel modulo finanziario.",
    "Plano de tratamento atualizado com sucesso!": "Piano di trattamento aggiornato con successo.",
    "Renovação do plano criada com sucesso!": "Rinnovo del piano creato con successo.",
    "Plano de tratamento cadastrado com sucesso!": "Piano di trattamento registrato con successo.",
    "Editar Plano de Tratamento": "Modifica piano di trattamento",
    "Renovar Plano de Tratamento": "Rinnova piano di trattamento",
    "Novo Plano de Tratamento": "Nuovo piano di trattamento",
    "Ex.: Reabilitação lombar": "Es.: riabilitazione lombare",
    "Selecione o profissional": "Seleziona il professionista",
    "Quantidade de sessões": "Numero di sedute",
    "Validade inicial": "Validità iniziale",
    "Validade final": "Validità finale",
    "Objetivo do tratamento, observações e combinados.": "Obiettivo del trattamento, osservazioni e accordi.",
    "Registrar nova venda na renovação": "Registra una nuova vendita al rinnovo",
    "Registrar venda do plano agora": "Registra ora la vendita del piano",
    "Quando marcado, o financeiro recebe uma receita de pacote vinculada ao plano.": "Se selezionato, nell'area finanziaria viene registrata un'entrata da pacchetto collegata al piano.",
    "Data da venda": "Data di vendita",
    "Descrição financeira": "Descrizione finanziaria",
    "Salvar plano": "Salva piano",
    "Criar renovação": "Crea rinnovo",
    "Cadastrar plano": "Registra piano",
    "Gestão de corpo clínico e especialidades": "Gestione dello staff clinico e delle specialità",
    "Editar Profissional": "Modifica professionista",
    "Profissional cadastrado com sucesso!": "Professionista registrato con successo.",
    "Profissional atualizado com sucesso!": "Professionista aggiornato con successo.",
    "Erro ao carregar profissionais": "Errore nel caricamento dei professionisti",
    "Nenhum profissional encontrado": "Nessun professionista trovato",
    "Catálogo de setores": "Catalogo dei settori",
    "Use este cadastro para padronizar leads, profissionais e financeiro.": "Usa questa anagrafica per standardizzare lead, professionisti e finanze.",
    "Setor removido com sucesso!": "Settore rimosso con successo.",
    "Setor adicionado com sucesso!": "Settore aggiunto con successo.",
    "Erro ao remover setor": "Errore nella rimozione del settore",
    "Erro ao adicionar setor": "Errore nell'aggiunta del settore",
    "Novo setor": "Nuovo settore",
    "Adicionar setor": "Aggiungi settore",
    "Buscar por nome, setor ou especialidade...": "Cerca per nome, settore o specialità...",
    "Especialidade": "Specialità",
    "Especialidades": "Specialità",
    "Registro": "Numero di iscrizione",
    "Cor na agenda": "Colore nel calendario",
    "Duração da consulta": "Durata della visita",
    "A duração da consulta deve ser de pelo menos 15 minutos.": "La durata della visita deve essere di almeno 15 minuti.",
    "Limite de pacientes por horário": "Limite di pazienti per fascia oraria",
    "Limite por horário": "Limite per fascia",
    "Minutos": "Minuti",
    "Ex.: Traumato, Ortopedia": "Es.: traumatologia, ortopedia",
    "Horários / Agenda": "Orari / Calendario",
    "Configure a capacidade semanal do profissional. Isso alimenta a ocupação percentual da agenda.": "Configura la capacità settimanale del professionista. Questo dato alimenta la percentuale di occupazione del calendario.",
    "Segunda": "Lunedì",
    "Terça": "Martedì",
    "Quarta": "Mercoledì",
    "Quinta": "Giovedì",
    "Sexta": "Venerdì",
    "Sábado": "Sabato",
    "Domingo": "Domenica",
    "Adicionar faixa": "Aggiungi fascia",
    "Sem horários configurados neste dia.": "Nessun orario configurato per questo giorno.",
    "Sem horários disponíveis": "Nessun orario disponibile",
    "Gerencie os agendamentos da clínica": "Gestisci il calendario degli appuntamenti della clinica",
    "Todos profissionais": "Tutti i professionisti",
    "Ver detalhes": "Vedi i dettagli",
    "Carregando dados...": "Caricamento dati...",
    "Erro ao carregar dados.": "Errore durante il caricamento dei dati.",
    "Nenhum Lead encontrado": "Nessun Lead trovato",
    "Erro ao carregar agendamentos": "Errore nel caricamento degli appuntamenti",
    "Horário inválido": "Orario non valido",
    "Não é possível criar agendamentos em datas anteriores à data atual.": "Non è possibile creare appuntamenti in date precedenti a quella attuale.",
    "Este profissional já atingiu o limite de pacientes para este horário. Escolha outro horário ou ajuste o limite no cadastro do profissional.": "Questo professionista ha già raggiunto il limite di pazienti per questa fascia oraria. Scegli un altro orario o modifica il limite nella scheda del professionista.",
    "Este profissional não possui faixa de horário cadastrada para o período selecionado. Ajuste a agenda do profissional ou escolha outro horário.": "Questo professionista non ha orari configurati per il periodo selezionato. Modifica l'agenda del professionista o scegli un altro orario.",
    "Agendado com sucesso": "Appuntamento creato con successo",
    "Agendamento atualizado": "Appuntamento aggiornato",
    "Receita gerada com sucesso": "Entrata registrata con successo",
    "Selecionar plano": "Seleziona piano",
    "Sem plano": "Nessun piano",
    "validade até": "valido fino al",
    "Nenhum plano vendido ou ativo disponível para este paciente e profissional.": "Nessun piano venduto o attivo disponibile per questo paziente e professionista.",
    "Selecione paciente e profissional para usar um plano.": "Seleziona paziente e professionista per usare un piano.",
    "Intervalos de": "Intervalli di",
    "minutos": "minuti",
    "Atendimentos vinculados ao plano ficam com valor por sessão zerado. O faturamento entra na venda do plano.": "Gli appuntamenti collegati al piano hanno importo per seduta pari a zero. Il fatturato viene registrato nella vendita del piano.",
    "Agendar": "Prenota",
    "Detalhes do Agendamento": "Dettagli dell'appuntamento",
    "Criado em": "Creato il",
    "Histórico de importação": "Cronologia importazioni",
    "A reversão deve seguir a ordem inversa da importação para preservar os vínculos.": "Il ripristino deve seguire l'ordine inverso dell'importazione per preservare i collegamenti.",
    "Nenhuma importação registrada para esta clínica.": "Nessuna importazione registrata per questa clinica.",
    "Revertida": "Rollback eseguito",
    "Rastreáveis": "Tracciabili",
    "Revertido em": "Ripristinato il",
    "Reverter": "Ripristina",
    "Revertendo...": "Ripristino...",
    "Reverter este lote removerá os registros importados por ele. Deseja continuar?": "Il ripristino di questo lotto rimuoverà i record importati. Vuoi continuare?",
    "CPFs inválidos legados": "CPF legacy non validi",
    "Reprocessados por ID externo": "Rielaborati per ID esterno",
    "Importação revertida com sucesso.": "Importazione ripristinata con successo.",
    "registros removidos": "record rimossi",
    "Erro ao carregar histórico de importação.": "Errore durante il caricamento della cronologia importazioni.",
    "Erro ao reverter importação.": "Errore durante il ripristino dell'importazione.",
    "Importação bloqueada pela ordem de dependência. Consulte o histórico da clínica.": "Importazione bloccata dall'ordine delle dipendenze. Consulta la cronologia della clinica.",
    "Remarcado": "Riprogrammato",
    "Remarcar agendamento": "Riprogramma appuntamento",
    "Selecione a nova data, horário e profissional para atualizar o agendamento.": "Seleziona nuova data, orario e professionista per aggiornare l'appuntamento.",
    "Nova data": "Nuova data",
    "Informe a nova data, horário e profissional para remarcar.": "Indica nuova data, orario e professionista per riprogrammare.",
    "Não é possível remarcar para datas anteriores à data atual.": "Non è possibile riprogrammare in date precedenti a quella attuale.",
    "O plano vinculado pertence a outro profissional. Remova o plano ou mantenha o profissional atual.": "Il piano collegato appartiene a un altro professionista. Rimuovi il piano o mantieni il professionista attuale.",
    "Atual": "Attuale",
    "Motivo do cancelamento": "Motivo dell'annullamento",
    "Preencha quando o agendamento for cancelado": "Compila quando l'appuntamento viene annullato",
    "Disponível somente para status cancelado": "Disponibile solo per stato annullato",
    "Informe pelo menos 30 caracteres para registrar o cancelamento.": "Inserisci almeno 30 caratteri per registrare l'annullamento.",
    "Informe um motivo do cancelamento com pelo menos 30 caracteres.": "Inserisci un motivo di annullamento con almeno 30 caratteri.",
    "Gerar receita": "Registra entrata",
    "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.": "Registra un'entrata collegata a questo appuntamento, al paziente e al professionista.",
    "Agendamento cancelado": "Appuntamento annullato",
    "Receita já gerada": "Entrata già registrata",
    "Sessão coberta por plano": "Seduta coperta dal piano",
    "Não é permitido gerar receita para um agendamento com status cancelado.": "Non è possibile registrare entrate per un appuntamento annullato.",
    "Este atendimento consome uma sessão do plano vinculado. O faturamento deve ser registrado na venda do plano, não por atendimento.": "Questo appuntamento consuma una seduta del piano collegato. Il fatturato deve essere registrato nella vendita del piano, non per singolo appuntamento.",
    "Data do pagamento": "Data di pagamento",
    "Descrição da receita": "Descrizione dell'entrata",
    "Gerando receita...": "Registrazione entrata...",
    "Receita indisponível": "Entrata non disponibile",
    "Coberto por plano": "Coperto da piano",
    "Receita gerada": "Entrata registrata",
    "Eficiência assistencial, recorrência e satisfação do paciente em um único lugar.": "Efficienza assistenziale, continuità e soddisfazione del paziente in un'unica vista.",
    "Carregando indicadores assistenciais...": "Caricamento degli indicatori assistenziali...",
    "Meta de retenção atualizada com sucesso!": "Obiettivo di fidelizzazione aggiornato con successo.",
    "NPS atualizado com sucesso!": "NPS aggiornato con successo.",
    "NPS registrado com sucesso!": "NPS registrato con successo.",
    "Responder": "Rispondi",
    "Responder NPS": "Rispondi NPS",
    "Registre a resposta do NPS para o plano selecionado.": "Registra la risposta NPS per il piano selezionato.",
    "Registros de NPS já respondidos pelo paciente.": "Registri NPS già compilati dal paziente.",
    "Link da pesquisa copiado.": "Link del questionario copiato.",
    "Novo link gerado e copiado.": "Nuovo link generato e copiato.",
    "Não foi possível copiar o link.": "Non è stato possibile copiare il link.",
    "Esta pesquisa já foi respondida. O link não pode ser copiado novamente.": "Questo questionario è già stato compilato. Il link non può essere copiato di nuovo.",
    "Esta pesquisa expirou. Gere uma nova pesquisa para enviar um link válido.": "Questo questionario è scaduto. Genera un nuovo questionario per inviare un link valido.",
    "Esta pesquisa expirou. Gere um novo link para continuar.": "Il questionario è scaduto. Genera un nuovo link per continuare.",
    "O link desta pesquisa já foi emitido de forma protegida. Use o envio automático ou gere uma nova pesquisa.": "Il link di questo questionario è già stato emesso in modo protetto. Usa l'invio automatico o genera un nuovo questionario.",
    "Não foi possível copiar o link porque a pesquisa não foi encontrada ou não pertence mais a esta clínica.": "Non è stato possibile copiare il link perché il questionario non è stato trovato o non appartiene più a questa clinica.",
    "Não foi possível copiar o link por um erro inesperado. Tente novamente em instantes.": "Non è stato possibile copiare il link a causa di un errore imprevisto. Riprova tra poco.",
    "O link foi gerado, mas o navegador bloqueou a cópia automática. Permita o acesso à área de transferência e tente novamente.": "Il link è stato generato, ma il browser ha bloccato la copia automatica. Consenti l'accesso agli appunti e riprova.",
    "Pacientes atendidos": "Pazienti seguiti",
    "Primeira consulta": "Prima visita",
    "Avaliações no período selecionado": "Valutazioni nel periodo selezionato",
    "Consultas avulsas": "Visite singole",
    "Atendimentos sem plano vinculado": "Sedute senza piano collegato",
    "Pacientes em tratamento": "Pazienti in trattamento",
    "Pacientes com plano no período": "Pazienti con piano nel periodo",
    "Pacientes atendidos no período": "Pazienti seguiti nel periodo",
    "Resumo dos atendimentos concluídos entre": "Riepilogo delle sedute completate tra",
    "Atendimentos": "Sedute",
    "Com retorno": "Con ritorno",
    "Nenhum atendimento concluído no período selecionado.": "Nessuna seduta completata nel periodo selezionato.",
    "atendimento concluído": "seduta completata",
    "atendimentos concluídos": "sedute completate",
    "Paciente recorrente": "Paziente ricorrente",
    "Paciente novo no período": "Nuovo paziente nel periodo",
    "Primeiro atendimento": "Prima seduta",
    "Último atendimento": "Ultima seduta",
    "Tipos de atendimento": "Tipi di seduta",
    "Taxa de retenção": "Tasso di fidelizzazione",
    "resposta": "risposta",
    "respostas": "risposte",
    "Horas ocupadas": "Ore occupate",
    "Horários ocupados": "Fasce occupate",
    "Horários": "Fasce",
    "horários disponíveis": "fasce disponibili",
    "Absenteísmo": "Assenteismo",
    "com retorno no período": "con visita di controllo nel periodo",
    "NPS pós-tratamento": "NPS post-trattamento",
    "horários ativos": "appuntamenti nel periodo",
    "faltas no período": "assenze nel periodo",
    "Novas x recorrentes": "Nuove vs ricorrenti",
    "Novas consultas x retornos": "Nuove visite vs visite di controllo",
    "Novas consultas x recorrentes": "Prime visite vs ricorrenti",
    "Resumo do NPS": "Riepilogo NPS",
    "faltas em": "assenze su",
    "horários": "orari",
    "Mix assistencial": "Mix assistenziale",
    "Promotores": "Promotori",
    "Passivos": "Passivi",
    "Detratores": "Detrattori",
    "Leitura operacional": "Lettura operativa",
    "Cruze eficiência, faltas e satisfação para agir mais rápido na rotina da clínica.": "Analizza efficienza, assenze e soddisfazione per intervenire più rapidamente nella routine della clinica.",
    "Retornos por paciente": "Sedute per paziente",
    "Média de sessões concluídas por paciente no período.": "Media di sedute completate per paziente nel periodo.",
    "Absenteísmo x conclusão": "Assenze vs completamento",
    "Faltas comparadas aos atendimentos concluídos no mesmo recorte.": "Assenze confrontate con le sedute completate nello stesso intervallo.",
    "Pacientes fidelizados": "Pazienti fidelizzati",
    "Pacientes com 2 ou mais atendimentos concluídos no período.": "Pazienti con 2 o più sedute completate nel periodo.",
    "Agenda do dia": "Calendario giornaliero",
    "Agenda da semana": "Calendario settimanale",
    "Agenda do mês": "Calendario mensile",
    "horários ocupados": "slot occupati",
    "Horas": "Ore",
    "Concluídos": "Completati",
    "de ocupação sobre": "di occupazione su",
    "disponíveis": "disponibili",
    "Sem carga horária configurada nos profissionais para calcular a taxa percentual.": "Nessun monte ore configurato per i professionisti per calcolare la percentuale.",
    "Meta de retenção": "Obiettivo di fidelizzazione",
    "Pacientes com 2 ou mais atendimentos concluídos no período selecionado.": "Pazienti con 2 o più sedute completate nel periodo selezionato.",
    "pacientes retornaram no período.": "pazienti fidelizzati nel periodo.",
    "Defina uma meta percentual para acompanhar a fidelização da base.": "Definisci un obiettivo percentuale per monitorare la fidelizzazione della base pazienti.",
    "Meta mensal (%)": "Obiettivo mensile (%)",
    "Segmentação por setor": "Analisi per settore",
    "Compare ocupação, retenção e satisfação para encontrar gargalos operacionais.": "Confronta occupazione, fidelizzazione e soddisfazione per individuare criticità operative.",
    "Ocupação": "Occupazione",
    "Retenção": "Fidelizzazione",
    "Respostas": "Risposte",
    "concluídos": "completati",
    "faltas": "assenze",
    "novas": "nuove",
    "recorrentes": "ricorrenti",
    "Detalhes do setor": "Dettagli del settore",
    "Período considerado": "Periodo considerato",
    "Agendamentos realizados": "Appuntamenti completati",
    "Agendamentos cancelados": "Appuntamenti annullati",
    "Taxa de comparecimento": "Tasso di presenza",
    "Novos atendimentos": "Nuovi appuntamenti",
    "Atendimentos recorrentes": "Appuntamenti ricorrenti",
    "Profissionais vinculados": "Professionisti collegati",
    "Nenhum profissional vinculado a este setor.": "Nessun professionista collegato a questo settore.",
    "Sem registro informado": "Nessuna registrazione indicata",
    "Leitura do setor": "Lettura del settore",
    "Respostas NPS": "Risposte NPS",
    "Média de sessões por paciente": "Media sedute per paziente",
    "Filtro de detalhes": "Filtro dettagli",
    "Selecione uma opção para listar paciente e profissional vinculados.": "Seleziona un'opzione per elencare il paziente e il professionista collegati.",
    "Total encontrado": "Totale trovato",
    "Buscar nos detalhes": "Cerca nei dettagli",
    "Ordenar por": "Ordina per",
    "Mais recentes": "Più recenti",
    "Nome do paciente": "Nome paziente",
    "Mais atendimentos": "Più appuntamenti",
    "Carregando detalhes...": "Caricamento dettagli...",
    "Erro ao carregar detalhes.": "Errore durante il caricamento dei dettagli.",
    "Paciente não informado": "Paziente non indicato",
    "Profissional não informado": "Professionista non indicato",
    "Nenhum registro encontrado para este filtro.": "Nessun record trovato per questo filtro.",
    "Nenhum dado assistencial encontrado no período selecionado.": "Nessun dato assistenziale nel periodo selezionato.",
    "Fila de NPS automático": "Questionari NPS automatici",
    "Todo plano concluído gera um link público para o paciente responder após o tratamento.": "Ogni piano concluso genera un link pubblico per consentire al paziente di rispondere dopo il trattamento.",
    "Nenhuma pesquisa automática no período selecionado.": "Nessun questionario automatico nel periodo selezionato.",
    "Pendentes": "In sospeso",
    "Respondidas": "Risposte",
    "Ação": "Azione",
    "Respondida": "Risposta",
    "Expirada": "Scaduto",
    "Em": "Il",
    "Criada em": "Creata il",
    "Expirou em {date}": "Scaduto il {date}",
    "Copiar link": "Copia link",
    "Gerar novo link": "Genera nuovo link",
    "O link público segue o formato": "Il link pubblico ha questo formato",
    "e pode ser enviado pela equipe no canal preferido.": "e può essere inviato dal team tramite il canale preferito.",
    "Feedbacks recentes": "Feedback recenti",
    "Leitura rápida da satisfação capturada após os tratamentos.": "Panoramica rapida della soddisfazione rilevata dopo i trattamenti.",
    "Nenhum NPS registrado ainda.": "Nessun NPS ancora registrato.",
    "Sem respostas ainda": "Nessuna risposta al momento",
    "Excelência percebida": "Eccellenza percepita",
    "Boa satisfação": "Buona soddisfazione",
    "Atenção aos detratores": "Attenzione ai detrattori",
    "Experiência crítica": "Esperienza critica",
    "Registrar NPS pós-tratamento": "Registra NPS post-trattamento",
    "Um feedback por plano de tratamento. Se já existir, o registro será atualizado.": "Un solo feedback per piano di trattamento. Se esiste già, verrà aggiornato.",
    "Cadastre ou avance planos de tratamento para começar a registrar NPS.": "Registra o porta avanti i piani di trattamento per iniziare a raccogliere l'NPS.",
    "Nota NPS (0 a 10)": "Punteggio NPS (0-10)",
    "Comentário": "Commento",
    "O que o paciente destacou sobre a experiência?": "Che cosa ha evidenziato il paziente dell'esperienza?",
    "Última atualização em": "Ultimo aggiornamento il",
    "Atualizar NPS": "Aggiorna NPS",
    "Registrar NPS": "Registra NPS",
    "Selecione a clínica": "Seleziona la clinica",
    "Tema claro": "Tema chiaro",
    "Tema escuro": "Tema scuro",
    "Ativar tema claro": "Attiva tema chiaro",
    "Ativar tema escuro": "Attiva tema scuro",
    "Voltar para a página inicial": "Torna alla pagina iniziale",
    "Portal de acesso do super admin": "Portale di accesso super admin",
    "Use suas credenciais para entrar na camada central da plataforma.": "Usa le tue credenziali per accedere al livello centrale della piattaforma.",
    "Entre com suas credenciais para acessar a plataforma.": "Inserisci le credenziali per accedere alla piattaforma.",
    "Não tem uma conta?": "Non hai un account?",
    "Já tem uma conta?": "Hai già un account?",
    "Seu nome": "Il tuo nome",
    "Sua cidade": "La tua città",
    "Mínimo 6 caracteres": "Minimo 6 caratteri",
    "Mínimo 8 caracteres": "Minimo 8 caratteri",
    "Repita sua senha": "Ripeti la password",
    "Nome da clínica": "Nome della clinica",
    "Nome da sua clínica": "Nome della tua clinica",
    "Telefone da clínica": "Telefono della clinica",
    "E-mail da clínica": "E-mail della clinica",
    "Rua, avenida, etc.": "Via, viale, ecc.",
    "Nome completo do responsável": "Nome completo del responsabile",
    "E-mail do responsável": "E-mail del responsabile",
    "Confirmar senha": "Conferma password",
    "Buscar endereço pelo CEP...": "Ricerca indirizzo tramite CAP...",
    "Cadastro realizado!": "Registrazione completata.",
    "Logo você receberá um e-mail de confirmação com a liberação do seu acesso!": "Riceverai presto un'e-mail di conferma con l'abilitazione del tuo accesso.",
    "Desde já...": "Fin da ora...",
    "Seja bem-vindo!!!": "Benvenuto!",
    "Carregando métricas...": "Caricamento metriche...",
    "Selecione uma clínica para visualizar o dashboard.": "Seleziona una clinica per visualizzare la dashboard.",
    "Visão geral de": "Panoramica di",
    "Indicadores exibidos para": "Indicatori mostrati per",
    "Visão consolidada da clínica": "Vista consolidata della clinica",
    "Visão individual por setor": "Vista individuale per settore",
    "Indicadores gerenciais": "Indicatori gestionali",
    "Indicadores com fonte técnica segura para o período e setor selecionados.": "Indicatori con fonte tecnica affidabile per il periodo e il settore selezionati.",
    "Primeiras consultas": "Prime visite",
    "Agenda classificada como primeiro atendimento": "Agenda classificata come prima visita",
    "Agendamentos sem plano de tratamento vinculado": "Appuntamenti senza piano di trattamento collegato",
    "Vendas confirmadas por data da venda": "Vendite confermate per data di vendita",
    "Despesas por competência / (agendamentos vinculados + lançamentos manuais)": "Spese per competenza / (appuntamenti collegati + registrazioni manuali dei ricavi)",
    "Resultado financeiro / (agendamentos vinculados + lançamentos manuais)": "Risultato finanziario / (appuntamenti collegati + registrazioni manuali dei ricavi)",
    "Resumo por setor": "Riepilogo per settore",
    "Visão consolidada por setor com dados operacionais e financeiros permitidos para o seu perfil.": "Vista consolidata per settore con dati operativi e finanziari consentiti per il tuo profilo.",
    "Visão individual do setor selecionado com dados permitidos para o seu perfil.": "Vista individuale del settore selezionato con dati consentiti per il tuo profilo.",
    "Agendamentos": "Appuntamenti",
    "Entradas financeiras": "Entrate finanziarie",
    "Despesas reconhecidas por competência": "Spese rilevate per competenza",
    "Despesas por competência do período": "Spese per competenza del periodo",
    "Resultado financeiro": "Risultato finanziario",
    "Entradas realizadas menos despesas por competência, sem DRE contábil.": "Entrate realizzate meno spese per competenza, senza trattamento da conto economico contabile.",
    "Entradas financeiras por lançamento realizado": "Entrate finanziarie per movimento realizzato",
    "Sem dados setoriais confiáveis para o período selecionado.": "Nessun dato settoriale affidabile per il periodo selezionato.",
    "Ano anterior": "Anno precedente",
    "Próximo ano": "Anno successivo",
    "Mês Atual": "Mese corrente",
    "no período": "nel periodo",
    "Últimos 30 dias": "Ultimi 30 giorni",
    "Leads convertidos": "Lead convertiti",
    "Atrasados": "In ritardo",
    "Para hoje": "Per oggi",
    "Próximos": "Prossimi",
    "Configuração de follow-up": "Impostazioni follow-up",
    "Defina em quantos dias a clínica costuma retomar um lead pendente.": "Definisci dopo quanti giorni la clinica di solito ricontatta un lead in sospeso.",
    "Intervalo padrão (dias)": "Intervallo predefinito (giorni)",
    "Salvar intervalo": "Salva intervallo",
    "Nome da campanha": "Nome della campagna",
    "Canal": "Canale",
    "Publico alvo": "Pubblico di riferimento",
    "Público alvo": "Pubblico di riferimento",
    "Instagram, Google...": "Instagram, Google...",
    "Ex: Dor lombar": "Es.: dolore lombare",
    "Campanha criada com sucesso!": "Campagna creata con successo.",
    "Criar campanha": "Crea campagna",
    "Criar Campanha": "Crea campagna",
    "Mes atual": "Mese corrente",
    "Campanhas, agendamentos e desempenho comercial.": "Campagne, appuntamenti e risultati commerciali.",
    "Erro ao carregar dados de marketing": "Errore nel caricamento dei dati marketing",
    "Atualizando dados de marketing...": "Aggiornamento dati marketing...",
    "Filtrar por origem": "Filtra per origine",
    "Filtrar por campanha": "Filtra per campagna",
    "Todas as campanhas": "Tutte le campagne",
    "Leads no período": "Lead nel periodo",
    "marcaram consulta": "hanno prenotato una visita",
    "convertidos": "convertiti",
    "Conversão comercial": "Conversione commerciale",
    "Investimento em campanhas": "Investimento campagne",
    "Somatório das campanhas filtradas": "Totale delle campagne filtrate",
    "Somatorio das campanhas filtradas": "Totale delle campagne filtrate",
    "Detalhes da conversão comercial": "Dettagli della conversione commerciale",
    "Detalhes do investimento em campanhas": "Dettagli dell'investimento campagne",
    "Período analisado": "Periodo analizzato",
    "Os dados abaixo respeitam os filtros atuais da página Marketing.": "I dati sotto rispettano i filtri attuali della pagina Marketing.",
    "Leads considerados neste cálculo": "Lead considerati in questo calcolo",
    "Marcaram consulta": "Hanno prenotato una visita",
    "Leads que avançaram para agendamento": "Lead passati alla prenotazione",
    "Leads convertidos em pacientes": "Lead convertiti in pazienti",
    "Taxa de conversão": "Tasso di conversione",
    "Percentual de leads convertidos": "Percentuale di lead convertiti",
    "Leads considerados na conversão": "Lead considerati nella conversione",
    "Investimento total": "Investimento totale",
    "Campanhas filtradas": "Campagne filtrate",
    "Campanhas consideradas no período": "Campagne considerate nel periodo",
    "Investimento médio": "Investimento medio",
    "Média por campanha filtrada": "Media per campagna filtrata",
    "Custo por lead": "Costo per lead",
    "Investimento dividido pelos leads do período": "Investimento diviso per i lead del periodo",
    "Detalhamento de campanhas": "Dettaglio campagne",
    "Período": "Periodo",
    "Nenhuma campanha encontrada com os filtros atuais.": "Nessuna campagna trovata con i filtri attuali.",
    "Nenhum lead convertido no período selecionado.": "Nessun lead convertito nel periodo selezionato.",
    "Meta de agendamento": "Obiettivo appuntamenti",
    "Progresso:": "Avanzamento:",
    "Custo por novo paciente no período": "Costo per nuovo paziente nel periodo",
    "Receita média por paciente no período": "Ricavi medi per paziente nel periodo",
    "novos pacientes": "nuovi pazienti",
    "Receita projetada": "Ricavi previsti",
    "Alertas operacionais": "Avvisi operativi",
    "Follow-ups vencidos / NPS pendentes": "Follow-up scaduti / NPS in sospeso",
    "Fila unificada de follow-up vencido e NPS pendente.": "Coda unica per follow-up scaduti e NPS in sospeso.",
    "Nenhum alerta operacional no momento.": "Nessun avviso operativo al momento.",
    "Follow-up vencido": "Follow-up scaduto",
    "NPS pendente": "NPS in sospeso",
    "Lead precisa de novo contato comercial.": "Il Lead richiede un nuovo contatto commerciale.",
    "Plano": "Piano",
    "aguardando envio ou resposta.": "in attesa di invio o risposta.",
    "Meta mensal de agendamento": "Obiettivo mensile appuntamenti",
    "Defina a meta da clínica e acompanhe o progresso do mês atual.": "Definisci l'obiettivo della clinica e monitora l'avanzamento del mese corrente.",
    "Quantidade de agendamentos": "Numero di appuntamenti",
    "Salvar meta": "Salva obiettivo",
    "agendamentos válidos no período atual.": "appuntamenti validi nel periodo corrente.",
    "Meta mensal de agendamento atualizada!": "Obiettivo mensile di appuntamenti aggiornato.",
    "Erro ao atualizar meta": "Errore nell'aggiornamento dell'obiettivo",
    "Relatório por origem": "Report per origine",
    "Relatório por campanha": "Report per campagna",
    "Relatório por setor": "Report per settore",
    "Marcou": "Prenotati",
    "Convertidos": "Convertiti",
    "Taxa": "Tasso",
    "Investimento": "Investimento",
    "Nenhum dado no período selecionado.": "Nessun dato nel periodo selezionato.",
    "Nenhuma campanha cadastrada.": "Nessuna campagna registrata.",
    "Sem canal": "Nessun canale",
    "Campanhas cadastradas": "Campagne",
    "Público": "Pubblico",
    "Orçamento": "Budget",
    "Nenhuma campanha cadastrada ainda.": "Nessuna campagna ancora registrata.",
    "Ativa": "Attiva",
    "Inativa": "Inattiva",
    "Editar campanha": "Modifica campagna",
    "Ativar campanha": "Attiva campagna",
    "Desativar campanha": "Disattiva campagna",
    "Remover campanha": "Rimuovi campagna",
    "Campanha atualizada com sucesso!": "Campagna aggiornata con successo.",
    "Status da campanha atualizado!": "Stato della campagna aggiornato.",
    "Campanha removida da gestão ativa.": "Campagna rimossa dalla gestione attiva.",
    "leads": "lead",
    "marcaram": "prenotati",
    "Nenhum dado setorial no período selecionado.": "Nessun dato settoriale nel periodo selezionato.",
    "Leads filtrados": "Lead filtrati",
    "Lead": "Lead",
    "clínica": "clinica",
    "Resultado": "Esito",
    "Nenhum lead encontrado com os filtros atuais.": "Nessun lead trovato con i filtri attuali.",
    "Follow-up": "Follow-up",
    "Editar Lead": "Modifica lead",
    "Selecione o setor": "Seleziona il settore",
    "Sem campanha": "Nessuna campagna",
    "Resultado comercial": "Esito commerciale",
    "Pendente": "In sospeso",
    "Marcou consulta": "Visita prenotata",
    "Não marcou": "Nessuna visita prenotata",
    "Próximo follow-up": "Prossimo follow-up",
    "Hora": "Ora",
    "Follow-up no horário": "Follow-up in orario",
    "Existem leads com follow-up para agora.": "Ci sono lead con follow-up previsto ora.",
    "Motivo de não agendamento": "Motivo della mancata prenotazione",
    "Sem setor": "Nessun settore",
    "Setor da venda": "Settore della vendita",
    "Setor do atendimento": "Settore della prestazione",
    "O setor informado diverge do setor histórico da origem": "Il settore indicato differisce dal settore storico dell'origine",
    "O setor histórico de um recebível originado não pode ser reclassificado": "Il settore storico di un credito collegato a un'origine non può essere riclassificato",
    "Nenhum lead encontrado": "Nessun lead trovato",
    "Todas as origens": "Tutte le origini",
    "Nenhum lead neste grupo.": "Nessun lead in questo gruppo.",
    "Abrir WhatsApp": "Apri WhatsApp",
    "Abrir no WhatsApp": "Apri in WhatsApp",
    "WhatsApp": "WhatsApp",
    "Telefone não informado": "Telefono non indicato",
    "Telefone inválido": "Telefono non valido",
    "Conversar pelo WhatsApp": "Chatta su WhatsApp",
    "Informe um telefone válido para abrir o WhatsApp.": "Inserisci un numero di telefono valido per aprire WhatsApp.",
    "Oi {name}, tudo bem com você? Aqui é da {clinicName}.": "Ciao {name}, tutto bene? Ti contattiamo da {clinicName}.",
    "Tudo bem?": "Tutto bene?",
    "Tudo bem com você?": "Tutto bene?",
    "Tudo bem? Estou entrando em contato sobre seu agendamento.": "Tutto bene? Ti contatto per il tuo appuntamento.",
    "Converter em paciente": "Converti in paziente",
    "Converter": "Converti",
    "Convertendo...": "Conversione...",
    "Vincular a este paciente": "Collega a questo paziente",
    "Criar novo paciente": "Crea nuovo paziente",
    "Lead vinculado ao paciente existente.": "Lead collegato al paziente esistente.",
    "O cadastro do paciente será criado a partir deste lead e a conversão ficará registrada no funil.": "La scheda paziente sarà creata da questo Lead e la conversione sarà registrata nel Funnel.",
    "Agendar consulta": "Prenota visita",
    "Agendar consulta do lead": "Prenota visita del lead",
    "Agendando...": "Prenotazione...",
    "O agendamento será criado usando o paciente já convertido a partir deste lead.": "L'appuntamento verrà creato usando il paziente già convertito da questo Lead.",
    "Para agendar uma consulta a partir do lead, primeiro converta esse lead em paciente.": "Per prenotare una visita da un Lead, convertilo prima in paziente.",
    "Limpar data": "Cancella data",
    "Informe uma data de nascimento válida.": "Inserisci una data di nascita valida.",
    "A data de nascimento não pode ser futura.": "La data di nascita non può essere futura.",
    "Informe uma data de nascimento a partir de 1900.": "Inserisci una data di nascita a partire dal 1900.",
    "Bairro": "Quartiere",
    "Prontuário do paciente": "Cartella clinica del paziente",
    "Módulo clínico": "Modulo clinico",
    "Ficha de Avaliação": "Scheda di valutazione",
    "Avaliações clínicas por paciente": "Valutazioni cliniche per paziente",
    "Abrir prontuário": "Apri cartella clinica",
    "Paciente selecionado": "Paziente selezionato",
    "Último registro": "Ultima registrazione",
    "Não foi possível carregar pacientes.": "Non e stato possibile caricare i pazienti.",
    "Não foi possível carregar a ficha de avaliação.": "Non e stato possibile caricare la scheda di valutazione.",
    "Nenhum paciente encontrado para a busca.": "Nessun paziente trovato per la ricerca.",
    "Selecione um paciente para abrir a ficha de avaliação.": "Seleziona un paziente per aprire la scheda di valutazione.",
    "Dados de identificação não informados": "Dati identificativi non indicati",
    "Evoluções": "Evoluzioni",
    "Evolução": "Evoluzione",
    "Alta": "Dimissione",
    "Avaliações": "Valutazioni",
    "Avaliação inicial": "Valutazione iniziale",
    "Avaliação padrão": "Valutazione predefinita",
    "Nova Avaliação": "Nuova valutazione",
    "Registrar avaliação clínica": "Registra valutazione clinica",
    "Tipo de avaliação": "Tipo di valutazione",
    "Título da avaliação": "Titolo della valutazione",
    "Objetivo clínico": "Obiettivo clinico",
    "Achados principais": "Riscontri principali",
    "Hipótese / diagnóstico funcional": "Ipotesi / diagnosi funzionale",
    "Plano terapêutico": "Piano terapeutico",
    "Salvar avaliação": "Salva valutazione",
    "Avaliação registrada com sucesso.": "Valutazione registrata con successo.",
    "Não foi possível salvar a avaliação clínica.": "Non è stato possibile salvare la valutazione clinica.",
    "Informações do paciente": "Informazioni del paziente",
    "Fluxo clínico": "Flusso clinico",
    "Paciente → Avaliação → Evoluções": "Paziente → Valutazione → Evoluzioni",
    "Cada avaliação organiza as evoluções clínicas vinculadas ao atendimento.": "Ogni valutazione organizza le evoluzioni cliniche collegate alla seduta.",
    "Pesquisa clínica": "Ricerca clinica",
    "Busque por sintomas, diagnóstico, profissional, data, conduta ou plano terapêutico.": "Cerca per sintomi, diagnosi, professionista, data, condotta o piano terapeutico.",
    "Buscar no prontuário clínico": "Cerca nella cartella clinica",
    "Selecione uma avaliação antes de criar uma evolução.": "Seleziona una valutazione prima di creare un'evoluzione.",
    "Buscar sintomas, diagnósticos, medicamentos ou palavras-chave": "Cerca sintomi, diagnosi, farmaci o parole chiave",
    "Avaliações registradas": "Valutazioni registrate",
    "Cada avaliação agrupa as evoluções clínicas vinculadas.": "Ogni valutazione raggruppa le evoluzioni cliniche collegate.",
    "Carregando avaliações...": "Caricamento valutazioni...",
    "Nenhuma avaliação encontrada para a busca.": "Nessuna valutazione trovata per la ricerca.",
    "Nenhuma avaliação clínica registrada.": "Nessuna valutazione clinica registrata.",
    "Evoluções vinculadas": "Evoluzioni collegate",
    "Criar evolução": "Crea evoluzione",
    "Editar evolução": "Modifica evoluzione",
    "Editar evolução clínica": "Modifica evoluzione clinica",
    "Evolução atualizada com sucesso.": "Evoluzione aggiornata con successo.",
    "Nenhuma evolução vinculada a esta avaliação.": "Nessuna evoluzione collegata a questa valutazione.",
    "Esta avaliação reúne evoluções antigas sem vínculo direto, preservando o histórico clínico integral.": "Questa valutazione raccoglie evoluzioni precedenti senza collegamento diretto, preservando l'intera storia clinica.",
    "Histórico preservado": "Storico preservato",
    "Evoluções antigas sem avaliação vinculada.": "Evoluzioni precedenti senza valutazione collegata.",
    "Preservar histórico clínico registrado antes da estrutura Avaliação → Evoluções.": "Preservare la storia clinica registrata prima della struttura Valutazione → Evoluzioni.",
    "registro(s)": "registro/i",
    "Documentos": "Documenti",
    "Plano de Tratamento": "Piano di trattamento",
    "Erro ao carregar histórico do paciente": "Errore durante il caricamento dello storico del paziente",
    "Carregando histórico do paciente...": "Caricamento storico del paziente...",
    "Resumo clínico": "Riepilogo clinico",
    "Situação clínica do paciente": "Situazione clinica del paziente",
    "Informações essenciais para orientar o atendimento atual.": "Informazioni essenziali per guidare la seduta attuale.",
    "Editar cadastro": "Modifica anagrafica",
    "Sessões restantes": "Sedute rimanenti",
    "Próxima consulta": "Prossima visita",
    "Última avaliação": "Ultima valutazione",
    "Pendência financeira": "Pendenza finanziaria",
    "Timeline Clínica": "Timeline clinica",
    "Histórico cronológico dos principais eventos do paciente.": "Cronologia dei principali eventi del paziente.",
    "Nenhum evento clínico encontrado.": "Nessun evento clinico trovato.",
    "Abrir registro": "Apri registro",
    "Cadastro realizado": "Registrazione completata",
    "Consulta agendada": "Visita prenotata",
    "Avaliação criada": "Valutazione creata",
    "Evolução registrada": "Evoluzione registrata",
    "Plano vendido": "Piano venduto",
    "Documento anexado": "Documento allegato",
    "Pagamento registrado": "Pagamento registrato",
    "Pesquisar por nome, categoria ou data": "Cerca per nome, categoria o data",
    "Todas as categorias": "Tutte le categorie",
    "Relacionamento": "Relazione",
    "realizadas": "eseguite",
    "Sessões realizadas": "Sedute eseguite",
    "Objetivos alcançados": "Obiettivi raggiunti",
    "Pendências": "Pendenze",
    "Finalizada": "Finalizzata",
    "Ocultar detalhes": "Nascondi dettagli",
    "URL da foto": "URL della foto",
    "Foto do paciente": "Foto del paziente",
    "Gerenciar foto do paciente": "Gestisci foto del paziente",
    "Inserir foto": "Aggiungi foto",
    "Alterar foto": "Cambia foto",
    "Selecionar imagem": "Seleziona immagine",
    "Remover foto": "Rimuovi foto",
    "Salvar foto": "Salva foto",
    "Processando imagem...": "Elaborazione immagine...",
    "Enviando foto...": "Caricamento foto...",
    "Removendo foto...": "Rimozione foto...",
    "Pré-visualização da foto do paciente": "Anteprima della foto del paziente",
    "Use uma imagem nítida e centralizada. A pré-visualização mostra o recorte final.": "Usa un'immagine nitida e centrata. L'anteprima mostra il ritaglio finale.",
    "Formatos aceitos: JPG, PNG e WEBP. Tamanho máximo:": "Formati supportati: JPG, PNG e WEBP. Dimensione massima:",
    "Selecione uma imagem JPG, PNG ou WEBP.": "Seleziona un'immagine JPG, PNG o WEBP.",
    "A imagem selecionada excede o tamanho máximo permitido.": "L'immagine selezionata supera la dimensione massima consentita.",
    "Não foi possível processar a imagem selecionada.": "Non è stato possibile elaborare l'immagine selezionata.",
    "Foto do paciente atualizada com sucesso.": "Foto del paziente aggiornata correttamente.",
    "Foto do paciente removida com sucesso.": "Foto del paziente rimossa correttamente.",
    "Alergias": "Allergie",
    "Medicamentos em uso": "Farmaci in uso",
    "Condições clínicas": "Condizioni cliniche",
    "Condições clínicas relevantes": "Condizioni cliniche rilevanti",
    "Alertas clínicos": "Avvisi clinici",
    "Alertas clínicos importantes": "Avvisi clinici importanti",
    "Plano atual": "Piano attuale",
    "Sem plano ativo": "Nessun piano attivo",
    "Convênio não informado": "Assicurazione non indicata",
    "Total de consultas": "Totale visite",
    "Última consulta": "Ultima visita",
    "Acompanhamento": "Monitoraggio",
    "anos": "anni",
    "Plano ativo": "Piano attivo",
    "Nova Evolução": "Nuova evoluzione",
    "Registrar evolução clínica": "Registra evoluzione clinica",
    "Template inteligente": "Modello intelligente",
    "Selecionar modelo": "Seleziona modello",
    "Profissional responsável": "Professionista responsabile",
    "Não vincular": "Non collegare",
    "Selecionar profissional": "Seleziona professionista",
    "Resumo rápido": "Riepilogo rapido",
    "Resumo para aparecer na timeline": "Riepilogo da mostrare nella timeline",
    "Escala de Dor": "Scala del dolore",
    "Testes Clínicos": "Test clinici",
    "Conduta": "Gestione terapeutica",
    "Registre o relato clínico da evolução do paciente.": "Registra il racconto clinico dell'evoluzione del paziente.",
    "Ex.: 0/10, 5/10 ou EVA 7": "Es.: 0/10, 5/10 o VAS 7",
    "Registre testes clínicos realizados e seus resultados.": "Registra i test clinici eseguiti e i relativi risultati.",
    "Registre a conduta adotada e orientações realizadas.": "Documenta la gestione terapeutica adottata e le indicazioni fornite.",
    "S - Subjetivo": "S - Soggettivo",
    "O - Objetivo": "O - Oggettivo",
    "A - Avaliação": "A - Valutazione",
    "P - Plano": "P - Piano",
    "Tags clínicas": "Tag cliniche",
    "Separe por vírgula": "Separa con virgole",
    "Anexos": "Allegati",
    "Imagem": "Immagine",
    "Exame": "Esame",
    "Documento": "Documento",
    "Outros": "Altro",
    "Remover": "Rimuovi",
    "Salvar evolução": "Salva evoluzione",
    "Evolução registrada com sucesso.": "Evoluzione registrata con successo.",
    "Não foi possível salvar a evolução clínica.": "Non è stato possibile salvare l'evoluzione clinica.",
    "Linha do tempo clínica": "Timeline clinica",
    "Carregando evoluções...": "Caricamento evoluzioni...",
    "Nenhuma evolução encontrada para a busca.": "Nessuna evoluzione trovata per la ricerca.",
    "Nenhuma evolução clínica registrada.": "Nessuna evoluzione clinica registrata.",
    "Última alteração": "Ultima modifica",
    "Anexar documento": "Allega documento",
    "Envie PDFs, exames, imagens ou fotos vinculadas ao prontuário do paciente.": "Carica PDF, esami, immagini o foto collegati alla cartella clinica del paziente.",
    "Anexar": "Allega",
    "Selecionar arquivo": "Seleziona file",
    "Selecionar arquivos": "Seleziona più file",
    "Nenhum arquivo selecionado": "Nessun file selezionato",
    "Nenhum arquivo escolhido": "Nessun file scelto",
    "Arquivo selecionado": "File selezionato",
    "{count} arquivos selecionados": "{count} file selezionati",
    "Remover arquivo": "Rimuovi file",
    "Trocar arquivo": "Sostituisci file",
    "Formatos aceitos: {formats}": "Formati supportati: {formats}",
    "Limite de tamanho: {size}": "Dimensione massima: {size}",
    "Enviando...": "Invio...",
    "Documento anexado com sucesso.": "Documento allegato con successo.",
    "Selecione um arquivo para anexar.": "Seleziona un file da allegare.",
    "Arquivo": "File",
    "Foto": "Foto",
    "O arquivo deve ter no máximo 15MB.": "Il file deve essere al massimo di 15 MB.",
    "O arquivo selecionado possui mais de {max} MB.\nSelecione um arquivo menor para continuar.": "Il file selezionato supera {max} MB.\nSeleziona un file più piccolo per continuare.",
    "O tamanho máximo permitido para upload é {max} MB.": "La dimensione massima consentita per il caricamento è di {max} MB.",
    "Limite de armazenamento atingido. Remova arquivos ou solicite ampliação da capacidade.": "Limite di archiviazione raggiunto. Rimuovi file o richiedi un aumento della capacità.",
    "Tipo de arquivo incompatível com a categoria selecionada.": "Tipo di file incompatibile con la categoria selezionata.",
    "Não foi possível anexar o documento.": "Non è stato possibile allegare il documento.",
    "Não foi possível abrir o documento.": "Non è stato possibile aprire il documento.",
    "Documentos do prontuário": "Documenti della cartella clinica",
    "Arquivos anexados diretamente ao prontuário do paciente.": "File allegati direttamente alla cartella clinica del paziente.",
    "Carregando documentos...": "Caricamento documenti...",
    "Nenhum documento anexado ao prontuário.": "Nessun documento allegato alla cartella clinica.",
    "Enviado em": "Inviato il",
    "Visualizar": "Visualizza",
    "Baixar": "Scarica",
    "Documentos vinculados": "Documenti collegati",
    "selecionado(s)": "selezionato/i",
    "{count} documento vinculado": "{count} documento collegato",
    "{count} documentos vinculados": "{count} documenti collegati",
    "Nenhum documento vinculado a este registro.": "Nessun documento collegato a questa registrazione.",
    "Documentos disponíveis para vincular": "Documenti disponibili da collegare",
    "Remover vínculo": "Rimuovi collegamento",
    "Vincular documento": "Collega documento",
    "Tipos aceitos: PDF, JPG, PNG e WEBP. Limite:": "Tipi accettati: PDF, JPG, PNG e WEBP. Limite:",
    "Nenhum documento do paciente disponível para vincular.": "Nessun documento del paziente disponibile da collegare.",
    "Documento removido com sucesso.": "Documento rimosso con successo.",
    "Vinculado a": "Collegato a",
    "registro clínico": "registro clinico",
    "registros clínicos": "registri clinici",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite: 15MB.": "Tipi accettati: PDF, JPG, PNG, WEBP e GIF. Limite: 15 MB.",
    "Tipos aceitos: PDF, JPG, PNG, WEBP e GIF. Limite:": "Tipi accettati: PDF, JPG, PNG, WEBP e GIF. Limite:",
    "Armazenamento": "Archiviazione",
    "{used} de {total} utilizados": "{used} di {total} utilizzati",
    "Arquivos": "File",
    "Utilizado": "Utilizzato",
    "Disponível": "Disponibile",
    "Limite atingido": "Limite raggiunto",
    "Próximo do limite": "Vicino al limite",
    "Atenção": "Attenzione",
    "Normal": "Normale",
    "Sua clínica está próxima de atingir o limite de armazenamento.": "La clinica è vicina al limite di archiviazione.",
    "Sua clínica está utilizando mais de 80% da capacidade contratada.": "La clinica sta utilizzando più dell'80% della capacità contrattuale.",
    "Evolução de": "Evoluzione del",
    "Anexos vinculados às evoluções clínicas do paciente.": "Allegati collegati alle evoluzioni cliniche del paziente.",
    "Receita do paciente": "Ricavi del paziente",
    "Carregando financeiro do paciente...": "Caricamento dati finanziari del paziente...",
    "Nenhum lançamento financeiro encontrado.": "Nessun movimento finanziario trovato.",
    "Último pagamento": "Ultimo pagamento",
    "Paciente desde": "Paziente dal",
    "Preferências e campanhas": "Preferenze e campagne",
    "Sem dados adicionais de marketing vinculados no cadastro atual.": "Nessun dato marketing aggiuntivo collegato alla scheda attuale.",
    "Dados comerciais e origem do relacionamento do paciente.": "Dati commerciali e origine della relazione con il paziente.",
    "Registrar queixa principal, objetivos do paciente e contexto clínico inicial.": "Registra il disturbo principale, gli obiettivi del paziente e il contesto clinico iniziale.",
    "Descrever achados relevantes da avaliação física, testes funcionais e observações clínicas.": "Descrivi i riscontri rilevanti della valutazione fisica, i test funzionali e le osservazioni cliniche.",
    "Definir hipótese clínica, diagnóstico funcional e prioridades terapêuticas.": "Definisci ipotesi clinica, diagnosi funzionale e priorità terapeutiche.",
    "Definir plano terapêutico, frequência, metas e critérios de acompanhamento.": "Definisci piano terapeutico, frequenza, obiettivi e criteri di monitoraggio.",
    "Comparar evolução clínica, adesão ao plano e novas demandas do paciente.": "Confronta evoluzione clinica, aderenza al piano e nuove esigenze del paziente.",
    "Registrar mudanças de mobilidade, força, dor, função e desempenho.": "Registra cambiamenti di mobilità, forza, dolore, funzione e performance.",
    "Revisar interpretação clínica frente aos objetivos terapêuticos.": "Rivedi l'interpretazione clinica rispetto agli obiettivi terapeutici.",
    "Atualizar condutas, metas, frequência e próximos passos.": "Aggiorna condotte, obiettivi, frequenza e prossimi passi.",
    "Registrar critérios clínicos de alta e percepção funcional do paciente.": "Registra criteri clinici di dimissione e percezione funzionale del paziente.",
    "Documentar achados finais, evolução funcional e pontos de atenção.": "Documenta riscontri finali, evoluzione funzionale e punti di attenzione.",
    "Concluir ciclo terapêutico conforme objetivos atingidos ou encaminhamentos necessários.": "Concludi il ciclo terapeutico secondo obiettivi raggiunti o invii necessari.",
    "Orientar manutenção, prevenção de recidiva e sinais para retorno.": "Orienta mantenimento, prevenzione delle recidive e segnali per il ritorno.",
    "Avaliação inicial e definição de conduta.": "Valutazione iniziale e definizione della condotta.",
    "Paciente relata queixa principal, início dos sintomas, fatores de melhora/piora e expectativas do tratamento.": "Il paziente riferisce disturbo principale, insorgenza dei sintomi, fattori di miglioramento/peggioramento e aspettative del trattamento.",
    "Realizada avaliação física, inspeção, testes funcionais e registro dos achados relevantes.": "Eseguite valutazione fisica, ispezione, test funzionali e registrazione dei reperti rilevanti.",
    "Hipótese clínica inicial compatível com o quadro apresentado. Definidas prioridades terapêuticas.": "Ipotesi clinica iniziale compatibile con il quadro presentato. Priorità terapeutiche definite.",
    "Orientações iniciais, proposta de plano terapêutico e agendamento das próximas sessões.": "Indicazioni iniziali, proposta di piano terapeutico e programmazione delle prossime sedute.",
    "Acompanhamento de retorno.": "Follow-up di ritorno.",
    "Paciente em retorno para acompanhamento da resposta clínica e evolução funcional.": "Paziente in ritorno per monitorare la risposta clinica e l'evoluzione funzionale.",
    "Registrar testes clínicos ou funcionais realizados no retorno.": "Registrare test clinici o funzionali eseguiti al ritorno.",
    "Manter ou ajustar condutas conforme resposta clínica observada.": "Mantenere o adattare le condotte secondo la risposta clinica osservata.",
    "Paciente relata evolução desde a última consulta, adesão às orientações e sintomas atuais.": "Il paziente riferisce evoluzione dall'ultima visita, adesione alle indicazioni e sintomi attuali.",
    "Reavaliados sinais clínicos, mobilidade, dor, função e resposta às condutas anteriores.": "Rivalutati segni clinici, mobilità, dolore, funzione e risposta agli interventi precedenti.",
    "Quadro em acompanhamento, com ajustes conforme resposta clínica observada.": "Quadro in monitoraggio, con aggiustamenti secondo la risposta clinica osservata.",
    "Manter ou ajustar condutas, reforçar orientações e programar próximo retorno.": "Mantenere o adattare le condotte, rinforzare le indicazioni e programmare il prossimo ritorno.",
    "Reavaliação clínica periódica.": "Rivalutazione clinica periodica.",
    "Paciente informa percepção de progresso, limitações persistentes e novas queixas, se houver.": "Il paziente riferisce percezione di progresso, limitazioni persistenti e nuovi disturbi, se presenti.",
    "Comparados indicadores funcionais, amplitude, força, dor e desempenho em relação à avaliação anterior.": "Confrontati indicatori funzionali, ampiezza, forza, dolore e prestazione rispetto alla valutazione precedente.",
    "Evolução analisada frente aos objetivos terapêuticos e necessidade de ajustes.": "Evoluzione analizzata rispetto agli obiettivi terapeutici e alla necessità di aggiustamenti.",
    "Atualizar metas, condutas e frequência conforme evolução clínica.": "Aggiornare obiettivi, condotte e frequenza secondo l'evoluzione clinica.",
    "Alta clínica": "Dimissione clinica",
    "Evolução de alta clínica.": "Evoluzione di dimissione clinica.",
    "Evolução final do ciclo terapêutico, com critérios de alta e orientações registradas.": "Evoluzione finale del ciclo terapeutico, con criteri di dimissione e indicazioni registrate.",
    "Registrar testes finais, medidas funcionais e critérios clínicos avaliados.": "Registrare test finali, misure funzionali e criteri clinici valutati.",
    "Encerramento do ciclo terapêutico.": "Chiusura del ciclo terapeutico.",
    "Paciente relata melhora funcional e compreensão das orientações para manutenção.": "Il paziente riferisce miglioramento funzionale e comprensione delle indicazioni di mantenimento.",
    "Critérios clínicos e funcionais de alta avaliados e registrados.": "Criteri clinici e funzionali di dimissione valutati e registrati.",
    "Paciente apto para alta clínica conforme evolução e objetivos atingidos.": "Paziente idoneo alla dimissione clinica secondo evoluzione e obiettivi raggiunti.",
    "Orientações domiciliares, sinais de alerta e recomendação de retorno se necessário.": "Indicazioni domiciliari, segnali di allerta e raccomandazione di ritorno se necessario.",
    "Ajuste de tratamento": "Aggiustamento del trattamento",
    "Ajuste de conduta terapêutica.": "Aggiustamento della condotta terapeutica.",
    "Evolução registrada para ajuste do plano terapêutico conforme resposta do paciente.": "Evoluzione registrata per adattare il piano terapeutico secondo la risposta del paziente.",
    "Registrar achados ou testes que justificam o ajuste terapêutico.": "Registrare reperti o test che giustificano l'aggiustamento terapeutico.",
    "Paciente relata resposta parcial, desconfortos ou necessidade de mudança no plano.": "Il paziente riferisce risposta parziale, disagi o necessità di modificare il piano.",
    "Observados achados que indicam ajuste de carga, técnica, frequência ou estratégia.": "Osservati reperti che indicano aggiustamento di carico, tecnica, frequenza o strategia.",
    "Conduta revisada de acordo com tolerância, resposta clínica e objetivos.": "Condotta rivista in base a tolleranza, risposta clinica e obiettivi.",
    "Aplicar ajustes definidos e monitorar resposta nas próximas sessões.": "Applicare gli aggiustamenti definiti e monitorare la risposta nelle prossime sedute.",
    "Ansiedade": "Ansia",
    "Hipertensão": "Ipertensione",
    "Diabetes": "Diabete",
    "Obesidade": "Obesità",
    "Selecione": "Seleziona",
    "Valor previsto": "Valore previsto",
    "Apagar lead": "Elimina lead",
    "Apagar": "Elimina",
    "Esse lead será removido permanentemente do cadastro.": "Questo Lead sarà rimosso definitivamente dall'anagrafica.",
    "Novo": "Nuovo",
    "Em Contato": "In contatto",
    "Em contato": "In contatto",
    "Convertido": "Convertito",
    "Não agendado": "Non prenotato",
    "Preço": "Prezzo",
    "Sem retorno": "Nessuna risposta",
    "Sem interesse": "Nessun interesse",
    "Conflito de horário": "Conflitto di orario",
    "Já está em tratamento": "Già in trattamento",
    "Distância": "Distanza",
    "Conveniência": "Convenienza",
    "Outro": "Altro",
    "Indicação": "Passaparola",
    "Origens do Lead": "Origini del lead",
    "Use este cadastro para padronizar as origens utilizadas no funil.": "Usa questa anagrafica per standardizzare le origini usate nel funnel.",
    "Nova origem": "Nuova origine",
    "Adicionar origem": "Aggiungi origine",
    "Origem adicionada com sucesso!": "Origine aggiunta con successo.",
    "Origem removida com sucesso!": "Origine rimossa con successo.",
    "Erro ao adicionar origem": "Errore nell'aggiunta dell'origine",
    "Erro ao remover origem": "Errore nella rimozione dell'origine",
    "Data de criação": "Data di creazione",
    "Todos os períodos": "Tutti i periodi",
    "Site": "Sito",
    "Presencial": "Di persona",
    "Fisioterapia": "Fisioterapia",
    "Acupuntura": "Agopuntura",
    "Pilates": "Pilates",
    "Pacientes Ativos": "Pazienti attivi",
    "Consultas": "Visite",
    "Status dos Agendamentos": "Stato degli appuntamenti",
    "Agendado": "Prenotato",
    "Em atendimento": "Prestazione in corso",
    "Atendimento realizado": "Prestazione completata",
    "Faltou": "Assente",
    "Em Andamento": "In corso",
    "Concluído": "Completato",
    "Não Compareceu": "Non si è presentato",
    "Não foi possível marcar o agendamento como atendimento realizado.": "Non è stato possibile segnare l'appuntamento come trattamento completato.",
    "CAC": "CAC",
    "Custo de Aquisição": "Costo di acquisizione",
    "LTV": "LTV",
    "Valor por Paciente": "Valore per paziente",
    "ROI": "ROI",
    "Retorno sobre Investimento": "Ritorno sull'investimento",
    "Pesquisa de experiência": "Sondaggio sull'esperienza",
    "Carregando pesquisa...": "Caricamento sondaggio...",
    "Esta pesquisa não está disponível.": "Questo sondaggio non è disponibile.",
    "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.": "Dai un punteggio da 0 a 10 e, se vuoi, lascia un commento al team.",
    "Sua nota": "Il tuo punteggio",
    "Conte para a clínica o que funcionou bem ou o que pode melhorar.": "Racconta alla clinica cosa ha funzionato bene o cosa può migliorare.",
    "Enviar resposta": "Invia risposta",
    "Desenvolvido por": "Sviluppato da",
    "Operação inteligente para clínicas em crescimento": "Gestione intelligente per cliniche in crescita",
    "organiza a jornada da clínica do primeiro acesso até a operação diária.": "organizza il percorso della clinica dal primo accesso alla gestione quotidiana.",
    "Uma camada de gestão criada para abrir clínicas, estruturar equipes e acompanhar marketing, agenda, atendimento e resultados com mais clareza.": "Un livello gestionale pensato per avviare cliniche, strutturare team e monitorare marketing, calendario, assistenza e risultati con maggiore chiarezza.",
    "Começar onboarding": "Inizia onboarding",
    "Acessar plataforma": "Accedi alla piattaforma",
    "Portal Principal": "Portale principale",
    "Onboarding guiado": "Onboarding guidato",
    "Cadastre a clínica e o administrador principal em um fluxo único e direto, com acesso imediato para começar a operação no": "Registra la clinica e l'amministratore principale in un flusso unico e diretto, con accesso immediato per iniziare a operare in",
    "Criar nova clínica": "Crea nuova clinica",
    "Onboarding rápido para novas clínicas": "Onboarding rapido per nuove cliniche",
    "Operação financeira, agenda e atendimento em um só lugar": "Finanze, calendario e assistenza in un'unica piattaforma",
    "Estrutura preparada para multi-clínica e administração central": "Struttura pronta per multi-clinica e amministrazione centralizzata",
    "Entrada pública clara para login, cadastro e expansão do produto.": "Punto di accesso pubblico chiaro per login, registrazione ed espansione del prodotto.",
    "Clínicas organizadas com isolamento de dados e administração por unidade.": "Cliniche organizzate con isolamento dei dati e amministrazione per unità.",
    "Visão integrada de operação, comercial, agenda, atendimento e financeiro.": "Vista integrata di gestione, commerciale, calendario, assistenza e finanze.",
    "Plataforma para operação de clínicas, equipes e crescimento com mais clareza.": "Piattaforma per la gestione di cliniche, team e crescita con maggiore chiarezza.",
    "Estruture sua clínica e inicie a operação com governança desde o primeiro acesso.": "Struttura la tua clinica e avvia l'operatività con governance fin dal primo accesso.",
    "Configure a clínica e o administrador principal no mesmo fluxo.": "Configura la clinica e l'amministratore principale nello stesso flusso.",
    "Selecione uma nota de 0 a 10.": "Seleziona un punteggio da 0 a 10.",
    "Resposta enviada com sucesso.": "Risposta inviata con successo.",
    "Acesso em análise ou bloqueado!": "Accesso in fase di revisione o bloccato.",
    "Acesso bloqueado": "Accesso bloccato",
    "Seu perfil atual não possui permissão para acessar esta página.": "Il tuo profilo attuale non dispone dei permessi per accedere a questa pagina.",
    "Você não tem permissão para executar esta ação.": "Non hai il permesso per eseguire questa azione.",
    "Você não tem permissão para editar a agenda.": "Non hai il permesso per modificare l'agenda.",
    "Você não tem permissão para registrar lançamentos financeiros.": "Non hai il permesso per registrare movimenti finanziari.",
    "Permissões do profissional": "Permessi del professionista",
    "Defina quais módulos este profissional poderá acessar.": "Definisci a quali moduli può accedere questo professionista.",
    "Prontuário": "Cartella clinica",
    "Este portal é exclusivo para super admin.": "Questo portale è riservato ai super admin.",
    "CEP": "CAP",
    "Resumo": "Riepilogo",
    "Agendamentos sem plano de tratamento": "Appuntamenti senza piano terapeutico",
    "Atendimentos Avulsos": "Visite singole",
    "Ver histórico completo": "Vedi cronologia completa",
    "Falha ao consultar CEP": "Errore nella consultazione del CAP",
    "CEP não encontrado.": "CAP non trovato.",
    "Não foi possível consultar o CEP.": "Non è stato possibile consultare il CAP.",
    "Algo deu errado. Tente novamente.": "Qualcosa è andato storto. Riprova.",
    "Revise os campos informados e tente novamente.": "Controlla i campi inseriti e riprova.",
    "Preencha o campo obrigatório: {fieldLabel}.": "Compila il campo obbligatorio: {fieldLabel}.",
    "Preencha todos os campos obrigatórios.": "Compila tutti i campi obbligatori.",
    "Revise o valor informado para o campo: {fieldLabel}.": "Controlla il valore inserito nel campo: {fieldLabel}.",
    "Revise os campos clínicos informados.": "Controlla i campi clinici inseriti.",
    "O campo informado precisa ser maior ou igual a 15.": "Il campo deve essere maggiore o uguale a 15.",
    "Aparência": "Aspetto",
    "Alterne entre tema claro e escuro.": "Passa dal tema chiaro a quello scuro.",
    "Escolha o idioma usado nos menus e páginas.": "Scegli la lingua usata nei menu e nelle pagine.",
    "Moeda usada nos valores financeiros.": "Valuta utilizzata per i valori finanziari.",
    "Termine a sessão neste dispositivo.": "Termina la sessione su questo dispositivo.",
    "Control Tower": "Torre di controllo",
    "Workspace": "Area di lavoro",
    "Não foi possível carregar os modelos clínicos.": "Non è stato possibile caricare i modelli clinici.",
    "Modelo clínico salvo como rascunho.": "Modello clinico salvato come bozza.",
    "Modelo clínico criado como rascunho.": "Modello clinico creato come bozza.",
    "Não foi possível salvar o modelo clínico.": "Non è stato possibile salvare il modello clinico.",
    "Não foi possível concluir a ação.": "Non è stato possibile completare l'azione.",
    "Modelo duplicado como rascunho.": "Modello duplicato come bozza.",
    "Modelo publicado com sucesso.": "Modello pubblicato correttamente.",
    "Modelo inativado com sucesso.": "Modello disattivato correttamente.",
    "Modelo reativado com sucesso.": "Modello riattivato correttamente.",
    "Gerencie modelos versionados para avaliações, evoluções e outros registros clínicos, preservando o histórico.": "Gestisci modelli versionati per valutazioni, evoluzioni e altri documenti clinici, preservandone lo storico.",
    "Novo modelo clínico": "Nuovo modello clinico",
    "Modelo clínico": "Modello clinico",
    "Versões": "Versioni",
    "Associações": "Associazioni",
    "Última atualização": "Ultimo aggiornamento",
    "Modelo padrão": "Modello predefinito",
    "Publicado": "Pubblicato",
    "Todos os serviços e procedimentos": "Tutti i servizi e tutte le procedure",
    "Pré-visualizar": "Anteprima",
    "Duplicar": "Duplica",
    "Editar rascunho": "Modifica bozza",
    "Publicar": "Pubblica",
    "Nova versão": "Nuova versione",
    "Nenhum modelo clínico encontrado.": "Nessun modello clinico trovato.",
    "Editar rascunho do modelo": "Modifica la bozza del modello",
    "Criar modelo clínico": "Crea modello clinico",
    "Setores vinculados": "Reparti collegati",
    "Serviços e procedimentos vinculados": "Servizi e procedure collegati",
    "Estrutura do modelo": "Struttura del modello",
    "Organize as seções e os campos do modelo. Alterações estruturais ficam disponíveis somente após a publicação.": "Organizza le sezioni e i campi del modello. Le modifiche strutturali diventano disponibili solo dopo la pubblicazione.",
    "Adicionar seção": "Aggiungi sezione",
    "Identificador técnico": "Identificatore tecnico",
    "Título da seção": "Titolo della sezione",
    "Descrição da seção": "Descrizione della sezione",
    "Identificador do campo": "Identificatore del campo",
    "Rótulo": "Etichetta",
    "Tipo de campo": "Tipo di campo",
    "Texto curto": "Testo breve",
    "Texto longo": "Testo esteso",
    "Escala numérica": "Scala numerica",
    "Seleção única": "Selezione singola",
    "Seleção múltipla": "Selezione multipla",
    "Caixa de seleção": "Casella di selezione",
    "Data civil": "Data",
    "Texto informativo": "Testo informativo",
    "Sim/Não": "Sì/No",
    "Medida com unidade": "Misura con unità",
    "Obrigatório": "Obbligatorio",
    "Opções": "Opzioni",
    "Nova seção": "Nuova sezione",
    "Novo campo": "Nuovo campo",
    "Testes clínicos": "Test clinici",
    "Mover seção para cima": "Sposta la sezione verso l'alto",
    "Mover seção para baixo": "Sposta la sezione verso il basso",
    "Excluir seção": "Elimina sezione",
    "Mover campo para cima": "Sposta il campo verso l'alto",
    "Mover campo para baixo": "Sposta il campo verso il basso",
    "Excluir campo": "Elimina campo",
    "opcao_1:Opção 1, opcao_2:Opção 2": "opcao_1:Opzione 1, opcao_2:Opzione 2",
    "{count} vínculo selecionado": "{count} collegamento selezionato",
    "{count} vínculos selecionados": "{count} collegamenti selezionati",
    "Adicionar campo": "Aggiungi campo",
    "Após publicar, a versão fica imutável. Para alterar a estrutura depois disso, crie uma nova versão.": "Dopo la pubblicazione, la versione diventa immutabile. Per modificare la struttura, crea una nuova versione.",
    "Salvar rascunho": "Salva bozza",
    "Pré-visualizar modelo": "Anteprima modello",
    "Nenhuma versão disponível para pré-visualização.": "Nessuna versione disponibile per l'anteprima.",
    "Selecionar": "Seleziona",
    "Marcado": "Selezionato",
    "Selecionar modelo clínico": "Seleziona modello clinico",
    "Sem modelo versionado": "Nessun modello versionato",
    "Selecionar agendamento": "Seleziona appuntamento",
    "Personalizado": "Personalizzato",
    "Leads por origem": "Lead per origine",
    "Nenhum lead encontrado para o período selecionado.": "Nessun lead trovato nel periodo selezionato.",
    "Sem base suficiente": "Base dati insufficiente",
    "Atribuição insuficiente": "Attribuzione insufficiente",
    "Custo realizado ausente": "Costo effettivo mancante",
    "Coorte longitudinal insuficiente": "Coorte longitudinale insufficiente",
    "Dados insuficientes": "Dati insufficienti",
    "Dashboard Executivo": "Dashboard esecutiva",
    "Visão consolidada da clínica para decisões rápidas, sem duplicar os módulos detalhados.": "Vista consolidata della clinica per decisioni rapide, senza duplicare i moduli dettagliati.",
    "A data final não pode ser anterior à data inicial.": "La data finale non può precedere la data iniziale.",
    "dia": "giorno",
    "Atualizando...": "Aggiornamento...",
    "Não foi possível carregar os indicadores executivos agora. Tente novamente em instantes.": "Non è stato possibile caricare gli indicatori esecutivi ora. Riprova tra poco.",
    "Resumo executivo do período selecionado com os blocos permitidos para o seu perfil.": "Riepilogo esecutivo del periodo selezionato con i blocchi consentiti al tuo profilo.",
    "Leads criados": "Lead creati",
    "Registros que compõem este indicador no período e setor selecionados.": "Record inclusi in questo indicatore per il periodo e il settore selezionati.",
    "Leads agendados": "Lead prenotati",
    "Agendado no período": "Prenotato nel periodo",
    "Não agendado no período": "Non prenotato nel periodo",
    "Convertido no período": "Convertito nel periodo",
    "Não convertido no período": "Non convertito nel periodo",
    "Atendimentos concluídos": "Visite completate",
    "Atendimentos no período": "Visite nel periodo",
    "Não concluído": "Non completato",
    "Faltas": "Assenze",
    "Falta": "Assenza",
    "Sem falta": "Nessuna assenza",
    "Leads criados no período": "Lead creati nel periodo",
    "Agendados": "Prenotati",
    "Não agendados": "Non prenotati",
    "Leads por status": "Lead per stato",
    "Atendimentos realizados no período": "Visite completate nel periodo",
    "Agendamentos vinculados e lançamentos manuais": "Appuntamenti collegati e registrazioni manuali dei ricavi",
    "Atendimentos realizados": "Visite completate",
    "consultas no período": "visite nel periodo",
    "Pacientes ativos": "Pazienti attivi",
    "Cadastro canônico de pacientes": "Registro canonico dei pazienti",
    "Receita realizada": "Ricavi realizzati",
    "lançamentos": "registrazioni",
    "Seu perfil pode acessar o Dashboard, mas ainda não possui permissões para blocos executivos detalhados.": "Il tuo profilo può accedere alla Dashboard, ma non ha ancora i permessi per i blocchi esecutivi dettagliati.",
    "Captação e comercial": "Acquisizione e commerciale",
    "Funil de entrada resumido; a análise detalhada continua nos módulos de Leads e Marketing.": "Funnel di ingresso riepilogato; l'analisi dettagliata resta nei moduli Lead e Marketing.",
    "Abrir Marketing": "Apri Marketing",
    "Abrir Leads": "Apri Lead",
    "Taxa de agendamento": "Tasso di prenotazione",
    "agendados": "prenotati",
    "Conversão em paciente": "Conversione in paziente",
    "Principal origem": "Origine principale",
    "Sem origem identificada": "Nessuna origine identificata",
    "Sem permissão para visualizar indicadores comerciais.": "Nessun permesso per visualizzare gli indicatori commerciali.",
    "Fonte oficial: métricas de CAC, LTV e ROI.": "Fonte ufficiale: metriche CAC, LTV e ROI.",
    "Custo de aquisição atribuído.": "Costo di acquisizione attribuito.",
    "ROI de marketing": "ROI marketing",
    "Retorno atribuído ao investimento.": "Ritorno attribuito all'investimento.",
    "Operação clínica": "Operatività clinica",
    "Agenda, comparecimento e continuidade do cuidado em visão resumida.": "Agenda, presenze e continuità assistenziale in vista riepilogativa.",
    "Abrir Atendimento": "Apri Assistenza",
    "Abrir Agenda": "Apri Agenda",
    "Consultas no período": "Visite nel periodo",
    "Taxa de conclusão": "Tasso di completamento",
    "Taxa de faltas": "Tasso di assenze",
    "Somente atendimentos concluídos no período e setor selecionados.": "Solo le visite completate nel periodo e nel settore selezionati.",
    "Somente faltas no período e setor selecionados.": "Solo le assenze nel periodo e nel settore selezionati.",
    "Vendas confirmadas de planos no período e setor selecionados.": "Vendite confermate di piani nel periodo e nel settore selezionati.",
    "Pacientes distintos": "Pazienti distinti",
    "Valor total vendido": "Valore totale venduto",
    "Forma de pagamento recebida": "Metodo di pagamento ricevuto",
    "Pacientes com atendimento concluído": "Pazienti con visite completate",
    "Pacientes com falta": "Pazienti con assenze",
    "Faltas no período": "Assenze nel periodo",
    "Detalhamento por profissional": "Dettaglio per professionista",
    "Tipo de atendimento não identificado": "Tipo di visita non identificato",
    "Profissional não identificado": "Professionista non identificato",
    "Retenção de pacientes": "Fidelizzazione dei pazienti",
    "pacientes com continuidade": "pazienti con continuità",
    "Sem permissão para visualizar indicadores da agenda.": "Nessun permesso per visualizzare gli indicatori dell'agenda.",
    "Entradas realizadas, despesas por competência e resultado do período com origem no serviço financeiro oficial.": "Entrate realizzate, spese per competenza e risultato del periodo provenienti dal servizio finanziario ufficiale.",
    "Abrir Financeiro": "Apri Finanze",
    "Despesas realizadas": "Spese realizzate",
    "Resultado do período": "Risultato del periodo",
    "Por lançamento financeiro": "Per registrazione finanziaria",
    "Sem permissão para visualizar indicadores financeiros.": "Nessun permesso per visualizzare gli indicatori finanziari.",
    "Entradas financeiras no período": "Entrate finanziarie nel periodo",
    "Entradas por setor": "Entrate per settore",
    "Despesas reconhecidas por competência no período": "Spese rilevate per competenza nel periodo",
    "Valor líquido de planos vendidos": "Valore netto dei piani venduti",
    "Sem dados setoriais no período": "Nessun dato settoriale nel periodo",
    "Não foi possível carregar os repasses profissionais.": "Impossibile caricare i compensi professionali.",
    "Não foi possível carregar os filtros de repasse.": "Impossibile caricare i filtri dei compensi.",
    "Repasses pendentes gerados com sucesso.": "Compensi pendenti generati correttamente.",
    "Não foi possível gerar repasses pendentes.": "Impossibile generare i compensi pendenti.",
    "Repasse aprovado com sucesso.": "Compenso approvato correttamente.",
    "Não foi possível aprovar o repasse.": "Impossibile approvare il compenso.",
    "Pagamento de repasse registrado com sucesso.": "Pagamento del compenso registrato correttamente.",
    "Não foi possível registrar o pagamento do repasse.": "Impossibile registrare il pagamento del compenso.",
    "Repasse cancelado com sucesso.": "Compenso annullato correttamente.",
    "Não foi possível cancelar o repasse.": "Impossibile annullare il compenso.",
    "Repasses profissionais": "Compensi professionali",
    "Repasses calculados a partir de produção clínica válida, separados de venda, recebível e caixa do paciente.": "Compensi calcolati da produzione clinica valida, separati da vendita, credito e cassa del paziente.",
    "Aprovados": "Approvati",
    "Valor a pagar": "Importo da pagare",
    "Valor pago": "Importo pagato",
    "Bloqueados": "Bloccati",
    "Cancelados": "Annullati",
    "Modalidade": "Modalita",
    "Valor fixo": "Importo fisso",
    "Percentual da produção": "Percentuale della produzione",
    "Com saldo": "Con saldo",
    "Sem saldo": "Senza saldo",
    "Produção base": "Produzione base",
    "Regra": "Regola",
    "Valor do repasse": "Importo del compenso",
    "Nenhum repasse profissional encontrado": "Nessun compenso professionale trovato",
    "Aprovar": "Approva",
    "Pagar": "Paga",
    "Detalhes do repasse": "Dettagli del compenso",
    "Produção de origem": "Produzione di origine",
    "Versão": "Versione",
    "Saldo a pagar": "Saldo da pagare",
    "Bloqueio": "Blocco",
    "Valor da produção não disponível para cálculo": "Valore della produzione non disponibile per il calcolo",
    "Nenhuma regra de repasse aplicável": "Nessuna regola applicabile per il compenso professionale",
    "Conflito entre regras de repasse": "Conflitto tra regole per i compensi professionali",
    "Produção clínica cancelada": "Produzione clinica annullata",
    "Repasse fixo não autorizado para produção de cortesia": "Compenso fisso non autorizzato per una produzione gratuita",
    "Moeda da regra de repasse incompatível com a produção": "La valuta della regola di compenso non corrisponde a quella della produzione",
    "Regra de repasse inválida": "Regola per il compenso professionale non valida",
    "Motivo do bloqueio não disponível": "Motivo del blocco non disponibile",
    "Produção clínica de {date}": "Produzione clinica del {date}",
    "Produção clínica vinculada": "Produzione clinica associata",
    "Status do repasse não disponível": "Stato del compenso non disponibile",
    "Regra de repasse não disponível": "Regola di compenso non disponibile",
    "Registrar pagamento": "Registra pagamento",
    "Cancelar repasse": "Annulla compenso",
    "O repasse será cancelado sem alterar venda, recebível ou caixa do paciente.": "Il compenso sarà annullato senza modificare vendita, credito o cassa del paziente.",
    "Não foi possível carregar regras de repasse.": "Impossibile caricare le regole di compenso.",
    "Regra de repasse criada com sucesso.": "Regola di compenso creata correttamente.",
    "Não foi possível criar a regra de repasse.": "Impossibile creare la regola di compenso.",
    "Regra de repasse inativada com sucesso.": "Regola di compenso disattivata correttamente.",
    "Não foi possível inativar a regra de repasse.": "Impossibile disattivare la regola di compenso.",
    "Nova regra de repasse": "Nuova regola di compenso",
    "Quando serviço e setor são informados, esta regra só se aplica se ambos corresponderem.": "Quando sono indicati sia un servizio sia un settore, questa regola si applica solo se entrambi corrispondono.",
    "Prioridade das regras: serviço + setor, serviço, setor e regra geral.": "Priorità delle regole: servizio + settore, servizio, settore e regola generale.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional, serviço e setor.": "Esiste già una regola di compenso attiva o con un periodo di validità sovrapposto per lo stesso professionista, servizio e settore.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e serviço.": "Esiste già una regola di compenso attiva o con un periodo di validità sovrapposto per lo stesso professionista e servizio.",
    "Já existe uma regra de repasse vigente ou com vigência sobreposta para este mesmo profissional e setor.": "Esiste già una regola di compenso attiva o con un periodo di validità sovrapposto per lo stesso professionista e settore.",
    "Já existe uma regra geral de repasse vigente ou com vigência sobreposta para este profissional.": "Esiste già una regola generale di compenso attiva o con un periodo di validità sovrapposto per questo professionista.",
    "Setor opcional": "Settore opzionale",
    "Serviço opcional": "Servizio opzionale",
    "Percentual": "Percentuale",
    "Vigência inicial": "Inizio validita",
    "Vigência final": "Fine validita",
    "Permitir cortesia": "Consenti cortesia",
    "Criar regra": "Crea regola",
    "Vigência": "Validita",
    "Nenhuma regra de repasse encontrada": "Nessuna regola di compenso trovata",
    "Bloco sem permissão": "Blocco senza permesso",
    "Oculto": "Nascosto",
    "Fonte": "Fonte",
    "registros": "record",
    "Abrir origem": "Apri origine",
    "Nenhum dado gerencial encontrado": "Nessun dato gestionale trovato",
    "Repasse calculado": "Compenso calcolato",
    "Repasse pago": "Compenso pagato",
    "Repasse a pagar": "Compenso da pagare",
    "Não foi possível carregar a visão gerencial.": "Impossibile caricare la vista gestionale.",
    "Não foi possível exportar a visão gerencial.": "Impossibile esportare la vista gestionale.",
    "Visão gerencial": "Vista gestionale",
    "Vendido, produzido, recebido e repasses em fontes separadas, com comparações gerenciais temporais.": "Venduto, prodotto, incassato e compensi in fonti separate, con confronti gestionali temporali.",
    "Exportar": "Esporta",
    "Exportar CSV": "Esporta CSV",
    "Excel": "Excel",
    "PDF": "PDF",
    "Gerando exportação": "Generazione esportazione",
    "Gerando relatório": "Generazione report",
    "Gerar relatório": "Genera report",
    "Exportação gerencial concluída.": "Esportazione gestionale completata.",
    "Relatório gerencial gerado com sucesso.": "Report gestionale generato con successo.",
    "Não foi possível gerar a exportação.": "Impossibile generare l'esportazione.",
    "Não foi possível gerar o relatório.": "Impossibile generare il report.",
    "Histórico de lotes": "Storico lotti",
    "Detalhamento dos snapshots": "Dettaglio snapshot",
    "Confiabilidade": "Affidabilita",
    "Somente confirmados": "Solo confermati",
    "Incluir inferidos": "Includi inferiti",
    "Incluir legados": "Includi legacy",
    "Todos os dados": "Tutti i dati",
    "Visão parcial": "Vista parziale",
    "Alguns blocos foram ocultados por permissão e não foram retornados no payload.": "Alcuni blocchi sono stati nascosti per permesso e non sono stati restituiti nel payload.",
    "Sem base": "Senza base",
    "Série temporal": "Serie temporale",
    "Pendências de qualidade dos dados": "Pendenze di qualita dei dati",
    "Planos e pacotes": "Piani e pacchetti",
    "Planos vendidos": "Piani venduti",
    "Valor vendido em planos": "Valore venduto in piani",
    "Sessões produzidas": "Sessioni prodotte",
    "Limitações": "Limitazioni",
    "Escala de dor": "Scala del dolore",
    "Rateio gerencial habilitado.": "Ripartizione gestionale abilitata.",
    "Rateio gerencial desabilitado.": "Ripartizione gestionale disabilitata.",
    "Simulação de rateio concluída.": "Simulazione di ripartizione completata.",
    "Rateio gerencial aplicado.": "Ripartizione gestionale applicata.",
    "Aplicação de rateio revertida.": "Applicazione di ripartizione annullata.",
    "Elegibilidade da categoria atualizada.": "Idoneita della categoria aggiornata.",
    "Regra de rateio criada em rascunho.": "Regola di ripartizione creata come bozza.",
    "Regra de rateio ativada.": "Regola di ripartizione attivata.",
    "Regra de rateio inativada.": "Regola di ripartizione disattivata.",
    "Nova versão criada em rascunho.": "Nuova versione creata come bozza.",
    "Rateio gerencial por setor": "Ripartizione gestionale per settore",
    "Configura regras percentuais por setor sem aplicar valores nesta etapa.": "Configura regole percentuali per settore senza applicare valori in questa fase.",
    "Habilitar rateio": "Abilita ripartizione",
    "Simular rateio": "Simula ripartizione",
    "Inicio": "Inizio",
    "Simulando": "Simulazione",
    "Simular": "Simula",
    "Aviso: simulação de rateio não aplicada": "Avviso: simulazione di ripartizione non applicata",
    "Aplicação controlada do rateio": "Applicazione controllata della ripartizione",
    "Fingerprint da simulação": "Fingerprint della simulazione",
    "Fingerprint da aplicação": "Fingerprint dell'applicazione",
    "Aplicar rateio gerencial": "Applica ripartizione gestionale",
    "Cancelar aplicação": "Annulla applicazione",
    "Observação da aplicação": "Nota dell'applicazione",
    "Chave de idempotência": "Chiave di idempotenza",
    "Aplicando": "Applicazione",
    "Confirmar aplicação": "Conferma applicazione",
    "Aplicações de rateio": "Applicazioni di ripartizione",
    "Aplicada em": "Applicata il",
    "Período da aplicação": "Periodo dell'applicazione",
    "Total aplicado": "Totale applicato",
    "Nenhuma aplicação de rateio encontrada": "Nessuna applicazione di ripartizione trovata",
    "Aplicada": "Applicata",
    "Aplicação revertida": "Applicazione annullata",
    "Detalhar aplicação": "Dettaglia applicazione",
    "Preparar reversão": "Prepara annullamento",
    "Itens aplicados": "Elementi applicati",
    "Setores aplicados": "Settori applicati",
    "Motivo da reversão": "Motivo dell'annullamento",
    "Revertendo": "Annullamento",
    "Reverter aplicação": "Annulla applicazione",
    "Despesas encontradas": "Spese trovate",
    "Elegíveis": "Idonee",
    "Simuladas": "Simulate",
    "Total original": "Totale originale",
    "Total distribuído": "Totale distribuito",
    "Diferença de arredondamento": "Differenza di arrotondamento",
    "Participação": "Partecipazione",
    "Valor simulado": "Valore simulato",
    "Nenhum valor simulado por setor": "Nessun valore simulato per settore",
    "Qtd.": "Qta.",
    "Nenhuma despesa fora da simulação": "Nessuna spesa fuori dalla simulazione",
    "Despesa": "Spesa",
    "Competência da despesa": "Data di competenza della spesa",
    "Original": "Originale",
    "Distribuído": "Distribuito",
    "Despesa simulada": "Spesa simulata",
    "Rateio desabilitado": "Ripartizione disabilitata",
    "Despesa não elegível": "Spesa non idonea",
    "Categoria não elegível": "Categoria non idonea",
    "Sem regra aplicável": "Nessuna regola applicabile",
    "Sem competência": "Dati di competenza mancanti",
    "Competência não confirmada": "Classificazione per competenza non confermata",
    "Moeda não compatível": "Valuta non compatibile",
    "Despesa direta": "Spesa diretta",
    "Regra inválida": "Regola non valida",
    "Não simulada": "Non simulata",
    "Categorias elegíveis": "Categorie idonee",
    "Não elegível": "Non idonea",
    "Usar padrão da clínica": "Usa standard della clinica",
    "Usar regra da categoria": "Usa regola della categoria",
    "Nova regra de rateio": "Nuova regola di ripartizione",
    "Nome da regra": "Nome della regola",
    "Escopo": "Ambito",
    "Padrão da clínica": "Standard della clinica",
    "Categoria de despesa": "Categoria di spesa",
    "Sem categoria": "Senza categoria",
    "Soma dos percentuais": "Somma percentuali",
    "Criar rascunho": "Crea bozza",
    "Nenhuma regra de rateio encontrada": "Nessuna regola di ripartizione trovata",
    "Ativar regra": "Attiva regola",
    "Inativar regra": "Disattiva regola",
    "Histórico": "Storico",
    "Histórico de versões": "Storico versioni",
    "Dinheiro": "Contanti",
    "Cheque": "Assegno",
    "Cartão de crédito": "Carta di credito",
    "Cartão de débito": "Carta di debito",
    "Cartão de crédito parcelado": "Carta di credito rateale",
    "Transferência": "Bonifico bancario",
    "Convênio": "Convenzione",
    "Aluguel": "Affitto",
    "Contas": "Utenze",
    "Salários": "Stipendi",
    "Materiais": "Materiali di consumo",
    "Equipamentos": "Attrezzature",
    "Impostos": "Imposte",
    "Manutenção": "Manutenzione",
    "Fixa": "Fissa",
    "Variável": "Variabile",
    "Próximo": "Successivo",
    "Modo de visualização": "Vista calendario",
    "Nenhum agendamento encontrado para este período.": "Nessun appuntamento trovato per questo periodo.",
    "Sem agendamentos": "Nessun appuntamento",
    "Planos vendidos no período": "Piani venduti nel periodo",
    "Desempenho comercial e financeiro dos planos vendidos no intervalo selecionado.": "Andamento commerciale e finanziario dei piani venduti nell'intervallo selezionato.",
    "Valor líquido total dos planos vendidos": "Valore netto totale dei piani venduti",
    "Valor pendente": "Importo da incassare",
    "Plano mais vendido": "Piano più venduto",
    "Plano com maior valor líquido vendido": "Piano con il valore netto venduto più alto",
    "Buscar por nome do plano": "Cerca per nome del piano",
    "Digite o nome do plano": "Inserisci il nome del piano",
    "Tipo do plano": "Tipo di piano",
    "Situação financeira": "Situazione finanziaria",
    "Todas as situações": "Tutte le situazioni",
    "Parcial": "Parzialmente pagato",
    "Qtde. vendida": "Qtà venduta",
    "Detalhes das vendas do plano": "Dettagli delle vendite del piano",
    "Parcelamento": "Rateizzazione",
    "Profissional ou responsável": "Professionista o responsabile",
    "Não informado": "Non indicato",
    "à vista": "Pagamento unico",
    "parcelas": "rate",
    "Período do relatório": "Periodo del report",
    "Relatório exportado com sucesso.": "Report esportato correttamente.",
    "Não foi possível abrir a exportação.": "Non è stato possibile aprire l'esportazione.",
    "Erro ao carregar o relatório de planos vendidos.": "Errore durante il caricamento del report dei piani venduti.",
    "Ordenar em ordem crescente": "Ordina in modo crescente",
    "Ordenar em ordem decrescente": "Ordina in modo decrescente",
    "Meta de faturamento": "Obiettivo di fatturato",
    "Acompanhe o progresso do período selecionado.": "Monitora l'avanzamento del periodo selezionato.",
    "Realizado": "Fatturato",
    "Atingido": "Avanzamento",
    "Meta atingida no período.": "Obiettivo raggiunto nel periodo.",
    "Nenhuma meta financeira vigente para o período.": "Non è presente un obiettivo finanziario in vigore per il periodo.",
    "Indicadores por unidade de receita": "Indicatori per unità di ricavo",
    "Não disponível": "Non disponibile",
    "unidades": "unità",
    "Receita realizada por agendamento vinculado ou lançamento manual": "Ricavo realizzato per appuntamento collegato o registrazione manuale",
    "Base da métrica: atendimentos vinculados e lançamentos manuais de receita pagos no período.": "Base della metrica: appuntamenti collegati e registrazioni manuali dei ricavi pagati nel periodo.",
    "Unidades consideradas": "Unità considerate",
    "Custo por unidade de receita": "Costo per unità di ricavo",
    "Resultado por unidade de receita": "Risultato per unità di ricavo",
    "Ponto de equilíbrio em unidades de receita": "Punto di pareggio in unità di ricavo",
    "Formas de pagamento": "Metodi di pagamento",
    "Cadastre meios de pagamento, adquirentes, prazos e taxas para preparar o cálculo automático financeiro.": "Registra metodi di pagamento, acquirer, tempi di accredito e commissioni per preparare il calcolo automatico.",
    "Nova forma de pagamento": "Nuovo metodo di pagamento",
    "Editar forma de pagamento": "Modifica metodo di pagamento",
    "Todas as adquirentes": "Tutti gli acquirer",
    "Todos os status": "Tutti gli stati",
    "Adquirente/maquininha": "Acquirer/terminale",
    "Tipo aceito": "Tipo accettato",
    "Taxa percentual": "Commissione percentuale",
    "Taxa fixa": "Commissione fissa",
    "Prazo de recebimento": "Tempi di accredito",
    "Limite de parcelas": "Limite rate",
    "Taxas por parcela": "Commissioni per rata",
    "Taxas": "Commissioni",
    "Nenhuma forma de pagamento encontrada.": "Nessun metodo di pagamento trovato.",
    "Forma de pagamento cadastrada.": "Metodo di pagamento creato.",
    "Forma de pagamento atualizada.": "Metodo di pagamento aggiornato.",
    "Forma de pagamento excluída.": "Metodo di pagamento eliminato.",
    "Não foi possível excluir a forma de pagamento.": "Impossibile eliminare il metodo di pagamento.",
    "Preencha os campos obrigatérios.": "Compila i campi obbligatori.",
    "dias": "giorni",
    "Informe a taxa percentual cobrada em cada quantidade de parcelas.": "Indica la commissione percentuale applicata per ogni numero di rate.",
    "Essas configurações ainda não alteram lançamentos existentes; elas deixam a clínica pronta para cálculo automático de taxas.": "Queste configurazioni non modificano ancora i movimenti esistenti; preparano la clinica al calcolo automatico delle commissioni.",
    "Carregando relatórios financeiros...": "Caricamento dei report finanziari...",
    "Visão gerencial para acompanhar entradas, saídas e saldo de caixa.": "Vista gestionale per monitorare entrate, uscite e saldo di cassa.",
    "Erro ao excluir receita": "Errore nell'eliminazione dell'entrata",
    "Erro ao excluir despesa": "Errore nell'eliminazione della spesa",
    "Erro ao carregar dados financeiros": "Errore nel caricamento dei dati finanziari",
    "Excluir receita": "Elimina entrata",
    "Excluir despesa": "Elimina spesa",
    "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.": "Questa entrata sarà eliminata e non apparirà più negli elenchi e negli indicatori.",
    "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.": "Questa spesa sarà eliminata e non apparirà più negli elenchi e negli indicatori.",
    "Não vinculado": "Non collegato",
    "Paciente removido": "Paziente rimosso",
    "Profissional removido": "Professionista rimosso",
    "Fixas": "Fisse",
    "Variáveis": "Variabili",
    "Leitura gerencial": "Analisi gestionale",
    "Um resumo rápido do comportamento financeiro no recorte atual.": "Una sintesi rapida dell'andamento finanziario nel periodo selezionato.",
    "Recebimento médio de caixa": "Incasso medio di cassa",
    "em pacotes": "da pacchetti",
    "Indicadores de competência permanecem indisponíveis até haver produção canônica.": "Gli indicatori per competenza restano non disponibili finché non sono disponibili registrazioni canoniche della produzione.",
    "Onde fatura": "Ricavi per settore",
    "Receita distribuída por setor.": "Distribuzione dei ricavi per settore.",
    "Nenhuma receita com setor vinculado no período.": "Nessun ricavo con settore collegato nel periodo.",
    "Quem fatura": "Ricavi per professionista",
    "Receita distribuída por profissional.": "Distribuzione dei ricavi per professionista.",
    "Nenhuma receita vinculada a profissional no período.": "Nessun ricavo collegato a un professionista nel periodo.",
    "Como entra": "Modalità di incasso",
    "Receita distribuída por forma de pagamento.": "Distribuzione dei ricavi per metodo di pagamento.",
    "Nenhuma receita registrada no período.": "Nessun ricavo registrato nel periodo.",
    "CSV": "CSV",
    "Data final": "Data finale",
    "Data inicial": "Data iniziale",
    "Despesa excluída com sucesso!": "Spesa eliminata con successo.",
    "Despesa registrada com sucesso!": "Spesa registrata con successo!",
    "Despesas fixas": "Spese fisse",
    "Despesas variáveis": "Spese variabili",
    "Dia": "Giorno",
    "Lançamentos": "Movimenti",
    "Limpar filtros": "Cancella filtri",
    "Mês": "Mese",
    "Nenhum plano encontrado com os filtros selecionados.": "Nessun piano corrisponde ai filtri selezionati.",
    "Não alocada": "Non assegnata",
    "OK": "OK",
    "Observações": "Note",
    "Por profissional": "Per professionista",
    "Quantidade vendida": "Quantità venduta",
    "Receita excluída com sucesso!": "Entrata eliminata con successo.",
    "Registrar": "Registra",
    "Salvar": "Salva",
    "Selecione uma clínica.": "Seleziona una clinica.",
    "Sem dados no período": "Nessun dato nel periodo",
    "Semana": "Settimana",
    "Situação": "Stato",
    "Situação do pagamento": "Stato del pagamento",
    "Ticket médio": "Valore medio",
    "Totais consolidados": "Totali consolidati",
    "Total": "Totale",
    "Total de planos vendidos": "Totale piani venduti",
    "Valor unit.": "Valore unit.",
    "Valor unitário": "Valore unitario",
    "Visão Geral": "Panoramica",
    "Últimas Despesas": "Ultime spese",
    "Últimas Receitas": "Ultime entrate",
    "min": "min",
    "Salvar alterações": "Salva modifiche",
    "Selecione uma clínica": "Seleziona una clinica",
    "Acréscimo": "Maggiorazione",
    "Classificar setor": "Assegna settore",
    "Dado": "Dato",
    "Confiabilidade do dado": "Affidabilità dei dati",
    "Desconto": "Sconto",
    "Preencha os campos obrigatórios.": "Compila i campi obbligatori.",
    "Recorrente": "Ricorrente",
    "Registros": "Registrazioni",
    "Sem vínculo": "Nessun collegamento",
    "Vincular paciente": "Collega paziente",
    "Vincular profissional": "Collega professionista",
    "Importado": "Importato",
    "Sem regra": "Nessuna regola di compenso",
    "Receita gerencial líquida": "Ricavo gestionale netto",
    "Resultado gerencial após custos diretos": "Risultato gestionale dopo i costi diretti",
    "Nome completo": "Nome completo",
    "Histórico do paciente": "Storico del paziente",
    "Atendimentos e financeiro do paciente.": "Prestazioni e situazione finanziaria del paziente.",
    "Faltas registradas": "Assenze registrate",
    "Histórico de atendimentos": "Storico delle prestazioni",
    "Nenhum atendimento encontrado para este paciente.": "Nessuna prestazione trovata per questo paziente.",
    "Vínculo": "Collegamento",
    "Agendamento sem plano de tratamento": "Appuntamento senza piano terapeutico",
    "Ativos": "Attivi",
    "Inativos": "Inattivi",
    "Exames": "Esami",
    "Imagens": "Immagini",
    "Fotos": "Foto",
    "Agendamentos do paciente sem vínculo com plano de tratamento.": "Appuntamenti del paziente non collegati a un piano terapeutico.",
    "Nenhum agendamento sem plano de tratamento encontrado para este paciente.": "Nessun appuntamento senza piano terapeutico trovato per questo paziente.",
    "Erro ao carregar pacientes": "Errore durante il caricamento dei pazienti",
    "Lead convertido em paciente com sucesso!": "Lead convertito in paziente con successo!",
    "Lead criado com sucesso!": "Lead creato con successo!",
    "Lead atualizado com sucesso!": "Lead aggiornato con successo!",
    "Pendências de follow-up": "Follow-up in sospeso",
    "Priorize os leads com retorno mais urgente.": "Dai priorità ai lead che richiedono il follow-up più urgente.",
    "Remover origem": "Rimuovi origine",
    "Buscar por nome ou telefone...": "Cerca per nome o telefono...",
    "Gerencie seus leads e conversões": "Gestisci lead e conversioni",
    "Consulta agendada a partir do lead!": "Appuntamento programmato a partire dal lead!",
    "Tentar novamente": "Riprova",
    "Apenas perfis de super admin podem acessar esta área.": "Solo i profili di super admin possono accedere a quest'area.",
    "Seu usuário não possui permissão para visualizar clínicas globais ou controlar acessos centrais.": "Il tuo account utente non dispone dell'autorizzazione per visualizzare le cliniche globali o gestire gli accessi centrali.",
    "Governança central do ecossistema": "Governance centrale dell'ecosistema",
    "Gerencie clínicas, super admins e trilhas de auditoria a partir de um único painel global.": "Gestisci cliniche, super admin e registri di audit da un unico pannello globale.",
    "Clínicas da plataforma": "Cliniche della piattaforma",
    "Busque clínicas, confira seus usuários e controle liberação ou bloqueio operacional.": "Cerca le cliniche, verifica i relativi utenti e gestisci l'abilitazione o il blocco operativo.",
    "Buscar clínica, cidade, usuário ou e-mail": "Cerca per clinica, città, utente o e-mail",
    "Carregando clínicas...": "Caricamento delle cliniche...",
    "Nenhuma clínica encontrada com o filtro informado.": "Nessuna clinica trovata con il filtro specificato.",
    "Bloqueada": "Bloccata",
    "Sem e-mail cadastrado": "Nessun indirizzo e-mail registrato",
    "Sem telefone cadastrado": "Nessun numero di telefono registrato",
    "Cidade não informada": "Città non indicata",
    "Bloquear clínica": "Blocca clinica",
    "Liberar clínica": "Sblocca clinica",
    "Usuários cadastrados": "Utenti registrati",
    "Nenhum usuário vinculado a esta clínica.": "Nessun utente è collegato a questa clinica.",
    "Bloqueado": "Bloccato",
    "Redefinir acesso": "Reimposta accesso",
    "Nova senha temporária": "Nuova password temporanea",
    "Reativar acesso": "Riattiva accesso",
    "Se ativado, a conta volta a acessar o painel global após a redefinição.": "Se attivato, l'account potrà accedere nuovamente al pannello globale dopo la reimpostazione.",
    "Salvar nova senha": "Salva nuova password",
    "Convide novos perfis globais, revogue acessos quando necessário e redefina credenciais com auditoria.": "Invita nuovi profili globali, revoca gli accessi quando necessario e reimposta le credenziali con tracciamento di audit.",
    "Buscar super admin por nome ou email": "Cerca un super admin per nome o e-mail",
    "Convidar super admin": "Invita super admin",
    "Carregando super admins...": "Caricamento dei super admin...",
    "Nenhum super admin encontrado com o filtro informado.": "Nessun super admin trovato con il filtro specificato.",
    "Desativado": "Disattivato",
    "Revogar": "Revoca",
    "Redefinir acesso do super admin": "Reimposta l'accesso del super admin",
    "Acesso redefinido com sucesso.": "Accesso reimpostato correttamente.",
    "Erro ao redefinir acesso": "Errore durante la reimpostazione dell'accesso",
    "Auditoria recente": "Audit recenti",
    "Últimas ações sensíveis executadas por super admins na plataforma.": "Ultime azioni sensibili eseguite dai super admin sulla piattaforma.",
    "Carregando trilha de auditoria...": "Caricamento del registro di audit...",
    "Nenhum evento de auditoria registrado ainda.": "Non è stato ancora registrato alcun evento di audit.",
    "Quando": "Quando",
    "Responsável": "Responsabile",
    "Alvo": "Oggetto",
    "Clínica bloqueada": "Clinica bloccata",
    "Clínica liberada": "Clinica sbloccata",
    "Super admin convidado": "Super admin invitato",
    "Super admin desativado": "Super admin disattivato",
    "Super admin reativado": "Super admin riattivato",
    "Acesso redefinido": "Accesso reimpostato",
    "Importação Zen Fisio - pacientes": "Importazione pazienti da Zen Fisio",
    "Importação Zen Fisio - profissionais": "Importazione professionisti da Zen Fisio",
    "Importação Zen Fisio - agenda": "Importazione agenda da Zen Fisio",
    "Importação Zen Fisio - financeiro": "Importazione dati finanziari da Zen Fisio",
    "Importação Zen Fisio - prontuário": "Importazione cartelle cliniche da Zen Fisio",
    "Importação Zen Fisio revertida": "Rollback dell'importazione da Zen Fisio",
    "Importa Zen Fisio": "Importazione da Zen Fisio",
    "Migração por clínica": "Migrazione per clinica",
    "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.": "Seleziona la clinica di destinazione prima di importare pazienti, professionisti, agenda, dati finanziari o cartelle cliniche da Zen Fisio.",
    "Clínica de destino": "Clinica di destinazione",
    "Selecione o arquivo": "Seleziona il file",
    "Nenhuma clínica selecionada": "Nessuna clinica selezionata",
    "Importados": "Importati",
    "Importe o arquivo pacientes.csv preservando o isolamento multi-tenant e gerando alertas de CPF e nascimento.": "Importa il file pacientes.csv mantenendo l'isolamento multi-tenant e generando avvisi su CPF e date di nascita.",
    "Importar pacientes": "Importa pazienti",
    "Importando pacientes...": "Importazione dei pazienti...",
    "Importe profissionais com setor automático, agenda padrão das 08:00 às 18:00, duração de 1 hora e limite de 1 paciente por horário.": "Importa professionisti con assegnazione automatica del settore, agenda predefinita dalle 08:00 alle 18:00, durata degli appuntamenti di 1 ora e limite di 1 paziente per fascia oraria.",
    "Importar profissionais": "Importa professionisti",
    "Importando profissionais...": "Importazione dei professionisti...",
    "Importe agendamentos históricos preservando o AgendamentoId, vinculando pacientes pelo id legado e profissionais pelo nome importado anteriormente.": "Importa gli appuntamenti storici mantenendo l'AgendamentoId e collegando i pazienti tramite l'ID legacy e i professionisti tramite il nome importato in precedenza.",
    "Importar agenda": "Importa agenda",
    "Importando agenda...": "Importazione dell'agenda...",
    "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.": "Importa entrate, spese e crediti storici raggruppando i movimenti con lo stesso AgendamentoID e mantenendo i pagamenti parziali.",
    "Importar financeiro": "Importa dati finanziari",
    "Importando financeiro...": "Importazione dei dati finanziari...",
    "Avaliações clínicas": "Valutazioni cliniche",
    "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.": "Importa le valutazioni di Zen Fisio come registrazioni legacy in Markdown collegate al paziente, al professionista e all'appuntamento.",
    "Importar avaliações": "Importa valutazioni",
    "Importando avaliações...": "Importazione delle valutazioni...",
    "Evoluções clínicas": "Evoluzioni cliniche",
    "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.": "Importa le evoluzioni cliniche di Zen Fisio consolidando le righe per prestazione e ignorando le risposte vuote.",
    "Importar evoluções": "Importa evoluzioni cliniche",
    "Importando evoluções...": "Importazione delle evoluzioni cliniche...",
    "Selecione um arquivo CSV.": "Seleziona un file CSV.",
    "Aguarde a importação em andamento terminar antes de iniciar outra.": "Attendi il completamento dell'importazione in corso prima di avviarne un'altra.",
    "Selecione a clínica de destino.": "Seleziona la clinica di destinazione.",
    "Importação finalizada com sucesso.": "Importazione completata correttamente.",
    "A importação não foi concluída. Tente novamente.": "Non è stato possibile completare l'importazione. Riprova.",
    "Ignorados": "Ignorati",
    "Registros com erro/alerta": "Record con errori o avvisi",
    "Importação finalizada": "Importazione completata",
    "Importação não concluída": "Importazione non completata",
    "Importação em andamento": "Importazione in corso",
    "Progresso da importação": "Avanzamento dell'importazione",
    "Não foi possível concluir a importação. Tente novamente.": "Non è stato possibile completare l'importazione. Riprova.",
    "Os dados estão sendo processados.": "I dati sono in fase di elaborazione.",
    "Nova importação": "Nuova importazione",
    "Alertas": "Avvisi",
    "CPFs duplicados": "CPF duplicati",
    "Nascimentos bloqueados": "Date di nascita bloccate",
    "Setores criados": "Settori creati",
    "Agenda padrão": "Agenda predefinita",
    "Horários inválidos": "Orari non validi",
    "Duplicados": "Duplicati",
    "Pagamentos": "Pagamenti",
    "Agrupados": "Raggruppati",
    "Sem agendamento": "Senza appuntamento",
    "Grupos vazios": "Gruppi vuoti",
    "Datas inválidas": "Date non valide",
    "Arquivo de alertas gerado": "File degli avvisi generato",
    "Baixe o CSV para revisar CPFs duplicados, nascimentos futuros e datas inválidas bloqueadas.": "Scarica il CSV per verificare CPF duplicati, date di nascita future e date non valide bloccate.",
    "Baixe o CSV para revisar setores criados automaticamente e registros ignorados.": "Scarica il CSV per verificare i settori creati automaticamente e i record ignorati.",
    "Baixe o CSV para revisar vínculos ausentes, horários inválidos, duplicidades e setores criados.": "Scarica il CSV per verificare collegamenti mancanti, orari non validi, duplicati e settori creati.",
    "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.": "Scarica il CSV per verificare collegamenti mancanti, metodi di pagamento senza corrispondenza, raggruppamenti e movimenti ignorati.",
    "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.": "Scarica il CSV per verificare appuntamenti mancanti, gruppi vuoti, duplicati e date non valide.",
    "Baixar alertas": "Scarica avvisi",
    "{count} usuário": "{count} utente",
    "{count} usuários": "{count} utenti",
    "{count} ativo": "{count} attivo",
    "{count} ativos": "{count} attivi",
    "{count} admin": "{count} admin",
    "{count} admins": "{count} admin",
    "A senha de {name} será redefinida e o acesso será reativado.": "La password di {name} verrà reimpostata e l'accesso sarà riattivato.",
    "A senha de {name} será redefinida mantendo o status atual da conta.": "La password di {name} verrà reimpostata mantenendo lo stato attuale dell'account.",
    "Nova senha para {name}.": "Nuova password per {name}.",
    "Destino selecionado: {clinic}": "Destinazione selezionata: {clinic}",
    "Destino selecionado: nenhuma clínica selecionada.": "Destinazione selezionata: nessuna clinica selezionata.",
    "Autenticação necessária.": "Autenticazione richiesta.",
    "Esta operação exige o perfil de super admin.": "Questa operazione richiede il ruolo di super admin.",
    "Clínica não encontrada.": "Clinica non trovata.",
    "Este e-mail já está cadastrado.": "Questo indirizzo e-mail è già registrato.",
    "Super admin não encontrado.": "Super admin non trovato.",
    "Deve existir ao menos um super admin ativo.": "Deve rimanere attivo almeno un super admin.",
    "Informe a clínica de destino.": "Indica la clinica di destinazione.",
    "Informe o conteúdo CSV.": "Fornisci il contenuto CSV.",
    "O histórico de importação está temporariamente indisponível.": "La cronologia delle importazioni non è temporaneamente disponibile.",
    "Lote de importação não encontrado.": "Lotto di importazione non trovato.",
    "Somente lotes ativos podem ser revertidos.": "È possibile annullare solo i lotti attivi.",
    "O nome deve ter pelo menos {minimum} caracteres.": "Il nome deve contenere almeno {minimum} caratteri.",
    "Informe um e-mail válido.": "Inserisci un indirizzo e-mail valido.",
    "A senha deve ter pelo menos {minimum} caracteres.": "La password deve contenere almeno {minimum} caratteri.",
    "A senha deve ter no máximo {maximum} caracteres.": "La password deve contenere al massimo {maximum} caratteri.",
    "A senha deve conter ao menos uma letra minúscula.": "La password deve contenere almeno una lettera minuscola.",
    "A senha deve conter ao menos uma letra maiúscula.": "La password deve contenere almeno una lettera maiuscola.",
    "A senha deve conter ao menos um número.": "La password deve contenere almeno un numero.",
    "O CSV não contém as colunas obrigatórias: {missing_headers}.": "Nel CSV mancano le colonne obbligatorie: {missing_headers}.",
    "Já existe uma importação ativa de {import_type} para esta clínica.": "Esiste già un'importazione attiva di {import_type} per questa clinica.",
    "Importação bloqueada. Execute primeiro: {missing_prerequisites}.": "Importazione bloccata. Esegui prima: {missing_prerequisites}.",
    "Importação bloqueada pela etapa posterior {blocking_import_type}.": "Importazione bloccata dalla fase successiva {blocking_import_type}.",
    "Reverta primeiro o lote posterior de {blocking_import_type}.": "Annulla prima il lotto successivo di {blocking_import_type}.",
    "Não é possível reverter {entity_type}: existem {dependency_count} dependências.": "Impossibile annullare {entity_type}: esistono {dependency_count} dipendenze.",
    "pacientes": "pazienti",
    "profissionais": "professionisti",
    "agendamentos": "appuntamenti",
    "A clínica": "La clinica",
    "Bloquear acesso": "Blocca accesso",
    "Clínica bloqueada com sucesso.": "Clinica bloccata correttamente.",
    "Clínica liberada com sucesso.": "Clinica sbloccata correttamente.",
    "Clínicas ativas": "Cliniche attive",
    "Clínicas cadastradas": "Cliniche registrate",
    "Conceder acesso de super admin": "Concedi accesso da super admin",
    "Confirmar convite": "Conferma invito",
    "Convidar novo super admin": "Invita un nuovo super admin",
    "Desativar super admin": "Disattiva super admin",
    "Email": "E-mail",
    "Erro ao atualizar status da clínica": "Errore durante l'aggiornamento dello stato della clinica",
    "Erro ao atualizar super admin": "Errore durante l'aggiornamento del super admin",
    "Erro ao carregar painel de super admin": "Errore durante il caricamento del pannello super admin",
    "Erro ao convidar super admin": "Errore durante l'invito del super admin",
    "Liberar acesso": "Concedi accesso",
    "Reativar super admin": "Riattiva super admin",
    "Revogar acesso": "Revoca accesso",
    "Super Admin": "Super Admin",
    "Super admin convidado com sucesso.": "Super admin invitato correttamente.",
    "Super admin desativado com sucesso.": "Super admin disattivato correttamente.",
    "Super admin reativado com sucesso.": "Super admin riattivato correttamente.",
    "Super admins ativos": "Super admin attivi",
    "Usuários monitorados": "Utenti monitorati",
    "com o email": "con l'e-mail",
    "passará a ter acesso global ao sistema": "avrà accesso globale al sistema",
    "perderá o acesso ao painel global até nova liberação.": "perderà l'accesso al pannello globale finché non verrà nuovamente autorizzato.",
    "terá o acesso ao sistema interrompido até nova liberação.": "avrà l'accesso al sistema sospeso finché la clinica non verrà sbloccata.",
    "voltará a acessar a plataforma normalmente.": "tornerà ad accedere normalmente alla piattaforma.",
    "voltará a acessar o painel global normalmente.": "tornerà ad accedere normalmente al pannello globale.",
  },
};

const PATTERN_TRANSLATIONS: PatternRule[] = [
  {
    pattern: /^Visão geral de (.+)$/u,
    replacements: {
      "pt-PT": "Visão geral de $1",
      en: "Overview of $1",
      es: "Vista general de $1",
      it: "Panoramica di $1",
    },
  },
  {
    pattern: /^(\d+) convertidos$/u,
    replacements: {
      "pt-PT": "$1 convertidos",
      en: "$1 converted",
      es: "Convertidos: $1",
      it: "$1 convertiti",
    },
  },
  {
    pattern: /^\+(\d+) este mês$/u,
    replacements: {
      "pt-PT": "+$1 neste mês",
      en: "+$1 this month",
      es: "+$1 este mes",
      it: "+$1 questo mese",
    },
  },
  {
    pattern: /^(\d+) faltas$/u,
    replacements: {
      "pt-PT": "$1 faltas",
      en: "$1 no-shows",
      es: "Ausencias: $1",
      it: "$1 assenze",
    },
  },
  {
    pattern: /^Resposta registrada em (.+)\. Obrigado pelo feedback\.$/u,
    replacements: {
      "pt-PT": "Resposta registada em $1. Obrigado pelo feedback.",
      en: "Response recorded on $1. Thank you for the feedback.",
      es: "Respuesta registrada el $1. Gracias por su opinión.",
      it: "Risposta registrata il $1. Grazie per il feedback.",
    },
  },
  {
    pattern: /^Versão (.+)$/u,
    replacements: {
      "pt-PT": "Versão $1",
      en: "Version $1",
      es: "Versión $1",
      it: "Versione $1",
    },
  },
  {
    pattern: /^Ol[áa], (.+)\.$/u,
    replacements: {
      "pt-PT": "Olá, $1.",
      en: "Hello, $1.",
      es: "Hola, $1.",
      it: "Ciao, $1.",
    },
  },
  {
    pattern: /^Como foi sua experi[êe]ncia com (.+) no plano (.+)\?$/u,
    replacements: {
      "pt-PT": "Como foi a sua experiência com $1 no plano $2?",
      en: "How was your experience with $1 on the $2 plan?",
      es: "¿Cómo fue su experiencia con $1 en el plan $2?",
      it: "Com'è stata la tua esperienza con $1 nel piano $2?",
    },
  },
];

const GLOSSARY_TRANSLATIONS: Record<Exclude<AppLanguage, "pt-BR">, GlossaryEntry[]> = {
  "pt-PT": [
    { source: "usuário", target: "utilizador" },
    { source: "usuários", target: "utilizadores" },
    { source: "Usuário", target: "Utilizador" },
    { source: "Usuários", target: "Utilizadores" },
    { source: "celular", target: "telemóvel" },
    { source: "Celular", target: "Telemóvel" },
    { source: "CPF", target: "NIF" },
    { source: "CEP", target: "código postal" },
    { source: "Cadastro", target: "Registo" },
    { source: "cadastro", target: "registo" },
    { source: "Cadastrar", target: "Registar" },
    { source: "cadastrar", target: "registar" },
    { source: "Agendamento", target: "Marcação" },
    { source: "agendamento", target: "marcação" },
    { source: "Agendamentos", target: "Marcações" },
    { source: "agendamentos", target: "marcações" },
    { source: "Agendar", target: "Marcar" },
    { source: "agendar", target: "marcar" },
    { source: "faturamento", target: "faturação" },
    { source: "Faturamento", target: "Faturação" },
    { source: "endereço", target: "morada" },
    { source: "Endereço", target: "Morada" },
    { source: "equipe", target: "equipa" },
    { source: "Equipe", target: "Equipa" },
    { source: "Salvando...", target: "A guardar..." },
    { source: "Salvar", target: "Guardar" },
    { source: "Carregando", target: "A carregar" },
    { source: "Você", target: "O seu perfil" },
    { source: "Senha", target: "Palavra-passe" },
    { source: "senha", target: "palavra-passe" },
    { source: "Convênio", target: "Seguro de saúde" },
    { source: "convênio", target: "seguro de saúde" },
  ],
  en: [
    { source: "Cl\u00ednicas da plataforma", target: "Platform clinics" },
    { source: "Busque cl\u00ednicas, confira seus usu\u00e1rios e controle libera\u00e7\u00e3o ou bloqueio operacional.", target: "Search clinics, review their users, and control operational release or blocking." },
    { source: "Buscar cl\u00ednica, cidade, usu\u00e1rio ou e-mail", target: "Search clinic, city, user, or email" },
    { source: "Carregando cl\u00ednicas...", target: "Loading clinics..." },
    { source: "Nenhuma cl\u00ednica encontrada com o filtro informado.", target: "No clinic found with the selected filter." },
    { source: "Ativa", target: "Active" },
    { source: "Bloqueada", target: "Blocked" },
    { source: "Sem e-mail cadastrado", target: "No email registered" },
    { source: "Sem telefone cadastrado", target: "No phone registered" },
    { source: "Cidade n\u00e3o informada", target: "City not provided" },
    { source: "usu\u00e1rio(s)", target: "user(s)" },
    { source: "ativo(s)", target: "active" },
    { source: "admin(s)", target: "admin(s)" },
    { source: "Bloquear cl\u00ednica", target: "Block clinic" },
    { source: "Liberar cl\u00ednica", target: "Release clinic" },
    { source: "Usu\u00e1rios cadastrados", target: "Registered users" },
    { source: "Nenhum usu\u00e1rio vinculado a esta cl\u00ednica.", target: "No user linked to this clinic." },
    { source: "Bloqueado", target: "Blocked" },
    { source: "Pesquisa de experiência", target: "Experience survey" },
    { source: "NPS pós-tratamento", target: "Post-treatment NPS" },
    { source: "Carregando pesquisa...", target: "Loading survey..." },
    { source: "Esta pesquisa não está disponível.", target: "This survey is not available." },
    { source: "Olá", target: "Hello" },
    { source: "Como foi sua experiência com", target: "How was your experience with" },
    { source: "no plano", target: "on the plan" },
    { source: "Sua nota", target: "Your score" },
    { source: "Comentário", target: "Comment" },
    { source: "Resposta registrada em", target: "Response recorded on" },
    { source: "Obrigado pelo feedback.", target: "Thank you for the feedback." },
    { source: "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.", target: "Give a score from 0 to 10 and, if you'd like, leave a comment for the team." },
    { source: "Detalhes do Agendamento", target: "Appointment details" },
    { source: "Campanhas, agendamentos e desempenho comercial.", target: "Campaigns, appointments, and sales performance." },
    { source: "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.", target: "Create a revenue entry linked to this appointment, patient, and professional." },
    { source: "Meta mensal de agendamento atualizada!", target: "Monthly appointment goal updated!" },
    { source: "Intervalo de follow-up atualizado!", target: "Follow-up interval updated!" },
    { source: "Status do lead atualizado!", target: "Lead status updated!" },
    { source: "Erro ao carregar dados de marketing", target: "Error loading marketing data" },
    { source: "Erro ao carregar leads", target: "Error loading leads" },
    { source: "Erro ao excluir lead", target: "Error deleting lead" },
    { source: "Lead criado com sucesso!", target: "Lead created successfully!" },
    { source: "Paciente criado com sucesso!", target: "Patient created successfully!" },
    { source: "Receita registrada com sucesso!", target: "Revenue recorded successfully!" },
    { source: "Selecione uma clínica.", target: "Select a clinic." },
    { source: "Selecione o setor", target: "Select the sector" },
    { source: "Sem campanha", target: "No campaign" },
    { source: "Sem setor", target: "No sector" },
    { source: "Sem vinculo", target: "No link" },
    { source: "Sem vínculo", target: "No link" },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais ou agenda do Zen Fisio.", target: "Select the destination clinic before importing patients, professionals, or schedule data from Zen Fisio." },
    { source: "Importe agendamentos históricos preservando o AgendamentoId, vinculando pacientes pelo id legado e profissionais pelo nome importado anteriormente.", target: "Import historical appointments while preserving AgendamentoId, linking patients by legacy id and professionals by the previously imported name." },
    { source: "Importar agenda", target: "Import schedule" },
    { source: "Importando agenda...", target: "Importing schedule..." },
    { source: "Importação da agenda concluída.", target: "Schedule import completed." },
    { source: "Horários inválidos", target: "Invalid times" },
    { source: "Duplicados", target: "Duplicates" },
    { source: "Baixe o CSV para revisar vínculos ausentes, horários inválidos, duplicidades e setores criados.", target: "Download the CSV to review missing links, invalid times, duplicates, and created sectors." },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda ou financeiro do Zen Fisio.", target: "Select the destination clinic before importing patients, professionals, schedule, or financial data from Zen Fisio." },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.", target: "Select the destination clinic before importing patients, professionals, schedule, financial records, or medical records from Zen Fisio." },
    { source: "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.", target: "Import historical revenues, expenses, and receivables by grouping movements with the same AgendamentoID and preserving partial payments." },
    { source: "Importar financeiro", target: "Import financial records" },
    { source: "Importando financeiro...", target: "Importing financial records..." },
    { source: "Importação financeira concluída.", target: "Financial import completed." },
    { source: "Preparando importação...", target: "Preparing import..." },
    { source: "Importando dados...", target: "Importing data..." },
    { source: "Validando registros...", target: "Validating records..." },
    { source: "Finalizando importação...", target: "Finishing import..." },
    { source: "Importação em andamento", target: "Import in progress" },
    { source: "Importação finalizada", target: "Import finished" },
    { source: "Importação não concluída", target: "Import not completed" },
    { source: "Importação finalizada com sucesso.", target: "Import completed successfully." },
    { source: "A importação não foi concluída. Tente novamente.", target: "The import was not completed. Try again." },
    { source: "Não foi possível concluir a importação. Tente novamente.", target: "We could not complete the import. Try again." },
    { source: "Os dados estão sendo processados.", target: "The data is being processed." },
    { source: "Progresso da importação", target: "Import progress" },
    { source: "Registros com erro/alerta", target: "Records with errors/alerts" },
    { source: "Nova importação", target: "New import" },
    { source: "Tentar novamente", target: "Try again" },
    { source: "Aguarde a importação em andamento terminar antes de iniciar outra.", target: "Wait for the current import to finish before starting another." },
    { source: "Agrupados", target: "Grouped" },
    { source: "Baixe o CSV para revisar vínculos ausentes, formas other, agrupamentos e movimentos ignorados.", target: "Download the CSV to review missing links, other payment methods, grouped records, and skipped movements." },
    { source: "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.", target: "Download the CSV to review missing links, unmatched payment methods, grouped records, and skipped movements." },
    { source: "Selecione o arquivo", target: "Select the file" },
    { source: "Avaliações clínicas", target: "Clinical assessments" },
    { source: "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.", target: "Import Zen Fisio assessments as legacy Markdown records linked to the patient, professional, and appointment." },
    { source: "Importar avaliações", target: "Import assessments" },
    { source: "Importando avaliações...", target: "Importing assessments..." },
    { source: "Evoluções clínicas", target: "Clinical evolutions" },
    { source: "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.", target: "Import Zen Fisio evolutions by consolidating rows per visit and ignoring empty answers." },
    { source: "Importar evoluções", target: "Import evolutions" },
    { source: "Importando evoluções...", target: "Importing evolutions..." },
    { source: "Importação de avaliações concluída.", target: "Assessment import completed." },
    { source: "Importação de evoluções concluída.", target: "Evolution import completed." },
    { source: "Importação Zen Fisio - pacientes", target: "Zen Fisio import - patients" },
    { source: "Importação Zen Fisio - profissionais", target: "Zen Fisio import - professionals" },
    { source: "Importação Zen Fisio - agenda", target: "Zen Fisio import - schedule" },
    { source: "Importação Zen Fisio - financeiro", target: "Zen Fisio import - financial records" },
    { source: "Importação Zen Fisio - prontuário", target: "Zen Fisio import - medical records" },
    { source: "Últimas ações sensíveis executadas por super admins na plataforma.", target: "Latest sensitive actions performed by super admins on the platform." },
    { source: "Sem detalhes adicionais.", target: "No additional details." },
    { source: "Convite emitido para", target: "Invitation sent to" },
    { source: "Senha redefinida e acesso reativado.", target: "Password reset and access reactivated." },
    { source: "Senha redefinida mantendo o status atual.", target: "Password reset while keeping the current status." },
    { source: "Acesso liberado.", target: "Access granted." },
    { source: "Acesso revogado.", target: "Access revoked." },
    { source: "Registros importados", target: "Imported records" },
    { source: "ignorados", target: "skipped" },
    { source: "alertas", target: "alerts" },
    { source: "Ação registrada com sucesso.", target: "Action recorded successfully." },
    { source: "Sem agendamento", target: "Missing appointment" },
    { source: "Grupos vazios", target: "Empty groups" },
    { source: "Datas inválidas", target: "Invalid dates" },
    { source: "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.", target: "Download the CSV to review missing appointments, empty groups, duplicates, and invalid dates." },
    { source: "Agendamento vinculado", target: "Linked appointment" },
    { source: "Selecionar agendamento", target: "Select appointment" },
    { source: "Vincular paciente", target: "Link patient" },
    { source: "Vincular profissional", target: "Link professional" },
    { source: "Classificar setor", target: "Assign sector" },
    { source: "Resultado comercial", target: "Sales outcome" },
    { source: "Proximo follow-up", target: "Next follow-up" },
    { source: "Próximo follow-up", target: "Next follow-up" },
    { source: "Motivo de não agendamento", target: "Reason for not scheduling" },
    { source: "Motivo de não agendamento", target: "Reason for not scheduling" },
    { source: "Motivo do cancelamento", target: "Cancellation reason" },
    { source: "Preencha quando o agendamento for cancelado", target: "Fill this in when the appointment is cancelled" },
    { source: "Gerar Receita", target: "Generate revenue" },
    { source: "Agendamento cancelado", target: "Cancelled appointment" },
    { source: "Nova Receita", target: "New revenue" },
    { source: "Nova Despesa", target: "New expense" },
    { source: "Novo Paciente", target: "New patient" },
    { source: "Novo Profissional", target: "New professional" },
    { source: "Novo Usuário", target: "New user" },
    { source: "Novo Agendamento", target: "New appointment" },
    { source: "Novo Lead", target: "New lead" },
    { source: "Nova Campanha", target: "New campaign" },
    { source: "Criar campanha", target: "Create campaign" },
    { source: "Criar Campanha", target: "Create campaign" },
    { source: "Data do Pagamento", target: "Payment date" },
    { source: "De", target: "From" },
    { source: "Até", target: "To" },
    { source: "Competência", target: "Accrual" },
    { source: "Vencimento", target: "Due date" },
    { source: "Plano vinculado", target: "Linked plan" },
    { source: "sessoes restantes", target: "sessions remaining" },
    { source: "sessões restantes", target: "sessions remaining" },
    { source: "validade até", target: "valid until" },
    { source: "validade até", target: "valid until" },
    { source: "Contato de Emergencia", target: "Emergency contact" },
    { source: "Contato de Emergência", target: "Emergency contact" },
    { source: "Histórico Médico / Observações", target: "Medical history / notes" },
    { source: "Histórico Médico / Observações", target: "Medical history / notes" },
    { source: "Data de Nascimento", target: "Birth date" },
    { source: "Nome completo", target: "Full name" },
    { source: "Nome completo do responsável", target: "Primary admin full name" },
    { source: "Endereço", target: "Address" },
    { source: "Endereco", target: "Address" },
    { source: "Logradouro", target: "Street" },
    { source: "Complemento", target: "Address line 2" },
    { source: "Gênero", target: "Gender" },
    { source: "Genero", target: "Gender" },
    { source: "Masculino", target: "Male" },
    { source: "Feminino", target: "Female" },
    { source: "Novo", target: "New" },
    { source: "Nova", target: "New" },
    { source: "Nome", target: "Name" },
    { source: "Telefone", target: "Phone" },
    { source: "Origem", target: "Source" },
    { source: "Setor", target: "Sector" },
    { source: "Campanha", target: "Campaign" },
    { source: "Status", target: "Status" },
    { source: "Observações", target: "Notes" },
    { source: "Observações", target: "Notes" },
    { source: "Paciente", target: "Patient" },
    { source: "Pacientes", target: "Patients" },
    { source: "Profissional", target: "Professional" },
    { source: "Profissionais", target: "Professionals" },
    { source: "Categoria", target: "Category" },
    { source: "Pagamento", target: "Payment" },
    { source: "Pago", target: "Paid" },
    { source: "Não Pago", target: "Unpaid" },
    { source: "Descrição", target: "Description" },
    { source: "Descrição", target: "Description" },
    { source: "Valor", target: "Amount" },
    { source: "Inicio", target: "Start" },
    { source: "Início", target: "Start" },
    { source: "Termino", target: "End" },
    { source: "Término", target: "End" },
    { source: "Tipo", target: "Type" },
    { source: "Criado em", target: "Created on" },
    { source: "Cancelar", target: "Cancel" },
    { source: "Criar", target: "Create" },
    { source: "Cadastrar", target: "Create" },
    { source: "Registrar", target: "Record" },
    { source: "Salvar", target: "Save" },
    { source: "Total de registros", target: "Total records" },
    { source: "Página", target: "Page" },
    { source: "Registros por página", target: "Records per page" },
    { source: "Anterior", target: "Previous" },
    { source: "Próxima", target: "Next" },
    { source: "Editar", target: "Edit" },
    { source: "Excluir", target: "Delete" },
    { source: "Atualizar", target: "Update" },
    { source: "Resumo Financeiro", target: "Financial summary" },
    { source: "Leads por Origem", target: "Leads by source" },
    { source: "Status dos Agendamentos", target: "Appointment status" },
    { source: "Nenhum dado disponível", target: "No data available" },
    { source: "Gerencie seus leads e conversões", target: "Manage your leads and conversions" },
    { source: "Novo Lead", target: "New lead" },
    { source: "Buscar por nome ou telefone...", target: "Search by name or phone..." },
    { source: "Filtrar status", target: "Filter status" },
    { source: "Todos", target: "All" },
    { source: "Gerencie acessos, funções e ativação dos usuários vinculados a", target: "Manage access, roles, and status for users linked to" },
    { source: "Usuários da Clínica", target: "Clinic users" },
    { source: "Permissões", target: "Permissions" },
    { source: "Administrador", target: "Administrator" },
    { source: "Profissional", target: "Professional" },
    { source: "Ativo", target: "Active" },
    { source: "Inativo", target: "Inactive" },
    { source: "Usuário", target: "User" },
    { source: "Buscar por nome ou email", target: "Search by name or email" },
    { source: "Todas as funções", target: "All roles" },
    { source: "Todos os status", target: "All statuses" },
    { source: "Administradores", target: "Administrators" },
    { source: "Ativos", target: "Active" },
    { source: "Inativos", target: "Inactive" },
    { source: "Limpar filtros", target: "Clear filters" },
    { source: "Exibindo", target: "Showing" },
    { source: "usuários", target: "users" },
    { source: "usuário", target: "user" },
    { source: "Filtros aplicados a esta lista.", target: "Filters applied to this list." },
    { source: "Carregando usuários...", target: "Loading users..." },
    { source: "Nenhum usuário adicional encontrado para esta clínica.", target: "No additional users found for this clinic." },
    { source: "Nenhum usuário encontrado com os filtros informados.", target: "No users found with the selected filters." },
    { source: "Função", target: "Role" },
    { source: "Ações", target: "Actions" },
    { source: "Você", target: "You" },
    { source: "Ativar usuário", target: "Activate user" },
    { source: "Desativar usuário", target: "Deactivate user" },
    { source: "Reativar usuário", target: "Reactivate user" },
    { source: "Editar", target: "Edit" },
    { source: "Novo usuário", target: "New user" },
    { source: "Novo usuário da clínica", target: "New clinic user" },
    { source: "Novo usuário da clínica", target: "New clinic user" },
    { source: "Nome completo", target: "Full name" },
    { source: "usuario@clínica.com", target: "user@clinic.com" },
    { source: "usuario@clinica.com", target: "user@clinic.com" },
    { source: "Senha inicial", target: "Initial password" },
    { source: "Função", target: "Role" },
    { source: "Salvando...", target: "Saving..." },
    { source: "Criar usuário", target: "Create user" },
    { source: "Criar usuário", target: "Create user" },
    { source: "Acessos da clínica", target: "Clinic access" },
    { source: "Usuário criado com sucesso.", target: "User created successfully." },
    { source: "Usuário atualizado com sucesso.", target: "User updated successfully." },
    { source: "Usuário reativado com sucesso.", target: "User reactivated successfully." },
    { source: "Usuário desativado com sucesso.", target: "User deactivated successfully." },
    { source: "Erro ao carregar usuários", target: "Error loading users" },
    { source: "Erro ao criar usuário", target: "Error creating user" },
    { source: "Erro ao atualizar usuário", target: "Error updating user" },
    { source: "Alterar permissão do usuário", target: "Change user permission" },
    { source: "Confirmar alteração", target: "Confirm change" },
    { source: "Processando...", target: "Processing..." },
    { source: "Confirmar", target: "Confirm" },
    { source: "Meta de faturamento atualizada com sucesso!", target: "Revenue goal updated successfully!" },
    { source: "Erro ao atualizar meta de faturamento", target: "Error updating revenue goal" },
    { source: "Erro ao carregar dados financeiros", target: "Error loading financial data" },
    { source: "Receita excluida com sucesso!", target: "Revenue deleted successfully!" },
    { source: "Receita excluída com sucesso!", target: "Revenue deleted successfully!" },
    { source: "Despesa excluida com sucesso!", target: "Expense deleted successfully!" },
    { source: "Despesa excluída com sucesso!", target: "Expense deleted successfully!" },
    { source: "Erro ao excluir receita", target: "Error deleting revenue" },
    { source: "Erro ao excluir despesa", target: "Error deleting expense" },
    { source: "Visão gerencial para responder quem fatura, onde fatura e quanto sobra.", target: "Management view of revenue, expenses, and margin by area." },
    { source: "Visão gerencial para responder quem fatura, onde fatura e quanto sobra.", target: "Management view of revenue, expenses, and margin by area." },
    { source: "Excluir receita", target: "Delete revenue" },
    { source: "Excluir despesa", target: "Delete expense" },
    { source: "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.", target: "This revenue entry will be deleted and will no longer appear in lists and indicators." },
    { source: "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.", target: "This revenue entry will be deleted and will no longer appear in lists and indicators." },
    { source: "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.", target: "This expense entry will be deleted and will no longer appear in lists and indicators." },
    { source: "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.", target: "This expense entry will be deleted and will no longer appear in lists and indicators." },
    { source: "Receitas", target: "Revenue" },
    { source: "Margem", target: "Margin" },
    { source: "Período inicial", target: "Start period" },
    { source: "Período final", target: "End period" },
    { source: "Mês atual", target: "Current month" },
    { source: "Filtrar por origem", target: "Filter by source" },
    { source: "Filtrar por campanha", target: "Filter by campaign" },
    { source: "Todas as campanhas", target: "All campaigns" },
    { source: "Todas as origens", target: "All sources" },
    { source: "Campanhas, agendamentos e desempenho comercial.", target: "Campaigns, appointments, and sales performance." },
    { source: "Leads no período", target: "Leads in the period" },
    { source: "marcaram consulta", target: "booked an appointment" },
    { source: "Conversão comercial", target: "Sales conversion" },
    { source: "Investimento em campanhas", target: "Campaign spend" },
    { source: "Somatório das campanhas filtradas", target: "Total for filtered campaigns" },
    { source: "Meta de agendamento", target: "Appointment goal" },
    { source: "Progresso:", target: "Progress:" },
    { source: "Pacientes atendidos", target: "Patients seen" },
    { source: "com retorno no período", target: "with return visits in the period" },
    { source: "Taxa de retenção", target: "Retention rate" },
    { source: "NPS pós-tratamento", target: "Post-treatment NPS" },
    { source: "respostas", target: "responses" },
    { source: "Horas ocupadas", target: "Booked hours" },
    { source: "horários ativos", target: "active time slots" },
    { source: "Absenteísmo", target: "No-show rate" },
    { source: "faltas no período", target: "no-shows in the period" },
    { source: "Novas x recorrentes", target: "New vs recurring" },
    { source: "Novas consultas x retornos", target: "New appointments vs follow-ups" },
    { source: "Meta de retencao atualizada com sucesso!", target: "Retention goal updated successfully!" },
    { source: "Meta de retenção atualizada com sucesso!", target: "Retention goal updated successfully!" },
    { source: "Selecione um plano e informe a nota NPS.", target: "Select a plan and enter the NPS score." },
    { source: "NPS atualizado com sucesso!", target: "NPS updated successfully!" },
    { source: "NPS registrado com sucesso!", target: "NPS recorded successfully!" },
    { source: "Link da pesquisa copiado.", target: "Survey link copied." },
    { source: "Não foi possível copiar o link.", target: "Could not copy the link." },
    { source: "Não foi possível copiar o link.", target: "Could not copy the link." },
    { source: "Metas e Períodos", target: "Goals and periods" },
    { source: "Cadastre metas mensais, trimestrais ou personalizadas e acompanhe o realizado de cada janela.", target: "Create monthly, quarterly, or custom goals and track progress for each period." },
    { source: "Novo período", target: "New period" },
    { source: "Períodos ativos", target: "Active periods" },
    { source: "Períodos planejados", target: "Planned periods" },
    { source: "Configuração efetiva atual", target: "Current active configuration" },
    { source: "Carregando períodos e análises...", target: "Loading periods and analysis..." },
    { source: "Nenhum período cadastrado", target: "No periods created" },
    { source: "Configuração do período", target: "Period settings" },
    { source: "Alcance das metas", target: "Goal progress" },
    { source: "Faturamento", target: "Revenue" },
    { source: "Retenção", target: "Retention" },
    { source: "Agendamentos", target: "Appointments" },
    { source: "Pendências de follow-up", target: "Follow-up queue" },
    { source: "Priorize os leads com retorno mais urgente.", target: "Prioritize leads with the most urgent return." },
    { source: "Nenhum follow-up pendente no momento.", target: "There are no pending follow-ups right now." },
    { source: "Abrir lead", target: "Open lead" },
    { source: "Abrir próximo follow-up", target: "Open next follow-up" },
    { source: "Visão Geral", target: "Overview" },
    { source: "Últimas Receitas", target: "Latest revenue" },
    { source: "Últimas Despesas", target: "Latest expenses" },
    { source: "Data", target: "Date" },
    { source: "Não alocada", target: "Unassigned" },
    { source: "Registrar NPS pós-tratamento", target: "Record post-treatment NPS" },
    { source: "Um feedback por plano de tratamento. Se já existir, o registro será atualizado.", target: "One feedback entry per treatment plan. If one already exists, it will be updated." },
    { source: "Cadastre ou avance planos de tratamento para começar a registrar NPS.", target: "Create or advance treatment plans to start recording NPS." },
    { source: "Plano de tratamento", target: "Treatment plan" },
    { source: "Selecione um plano", target: "Select a plan" },
    { source: "Nota NPS (0 a 10)", target: "NPS score (0 to 10)" },
    { source: "Comentário", target: "Comment" },
    { source: "O que o paciente destacou sobre a experiência?", target: "What did the patient highlight about the experience?" },
    { source: "Última atualização em", target: "Last updated on" },
    { source: "Atualizar NPS", target: "Update NPS" },
    { source: "Registrar NPS", target: "Record NPS" },
    { source: "Total de usuários", target: "Total users" },
    { source: "Usuários ativos", target: "Active users" },
    { source: "Administradores ativos", target: "Active administrators" },
    { source: "Somente administradores da clínica podem gerenciar acessos.", target: "Only clinic administrators can manage access." },
    { source: "Seu perfil atual não possui permissão para cadastrar ou editar usuários.", target: "Your current profile does not have permission to create or edit users." },
    { source: "Editar usuário", target: "Edit user" },
    { source: "Nova senha", target: "New password" },
    { source: "Usuário ativo", target: "Active user" },
    { source: "Define se esse usuário pode acessar a clínica.", target: "Defines whether this user can access the clinic." },
    { source: "Salvar alterações", target: "Save changes" },
  ],
  es: [
    { source: "Cl\u00ednicas da plataforma", target: "Cl\u00ednicas de la plataforma" },
    { source: "Busque cl\u00ednicas, confira seus usu\u00e1rios e controle libera\u00e7\u00e3o ou bloqueio operacional.", target: "Busque cl\u00ednicas, revise sus usuarios y controle la liberaci\u00f3n o el bloqueo operativo." },
    { source: "Buscar cl\u00ednica, cidade, usu\u00e1rio ou e-mail", target: "Buscar cl\u00ednica, ciudad, usuario o e-mail" },
    { source: "Carregando cl\u00ednicas...", target: "Cargando cl\u00ednicas..." },
    { source: "Nenhuma cl\u00ednica encontrada com o filtro informado.", target: "No se encontr\u00f3 ninguna cl\u00ednica con el filtro informado." },
    { source: "Ativa", target: "Activa" },
    { source: "Bloqueada", target: "Bloqueada" },
    { source: "Sem e-mail cadastrado", target: "Sin e-mail registrado" },
    { source: "Sem telefone cadastrado", target: "Sin tel\u00e9fono registrado" },
    { source: "Cidade n\u00e3o informada", target: "Ciudad no informada" },
    { source: "usu\u00e1rio(s)", target: "usuario(s)" },
    { source: "ativo(s)", target: "activo(s)" },
    { source: "admin(s)", target: "admin(s)" },
    { source: "Bloquear cl\u00ednica", target: "Bloquear cl\u00ednica" },
    { source: "Liberar cl\u00ednica", target: "Liberar cl\u00ednica" },
    { source: "Usu\u00e1rios cadastrados", target: "Usuários registrados" },
    { source: "Nenhum usu\u00e1rio vinculado a esta cl\u00ednica.", target: "Ning\u00fan usuario vinculado a esta cl\u00ednica." },
    { source: "Bloqueado", target: "Bloqueado" },
    { source: "Pesquisa de experiência", target: "Encuesta de experiencia" },
    { source: "NPS pós-tratamento", target: "NPS pos-tratamiento" },
    { source: "Carregando pesquisa...", target: "Cargando encuesta..." },
    { source: "Esta pesquisa não está disponível.", target: "Esta encuesta no est\u00e1 disponible." },
    { source: "Olá", target: "Hola" },
    { source: "Como foi sua experiência com", target: "C\u00f3mo fue su experiencia con" },
    { source: "no plano", target: "en el plan" },
    { source: "Sua nota", target: "Su puntuaci\u00f3n" },
    { source: "Comentário", target: "Comentário" },
    { source: "Resposta registrada em", target: "Respuesta registrada el" },
    { source: "Obrigado pelo feedback.", target: "Gracias por su opini\u00f3n." },
    { source: "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.", target: "Dé una puntuación de 0 a 10 y, si quiere, deje un comentario para el equipo." },
    { source: "Detalhes do Agendamento", target: "Detalles de la cita" },
    { source: "Campanhas, agendamentos e desempenho comercial.", target: "Campañas, citas y desempeño comercial." },
    { source: "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.", target: "Registre un ingreso vinculado a esta cita, al paciente y al profesional." },
    { source: "Meta mensal de agendamento atualizada!", target: "¡Meta mensual de citas actualizada!" },
    { source: "Intervalo de follow-up atualizado!", target: "¡Intervalo de Follow-up actualizado!" },
    { source: "Status do lead atualizado!", target: "¡Estado del Lead actualizado!" },
    { source: "Erro ao carregar dados de marketing", target: "Error al cargar los datos de marketing" },
    { source: "Erro ao carregar leads", target: "Error al cargar Leads" },
    { source: "Erro ao excluir lead", target: "Error al eliminar Lead" },
    { source: "Lead criado com sucesso!", target: "¡Lead creado con éxito!" },
    { source: "Paciente criado com sucesso!", target: "¡Paciente creado con éxito!" },
    { source: "Receita registrada com sucesso!", target: "¡Ingreso registrado con éxito!" },
    { source: "Selecione uma clínica", target: "Seleccione una clínica" },
    { source: "Selecione uma clínica.", target: "Seleccione una clínica." },
    { source: "Selecione o setor", target: "Seleccione el sector" },
    { source: "Sem campanha", target: "Sin campaña" },
    { source: "Sem setor", target: "Sin sector" },
    { source: "Sem vinculo", target: "Sin vínculo" },
    { source: "Sem vínculo", target: "Sin vínculo" },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais ou agenda do Zen Fisio.", target: "Seleccione la clínica de destino antes de importar pacientes, profesionales o agenda de Zen Fisio." },
    { source: "Importe agendamentos históricos preservando o AgendamentoId, vinculando pacientes pelo id legado e profissionais pelo nome importado anteriormente.", target: "Importe citas históricas preservando el AgendamentoId, vinculando pacientes por id legado y profesionales por el nombre importado anteriormente." },
    { source: "Importar agenda", target: "Importar agenda" },
    { source: "Importando agenda...", target: "Importando agenda..." },
    { source: "Importação da agenda concluída.", target: "Importación de agenda concluida." },
    { source: "Horários inválidos", target: "Horarios inválidos" },
    { source: "Duplicados", target: "Duplicados" },
    { source: "Baixe o CSV para revisar vínculos ausentes, horários inválidos, duplicidades e setores criados.", target: "Descargue el CSV para revisar vínculos ausentes, horarios inválidos, duplicidades y sectores creados." },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda ou financeiro do Zen Fisio.", target: "Seleccione la clínica de destino antes de importar pacientes, profesionales, agenda o financiero de Zen Fisio." },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.", target: "Seleccione la clínica de destino antes de importar pacientes, profesionales, agenda, financiero o historia clínica de Zen Fisio." },
    { source: "Importe receitas, despesas e recebíveis históricos agrupando movimentos do mesmo AgendamentoID e preservando pagamentos parciais.", target: "Importe ingresos, gastos y cuentas por cobrar históricas agrupando movimientos del mismo AgendamentoID y preservando pagos parciales." },
    { source: "Importar financeiro", target: "Importar financiero" },
    { source: "Importando financeiro...", target: "Importando financiero..." },
    { source: "Importação financeira concluída.", target: "Importación financiera concluida." },
    { source: "Preparando importação...", target: "Preparando importación..." },
    { source: "Importando dados...", target: "Importando datos..." },
    { source: "Validando registros...", target: "Validando registros..." },
    { source: "Finalizando importação...", target: "Finalizando importación..." },
    { source: "Importação em andamento", target: "Importación en curso" },
    { source: "Importação finalizada", target: "Importación finalizada" },
    { source: "Importação não concluída", target: "Importación no concluida" },
    { source: "Importação finalizada com sucesso.", target: "Importación finalizada con éxito." },
    { source: "A importação não foi concluída. Tente novamente.", target: "La importación no se completó. Inténtelo de nuevo." },
    { source: "Não foi possível concluir a importação. Tente novamente.", target: "No se pudo completar la importación. Inténtelo de nuevo." },
    { source: "Os dados estão sendo processados.", target: "Los datos se están procesando." },
    { source: "Progresso da importação", target: "Progreso de la importación" },
    { source: "Registros com erro/alerta", target: "Registros con error/alerta" },
    { source: "Nova importação", target: "Nueva importación" },
    { source: "Tentar novamente", target: "Intentar de nuevo" },
    { source: "Aguarde a importação em andamento terminar antes de iniciar outra.", target: "Espere a que termine la importación en curso antes de iniciar otra." },
    { source: "Agrupados", target: "Agrupados" },
    { source: "Baixe o CSV para revisar vínculos ausentes, formas other, agrupamentos e movimentos ignorados.", target: "Descargue el CSV para revisar vínculos ausentes, formas other, agrupaciones y movimientos omitidos." },
    { source: "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.", target: "Descargue el CSV para revisar vínculos ausentes, formas de pago sin correspondencia, agrupaciones y movimientos omitidos." },
    { source: "Selecione o arquivo", target: "Seleccione el archivo" },
    { source: "Avaliações clínicas", target: "Evaluaciones clínicas" },
    { source: "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.", target: "Importe evaluaciones de Zen Fisio como registros legados en Markdown, vinculados al paciente, profesional y cita." },
    { source: "Importar avaliações", target: "Importar evaluaciones" },
    { source: "Importando avaliações...", target: "Importando evaluaciones..." },
    { source: "Evoluções clínicas", target: "Evoluciones clínicas" },
    { source: "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.", target: "Importe evoluciones de Zen Fisio consolidando líneas por atención e ignorando respuestas vacías." },
    { source: "Importar evoluções", target: "Importar evoluciones" },
    { source: "Importando evoluções...", target: "Importando evoluciones..." },
    { source: "Importação de avaliações concluída.", target: "Importación de evaluaciones concluida." },
    { source: "Importação de evoluções concluída.", target: "Importación de evoluciones concluida." },
    { source: "Importação Zen Fisio - pacientes", target: "Importación Zen Fisio - pacientes" },
    { source: "Importação Zen Fisio - profissionais", target: "Importación Zen Fisio - profesionales" },
    { source: "Importação Zen Fisio - agenda", target: "Importación Zen Fisio - agenda" },
    { source: "Importação Zen Fisio - financeiro", target: "Importación Zen Fisio - financiero" },
    { source: "Importação Zen Fisio - prontuário", target: "Importación Zen Fisio - historia clínica" },
    { source: "Últimas ações sensíveis executadas por super admins na plataforma.", target: "Últimas acciones sensibles ejecutadas por super admins en la plataforma." },
    { source: "Sem detalhes adicionais.", target: "Sin detalles adicionales." },
    { source: "Convite emitido para", target: "Invitación enviada a" },
    { source: "Senha redefinida e acesso reativado.", target: "Contraseña restablecida y acceso reactivado." },
    { source: "Senha redefinida mantendo o status atual.", target: "Contraseña restablecida manteniendo el estado actual." },
    { source: "Acesso liberado.", target: "Acceso habilitado." },
    { source: "Acesso revogado.", target: "Acceso revocado." },
    { source: "Registros importados", target: "Registros importados" },
    { source: "ignorados", target: "omitidos" },
    { source: "alertas", target: "alertas" },
    { source: "Ação registrada com sucesso.", target: "Acción registrada correctamente." },
    { source: "Sem agendamento", target: "Sin cita" },
    { source: "Grupos vazios", target: "Grupos vacíos" },
    { source: "Datas inválidas", target: "Fechas inválidas" },
    { source: "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.", target: "Descargue el CSV para revisar citas ausentes, grupos vacíos, duplicidades y fechas inválidas." },
    { source: "Agendamento vinculado", target: "Cita vinculada" },
    { source: "Selecionar agendamento", target: "Seleccionar cita" },
    { source: "Vincular paciente", target: "Vincular paciente" },
    { source: "Vincular profissional", target: "Vincular profesional" },
    { source: "Classificar setor", target: "Asignar sector" },
    { source: "Resultado comercial", target: "Resultado comercial" },
    { source: "Proximo follow-up", target: "Próximo Follow-up" },
    { source: "Próximo follow-up", target: "Próximo Follow-up" },
    { source: "Motivo de não agendamento", target: "Motivo por el que no agendó" },
    { source: "Motivo de não agendamento", target: "Motivo por el que no agendó" },
    { source: "Motivo do cancelamento", target: "Motivo de la cancelación" },
    { source: "Preencha quando o agendamento for cancelado", target: "Complete este campo cuando se cancele la cita" },
    { source: "Gerar Receita", target: "Registrar ingreso" },
    { source: "Agendamento cancelado", target: "Cita cancelada" },
    { source: "Nova Receita", target: "Nuevo ingreso" },
    { source: "Nova Despesa", target: "Nuevo gasto" },
    { source: "Novo Paciente", target: "Nuevo paciente" },
    { source: "Novo Profissional", target: "Nuevo profesional" },
    { source: "Novo Usuário", target: "Nuevo usuario" },
    { source: "Novo Agendamento", target: "Nueva cita" },
    { source: "Novo Lead", target: "Nuevo Lead" },
    { source: "Nova Campanha", target: "Nueva campaña" },
    { source: "Criar campanha", target: "Crear campaña" },
    { source: "Criar Campanha", target: "Crear campaña" },
    { source: "Data do Pagamento", target: "Fecha de pago" },
    { source: "De", target: "Desde" },
    { source: "Até", target: "Hasta" },
    { source: "Competência", target: "Devengo" },
    { source: "Vencimento", target: "Vencimiento" },
    { source: "Plano vinculado", target: "Plan vinculado" },
    { source: "sessoes restantes", target: "sesiones restantes" },
    { source: "sessões restantes", target: "sesiones restantes" },
    { source: "validade até", target: "vigente hasta" },
    { source: "validade até", target: "vigente hasta" },
    { source: "Contato de Emergencia", target: "Contacto de emergencia" },
    { source: "Contato de Emergência", target: "Contacto de emergencia" },
    { source: "Histórico Médico / Observações", target: "Historia clínica / Observaciones" },
    { source: "Histórico Médico / Observações", target: "Historia clínica / Observaciones" },
    { source: "Data de Nascimento", target: "Fecha de nacimiento" },
    { source: "Nome completo", target: "Nombre completo" },
    { source: "Nome completo do responsável", target: "Nombre completo del responsable" },
    { source: "Endereço", target: "Dirección" },
    { source: "Endereco", target: "Dirección" },
    { source: "Logradouro", target: "Calle" },
    { source: "Complemento", target: "Complemento" },
    { source: "Gênero", target: "Género" },
    { source: "Genero", target: "Género" },
    { source: "Masculino", target: "Masculino" },
    { source: "Feminino", target: "Femenino" },
    { source: "Novo", target: "Nuevo" },
    { source: "Nova", target: "Nueva" },
    { source: "Nome", target: "Nombre" },
    { source: "Telefone", target: "Teléfono" },
    { source: "Origem", target: "Origen" },
    { source: "Setor", target: "Sector" },
    { source: "Campanha", target: "Campaña" },
    { source: "Status", target: "Estado" },
    { source: "Observações", target: "Observaciones" },
    { source: "Observações", target: "Observaciones" },
    { source: "Paciente", target: "Paciente" },
    { source: "Pacientes", target: "Pacientes" },
    { source: "Profissional", target: "Profesional" },
    { source: "Profissionais", target: "Profesionales" },
    { source: "Categoria", target: "Categoría" },
    { source: "Pagamento", target: "Pago" },
    { source: "Pago", target: "Pagado" },
    { source: "Não Pago", target: "No pagado" },
    { source: "Descrição", target: "Descripción" },
    { source: "Descrição", target: "Descripción" },
    { source: "Valor", target: "Importe" },
    { source: "Inicio", target: "Inicio" },
    { source: "Início", target: "Inicio" },
    { source: "Termino", target: "Fin" },
    { source: "Término", target: "Fin" },
    { source: "Tipo", target: "Tipo" },
    { source: "Criado em", target: "Creado el" },
    { source: "Cancelar", target: "Cancelar" },
    { source: "Criar", target: "Crear" },
    { source: "Cadastrar", target: "Registrar" },
    { source: "Registrar", target: "Registrar" },
    { source: "Salvar", target: "Guardar" },
    { source: "Total de registros", target: "Total de registros" },
    { source: "Página", target: "Página" },
    { source: "Registros por página", target: "Registros por página" },
    { source: "Anterior", target: "Anterior" },
    { source: "Próxima", target: "Siguiente" },
    { source: "Editar", target: "Editar" },
    { source: "Excluir", target: "Eliminar" },
    { source: "Atualizar", target: "Actualizar" },
    { source: "Resumo Financeiro", target: "Resumen financiero" },
    { source: "Leads por Origem", target: "Leads por origen" },
    { source: "Status dos Agendamentos", target: "Estado de las citas" },
    { source: "Nenhum dado disponível", target: "Sin datos disponibles" },
    { source: "Gerencie seus leads e conversões", target: "Gestione Leads y conversiones" },
    { source: "Novo Lead", target: "Nuevo Lead" },
    { source: "Buscar por nome ou telefone...", target: "Buscar por nombre o teléfono..." },
    { source: "Filtrar status", target: "Filtrar estado" },
    { source: "Todos", target: "Todos" },
    { source: "Gerencie acessos, funções e ativação dos usuários vinculados a", target: "Gestione el acceso, los roles y la activación de los usuarios vinculados a" },
    { source: "Usuários da Clínica", target: "Usuários de la clínica" },
    { source: "Permissões", target: "Permisos" },
    { source: "Administrador", target: "Administrador" },
    { source: "Profissional", target: "Profesional" },
    { source: "Ativo", target: "Activo" },
    { source: "Inativo", target: "Inactivo" },
    { source: "Usuário", target: "Usuário" },
    { source: "Buscar por nome ou email", target: "Buscar por nombre o e-mail" },
    { source: "Todas as funções", target: "Todos los roles" },
    { source: "Todos os status", target: "Todos los estados" },
    { source: "Administradores", target: "Administradores" },
    { source: "Ativos", target: "Activos" },
    { source: "Inativos", target: "Inactivos" },
    { source: "Limpar filtros", target: "Limpiar filtros" },
    { source: "Exibindo", target: "Mostrando" },
    { source: "usuários", target: "usuarios" },
    { source: "usuário", target: "usuario" },
    { source: "Filtros aplicados a esta lista.", target: "Hay filtros aplicados a esta lista." },
    { source: "Carregando usuários...", target: "Cargando usuarios..." },
    { source: "Nenhum usuário adicional encontrado para esta clínica.", target: "No hay usuarios adicionales en esta clínica." },
    { source: "Nenhum usuário encontrado com os filtros informados.", target: "No hay usuarios con los filtros seleccionados." },
    { source: "Função", target: "Rol" },
    { source: "Ações", target: "Acciones" },
    { source: "Você", target: "Tú" },
    { source: "Ativar usuário", target: "Activar usuario" },
    { source: "Desativar usuário", target: "Desactivar usuario" },
    { source: "Reativar usuário", target: "Reactivar usuario" },
    { source: "Novo usuário", target: "Nuevo usuario" },
    { source: "Novo usuário da clínica", target: "Nuevo usuario de la clínica" },
    { source: "Novo usuário da clínica", target: "Nuevo usuario de la clínica" },
    { source: "Nome completo", target: "Nombre completo" },
    { source: "usuario@clínica.com", target: "usuario@clinica.com" },
    { source: "usuario@clinica.com", target: "usuario@clinica.com" },
    { source: "Senha inicial", target: "Contraseña inicial" },
    { source: "Salvando...", target: "Guardando..." },
    { source: "Criar usuário", target: "Crear usuario" },
    { source: "Criar usuário", target: "Crear usuario" },
    { source: "Acessos da clínica", target: "Acceso a la clínica" },
    { source: "Usuário criado com sucesso.", target: "Usuário creado correctamente." },
    { source: "Usuário atualizado com sucesso.", target: "Usuário actualizado correctamente." },
    { source: "Usuário reativado com sucesso.", target: "Usuário reactivado correctamente." },
    { source: "Usuário desativado com sucesso.", target: "Usuário desactivado correctamente." },
    { source: "Erro ao carregar usuários", target: "Error al cargar los usuarios" },
    { source: "Erro ao criar usuário", target: "Error al crear el usuario" },
    { source: "Erro ao atualizar usuário", target: "Error al actualizar el usuario" },
    { source: "Alterar permissão do usuário", target: "Cambiar el rol del usuario" },
    { source: "Confirmar alteração", target: "Confirmar cambio" },
    { source: "Processando...", target: "Procesando..." },
    { source: "Confirmar", target: "Confirmar" },
    { source: "Meta de faturamento atualizada com sucesso!", target: "¡Meta de facturación actualizada con éxito!" },
    { source: "Erro ao atualizar meta de faturamento", target: "Error al actualizar la meta de facturación" },
    { source: "Erro ao carregar dados financeiros", target: "Error al cargar los datos financieros" },
    { source: "Receita excluida com sucesso!", target: "¡Ingreso eliminado con éxito!" },
    { source: "Receita excluída com sucesso!", target: "¡Ingreso eliminado con éxito!" },
    { source: "Despesa excluida com sucesso!", target: "¡Gasto eliminado con éxito!" },
    { source: "Despesa excluída com sucesso!", target: "¡Gasto eliminado con éxito!" },
    { source: "Erro ao excluir receita", target: "Error al eliminar el ingreso" },
    { source: "Erro ao excluir despesa", target: "Error al eliminar el gasto" },
    { source: "Visão gerencial para responder quem fatura, onde fatura e quanto sobra.", target: "Vista de gestión para entender quién genera ingresos, de dónde vienen y cuánto queda." },
    { source: "Visão gerencial para responder quem fatura, onde fatura e quanto sobra.", target: "Vista de gestión para entender quién genera ingresos, de dónde vienen y cuánto queda." },
    { source: "Excluir receita", target: "Eliminar ingreso" },
    { source: "Excluir despesa", target: "Eliminar gasto" },
    { source: "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.", target: "Este ingreso será eliminado y dejará de aparecer en las listas e indicadores." },
    { source: "Essa receita será excluída e deixará de aparecer nas listagens e indicadores.", target: "Este ingreso será eliminado y dejará de aparecer en las listas e indicadores." },
    { source: "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.", target: "Este gasto será eliminado y dejará de aparecer en las listas e indicadores." },
    { source: "Essa despesa será excluída e deixará de aparecer nas listagens e indicadores.", target: "Este gasto será eliminado y dejará de aparecer en las listas e indicadores." },
    { source: "Receitas", target: "Ingresos" },
    { source: "Margem", target: "Margen" },
    { source: "Período inicial", target: "Período inicial" },
    { source: "Período final", target: "Período final" },
    { source: "Mês atual", target: "Mes actual" },
    { source: "Filtrar por origem", target: "Filtrar por origen" },
    { source: "Filtrar por campanha", target: "Filtrar por campaña" },
    { source: "Todas as campanhas", target: "Todas las campañas" },
    { source: "Todas as origens", target: "Todos los orígenes" },
    { source: "Leads no período", target: "Leads en el período" },
    { source: "marcaram consulta", target: "agendaron consulta" },
    { source: "Conversão comercial", target: "Conversión comercial" },
    { source: "Investimento em campanhas", target: "Inversión en campañas" },
    { source: "Somatório das campanhas filtradas", target: "Suma de las campañas filtradas" },
    { source: "Meta de agendamento", target: "Meta de citas" },
    { source: "Progresso:", target: "Progreso:" },
    { source: "Pacientes atendidos", target: "Pacientes atendidos" },
    { source: "com retorno no período", target: "con seguimiento en el período" },
    { source: "Taxa de retenção", target: "Tasa de retención" },
    { source: "NPS pós-tratamento", target: "NPS pos-tratamiento" },
    { source: "respostas", target: "respuestas" },
    { source: "Horas ocupadas", target: "Horas ocupadas" },
    { source: "horários ativos", target: "horarios activos" },
    { source: "Absenteísmo", target: "Ausentismo" },
    { source: "faltas no período", target: "ausencias en el período" },
    { source: "Novas x recorrentes", target: "Nuevas vs recurrentes" },
    { source: "Novas consultas x retornos", target: "Consultas nuevas vs seguimientos" },
    { source: "Meta de retencao atualizada com sucesso!", target: "¡Meta de retención actualizada con éxito!" },
    { source: "Meta de retenção atualizada com sucesso!", target: "¡Meta de retención actualizada con éxito!" },
    { source: "Selecione um plano e informe a nota NPS.", target: "Seleccione un plan e informe la puntuación NPS." },
    { source: "NPS atualizado com sucesso!", target: "¡NPS actualizado con éxito!" },
    { source: "NPS registrado com sucesso!", target: "¡NPS registrado con éxito!" },
    { source: "Link da pesquisa copiado.", target: "Enlace de la encuesta copiado." },
    { source: "Não foi possível copiar o link.", target: "No fue posible copiar el enlace." },
    { source: "Não foi possível copiar o link.", target: "No fue posible copiar el enlace." },
    { source: "Metas e Períodos", target: "Metas y períodos" },
    { source: "Cadastre metas mensais, trimestrais ou personalizadas e acompanhe o realizado de cada janela.", target: "Registre metas mensuales, trimestrales o personalizadas y monitoree el avance de cada período." },
    { source: "Novo período", target: "Nuevo período" },
    { source: "Períodos ativos", target: "Períodos activos" },
    { source: "Períodos planejados", target: "Períodos planificados" },
    { source: "Configuração efetiva atual", target: "Configuración vigente" },
    { source: "Carregando períodos e análises...", target: "Cargando períodos y análisis..." },
    { source: "Nenhum período cadastrado", target: "Ningún período registrado" },
    { source: "Configuração do período", target: "Configuración del período" },
    { source: "Alcance das metas", target: "Avance de las metas" },
    { source: "Faturamento", target: "Facturación" },
    { source: "Retenção", target: "Retención" },
    { source: "Agendamentos", target: "Citas" },
    { source: "Pendências de follow-up", target: "Follow-ups pendientes" },
    { source: "Priorize os leads com retorno mais urgente.", target: "Priorice los Leads que necesitan retorno con mayor urgencia." },
    { source: "Nenhum follow-up pendente no momento.", target: "No hay Follow-ups pendientes en este momento." },
    { source: "Abrir lead", target: "Abrir Lead" },
    { source: "Abrir próximo follow-up", target: "Abrir próximo Follow-up" },
    { source: "Visão Geral", target: "Vista general" },
    { source: "Últimas Receitas", target: "Ingresos recientes" },
    { source: "Últimas Despesas", target: "Gastos recientes" },
    { source: "Data", target: "Fecha" },
    { source: "Não alocada", target: "No asignada" },
    { source: "Registrar NPS pós-tratamento", target: "Registrar NPS pos-tratamiento" },
    { source: "Um feedback por plano de tratamento. Se já existir, o registro será atualizado.", target: "Un registro de feedback por plan de tratamiento. Si ya existe, se actualizará." },
    { source: "Cadastre ou avance planos de tratamento para começar a registrar NPS.", target: "Registre o avance planes de tratamiento para comenzar a registrar NPS." },
    { source: "Plano de tratamento", target: "Plan de tratamiento" },
    { source: "Selecione um plano", target: "Seleccione un plan" },
    { source: "Nota NPS (0 a 10)", target: "Puntuación NPS (0 a 10)" },
    { source: "Comentário", target: "Comentário" },
    { source: "O que o paciente destacou sobre a experiência?", target: "¿Qué destacó el paciente sobre la experiencia?" },
    { source: "Última atualização em", target: "Última actualización el" },
    { source: "Atualizar NPS", target: "Actualizar NPS" },
    { source: "Registrar NPS", target: "Registrar NPS" },
    { source: "Total de usuários", target: "Total de usuarios" },
    { source: "Usuários ativos", target: "Usuários activos" },
    { source: "Administradores ativos", target: "Administradores activos" },
    { source: "Somente administradores da clínica podem gerenciar acessos.", target: "Solo los administradores de la clínica pueden gestionar el acceso." },
    { source: "Seu perfil atual não possui permissão para cadastrar ou editar usuários.", target: "Su perfil actual no tiene permiso para crear o editar usuarios." },
    { source: "Editar usuário", target: "Editar usuario" },
    { source: "Nova senha", target: "Nueva contraseña" },
    { source: "Deixe em branco para manter", target: "Deje este campo en blanco para mantener la actual" },
    { source: "Usuário ativo", target: "Usuário activo" },
    { source: "Define se esse usuário pode acessar a clínica.", target: "Indica si este usuario puede acceder a la clínica." },
    { source: "Salvar alterações", target: "Guardar cambios" },
  ],
  it: [
    { source: "Cl\u00ednicas da plataforma", target: "Cliniche della piattaforma" },
    { source: "Busque cl\u00ednicas, confira seus usu\u00e1rios e controle libera\u00e7\u00e3o ou bloqueio operacional.", target: "Cerca cliniche, controlla i loro utenti e gestisci abilitazione o blocco operativo." },
    { source: "Buscar cl\u00ednica, cidade, usu\u00e1rio ou e-mail", target: "Cerca clinica, città, utente o e-mail" },
    { source: "Carregando cl\u00ednicas...", target: "Caricamento cliniche..." },
    { source: "Nenhuma cl\u00ednica encontrada com o filtro informado.", target: "Nessuna clinica trovata con il filtro indicato." },
    { source: "Ativa", target: "Attiva" },
    { source: "Bloqueada", target: "Bloccata" },
    { source: "Sem e-mail cadastrado", target: "Nessuna e-mail registrata" },
    { source: "Sem telefone cadastrado", target: "Nessun telefono registrato" },
    { source: "Cidade n\u00e3o informada", target: "Città non indicata" },
    { source: "usu\u00e1rio(s)", target: "utente/i" },
    { source: "ativo(s)", target: "attivo/i" },
    { source: "admin(s)", target: "admin" },
    { source: "Bloquear cl\u00ednica", target: "Blocca clinica" },
    { source: "Liberar cl\u00ednica", target: "Abilita clinica" },
    { source: "Usu\u00e1rios cadastrados", target: "Utenti registrati" },
    { source: "Nenhum usu\u00e1rio vinculado a esta cl\u00ednica.", target: "Nessun utente collegato a questa clinica." },
    { source: "Bloqueado", target: "Bloccato" },
    { source: "Pesquisa de experiência", target: "Sondaggio sull'esperienza" },
    { source: "NPS pós-tratamento", target: "NPS post-trattamento" },
    { source: "Carregando pesquisa...", target: "Caricamento sondaggio..." },
    { source: "Esta pesquisa não está disponível.", target: "Questo sondaggio non è disponibile." },
    { source: "Olá", target: "Ciao" },
    { source: "Como foi sua experiência com", target: "Com'è stata la tua esperienza con" },
    { source: "no plano", target: "nel piano" },
    { source: "Sua nota", target: "Il tuo punteggio" },
    { source: "Comentário", target: "Commento" },
    { source: "Resposta registrada em", target: "Risposta registrata il" },
    { source: "Obrigado pelo feedback.", target: "Grazie per il feedback." },
    { source: "Dê uma nota de 0 a 10 e, se quiser, deixe um comentário para a equipe.", target: "Dai un punteggio da 0 a 10 e, se vuoi, lascia un commento per il team." },
    { source: "Leads por Origem", target: "Lead per origine" },
    { source: "Nenhum dado disponível", target: "Nessun dato disponibile" },
    { source: "Pacientes atendidos", target: "Pazienti seguiti" },
    { source: "Sessões por paciente", target: "Sedute per paziente" },
    { source: "Todas as origens", target: "Tutte le origini" },
    { source: "Salvar alterações", target: "Salva modifiche" },
    { source: "Detalhes do Agendamento", target: "Dettagli dell'appuntamento" },
    { source: "Campanhas, agendamentos e desempenho comercial.", target: "Campagne, appuntamenti e risultati commerciali." },
    { source: "Crie uma receita vinculada a este agendamento, ao paciente e ao profissional.", target: "Registra un'entrata collegata a questo appuntamento, al paziente e al professionista." },
    { source: "Meta mensal de agendamento atualizada!", target: "Obiettivo mensile di appuntamenti aggiornato!" },
    { source: "Intervalo de follow-up atualizado!", target: "Intervallo di follow-up aggiornato!" },
    { source: "Status do lead atualizado!", target: "Stato del lead aggiornato!" },
    { source: "Erro ao carregar dados de marketing", target: "Errore nel caricamento dei dati marketing" },
    { source: "Atualizando dados de marketing...", target: "Aggiornamento dati marketing..." },
    { source: "Erro ao carregar leads", target: "Errore durante il caricamento dei lead" },
    { source: "Erro ao excluir lead", target: "Errore durante l'eliminazione del lead" },
    { source: "Lead criado com sucesso!", target: "Lead creato con successo!" },
    { source: "Receita registrada com sucesso!", target: "Entrata registrata con successo." },
    { source: "Despesa registrada com sucesso!", target: "Spesa registrata con successo!" },
    { source: "Selecione uma cl\u00ednica.", target: "Seleziona una clinica." },
    { source: "Selecione a cl\u00ednica", target: "Seleziona la clinica" },
    { source: "Selecione o setor", target: "Seleziona il settore" },
    { source: "Sem campanha", target: "Nessuna campagna" },
    { source: "Sem setor", target: "Nessun settore" },
    { source: "Sem vinculo", target: "Senza collegamento" },
    { source: "Sem v\u00ednculo", target: "Senza collegamento" },
    { source: "Selecione a clínica de destino antes de importar pacientes, profissionais, agenda, financeiro ou prontuário do Zen Fisio.", target: "Seleziona la clinica di destinazione prima di importare pazienti, professionisti, agenda, dati finanziari o cartella clinica da Zen Fisio." },
    { source: "Importar financeiro", target: "Importa dati finanziari" },
    { source: "Importando financeiro...", target: "Importazione dati finanziari..." },
    { source: "Importação financeira concluída.", target: "Importazione finanziaria completata." },
    { source: "Preparando importação...", target: "Preparazione importazione..." },
    { source: "Importando dados...", target: "Importazione dati..." },
    { source: "Validando registros...", target: "Verifica dei record..." },
    { source: "Finalizando importação...", target: "Completamento importazione..." },
    { source: "Importação em andamento", target: "Importazione in corso" },
    { source: "Importação finalizada", target: "Importazione completata" },
    { source: "Importação não concluída", target: "Importazione non completata" },
    { source: "Importação finalizada com sucesso.", target: "Importazione completata con successo." },
    { source: "A importação não foi concluída. Tente novamente.", target: "L'importazione non è stata completata. Riprova." },
    { source: "Não foi possível concluir a importação. Tente novamente.", target: "Non è stato possibile completare l'importazione. Riprova." },
    { source: "Os dados estão sendo processados.", target: "I dati sono in elaborazione." },
    { source: "Progresso da importação", target: "Avanzamento importazione" },
    { source: "Registros com erro/alerta", target: "Record con errore/avviso" },
    { source: "Nova importação", target: "Nuova importazione" },
    { source: "Tentar novamente", target: "Riprova" },
    { source: "Aguarde a importação em andamento terminar antes de iniciar outra.", target: "Attendi la fine dell'importazione in corso prima di avviarne un'altra." },
    { source: "Baixe o CSV para revisar vínculos ausentes, formas de pagamento sem correspondência, agrupamentos e movimentos ignorados.", target: "Scarica il CSV per rivedere collegamenti mancanti, metodi di pagamento senza corrispondenza, raggruppamenti e movimenti ignorati." },
    { source: "Selecione o arquivo", target: "Seleziona il file" },
    { source: "Avaliações clínicas", target: "Valutazioni cliniche" },
    { source: "Importe avaliações do Zen Fisio como registros legados em Markdown, vinculados ao paciente, profissional e agendamento.", target: "Importa le valutazioni di Zen Fisio come record legacy in Markdown, collegati al paziente, al professionista e all'appuntamento." },
    { source: "Importar avaliações", target: "Importa valutazioni" },
    { source: "Importando avaliações...", target: "Importazione valutazioni..." },
    { source: "Evoluções clínicas", target: "Evoluzioni cliniche" },
    { source: "Importe evoluções do Zen Fisio consolidando linhas por atendimento e ignorando respostas vazias.", target: "Importa le evoluzioni di Zen Fisio consolidando le righe per visita e ignorando le risposte vuote." },
    { source: "Importar evoluções", target: "Importa evoluzioni" },
    { source: "Importando evoluções...", target: "Importazione evoluzioni..." },
    { source: "Importação de avaliações concluída.", target: "Importazione valutazioni completata." },
    { source: "Importação de evoluções concluída.", target: "Importazione evoluzioni completata." },
    { source: "Importação Zen Fisio - pacientes", target: "Importazione Zen Fisio - pazienti" },
    { source: "Importação Zen Fisio - profissionais", target: "Importazione Zen Fisio - professionisti" },
    { source: "Importação Zen Fisio - agenda", target: "Importazione Zen Fisio - agenda" },
    { source: "Importação Zen Fisio - financeiro", target: "Importazione Zen Fisio - dati finanziari" },
    { source: "Importação Zen Fisio - prontuário", target: "Importazione Zen Fisio - cartella clinica" },
    { source: "Últimas ações sensíveis executadas por super admins na plataforma.", target: "Ultime azioni sensibili eseguite dai super admin sulla piattaforma." },
    { source: "Sem detalhes adicionais.", target: "Nessun dettaglio aggiuntivo." },
    { source: "Convite emitido para", target: "Invito inviato a" },
    { source: "Senha redefinida e acesso reativado.", target: "Password reimpostata e accesso riattivato." },
    { source: "Senha redefinida mantendo o status atual.", target: "Password reimpostata mantenendo lo stato attuale." },
    { source: "Acesso liberado.", target: "Accesso abilitato." },
    { source: "Acesso revogado.", target: "Accesso revocato." },
    { source: "Registros importados", target: "Record importati" },
    { source: "ignorados", target: "ignorati" },
    { source: "alertas", target: "avvisi" },
    { source: "Ação registrada com sucesso.", target: "Azione registrata correttamente." },
    { source: "Sem agendamento", target: "Senza appuntamento" },
    { source: "Grupos vazios", target: "Gruppi vuoti" },
    { source: "Datas inválidas", target: "Date non valide" },
    { source: "Baixe o CSV para revisar agendamentos ausentes, grupos vazios, duplicidades e datas inválidas.", target: "Scarica il CSV per rivedere appuntamenti mancanti, gruppi vuoti, duplicati e date non valide." },
    { source: "Agendamento vinculado", target: "Appuntamento collegato" },
    { source: "Selecionar agendamento", target: "Seleziona appuntamento" },
    { source: "Vincular paciente", target: "Collega paziente" },
    { source: "Vincular profissional", target: "Collega professionista" },
    { source: "Classificar setor", target: "Assegna settore" },
    { source: "Resultado comercial", target: "Esito commerciale" },
    { source: "Proximo follow-up", target: "Prossimo follow-up" },
    { source: "Pr\u00f3ximo follow-up", target: "Prossimo follow-up" },
    { source: "Motivo de não agendamento", target: "Motivo della mancata prenotazione" },
    { source: "Motivo de n\u00e3o agendamento", target: "Motivo della mancata prenotazione" },
    { source: "Motivo do cancelamento", target: "Motivo della cancellazione" },
    { source: "Preencha quando o agendamento for cancelado", target: "Compila quando l'appuntamento viene cancellato" },
    { source: "Gerar Receita", target: "Registra entrata" },
    { source: "Nova Receita", target: "Nuova entrata" },
    { source: "Nova Despesa", target: "Nuova spesa" },
    { source: "Data do Pagamento", target: "Data di pagamento" },
    { source: "De", target: "Da" },
    { source: "At\u00e9", target: "Fino a" },
    { source: "Compet\u00eancia", target: "Competenza" },
    { source: "Vencimento", target: "Scadenza" },
    { source: "Data de Nascimento", target: "Data di nascita" },
    { source: "Nome completo", target: "Nome completo" },
    { source: "Nome completo do respons\u00e1vel", target: "Nome completo del responsabile" },
    { source: "Endereco", target: "Indirizzo" },
    { source: "Genero", target: "Genere" },
    { source: "Observações", target: "Osservazioni" },
    { source: "Novo", target: "Nuovo" },
    { source: "Nova", target: "Nuova" },
    { source: "Criar usuário", target: "Crea utente" },
    { source: "Criar usu\u00e1rio", target: "Crea utente" },
    { source: "Novo usu\u00e1rio", target: "Nuovo utente" },
    { source: "Novo usu\u00e1rio da cl\u00ednica", target: "Nuovo utente della clinica" },
    { source: "Novo usu\u00e1rio da cl\u00ednica", target: "Nuovo utente della clinica" },
    { source: "Usu\u00e1rios da Cl\u00ednica", target: "Utenti della clinica" },
    { source: "Usu\u00e1rio criado com sucesso.", target: "Utente creato con successo." },
    { source: "Usu\u00e1rio atualizado com sucesso.", target: "Utente aggiornato con successo." },
    { source: "Usu\u00e1rio reativado com sucesso.", target: "Utente riattivato con successo." },
    { source: "Usu\u00e1rio desativado com sucesso.", target: "Utente disattivato con successo." },
    { source: "Erro ao carregar usu\u00e1rios", target: "Errore durante il caricamento degli utenti" },
    { source: "Erro ao criar usu\u00e1rio", target: "Errore durante la creazione dell'utente" },
    { source: "Erro ao atualizar usu\u00e1rio", target: "Errore durante l'aggiornamento dell'utente" },
    { source: "Alterar permiss\u00e3o do usu\u00e1rio", target: "Modifica i permessi dell'utente" },
    { source: "Confirmar altera\u00e7\u00e3o", target: "Conferma modifica" },
    { source: "Gerencie seus leads e convers\u00f5es", target: "Gestisci lead e conversioni" },
    { source: "Buscar por nome ou telefone...", target: "Cerca per nome o telefono..." },
    { source: "Filtrar status", target: "Filtra stato" },
    { source: "Todos", target: "Tutti" },
    { source: "todos", target: "tutti" },
    { source: "Buscar por nome ou email", target: "Cerca per nome o e-mail" },
    { source: "Todas as fun\u00e7\u00f5es", target: "Tutti i ruoli" },
    { source: "Todos os status", target: "Tutti gli stati" },
    { source: "Administradores", target: "Amministratori" },
    { source: "Ativos", target: "Attivi" },
    { source: "ativos", target: "attivi" },
    { source: "Inativos", target: "Inattivi" },
    { source: "inativos", target: "inattivi" },
    { source: "Limpar filtros", target: "Cancella filtri" },
    { source: "Filtros aplicados a esta lista.", target: "Filtri applicati a questo elenco." },
    { source: "Exibindo", target: "Visualizzazione" },
    { source: "usu\u00e1rios", target: "utenti" },
    { source: "usu\u00e1rio", target: "utente" },
    { source: "Carregando usu\u00e1rios...", target: "Caricamento utenti..." },
    { source: "Nenhum usu\u00e1rio adicional encontrado para esta cl\u00ednica.", target: "Nessun altro utente trovato per questa clinica." },
    { source: "Nenhum usu\u00e1rio encontrado com os filtros informados.", target: "Nessun utente trovato con i filtri selezionati." },
    { source: "Voc\u00ea", target: "Tu" },
    { source: "Ativar usu\u00e1rio", target: "Attiva utente" },
    { source: "Desativar usu\u00e1rio", target: "Disattiva utente" },
    { source: "Reativar usu\u00e1rio", target: "Riattiva utente" },
    { source: "Senha inicial", target: "Password iniziale" },
    { source: "M\u00ednimo 6 caracteres", target: "Minimo 6 caratteri" },
    { source: "Minimo 6 caracteres", target: "Minimo 6 caratteri" },
    { source: "Nova senha", target: "Nuova password" },
    { source: "Deixe em branco para manter", target: "Lascia vuoto per mantenere quella attuale" },
    { source: "Editar usu\u00e1rio", target: "Modifica utente" },
    { source: "Usu\u00e1rio ativo", target: "Utente attivo" },
    { source: "usuario@cl\u00ednica.com", target: "utente@clinica.com" },
    { source: "usuario@clinica.com", target: "utente@clinica.com" },
    { source: "Acessos da cl\u00ednica", target: "Accessi della clinica" },
    { source: "Meta de faturamento atualizada com sucesso!", target: "Obiettivo di fatturato aggiornato con successo!" },
    { source: "Erro ao atualizar meta de faturamento", target: "Errore durante l'aggiornamento dell'obiettivo di fatturato" },
    { source: "Erro ao carregar dados financeiros", target: "Errore durante il caricamento dei dati finanziari" },
    { source: "Receita excluida com sucesso!", target: "Entrata eliminata con successo." },
    { source: "Receita exclu\u00edda com sucesso!", target: "Entrata eliminata con successo." },
    { source: "Despesa excluida com sucesso!", target: "Spesa eliminata con successo." },
    { source: "Despesa exclu\u00edda com sucesso!", target: "Spesa eliminata con successo." },
    { source: "Erro ao excluir receita", target: "Errore nell'eliminazione dell'entrata" },
    { source: "Erro ao excluir despesa", target: "Errore durante l'eliminazione della spesa" },
    { source: "Excluir receita", target: "Elimina entrata" },
    { source: "Excluir despesa", target: "Elimina spesa" },
    { source: "Receitas", target: "Ricavi" },
    { source: "Margem", target: "Margine" },
    { source: "Filtrar por origem", target: "Filtra per origine" },
    { source: "Filtrar por campanha", target: "Filtra per campagna" },
    { source: "marcaram consulta", target: "hanno prenotato una visita" },
    { source: "Custo por novo paciente no per\u00edodo", target: "Costo per nuovo paziente nel periodo" },
    { source: "Receita m\u00e9dia por paciente no per\u00edodo", target: "Ricavi medi per paziente nel periodo" },
    { source: "Investimento em campanhas", target: "Investimento campagne" },
    { source: "Somat\u00f3rio das campanhas filtradas", target: "Totale delle campagne filtrate" },
    { source: "Defina a meta da cl\u00ednica e acompanhe o progresso do m\u00eas atual.", target: "Definisci l'obiettivo della clinica e monitora l'avanzamento del mese corrente." },
    { source: "agendamentos v\u00e1lidos no per\u00edodo atual.", target: "appuntamenti validi nel periodo corrente." },
    { source: "Campanha removida da gest\u00e3o ativa.", target: "Campagna rimossa dalla gestione attiva." },
    { source: "Nenhum dado setorial no per\u00edodo selecionado.", target: "Nessun dato settoriale nel periodo selezionato." },
    { source: "Converter em paciente", target: "Converti in paziente" },
    { source: "Agendar consulta", target: "Prenota visita" },
    { source: "Editar Lead", target: "Modifica lead" },
    { source: "Excluir lead", target: "Elimina lead" },
    { source: "O cadastro do paciente ser\u00e1 criado a partir deste lead e a convers\u00e3o ficar\u00e1 registrada no funil.", target: "La scheda paziente sarà creata da questo Lead e la conversione sarà registrata nel Funnel." },
    { source: "O agendamento ser\u00e1 criado usando o paciente j\u00e1 convertido a partir deste lead.", target: "L'appuntamento verrà creato usando il paziente già convertito da questo Lead." },
    { source: "Esse lead ser\u00e1 removido permanentemente do cadastro.", target: "Questo Lead sarà rimosso definitivamente dall'anagrafica." },
    { source: "Pre\u00e7o", target: "Prezzo" },
    { source: "Conflito de hor\u00e1rio", target: "Conflitto di orario" },
    { source: "J\u00e1 est\u00e1 em tratamento", target: "Già in trattamento" },
    { source: "Indica\u00e7\u00e3o", target: "Passaparola" },
    { source: "P\u00fablico alvo", target: "Pubblico di riferimento" },
    { source: "Notifica\u00e7\u00f5es", target: "Notifiche" },
    { source: "Selecione um plano e informe a nota NPS.", target: "Seleziona un piano e inserisci il punteggio NPS." },
    { source: "NPS atualizado com sucesso!", target: "NPS aggiornato con successo!" },
    { source: "NPS registrado com sucesso!", target: "NPS registrato con successo!" },
    { source: "Link da pesquisa copiado.", target: "Link del questionario copiato." },
    { source: "Não foi possível copiar o link.", target: "Non è stato possibile copiare il link." },
    { source: "N\u00e3o foi poss\u00edvel copiar o link.", target: "Non è stato possibile copiare il link." },
    { source: "Metas e Per\u00edodos", target: "Obiettivi e periodi" },
    { source: "Novo per\u00edodo", target: "Nuovo periodo" },
    { source: "Per\u00edodos ativos", target: "Periodi attivi" },
    { source: "Per\u00edodos planejados", target: "Periodi pianificati" },
    { source: "Configura\u00e7\u00e3o efetiva atual", target: "Configurazione attiva corrente" },
    { source: "Carregando per\u00edodos e an\u00e1lises...", target: "Caricamento di periodi e analisi..." },
    { source: "Nenhum per\u00edodo cadastrado", target: "Nessun periodo registrato" },
    { source: "Configura\u00e7\u00e3o do per\u00edodo", target: "Configurazione del periodo" },
    { source: "Alcance das metas", target: "Avanzamento degli obiettivi" },
    { source: "Pend\u00eancias de follow-up", target: "Follow-up in sospeso" },
    { source: "Priorize os leads com retorno mais urgente.", target: "Dai priorità ai Lead da ricontattare con maggiore urgenza." },
    { source: "Nenhum follow-up pendente no momento.", target: "Nessun Follow-up in sospeso al momento." },
    { source: "Abrir lead", target: "Apri lead" },
    { source: "Abrir pr\u00f3ximo follow-up", target: "Apri prossimo follow-up" },
    { source: "Registrar NPS p\u00f3s-tratamento", target: "Registra NPS post-trattamento" },
    { source: "Um feedback por plano de tratamento. Se j\u00e1 existir, o registro ser\u00e1 atualizado.", target: "Un solo feedback per piano di trattamento. Se esiste gi\u00e0, verr\u00e0 aggiornato." },
    { source: "Cadastre ou avance planos de tratamento para come\u00e7ar a registrar NPS.", target: "Registra o porta avanti i piani di trattamento per iniziare a raccogliere l'NPS." },
    { source: "Plano de tratamento", target: "Piano di trattamento" },
    { source: "Selecione um plano", target: "Seleziona un piano" },
    { source: "Nota NPS (0 a 10)", target: "Punteggio NPS (0-10)" },
    { source: "Coment\u00e1rio", target: "Commento" },
    { source: "O que o paciente destacou sobre a experi\u00eancia?", target: "Che cosa ha evidenziato il paziente dell'esperienza?" },
    { source: "\u00daltima atualiza\u00e7\u00e3o em", target: "Ultimo aggiornamento il" },
    { source: "Atualizar NPS", target: "Aggiorna NPS" },
    { source: "Registrar NPS", target: "Registra NPS" },
    { source: "Total de usu\u00e1rios", target: "Totale utenti" },
    { source: "Usu\u00e1rios ativos", target: "Utenti attivi" },
    { source: "Administradores ativos", target: "Amministratori attivi" },
    { source: "Somente administradores da cl\u00ednica podem gerenciar acessos.", target: "Solo gli amministratori della clinica possono gestire gli accessi." },
    { source: "Seu perfil atual n\u00e3o possui permiss\u00e3o para cadastrar ou editar usu\u00e1rios.", target: "Il tuo profilo attuale non dispone dei permessi per creare o modificare utenti." },
    { source: "Define se esse usu\u00e1rio pode acessar a cl\u00ednica.", target: "Definisce se l'utente pu\u00f2 accedere alla clinica." },
  ],
};

function translateCore(value: string, language: AppLanguage): string {
  const normalizedValue = normalizeLookupValue(value);

  if (language === DEFAULT_LANGUAGE) {
    return normalizedValue;
  }

  const exactTranslation = findExactTranslation(
    normalizedValue,
    language as Exclude<AppLanguage, "pt-BR">,
  );
  if (exactTranslation) {
    return exactTranslation;
  }

  for (const rule of PATTERN_TRANSLATIONS) {
    if (rule.pattern.test(normalizedValue)) {
      return normalizedValue.replace(rule.pattern, rule.replacements[language]);
    }
  }

  const glossaryTranslation = translateWithGlossary(
    normalizedValue,
    language as Exclude<AppLanguage, "pt-BR">,
  );
  if (glossaryTranslation !== normalizedValue) {
    return glossaryTranslation;
  }

  return normalizedValue;
}

function findExactTranslation(
  value: string,
  language: Exclude<AppLanguage, "pt-BR">,
): string | undefined {
  const directMatch = EXACT_TRANSLATIONS[language][value];
  if (directMatch) {
    return directMatch;
  }

  for (const [sourceValue, translatedValue] of Object.entries(EXACT_TRANSLATIONS[language])) {
    if (normalizeLookupValue(sourceValue) === value) {
      return translatedValue;
    }
  }

  return undefined;
}

function normalizeLookupValue(value: string): string {
  return value;
}

function translateWithGlossary(
  value: string,
  language: Exclude<AppLanguage, "pt-BR">,
): string {
  return GLOSSARY_TRANSLATIONS[language]
    .slice()
    .sort((left, right) => right.source.length - left.source.length)
    .reduce((translatedValue, entry) => replaceGlossaryEntry(translatedValue, entry), value);
}

function replaceGlossaryEntry(value: string, entry: GlossaryEntry): string {
  const normalizedSource = normalizeLookupValue(entry.source);
  const escapedSource = escapeRegExp(normalizedSource);
  const needsBoundaries = /[\p{L}\p{N}]$/u.test(normalizedSource) && /^[\p{L}\p{N}]/u.test(normalizedSource);
  const pattern = needsBoundaries
    ? new RegExp(`(^|[^\\p{L}\\p{N}])(${escapedSource})(?=$|[^\\p{L}\\p{N}])`, "gu")
    : new RegExp(escapedSource, "gu");

  if (!needsBoundaries) {
    return value.replace(pattern, entry.target);
  }

  return value.replace(pattern, (match, prefix: string) => {
    const actualPrefix = match.startsWith(prefix) ? prefix : "";
    return `${actualPrefix}${entry.target}`;
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function translateStaticText(
  value: string,
  language: AppLanguage = getCurrentLanguage(),
  parameters?: TranslationParameters,
): string {
  if (!value) {
    return value;
  }

  const match = value.match(/^(\s*)(.*?)(\s*)$/su);
  if (!match) {
    return value;
  }

  const [, leadingWhitespace, coreValue, trailingWhitespace] = match;
  if (!coreValue) {
    return value;
  }

  let translatedValue = translateCore(coreValue, language);
  for (const [key, parameterValue] of Object.entries(parameters ?? {})) {
    translatedValue = translatedValue.split(`{${key}}`).join(String(parameterValue));
  }

  return `${leadingWhitespace}${translatedValue}${trailingWhitespace}`;
}
